
import React, { useState, useEffect, useMemo, useContext, createContext, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell } from 'recharts';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import notificationSound from './assets/Notification.mp3';
import DataManagementPanel from './components/DataManagementPanel';

const modelViewStateCache = new Map();
let modelViewerElementPromise = null;

function ensureModelViewerElementLoaded() {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.customElements?.get('model-viewer')) return Promise.resolve();
  if (!modelViewerElementPromise) {
    modelViewerElementPromise = import('@google/model-viewer').catch((error) => {
      modelViewerElementPromise = null;
      throw error;
    });
  }
  return modelViewerElementPromise;
}

const Icon = {
  Dashboard: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
  ),
  Package: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27,6.96 12,12.01 20.73,6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  ),
  ShoppingCart: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57L23 6H6"/>
    </svg>
  ),
  Tag: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>
    </svg>
  ),
  Palette: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="13.5" cy="6.5" r=".5"/><circle cx="17.5" cy="10.5" r=".5"/><circle cx="8.5" cy="7.5" r=".5"/><circle cx="6.5" cy="12.5" r=".5"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>
    </svg>
  ),
  Download: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  ),
  Copy: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  ),
  Link: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.07 0l2.12-2.12a5 5 0 0 0-7.07-7.07L11 4.93"/><path d="M14 11a5 5 0 0 0-7.07 0L4.81 13.12a5 5 0 0 0 7.07 7.07L13 19.07"/>
    </svg>
  ),
  Share: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.59 13.51l6.83 3.98"/><path d="M15.41 6.51L8.59 10.49"/>
    </svg>
  ),
  Scissors: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M20 4L8.12 15.88"/><path d="M14.47 14.48L20 20"/><path d="M8.12 8.12L12 12"/>
    </svg>
  ),
  Crop: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2v14a2 2 0 0 0 2 2h14"/><path d="M18 22V8a2 2 0 0 0-2-2H2"/>
    </svg>
  ),
  Settings: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  Plus: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  ),
  Edit: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  ),
  Trash: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3,6 5,6 21,6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    </svg>
  ),
  Eye: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ),
  Activity: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  ),
  Search: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
    </svg>
  ),
  Rocket: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 13l4 4M14 3c4.5 0 7 2.5 7 7 0 2.2-1.1 4.9-3 6.8-1.9 1.9-4.6 3-6.8 3-4.5 0-7-2.5-7-7 0-2.2 1.1-4.9 3-6.8C8.1 4.1 10.8 3 13 3Z"/>
      <path d="M9 15l6-6M12 12l3 3"/>
    </svg>
  ),
  Megaphone: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 11v2a3 3 0 0 0 3 3h3l7 4V4l-7 4H6a3 3 0 0 0-3 3Z"/>
      <path d="M14 7v10"/>
    </svg>
  ),
  Folder: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z"/>
    </svg>
  ),
  LogOut: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16,17 21,12 16,7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  User: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Users: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  Loading: ({ className = '', ...props }) => (
    <svg
      {...props}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={("animate-spin " + className).trim()}
    >
      <path d="M21 12a9 9 0 11-6.219-8.56"/>
    </svg>
  ),
  Upload: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17,8 12,3 7,8"/><line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  ),
  Alert: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  X: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Calendar: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  Percent: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/>
    </svg>
  ),
  Mail: (props) => (
    <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
    </svg>
  )
};

const API_BASE = '/shopapi';
const ADMIN_NOTIFICATION_SOUND_KEY = 'shopstarter_admin_notification_sound';
const DEFAULT_ADMIN_NOTIFICATION_SOUND = 'classic';
const PRODUCT_IMAGE_PLACEHOLDER_URL = `${API_BASE}/placeholder/120/120`;
const PRODUCT_IMAGE_INLINE_FALLBACK = `data:image/svg+xml;utf8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120" role="img" aria-label="No image"><rect width="120" height="120" fill="#f3f4f6"/><rect x="1" y="1" width="118" height="118" fill="none" stroke="#d1d5db" stroke-width="2"/><g fill="#9ca3af"><circle cx="42" cy="44" r="10"/><path d="M18 86l24-24 18 16 18-12 24 20z"/></g><text x="60" y="105" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="12" fill="#6b7280">No image</text></svg>')}`;

function applyFaviconHref(href) {
  try {
    if (typeof document === 'undefined') return;
    const rawHref = String(href || '/logo.svg').trim() || '/logo.svg';
    const finalHref = resolveMediaUrl(rawHref);
    const lower = finalHref.toLowerCase();
    let iconType = '';
    if (lower.endsWith('.ico') || lower.includes('.ico?')) iconType = 'image/x-icon';
    else if (lower.endsWith('.svg') || lower.includes('.svg?')) iconType = 'image/svg+xml';
    else if (lower.endsWith('.png') || lower.includes('.png?')) iconType = 'image/png';
    else if (lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.includes('.jpg?') || lower.includes('.jpeg?')) iconType = 'image/jpeg';
    const ensureLink = (relValue) => {
      let el = document.head.querySelector(`link[rel="${relValue}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', relValue);
        document.head.appendChild(el);
      }
      el.setAttribute('href', finalHref);
      if (iconType) el.setAttribute('type', iconType);
      else el.removeAttribute('type');
    };
    ensureLink('icon');
    ensureLink('shortcut icon');
  } catch {}
}

function applyProductImageFallback(event) {
  const img = event?.currentTarget;
  if (!img) return;
  const src = String(img.getAttribute('src') || '').trim();
  if (src && src !== PRODUCT_IMAGE_PLACEHOLDER_URL && img.dataset.placeholderTried !== '1') {
    img.dataset.placeholderTried = '1';
    img.src = PRODUCT_IMAGE_PLACEHOLDER_URL;
    return;
  }
  img.onerror = null;
  img.src = PRODUCT_IMAGE_INLINE_FALLBACK;
}

function readAdminNotificationSound() {
  try {
    const raw = String(localStorage.getItem(ADMIN_NOTIFICATION_SOUND_KEY) || '').trim().toLowerCase();
    if (['classic', 'custom', 'beep', 'off'].includes(raw)) return raw;
  } catch {}
  return DEFAULT_ADMIN_NOTIFICATION_SOUND;
}

function getStorefrontBaseUrl() {
  try {
    const loc = window.location;
    const protocol = loc.protocol;
    const hostname = loc.hostname;
    const port = String(loc.port || '');
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      if (port === '3001' || port === '3002') return `${protocol}//${hostname}:3000`;
    }
    if (hostname.startsWith('dosh-hazar.')) {
      return `${protocol}//${hostname.replace(/^dosh-hazar\./, '')}`;
    }
    return `${protocol}//${loc.host}`;
  } catch {
    return '';
  }
}

function resolveMediaUrl(u) {
  if (!u) return '';
  let s = String(u).trim();
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    s = s.slice(1, -1).trim();
  }
  s = s.replace(/\\/g, '/');
  if (/^https?:\/\//i.test(s) || s.startsWith('data:') || s.startsWith('blob:')) return s;
  if (s.startsWith('/shopapi/uploads/')) return s;
  if (s.startsWith('/uploads/')) return `/shopapi${s}`;
  if (s.startsWith('uploads/')) return `/shopapi/${s}`;
  if (!s.startsWith('/')) return `/shopapi/uploads/${s}`;
  return s;
}

function parseStringJsonArray(input) {
  try {
    const raw = Array.isArray(input) ? input : JSON.parse(input || '[]');
    return Array.isArray(raw) ? raw.map((entry) => String(entry || '').trim()).filter(Boolean) : [];
  } catch {
    return [];
  }
}

function parseProductVariants(input) {
  try {
    const raw = Array.isArray(input) ? input : JSON.parse(input || '[]');
    if (!Array.isArray(raw)) return [];
    return raw
      .map((entry, index) => ({
        id: String(entry?.id || `variant_${index + 1}`).trim(),
        label: String(entry?.label || '').trim(),
        sku: String(entry?.sku || '').trim(),
        description: String(entry?.description || '').trim(),
        price: Math.max(0, Number(entry?.price ?? entry?.basePrice) || 0),
        basePrice: Math.max(0, Number(entry?.basePrice ?? entry?.price) || 0),
        originalPrice: Number(entry?.originalPrice) > Number(entry?.price ?? entry?.basePrice)
          ? Number(entry.originalPrice)
          : null,
        stock: Math.max(0, parseInt(entry?.stock, 10) || 0),
        active: entry?.active !== false && entry?.active !== 0 && entry?.active !== 'false',
        img: String(entry?.img || entry?.imageUrl || '').trim(),
      }))
      .filter((entry) => entry.label || entry.sku || entry.id);
  } catch {
    return [];
  }
}

function summarizeProductForAdmin(product) {
  const gallery = parseStringJsonArray(product?.images);
  const variants = parseProductVariants(product?.variants);
  const activeVariants = variants.filter((variant) => variant.active !== false);
  const basePrice = Number(product?.baseDisplayPrice ?? product?.basePrice ?? product?.price) || 0;
  const baseOriginalPrice = Number(product?.baseOriginalPrice) > basePrice ? Number(product.baseOriginalPrice) : null;
  const baseStock = Number(product?.baseStock ?? product?.base_stock ?? product?.stock) || 0;
  const hideBaseModel = product?.hideBaseModel === true || product?.hide_base_model === 1 || product?.hide_base_model === true;
  const displayCandidate = [
    ...(!hideBaseModel ? [{ price: basePrice, originalPrice: baseOriginalPrice }] : []),
    ...activeVariants.map((variant) => ({
      price: Number(variant.price) || 0,
      originalPrice: Number(variant.originalPrice) || null
    }))
  ].reduce((best, current) => (
    !best || Number(current.price) < Number(best.price) ? current : best
  ), null);
  const displayPrice = Number(displayCandidate?.price ?? basePrice) || 0;
  const displayStock = activeVariants.length > 0
    ? (!hideBaseModel ? baseStock : 0) + activeVariants.reduce((sum, variant) => sum + (Number(variant.stock) || 0), 0)
    : baseStock;
  const title = String(product?.title || '').trim() || 'Untitled product';
  const category = String(product?.category || '').trim() || 'Uncategorized';
  const color = String(product?.color || '').trim() || '-';
  const primaryImage = String(product?.img || '').trim() || activeVariants[0]?.img || gallery[0] || PRODUCT_IMAGE_PLACEHOLDER_URL;
  return {
    ...product,
    images: gallery,
    variants,
    hideBaseModel: product?.hideBaseModel === true || product?.hide_base_model === 1 || product?.hide_base_model === true,
    _title: title,
    _category: category,
    _color: color,
    _primaryImage: primaryImage,
    _displayPrice: displayPrice,
    _displayOriginalPrice: Number(displayCandidate?.originalPrice) > displayPrice ? Number(displayCandidate.originalPrice) : null,
    _displayStock: displayStock,
    _variantCount: activeVariants.length,
    _isActive: product?.active !== false,
    _searchHaystack: `${title} ${category} ${color} ${String(product?.dna_id || '')} ${activeVariants.map((variant) => `${variant.label} ${variant.sku}`).join(' ')}`.toLowerCase()
  };
}

function hasFormChanges(current, initial) {
  try {
    return JSON.stringify(current) !== JSON.stringify(initial);
  } catch {
    return true;
  }
}

function confirmDiscardChanges(isDirty) {
  if (!isDirty) return true;
  return window.confirm('Discard unsaved changes?');
}

const PRIVILEGED_ROLES = new Set(['admin']);
const isPrivilegedRole = (role) => PRIVILEGED_ROLES.has(String(role || '').trim().toLowerCase());
const USER_ROLE_OPTIONS = ['admin', 'manager', 'staff'];
const USER_PERMISSION_GROUPS = [
  {
    id: 'dashboard',
    title: 'Dashboard & Monitoring',
    permissions: ['dashboard:view', 'admin.stream:read', 'analytics:read', 'observability:read', 'metrics:read']
  },
  {
    id: 'users',
    title: 'Users & Access',
    permissions: ['users:read', 'users:write']
  },
  {
    id: 'products',
    title: 'Catalog & Orders',
    permissions: ['products:read', 'products:write', 'products:delete', 'orders:read', 'orders:write', 'orders:status', 'orders:tracking', 'orders:invoice', 'exports:read']
  },
  {
    id: 'marketing',
    title: 'Marketing & Content',
    permissions: ['campaigns:read', 'campaigns:write', 'campaigns:delete', 'promotions:read', 'promotions:write', 'promotions:delete']
  },
  {
    id: 'support',
    title: 'Support & Reviews',
    permissions: ['reviews:read', 'reviews:moderate', 'help:read', 'help:reply', 'help:status', 'audit:read', 'audit:undo']
  },
  {
    id: 'ops',
    title: 'Media, Settings & Backup',
    permissions: ['media:read', 'media:write', 'media:delete', 'settings:read', 'settings:write', 'customization:write', 'legal:write', 'backup:manage', 'backup:restore', 'system:restart', 'notifications:manage']
  }
];
const ALL_USER_PERMISSIONS = USER_PERMISSION_GROUPS.flatMap(group => group.permissions);
const makePermissionPreset = (enabledKeys = []) => {
  const enabled = new Set(enabledKeys);
  const out = {};
  for (const key of ALL_USER_PERMISSIONS) out[key] = enabled.has(key);
  return out;
};
const ROLE_PERMISSION_PRESETS = {
  admin: makePermissionPreset(ALL_USER_PERMISSIONS),
  manager: makePermissionPreset([
    'dashboard:view',
    'admin.stream:read',
    'analytics:read',
    'metrics:read',
    'products:read',
    'products:write',
    'orders:read',
    'orders:write',
    'orders:status',
    'orders:tracking',
    'orders:invoice',
    'exports:read',
    'campaigns:read',
    'campaigns:write',
    'promotions:read',
    'promotions:write',
    'reviews:read',
    'reviews:moderate',
    'help:read',
    'help:reply',
    'help:status',
    'media:read',
    'media:write',
    'settings:read',
    'customization:write'
  ]),
  staff: makePermissionPreset([
    'dashboard:view',
    'admin.stream:read',
    'analytics:read',
    'metrics:read',
    'orders:read',
    'orders:status',
    'orders:tracking',
    'orders:invoice',
    'reviews:read',
    'help:read',
    'help:reply',
    'media:read'
  ])
};
const getRolePresetPermissions = (role) => {
  const normalized = String(role || 'staff').trim().toLowerCase();
  return { ...(ROLE_PERMISSION_PRESETS[normalized] || ROLE_PERMISSION_PRESETS.staff) };
};
const normalizePermissions = (permissions, role = 'staff') => {
  const base = getRolePresetPermissions(role);
  if (!permissions || typeof permissions !== 'object' || Array.isArray(permissions)) return base;
  for (const key of ALL_USER_PERMISSIONS) {
    if (Object.prototype.hasOwnProperty.call(permissions, key)) base[key] = !!permissions[key];
  }
  return base;
};
const hasUserPermission = (user, permission) => isPrivilegedRole(user?.role) || !!(user?.permissions && user.permissions[permission]);
const prettyPermissionLabel = (key) => key.split(':').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');

const HERO_BANNER_TEMPLATE = {
  id: '',
  contentEnabled: false,
  headline: '',
  subheadline: '',
  image: '',
  ctaLabel: '',
  href: '',
  imageHref: '',
  glowColor: '',
  titleColor: '#ffffff',
  descriptionColor: '#ffffff',
  titleGlowColor: '',
  descriptionGlowColor: '',
  titleGlowBlur: 10,
  descriptionGlowBlur: 8,
  titleGlowSpread: 2,
  descriptionGlowSpread: 1.5,
  titleGlowOpacity: 0.58,
  descriptionGlowOpacity: 0.48,
  active: true
};

const createHeroBanner = () => ({
  ...HERO_BANNER_TEMPLATE,
  id: `hero_${Date.now()}_${Math.random().toString(16).slice(2, 6)}`
});

const normalizeHeroBanners = (banners) => {
  if (!Array.isArray(banners)) return [];
  const timestamp = Date.now();
  return banners.map((banner, idx) => ({
    ...HERO_BANNER_TEMPLATE,
    ...banner,
    id: banner?.id && typeof banner.id === 'string' && banner.id.trim().length > 0
      ? banner.id
      : `hero_${timestamp}_${idx}`,
    contentEnabled: typeof banner?.contentEnabled === 'boolean'
      ? banner.contentEnabled
      : Boolean(String(banner?.headline || '').trim() || String(banner?.subheadline || '').trim() || String(banner?.ctaLabel || '').trim()),
    active: banner?.active === false ? false : true
  }));
};

const HOMEPAGE_BANNER_TEMPLATE = {
  id: '',
  image: '',
  href: '',
  position: 'between_featured',
  paddingTop: 8,
  paddingBottom: 8,
  active: true
};

const HOMEPAGE_BANNER_POSITIONS = [
  'top_of_home',
  'below_hero',
  'above_featured',
  'between_featured',
  'below_featured',
  'above_closing',
  'below_closing'
];

const DEFAULT_HOME_HERO_DESCRIPTION = 'Discover standout products, fresh arrivals, and practical finds curated for everyday life.';
const DEFAULT_SITE_TITLE = 'Your Store - Products Worth Sharing';
const DEFAULT_SITE_TAGLINE = 'Discover standout products, seasonal drops, and everyday essentials.';
const DEFAULT_META_PIXEL_ID = '';
const DEFAULT_SECONDARY_ACCENT_COLOR = '#38bdf8';
const DEFAULT_HOME_RAILS = [
  { id: 'sale', type: 'sale', title: 'On Sale', subtitle: 'Active deals and marked-down products.', category: '', active: true, alwaysShow: true },
  { id: 'stock', type: 'stock', title: 'In Stock', subtitle: 'Ready to add to cart and checkout.', category: '', active: true, alwaysShow: true },
  { id: 'category-skin-care', type: 'category', category: 'Skin Care', title: 'Skin Care', subtitle: '', active: true },
  { id: 'category-hair-care', type: 'category', category: 'Hair Care', title: 'Hair Care', subtitle: '', active: true },
  { id: 'category-personal-hygiene', type: 'category', category: 'Personal Hygiene', title: 'Personal Hygiene', subtitle: '', active: true }
];
const DEFAULT_HOME_SHORTCUTS = [
  { id: 'shortcut-skin-care', label: 'Skin Care', targetType: 'category', targetValue: 'Skin Care', icon: 'sparkle', color: '#f9a8d4', active: true },
  { id: 'shortcut-hair-care', label: 'Hair Care', targetType: 'category', targetValue: 'Hair Care', icon: 'drop', color: '#c4b5fd', active: true },
  { id: 'shortcut-hygiene', label: 'Personal Care', targetType: 'category', targetValue: 'Personal Hygiene', icon: 'heart', color: '#bae6fd', active: true },
  { id: 'shortcut-new', label: 'New Arrivals', targetType: 'sort', targetValue: 'newest', icon: 'star', color: '#fde68a', active: true }
];
const DEFAULT_HELP_FAQS = [
  { id: 'shipping', question: 'When will my order arrive?', answer: 'Inside Dhaka deliveries usually arrive within 1-2 working days. Orders going outside Dhaka typically take 2-4 working days depending on the courier route.', active: true },
  { id: 'returns', question: 'How do returns or exchanges work?', answer: 'We accept unused items within 3 days of delivery. Message us with your order ID and reason, and we will arrange the exchange or pickup support.', active: true },
  { id: 'payments', question: 'What payment options do you support?', answer: 'We currently support bKash (Personal), COD at the door, and bank transfers on request. Your payment instructions are shown during checkout.', active: true },
  { id: 'tracking', question: 'Where can I find tracking updates?', answer: 'Use the Track page in the header with your order ID. We also share courier links as soon as parcels are dispatched.', active: true }
];

const normalizeHelpFaqs = (value) => {
  const source = Array.isArray(value) ? value : DEFAULT_HELP_FAQS;
  return source.slice(0, 20).map((item, index) => ({
    id: String(item?.id || `faq-${index + 1}`).trim() || `faq-${index + 1}`,
    question: String(item?.question || ''),
    answer: String(item?.answer || ''),
    active: item?.active !== false
  }));
};

const createHelpFaq = () => ({
  id: `faq-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`,
  question: '',
  answer: '',
  active: true
});
const HOMEPAGE_RAIL_TYPES = ['sale', 'stock', 'prepurchase', 'category', 'newest', 'all'];

const createHomepageRail = () => ({
  id: `rail_${Date.now()}_${Math.random().toString(16).slice(2, 6)}`,
  type: 'category',
  title: 'New Section',
  subtitle: '',
  category: '',
  active: true,
  alwaysShow: false
});

const normalizeHomepageRails = (rails) => {
  const source = Array.isArray(rails) && rails.length ? rails : DEFAULT_HOME_RAILS;
  const timestamp = Date.now();
  return source.map((rail, idx) => ({
    id: rail?.id && String(rail.id).trim() ? String(rail.id).trim() : `rail_${timestamp}_${idx}`,
    type: HOMEPAGE_RAIL_TYPES.includes(String(rail?.type || '').toLowerCase()) ? String(rail.type).toLowerCase() : 'category',
    title: String(rail?.title || rail?.category || 'Products').trim(),
    subtitle: String(rail?.subtitle || '').trim(),
    category: String(rail?.category || '').trim(),
    active: rail?.active === false ? false : true,
    alwaysShow: rail?.alwaysShow === true
  }));
};

const normalizeHomepageShortcuts = (shortcuts) => {
  const source = Array.isArray(shortcuts) ? shortcuts : DEFAULT_HOME_SHORTCUTS;
  const timestamp = Date.now();
  return source.slice(0, 12).map((shortcut, index) => ({
    id: shortcut?.id && String(shortcut.id).trim() ? String(shortcut.id).trim() : `shortcut_${timestamp}_${index}`,
    label: String(shortcut?.label ?? 'Shortcut'),
    targetType: ['category', 'sort', 'stock'].includes(String(shortcut?.targetType || '').toLowerCase()) ? String(shortcut.targetType).toLowerCase() : 'category',
    targetValue: String(shortcut?.targetValue || shortcut?.category || '').trim(),
    icon: ['sparkle', 'drop', 'heart', 'star'].includes(String(shortcut?.icon || '').toLowerCase()) ? String(shortcut.icon).toLowerCase() : 'sparkle',
    iconImage: String(shortcut?.iconImage || '').trim(),
    color: normalizeCssColorToHex(shortcut?.color) || '#f9a8d4',
    active: shortcut?.active === false ? false : true
  }));
};

const createHomepageShortcut = () => ({
  id: `shortcut_${Date.now()}_${Math.random().toString(16).slice(2, 6)}`,
  label: 'New Shortcut',
  targetType: 'category',
  targetValue: '',
  icon: 'sparkle',
  iconImage: '',
  color: '#f9a8d4',
  active: true
});

const moveListItem = (items, from, to) => {
  const next = [...items];
  if (to < 0 || to >= next.length) return next;
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

const createHomepageBanner = () => ({
  ...HOMEPAGE_BANNER_TEMPLATE,
  id: `home_banner_${Date.now()}_${Math.random().toString(16).slice(2, 6)}`
});

const normalizeHomepageBanners = (banners) => {
  if (!Array.isArray(banners)) return [];
  const timestamp = Date.now();
  return banners.map((banner, idx) => {
    const rawPosition = String(banner?.position || '').trim();
    return {
      ...HOMEPAGE_BANNER_TEMPLATE,
      ...banner,
      id: banner?.id && typeof banner.id === 'string' && banner.id.trim().length > 0
        ? banner.id
        : `home_banner_${timestamp}_${idx}`,
      position: HOMEPAGE_BANNER_POSITIONS.includes(rawPosition) ? rawPosition : 'between_featured',
      paddingTop: Math.max(0, Math.min(64, Number(banner?.paddingTop ?? 8) || 0)),
      paddingBottom: Math.max(0, Math.min(64, Number(banner?.paddingBottom ?? 8) || 0)),
      active: banner?.active === false ? false : true
    };
  });
};

function normalizeCssColorToHex(input) {
  if (typeof input === 'undefined') return null;
  if (input === null) return null;
  const s0 = String(input).trim();
  if (!s0) return null;

  const hexMatch = s0.match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/);
  if (hexMatch) {
    let h = hexMatch[1];
    if (h.length === 3 || h.length === 4) {
      h = h.split('').map(ch => ch + ch).join('');
    }
    if (h.length === 8) {
      h = h.slice(0, 6);
    }
    if (h.length === 6) return `#${h.toLowerCase()}`;
    return null;
  }

  const fnMatch = s0.match(/^rgba?\((.*)\)$/i);
  if (!fnMatch) return null;
  let body = String(fnMatch[1] || '').trim();
  if (!body) return null;
  body = body.split('/')[0].trim();
  const parts = body.replace(/,/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (parts.length < 3) return null;

  const parseChannel = (token) => {
    const t = String(token || '').trim();
    if (!t) return null;
    if (t.endsWith('%')) {
      const p = Number.parseFloat(t.slice(0, -1));
      if (!Number.isFinite(p)) return null;
      return Math.max(0, Math.min(255, Math.round((p / 100) * 255)));
    }
    const n = Number.parseFloat(t);
    if (!Number.isFinite(n)) return null;
    return Math.max(0, Math.min(255, Math.round(n)));
  };

  const r = parseChannel(parts[0]);
  const g = parseChannel(parts[1]);
  const b = parseChannel(parts[2]);
  if (r === null || g === null || b === null) return null;
  const toHex2 = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex2(r)}${toHex2(g)}${toHex2(b)}`.toLowerCase();
}

function useDebouncedValue(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function getMediaKind(src = '') {
  const s = String(src || '').toLowerCase();
  if (!s) return 'unknown';
  if (s.match(/\.(mp4|webm|mov|mkv)(\?.*)?$/)) return 'video';
  if (s.match(/\.(mp3|wav|m4a|aac|ogg)(\?.*)?$/)) return 'audio';
  if (s.match(/\.(glb|gltf|obj|stl|usdz)(\?.*)?$/)) return 'model';
  if (s.match(/\.(pdf|txt|csv|md|doc|docx|xls|xlsx|ppt|pptx)(\?.*)?$/)) return 'document';
  if (s.match(/\.(jpg|jpeg|png|gif|webp|avif|svg|ico)(\?.*)?$/)) return 'image';
  return 'unknown';
}

function isModelPreviewable(src = '') {
  const s = String(src || '').toLowerCase();
  return s.match(/\.(glb|gltf|usdz|stl)(\?.*)?$/);
}

// --- CONTEXT PROVIDERS ---
const AuthContext = createContext();
const ApiContext = createContext();
const ToastsContext = createContext();

export function AuthProvider({ children }) {
  const value = useProvideAuth();
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function ApiProvider({ children }) {
  const auth = useAuth();
  const value = useProvideApi(auth);
  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

export function ToastsProvider({ children }) {
  const value = useProvideToasts();
  return <ToastsContext.Provider value={value}>{children}</ToastsContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
export function useApi() {
  return useContext(ApiContext);
}
export function useToasts() {
  return useContext(ToastsContext);
}

// --- PROVIDER HOOKS ---
const getStoredAdminToken = () => {
  try {
    const token = localStorage.getItem('admin_token');
    if (!token) return '';
    const parts = token.split('.');
    if (parts.length !== 3) {
      localStorage.removeItem('admin_token');
      return '';
    }
    const payload = JSON.parse(atob(parts[1] || ''));
    if (payload?.exp && (payload.exp * 1000) <= Date.now()) {
      localStorage.removeItem('admin_token');
      return '';
    }
    return token;
  } catch {
    try { localStorage.removeItem('admin_token'); } catch {}
    return '';
  }
};

function useProvideAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [csrfToken, setCsrfToken] = useState(() => {
    try {
      return sessionStorage.getItem('admin_csrf') || '';
    } catch {
      return '';
    }
  });

  const persistCsrfToken = React.useCallback((value) => {
    const nextToken = String(value || '').trim();
    setCsrfToken(nextToken);
    try {
      if (nextToken) {
        sessionStorage.setItem('admin_csrf', nextToken);
      } else {
        sessionStorage.removeItem('admin_csrf');
      }
    } catch {}
    return nextToken;
  }, []);

  const refreshCsrfToken = React.useCallback(async () => {
    const res = await fetch(`${API_BASE}/auth/csrf`, { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to refresh CSRF token');
    const data = await res.json().catch(() => ({}));
    return persistCsrfToken(data?.csrfToken || '');
  }, [persistCsrfToken]);

  useEffect(() => {
    const userData = localStorage.getItem('admin_user');
    if (userData) {
      try { setUser(JSON.parse(userData)); } catch {}
    }
    (async () => {
      try {
        await refreshCsrfToken().catch(() => '');
        const fallbackToken = getStoredAdminToken();
        const meRes = await fetch(`${API_BASE}/auth/me`, {
          credentials: 'include',
          headers: fallbackToken ? { Authorization: `Bearer ${fallbackToken}` } : {}
        });
        if (meRes.ok) {
          const meData = await meRes.json();
          if (meData?.user) {
            setUser(meData.user);
            localStorage.setItem('admin_user', JSON.stringify(meData.user));
          }
        } else {
          localStorage.removeItem('admin_user');
          setUser(null);
        }
      } catch {}
      setLoading(false);
    })();
  }, [refreshCsrfToken]);

  const login = async (username, password, remember = false) => {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, remember })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }
    const data = await response.json();
    if (data?.token) {
      localStorage.setItem('admin_token', data.token);
    } else {
      try { localStorage.removeItem('admin_token'); } catch {}
    }
    localStorage.setItem('admin_user', JSON.stringify(data.user));
    setUser(data.user);
    if (data?.csrfToken) {
      persistCsrfToken(data.csrfToken);
    }
    return data;
  };

  const logout = () => {
    try { fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' }); } catch {}
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    persistCsrfToken('');
    setUser(null);
  };

  const getAuthHeaders = () => {
    const token = getStoredAdminToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
    return headers;
  };

  const getCsrfToken = () => csrfToken;
  return { user, loading, login, logout, getAuthHeaders, getCsrfToken, refreshCsrfToken };
}

function useProvideApi(auth) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const request = React.useCallback(async (endpoint, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const isFormData = options.body instanceof FormData;
      const method = (options.method || 'GET').toUpperCase();

      const executeRequest = async () => {
        const headers = {
          ...auth.getAuthHeaders(),
          ...options.headers,
        };
        if (method !== 'GET' && auth.getCsrfToken) {
          const csrf = auth.getCsrfToken();
          if (csrf) headers['X-CSRF-Token'] = csrf;
        }
        if (!isFormData && !headers['Content-Type']) {
          headers['Content-Type'] = 'application/json';
        }
        return fetch(`${API_BASE}${endpoint}`, {
          ...options,
          headers,
          credentials: 'include'
        });
      };

      let response = await executeRequest();
      let errorData = null;
      let csrfRetried = false;

      while (!response.ok) {
        errorData = await response.json().catch(() => ({ error: 'Request failed' }));

        const canRetryCsrf =
          !csrfRetried &&
          method !== 'GET' &&
          response.status === 403 &&
          errorData?.error === 'CSRF token missing or invalid' &&
          typeof auth.refreshCsrfToken === 'function';

        if (canRetryCsrf) {
          const refreshedToken = await auth.refreshCsrfToken().catch(() => '');
          if (refreshedToken) {
            csrfRetried = true;
            response = await executeRequest();
            continue;
          }
        }

        if (response.status === 401) {
          try { auth.logout(); } catch {}
        }
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [auth]);

  const downloadFile = React.useCallback(async (endpoint, filename, options = {}) => {
    try {
      const method = String(options?.method || 'GET').toUpperCase();
      const extraHeaders = options?.headers && typeof options.headers === 'object' ? options.headers : {};
      const isFormData = options?.body instanceof FormData;
      const response = await fetch(`${API_BASE}${endpoint}`, {
        method,
        headers: {
          ...auth.getAuthHeaders(),
          ...extraHeaders,
          ...((!isFormData && method !== 'GET' && !extraHeaders['Content-Type']) ? { 'Content-Type': 'application/json' } : {})
        },
        body: options?.body,
        credentials: 'include'
      });
      if (!response.ok) {
        if (response.status === 401) {
          try { auth.logout(); } catch {}
        }
        throw new Error('Download failed');
      }
      const total = Number.parseInt(response.headers.get('content-length') || '0', 10);
      const reader = response.body?.getReader ? response.body.getReader() : null;
      let blob;
      if (!reader) {
        blob = await response.blob();
        if (typeof options?.onProgress === 'function') {
          options.onProgress(blob.size || 0, blob.size || total || 0);
        }
      } else {
        const chunks = [];
        let received = 0;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (value) {
            chunks.push(value);
            received += value.length || value.byteLength || 0;
            if (typeof options?.onProgress === 'function') {
              options.onProgress(received, total || 0);
            }
          }
        }
        blob = new Blob(chunks);
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw error;
    }
  }, [auth]);

  return React.useMemo(() => ({ request, downloadFile, loading, error }), [request, downloadFile, loading, error]);
}

function useProvideToasts() {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    if (!toasts.length) return;
    const timer = setTimeout(() => setToasts(prev => prev.slice(1)), 4000);
    return () => clearTimeout(timer);
  }, [toasts]);
  const addToast = React.useCallback((message, type = 'info', options = {}) => {
    const toastOptions = options && typeof options === 'object' ? options : {};
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts(prev => [...prev, { id, message, type, ...toastOptions }]);
  }, []);
  const removeToast = React.useCallback((toastId) => {
    setToasts(prev => prev.filter((toast) => toast.id !== toastId));
  }, []);
  return React.useMemo(() => ({ toasts, addToast, removeToast }), [toasts, addToast, removeToast]);
}

function VideoPlayer({ src, className = '', frameClassName = '' }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const togglePlay = () => {
    const mediaEl = videoRef.current;
    if (!mediaEl) return;
    if (mediaEl.paused) {
      mediaEl.play().catch(() => {});
    } else {
      mediaEl.pause();
    }
  };

  const toggleMute = () => {
    const mediaEl = videoRef.current;
    if (!mediaEl) return;
    mediaEl.muted = !mediaEl.muted;
    setMuted(mediaEl.muted);
  };

  const requestFullscreen = () => {
    const mediaEl = videoRef.current;
    if (!mediaEl) return;
    if (mediaEl.requestFullscreen) {
      mediaEl.requestFullscreen().catch(() => {});
      return;
    }
    if (mediaEl.webkitEnterFullscreen) {
      try { mediaEl.webkitEnterFullscreen(); } catch {}
    }
  };

  useEffect(() => {
    const mediaEl = videoRef.current;
    if (!mediaEl) return undefined;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onLoaded = () => setMuted(mediaEl.muted);
    mediaEl.addEventListener('play', onPlay);
    mediaEl.addEventListener('pause', onPause);
    mediaEl.addEventListener('loadedmetadata', onLoaded);
    return () => {
      mediaEl.removeEventListener('play', onPlay);
      mediaEl.removeEventListener('pause', onPause);
      mediaEl.removeEventListener('loadedmetadata', onLoaded);
    };
  }, []);

  return (
    <div className={`w-full ${className}`}>
      <div className={`relative w-full overflow-hidden bg-black ${frameClassName || 'aspect-video'}`}>
        <video
          ref={videoRef}
          src={src}
          className="w-full h-full object-contain"
          playsInline
          muted
          preload="metadata"
          controls={false}
          controlsList="nodownload noplaybackrate"
          disablePictureInPicture
          onClick={(e) => { e.stopPropagation(); togglePlay(); }}
        />

        {!playing && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); togglePlay(); }}
            className="absolute inset-0 flex items-center justify-center text-white/90"
            aria-label="Play video"
          >
            <span className="h-14 w-14 rounded-full bg-black/55 flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            </span>
          </button>
        )}

        <div
          className="absolute right-3 bottom-3 z-20 flex items-center gap-2 rounded-full bg-black/55 px-3 py-2 text-white backdrop-blur-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={togglePlay}
            className="h-8 w-8 flex items-center justify-center rounded bg-white/10 hover:bg-white/20"
            aria-label={playing ? 'Pause video' : 'Play video'}
          >
            {playing ? (
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><rect x="6" y="5" width="4" height="14"/><rect x="14" y="5" width="4" height="14"/></svg>
            ) : (
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            )}
          </button>
          <button
            type="button"
            onClick={toggleMute}
            className="h-8 w-8 flex items-center justify-center rounded bg-white/10 hover:bg-white/20"
            aria-label={muted ? 'Unmute video' : 'Mute video'}
          >
            {muted ? (
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M16.5 12L19 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M19 9.5L16.5 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M5 10v4h3l4 4V6L8 10H5z"/></svg>
            ) : (
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M5 10v4h3l4 4V6L8 10H5z"/><path d="M14.5 8.5a5 5 0 010 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/></svg>
            )}
          </button>
          <button
            type="button"
            onClick={requestFullscreen}
            className="h-8 w-8 flex items-center justify-center rounded bg-white/10 hover:bg-white/20"
            aria-label="Fullscreen"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M9 3H3v6" />
              <path d="M15 3h6v6" />
              <path d="M9 21H3v-6" />
              <path d="M15 21h6v-6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

function STLViewer({ src, interactive = true, className = '', immersive = false }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let disposed = false;
    let frame = 0;
    let resizeObserver = null;
    let controls = null;
    let renderer = null;
    let mesh = null;
    let camera = null;

    const boot = async () => {
      try {
        const [THREE, { STLLoader }, { OrbitControls }] = await Promise.all([
          import('three'),
          import('three/examples/jsm/loaders/STLLoader.js'),
          import('three/examples/jsm/controls/OrbitControls.js')
        ]);
        if (disposed || !containerRef.current) return;

        const activeContainer = containerRef.current;
        const cacheKey = `stl:${src}`;
        const scene = new THREE.Scene();
        scene.background = immersive ? null : new THREE.Color(0xf3f4f6);
        camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000);
        camera.position.set(0, 0, 4);

        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        activeContainer.appendChild(renderer.domElement);

        if (interactive) {
          controls = new OrbitControls(camera, renderer.domElement);
          controls.enableDamping = true;
          controls.dampingFactor = 0.08;
        }

        const ambient = new THREE.HemisphereLight(0xffffff, 0xaaaaaa, 1);
        scene.add(ambient);
        const dir = new THREE.DirectionalLight(0xffffff, 0.8);
        dir.position.set(5, 8, 6);
        scene.add(dir);

        const loader = new STLLoader();
        loader.load(
          src,
          (geometry) => {
            if (disposed) {
              geometry.dispose();
              return;
            }
            geometry.computeBoundingBox();
            geometry.computeVertexNormals();
            const material = new THREE.MeshStandardMaterial({ color: 0x9ca3af, metalness: 0.1, roughness: 0.6 });
            mesh = new THREE.Mesh(geometry, material);
            scene.add(mesh);

            const box = geometry.boundingBox;
            if (box) {
              const size = new THREE.Vector3();
              box.getSize(size);
              const center = new THREE.Vector3();
              box.getCenter(center);
              mesh.position.sub(center);
              const maxDim = Math.max(size.x, size.y, size.z) || 1;
              camera.position.set(0, 0, maxDim * 2.2);
              camera.near = maxDim / 100;
              camera.far = maxDim * 20;
              camera.updateProjectionMatrix();
            }

            const cached = modelViewStateCache.get(cacheKey);
            if (cached && cached.position && cached.target && controls) {
              camera.position.copy(cached.position);
              controls.target.copy(cached.target);
              controls.update();
            }
          },
          undefined,
          () => {}
        );

        const resize = () => {
          const targetContainer = containerRef.current;
          if (!targetContainer || !renderer || !camera) return;
          const { clientWidth, clientHeight } = targetContainer;
          const width = clientWidth || 1;
          const height = clientHeight || 1;
          renderer.setSize(width, height, false);
          camera.aspect = width / height;
          camera.updateProjectionMatrix();
        };
        resize();
        resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(activeContainer);

        const animate = () => {
          frame = requestAnimationFrame(animate);
          if (controls) controls.update();
          renderer.render(scene, camera);
        };
        animate();
      } catch {
        // Keep layout stable if viewer libraries fail to load.
      }
    };

    boot();

    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      if (resizeObserver) resizeObserver.disconnect();
      if (controls && camera) {
        modelViewStateCache.set(`stl:${src}`, {
          position: camera.position.clone(),
          target: controls.target.clone()
        });
      }
      if (controls) controls.dispose();
      if (renderer) renderer.dispose();
      if (mesh) {
        mesh.geometry.dispose();
        if (Array.isArray(mesh.material)) mesh.material.forEach(m => m.dispose());
        else mesh.material.dispose();
      }
      if (renderer?.domElement?.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [src, interactive, immersive]);

  return (
    <div
      ref={containerRef}
      className={`w-full h-[60vh] ${immersive ? 'bg-black rounded-none' : 'bg-gray-50 rounded-lg'} overflow-hidden ${className}`}
    />
  );
}

function ModelViewer({ src, interactive = false }) {
  const viewerRef = useRef(null);
  const [viewerReady, setViewerReady] = useState(() => {
    if (typeof window === 'undefined' || !window.customElements) return false;
    return !!window.customElements.get('model-viewer');
  });

  useEffect(() => {
    let cancelled = false;
    ensureModelViewerElementLoaded()
      .then(() => {
        if (!cancelled) setViewerReady(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!viewerReady) return undefined;
    const el = viewerRef.current;
    if (!el || !src) return undefined;
    const cacheKey = `model:${src}`;
    const cached = modelViewStateCache.get(cacheKey);
    if (cached) {
      if (cached.orbit) el.cameraOrbit = cached.orbit;
      if (cached.target) el.cameraTarget = cached.target;
      if (cached.fov) el.fieldOfView = cached.fov;
    }
    const handleCameraChange = () => {
      if (!interactive) return;
      try {
        modelViewStateCache.set(cacheKey, {
          orbit: el.cameraOrbit,
          target: el.cameraTarget,
          fov: el.fieldOfView
        });
      } catch {}
    };
    if (interactive) el.addEventListener('camera-change', handleCameraChange);
    return () => {
      if (interactive) {
        handleCameraChange();
        el.removeEventListener('camera-change', handleCameraChange);
      }
    };
  }, [src, interactive, viewerReady]);

  if (!viewerReady) {
    return (
      <div className="w-full h-full bg-gray-100 text-gray-500 text-xs flex items-center justify-center">
        Loading 3D preview...
      </div>
    );
  }

  return (
    <model-viewer
      ref={viewerRef}
      src={src}
      camera-controls={interactive ? true : undefined}
      ar
      interaction-prompt="none"
      style={{ width: '100%', height: '100%', pointerEvents: interactive ? 'auto' : 'none' }}
    />
  );
}

function MediaPreviewModal({ item, onClose }) {
  if (!item) return null;
  const src = resolveMediaUrl(item.url || item.src || item);
  const kind = getMediaKind(src);
  const isStl = String(src || '').toLowerCase().match(/\.(stl)(\?.*)?$/);
  const modalContent = (
    <div className="fixed inset-0 z-[500] bg-black/90" onClick={onClose}>
      <button
        type="button"
        onClick={onClose}
        className="absolute top-3 right-3 z-20 inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white hover:bg-black/70"
        aria-label="Close preview"
      >
        <Icon.X className="w-5 h-5" />
      </button>

      <div className="w-full h-full flex items-center justify-center p-2 sm:p-6 pointer-events-none">
        {kind === 'image' && (
          <img
            src={src}
            alt="Preview"
            className="max-w-full max-h-[92vh] object-contain pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
          />
        )}
        {kind === 'video' && (
          <div className="w-full max-w-[1400px] max-h-[92vh] pointer-events-auto" onClick={(e) => e.stopPropagation()}>
            <VideoPlayer src={src} className="w-full h-full" frameClassName="w-full h-[80vh] sm:h-[86vh] max-h-[92vh]" />
          </div>
        )}
        {kind === 'audio' && (
          <div className="w-full max-w-2xl px-4 py-5 rounded-lg bg-black/35 border border-white/20 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
            <audio src={src} controls className="w-full" />
          </div>
        )}
        {kind === 'model' && (
          isStl ? (
            <div className="w-full max-w-[1400px] h-[80vh] sm:h-[86vh] max-h-[92vh] pointer-events-auto" onClick={(e) => e.stopPropagation()}>
              <STLViewer src={src} interactive className="h-full" immersive />
            </div>
          ) : isModelPreviewable(src) ? (
            <div className="w-full max-w-[1400px] h-[80vh] sm:h-[86vh] max-h-[92vh] bg-black overflow-hidden pointer-events-auto" onClick={(e) => e.stopPropagation()}>
              <ModelViewer src={src} interactive />
            </div>
          ) : (
            <div className="text-sm text-white/90 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
              Preview is available for .glb, .gltf, .usdz, or .stl models. For textured models, use .glb (embedded textures).
            </div>
          )
        )}
        {kind === 'document' && (
          String(src).toLowerCase().includes('.pdf') ? (
            <iframe
              src={src}
              title="PDF Preview"
              className="w-full max-w-[1400px] h-[85vh] border border-white/20 bg-white pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="text-sm text-white space-y-3 bg-black/35 border border-white/20 rounded-lg px-4 py-4 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
              <div>Preview is limited for this document type.</div>
              <a href={src} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1.5 rounded border border-white/40 hover:bg-white/10">
                Open document
              </a>
            </div>
          )
        )}
        {kind === 'unknown' && (
          <div className="text-sm text-white pointer-events-auto" onClick={(e) => e.stopPropagation()}>Unsupported media type.</div>
        )}
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modalContent;
  return createPortal(modalContent, document.body);
}

// Reusable Media Picker for image fields
function MediaPicker({ label = 'Image', value, onChange, helpText, accept = '*/*', disabled = false, ratioHint, compact = false }) {
  const { request } = useApi();
  const auth = useAuth();
  const { addToast } = useToasts();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('library'); // 'library' | 'upload' | 'url'
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState(value || '');
  const [selected, setSelected] = useState(value || '');
  const panelId = React.useId();
  const [currentPath, setCurrentPath] = useState('');
  const [previewItem, setPreviewItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [hasMore, setHasMore] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [mediaMeta, setMediaMeta] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const pickerOffsetRef = useRef(0);
  const debouncedSearch = useDebouncedValue(searchTerm, 220);
  const PICKER_PAGE_SIZE = 72;
  const getKind = (src) => getMediaKind(src);
  const formatMediaRatio = (width, height) => {
    const w = Math.round(Number(width) || 0);
    const h = Math.round(Number(height) || 0);
    if (!w || !h) return '';
    const gcd = (a, b) => b ? gcd(b, a % b) : a;
    const divisor = gcd(w, h) || 1;
    return w + ' x ' + h + ' (' + Math.round(w / divisor) + ':' + Math.round(h / divisor) + ')';
  };

  const loadMedia = useCallback(async ({ reset = false } = {}) => {
    if (!open || tab !== 'library') return;
    const nextOffset = reset ? 0 : pickerOffsetRef.current;
    try {
      if (reset) setLoading(true);
      else setLoadingMore(true);
      const params = new URLSearchParams();
      params.set('includeMeta', '1');
      params.set('limit', String(PICKER_PAGE_SIZE));
      params.set('offset', String(nextOffset));
      if (currentPath) params.set('path', currentPath);
      if (debouncedSearch) params.set('q', debouncedSearch);
      if (typeFilter) params.set('type', typeFilter);

      const data = await request(`/media?${params.toString()}`);
      const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
      const total = Number.isFinite(Number(data?.total)) ? Number(data.total) : items.length;
      const nextHasMore = typeof data?.hasMore === 'boolean'
        ? data.hasMore
        : (items.length >= PICKER_PAGE_SIZE);

      pickerOffsetRef.current = nextOffset + items.length;
      setFiles((prev) => reset ? items : [...prev, ...items]);
      setTotalItems(total);
      setHasMore(nextHasMore);
    } catch {
      addToast('Failed to load media', 'error');
      if (reset) {
        setFiles([]);
        setTotalItems(0);
        setHasMore(false);
      }
    } finally {
      if (reset) setLoading(false);
      else setLoadingMore(false);
    }
  }, [open, tab, request, addToast, currentPath, debouncedSearch, typeFilter]);

  // Load media files when opened and tab is library
  useEffect(() => {
    if (!open || tab !== 'library') return;
    pickerOffsetRef.current = 0;
    loadMedia({ reset: true });
  }, [open, tab, currentPath, debouncedSearch, typeFilter, loadMedia]);

  useEffect(() => { setUrlInput(value || ''); setSelected(value || ''); }, [value]);

  useEffect(() => {
    const src = resolveMediaUrl(value);
    if (!value || !src || typeof window === 'undefined') {
      setMediaMeta(null);
      return undefined;
    }
    let cancelled = false;
    const kind = getKind(src);
    if (kind === 'image') {
      const img = new Image();
      img.onload = () => {
        if (!cancelled) {
          setMediaMeta({ kind: 'image', label: formatMediaRatio(img.naturalWidth, img.naturalHeight) });
        }
      };
      img.onerror = () => { if (!cancelled) setMediaMeta(null); };
      img.src = src;
      return () => { cancelled = true; };
    }
    if (kind === 'video') {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        if (!cancelled) {
          setMediaMeta({ kind: 'video', label: formatMediaRatio(video.videoWidth, video.videoHeight) });
        }
      };
      video.onerror = () => { if (!cancelled) setMediaMeta(null); };
      video.src = src;
      return () => { cancelled = true; video.removeAttribute('src'); video.load(); };
    }
    setMediaMeta({ kind, label: kind.toUpperCase() });
    return () => { cancelled = true; };
  }, [value]);

  const handleUpload = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(fileList).forEach(f => fd.append('files', f));
      const xhr = new XMLHttpRequest();
      await new Promise((resolve, reject) => {
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error('Upload failed'));
        });
        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.open('POST', '/shopapi/media');
        xhr.withCredentials = true;
        try { const headers = auth.getAuthHeaders(); Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v)); } catch {}
        xhr.send(fd);
      });
      let uploaded = null;
      try { const payload = JSON.parse(xhr.responseText || '{}'); uploaded = payload?.files?.[0]?.url || null; } catch {}
      if (uploaded) {
        setSelected(resolveMediaUrl(uploaded));
        addToast('File uploaded', 'success');
        setTab('library');
        // Refresh library
        try {
          pickerOffsetRef.current = 0;
          await loadMedia({ reset: true });
        } catch {}
      } else {
        addToast('Upload completed but URL not returned', 'error');
      }
    } catch {
      addToast('Failed to upload', 'error');
    } finally {
      setUploading(false);
    }
  };

  const applySelection = () => {
    let finalUrl = selected || urlInput || '';
    if (finalUrl) {
      // strip surrounding quotes
      finalUrl = finalUrl.replace(/^['"]|['"]$/g, '').trim();
    }
    onChange && onChange(finalUrl);
    setOpen(false);
  };

  const preview = (src) => {
    const resolvedSrc = resolveMediaUrl(src);
    if (!src) {
      return (
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50 text-[10px] text-gray-400">None</div>
      );
    }
    const kind = getKind(resolvedSrc);
    if (kind === 'video') {
      return (
        <video
          src={resolvedSrc}
          className="h-10 w-10 rounded object-cover border bg-gray-50"
          muted
          playsInline
          preload="metadata"
          controls={false}
          controlsList="nodownload noplaybackrate"
          disablePictureInPicture
        />
      );
    }
    if (kind === 'model') {
      return (
        <div className="h-10 w-10 rounded border bg-gray-50 flex items-center justify-center text-[10px] text-gray-500">3D</div>
      );
    }
    if (kind === 'audio') {
      return (
        <div className="h-10 w-10 rounded border bg-purple-50 text-purple-700 text-[10px] font-semibold flex items-center justify-center">AUDIO</div>
      );
    }
    if (kind === 'document') {
      return (
        <div className="h-10 w-10 rounded border bg-blue-50 text-blue-700 text-[10px] font-semibold flex items-center justify-center">DOC</div>
      );
    }
    return (
      <img src={resolvedSrc} alt="preview" className="h-10 w-10 rounded object-cover border" loading="lazy" decoding="async" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
    );
  };

  const sortedFiles = useMemo(() => ([...(files || [])].sort((a, b) => {
    if (a.type === 'dir' && b.type !== 'dir') return -1;
    if (a.type !== 'dir' && b.type === 'dir') return 1;
    return String(a.name || '').localeCompare(String(b.name || ''));
  })), [files]);
  const pathCrumbs = useMemo(() => {
    const segments = currentPath ? currentPath.split('/').filter(Boolean) : [];
    return segments.map((name, index) => ({
      name,
      path: segments.slice(0, index + 1).join('/')
    }));
  }, [currentPath]);
  const selectedKind = getKind(resolveMediaUrl(selected || urlInput || value || ''));
  const currentDirectoryLabel = currentPath || 'Uploads';
  const aspectHintText = (typeof ratioHint === 'string' && ratioHint.trim())
    ? ratioHint.trim()
    : 'Flexible';
  const ratioDisplayText = mediaMeta?.label || aspectHintText;
  const ratioLabel = mediaMeta?.label ? 'Actual' : 'Target';

  return (
    <div className={compact ? "min-w-0 space-y-1" : "min-w-0 space-y-1.5"}>
      {label && (
        <div className={"flex min-w-0 items-center gap-2 font-medium text-gray-700 " + (compact ? "text-xs" : "text-sm")}>
          <span>{label}</span>
          <span
            className="inline-flex h-5 min-w-5 items-center justify-center rounded-full border border-gray-200 bg-gray-50 px-1.5 text-[10px] font-semibold text-gray-500"
            title={[helpText, ratioLabel + ': ' + ratioDisplayText].filter(Boolean).join(' • ')}
          >
            i
          </span>
          <span className="truncate text-[11px] font-normal text-gray-400">{ratioLabel}: {ratioDisplayText}</span>
        </div>
      )}
      <div className={"rounded-lg border border-gray-200 bg-gray-50/70 " + (compact ? "p-1.5" : "p-2")}>
        <div className="flex min-w-0 items-start gap-2">
          {preview(value)}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => setOpen(true)} className={"rounded-md border border-gray-300 bg-white font-medium text-gray-700 hover:bg-gray-100 " + (compact ? "px-2 py-1.5 text-xs" : "px-3 py-2 text-sm")} disabled={disabled}>
                Select media
              </button>
              {value && (
                <button type="button" onClick={() => onChange && onChange('')} className={"rounded-md border border-red-200 bg-white font-medium text-red-600 hover:bg-red-50 " + (compact ? "px-2 py-1.5 text-xs" : "px-3 py-2 text-sm")} disabled={disabled}>
                  Clear
                </button>
              )}
            </div>
            {!value && <div className="mt-1 text-[11px] leading-4 text-gray-400">No media selected</div>}
          </div>
        </div>
      </div>

      {open && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[400] flex items-center justify-center bg-gray-950/50 p-3 sm:p-5"
          onMouseDown={(event) => { if (event.target === event.currentTarget) setOpen(false); }}
        >
          <div id={panelId} className="flex h-[88vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Icon.Folder className="h-5 w-5 shrink-0 text-blue-600" />
                    <h3 className="truncate text-base font-semibold text-gray-900">Media Browser</h3>
                  </div>
                  {label && <p className="truncate text-xs text-gray-500">{label} · {ratioLabel}: {ratioDisplayText}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {[
                  { id: 'library', title: 'Library', icon: Icon.Folder },
                  { id: 'upload', title: 'Upload', icon: Icon.Upload },
                  { id: 'url', title: 'URL', icon: null }
                ].map((item) => {
                  const TabIcon = item.icon;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setTab(item.id)}
                      title={item.title}
                      aria-label={item.title}
                      className={'inline-flex h-10 w-10 items-center justify-center rounded-lg border transition ' + (tab === item.id ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-100')}
                    >
                      {TabIcon ? <TabIcon className="h-5 w-5" /> : (
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M10 13a5 5 0 0 0 7.07 0l2.12-2.12a5 5 0 0 0-7.07-7.07L11 4.93" />
                          <path d="M14 11a5 5 0 0 0-7.07 0L4.81 13.12a5 5 0 0 0 7.07 7.07L13 19.07" />
                        </svg>
                      )}
                    </button>
                  );
                })}
                <button type="button" onClick={() => setOpen(false)} className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900" aria-label="Close media picker" title="Close">
                  <Icon.X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)]">
              <aside className="hidden border-r border-gray-200 bg-gray-50/80 p-3 md:block">
                <div className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Location</div>
                <button
                  type="button"
                  onClick={() => setCurrentPath('')}
                  className={'mb-2 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm ' + (!currentPath ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700 hover:bg-white')}
                >
                  <Icon.Folder className="h-5 w-5 text-blue-600" />
                  Uploads
                </button>
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                  <div className="text-[11px] font-medium text-gray-500">Current folder</div>
                  <div className="mt-1 break-words text-sm font-semibold text-gray-900">{currentDirectoryLabel}</div>
                  <div className="mt-2 text-xs text-gray-500">{totalItems} item{totalItems === 1 ? '' : 's'} found</div>
                </div>
                <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
                  <div className="text-[11px] font-medium text-gray-500">Selection</div>
                  <div className="mt-2 flex items-center gap-2">
                    {preview(selected || value)}
                    <div className="min-w-0 text-xs text-gray-600">
                      <div className="truncate font-medium text-gray-800">{selected ? 'Ready to apply' : 'Nothing selected'}</div>
                      <div className="truncate text-gray-400">{selectedKind || 'media'}</div>
                    </div>
                  </div>
                </div>
              </aside>

              <div className="flex min-h-0 flex-col">
                <div className="border-b border-gray-200 bg-white px-4 py-3">
                  {tab === 'library' ? (
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setCurrentPath('')}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                          title="Uploads root"
                          aria-label="Uploads root"
                        >
                          <Icon.Folder className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => { if (currentPath) setCurrentPath(currentPath.split('/').slice(0, -1).join('/')); }}
                          disabled={!currentPath}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                          title="Parent folder"
                          aria-label="Parent folder"
                        >
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M15 18l-6-6 6-6" />
                          </svg>
                        </button>
                        <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-sm">
                          <button type="button" onClick={() => setCurrentPath('')} className={!currentPath ? 'font-semibold text-blue-700' : 'text-blue-700 hover:underline'}>Uploads</button>
                          {pathCrumbs.map((crumb) => (
                            <React.Fragment key={crumb.path}>
                              <span className="text-gray-300">/</span>
                              <button type="button" onClick={() => setCurrentPath(crumb.path)} className={crumb.path === currentPath ? 'whitespace-nowrap font-semibold text-gray-900' : 'whitespace-nowrap text-blue-700 hover:underline'}>{crumb.name}</button>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="relative min-w-[220px] flex-1">
                          <Icon.Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search media..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <select
                          value={typeFilter}
                          onChange={(e) => setTypeFilter(e.target.value)}
                          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          title="Type filter"
                        >
                          <option value="all">All media</option>
                          <option value="image">Images</option>
                          <option value="video">Videos</option>
                          <option value="model">3D Models</option>
                          <option value="folders">Folders</option>
                        </select>
                      </div>
                    </div>
                  ) : tab === 'upload' ? (
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <Icon.Upload className="h-5 w-5 text-blue-600" />
                      Upload files into <span className="font-semibold text-gray-900">{currentDirectoryLabel}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <svg className="h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M10 13a5 5 0 0 0 7.07 0l2.12-2.12a5 5 0 0 0-7.07-7.07L11 4.93" />
                        <path d="M14 11a5 5 0 0 0-7.07 0L4.81 13.12a5 5 0 0 0 7.07 7.07L13 19.07" />
                      </svg>
                      Add a direct media URL
                    </div>
                  )}
                </div>

                <div className="min-h-0 flex-1 overflow-auto bg-gray-50/40 p-4">
                  {tab === 'library' && (
                    <>
                      {loading ? (
                        <div className="flex h-64 items-center justify-center text-sm text-gray-500">
                          <Icon.Loading className="mr-2 h-5 w-5 text-blue-600" />
                          Loading media...
                        </div>
                      ) : sortedFiles.length === 0 ? (
                        <div className="flex h-64 flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white text-center text-sm text-gray-500">
                          <Icon.Upload className="mb-2 h-8 w-8 text-gray-400" />
                          <div className="font-medium text-gray-700">No media found</div>
                          <div>{searchTerm ? 'Try a different search or filter.' : 'Upload files or open another folder.'}</div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                          {sortedFiles.map((f) => {
                            const fileUrl = resolveMediaUrl(f.url);
                            const isDir = f.type === 'dir';
                            const kind = getKind(fileUrl);
                            const active = !isDir && selected === fileUrl;
                            return (
                              <div key={f.path || f.name} className={'group relative overflow-hidden rounded-xl border bg-white transition hover:border-blue-300 hover:shadow-sm ' + (active ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200')}>
                                {isDir ? (
                                  <button type="button" onClick={() => setCurrentPath(f.path)} className="flex h-36 w-full flex-col items-center justify-center gap-3 p-3 text-center hover:bg-blue-50/60">
                                    <Icon.Folder className="h-12 w-12 text-amber-500" />
                                    <span className="w-full truncate text-sm font-semibold text-gray-800">{f.name}</span>
                                  </button>
                                ) : (
                                  <>
                                    <button type="button" onClick={() => setSelected(fileUrl)} className="block w-full text-left">
                                      <div className="flex h-28 items-center justify-center bg-gray-100">
                                        {kind === 'image' ? (
                                          <img src={fileUrl} alt={f.name} className="h-full w-full object-cover" loading="lazy" decoding="async" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                        ) : kind === 'video' ? (
                                          <div className="flex flex-col items-center gap-2 text-gray-600"><svg className="h-10 w-10" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg><span className="text-xs font-semibold">VIDEO</span></div>
                                        ) : kind === 'audio' ? (
                                          <div className="flex flex-col items-center gap-2 text-purple-700"><svg className="h-10 w-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg><span className="text-xs font-semibold">AUDIO</span></div>
                                        ) : kind === 'model' ? (
                                          <div className="flex flex-col items-center gap-2 text-gray-600"><Icon.Package className="h-10 w-10" /><span className="text-xs font-semibold">3D</span></div>
                                        ) : (
                                          <div className="flex flex-col items-center gap-2 text-blue-700"><svg className="h-10 w-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg><span className="text-xs font-semibold">FILE</span></div>
                                        )}
                                      </div>
                                      <div className="p-2">
                                        <div className="truncate text-xs font-semibold text-gray-800">{f.name}</div>
                                        <div className="mt-0.5 truncate text-[11px] uppercase text-gray-400">{kind}</div>
                                      </div>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); setPreviewItem(f); }}
                                      aria-label="Preview"
                                      title="Preview"
                                      className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-gray-700 opacity-0 shadow-sm transition group-hover:opacity-100"
                                    >
                                      <Icon.Eye className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {hasMore && (
                        <div className="mt-4 text-center">
                          <button
                            type="button"
                            onClick={() => loadMedia({ reset: false })}
                            disabled={loadingMore}
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          >
                            {loadingMore && <Icon.Loading className="h-4 w-4" />}
                            {loadingMore ? 'Loading more...' : 'Load more'}
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {tab === 'upload' && (
                    <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center">
                      <Icon.Upload className="mx-auto h-12 w-12 text-blue-600" />
                      <div className="mt-3 text-base font-semibold text-gray-900">Upload media</div>
                      <p className="mt-1 text-sm text-gray-500">Choose one or more files. They will be added to the current folder.</p>
                      <input type="file" accept={accept} multiple onChange={(e) => handleUpload(e.target.files)} disabled={uploading} className="mt-5 max-w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm" />
                      {uploading && <div className="mt-3 inline-flex items-center gap-2 text-sm text-gray-500"><Icon.Loading className="h-4 w-4 text-blue-600" />Uploading...</div>}
                    </div>
                  )}

                  {tab === 'url' && (
                    <div className="mx-auto max-w-2xl rounded-xl border border-gray-200 bg-white p-5">
                      <label className="block text-sm font-medium text-gray-700">Media URL</label>
                      <input type="text" value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="https://example.com/file.ext" className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <div className="mt-3 flex items-center gap-3 rounded-lg bg-gray-50 p-3">
                        {preview(urlInput)}
                        <div className="min-w-0 text-sm text-gray-600">
                          <div className="font-medium text-gray-900">External media</div>
                          <div className="truncate text-xs text-gray-500">Paste any media URL. HTTPS is recommended.</div>
                        </div>
                      </div>
                      <div className="mt-4 text-right">
                        <button type="button" onClick={() => setSelected(urlInput)} disabled={!urlInput} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Use URL</button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-3 border-t border-gray-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-3">
                    {preview(selected || value)}
                    <div className="min-w-0 text-xs text-gray-500">
                      <div className="truncate font-semibold text-gray-800">{selected ? 'Selected media ready' : 'No media selected'}</div>
                      <div className="truncate">{(selected || '').slice(0, 96) || 'Choose a file, upload media, or use a URL.'}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <button type="button" onClick={() => setOpen(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                    <button type="button" onClick={applySelection} disabled={!selected && !urlInput} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-gray-400">Apply</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      {previewItem && (
        <MediaPreviewModal item={previewItem} onClose={() => setPreviewItem(null)} />
      )}
    </div>
  );
}

const currency = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '৳0';
  try {
    return `৳${num.toLocaleString()}`;
  } catch {
    return `৳${num}`;
  }
};

function AdminPriceDisplay({ price, originalPrice, compact = false, align = 'left' }) {
  const current = Math.max(0, Number(price || 0) || 0);
  const original = Math.max(0, Number(originalPrice || 0) || 0);
  const hasDiscount = original > current;
  const alignment = align === 'right' ? 'items-end text-right' : 'items-start text-left';
  return (
    <div className={`flex flex-col ${alignment}`}>
      {hasDiscount && (
        <span className={`${compact ? 'text-[11px]' : 'text-xs'} text-gray-400 line-through`}>
          {currency(original)}
        </span>
      )}
      <span className={`${compact ? 'text-sm' : 'text-base'} font-semibold text-gray-900`}>
        {currency(current)}
      </span>
    </div>
  );
}

function getAdminOrderOptionLabel(item, productLookup) {
  const explicitLabel = String(item?.variantLabel || '').trim();
  if (explicitLabel) return explicitLabel;
  const productId = String(item?.productId || item?.id || '').trim();
  const product = productLookup?.get(productId);
  if (product && (Array.isArray(product.variants) ? product.variants.length > 0 : Number(product._variantCount || 0) > 0)) {
    return 'Base model';
  }
  return '';
}

const REQUIRED_PAYMENT_OVERRIDE_LABEL = '__required_payment_override__';
 
 
 // Open invoice in a new window for a given order ID
 function openInvoice(orderId) {
   try {
     const w = window.open('', '_blank');
      if (!w) { alert('Pop-up blocked. Allow pop-ups for this site.'); return; }
      w.document.write('<!doctype html><html><head><title>Invoice</title></head><body>Loading invoice...</body></html>');
      const token = getStoredAdminToken();
      fetch(`/shopapi/orders/${encodeURIComponent(orderId)}/invoice-link`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include'
      })
        .then(async (r) => {
          if (!r.ok) throw new Error('Unable to generate invoice link');
          return r.json();
        })
        .then((data) => {
          const target = String(data?.url || '').trim();
          if (!/^https?:\/\//i.test(target)) throw new Error('Invoice link missing');
          w.location.replace(target);
          w.focus();
        })
        .catch(() => { w.document.body.innerText = 'Failed to load invoice.'; });
    } catch {}
  }

 // Open shipping paper (admin-only) in a new window for a given order ID
 function openShippingPaper(orderId) {
   try {
     const w = window.open('', '_blank');
     if (!w) { alert('Pop-up blocked. Allow pop-ups for this site.'); return; }
     w.document.write('<!doctype html><html><head><title>Shipping Slip</title></head><body>Loading shipping slip...</body></html>');
     const token = getStoredAdminToken();
     fetch(`/shopapi/orders/${encodeURIComponent(orderId)}/shipping-paper`, {
       headers: token ? { Authorization: `Bearer ${token}` } : {},
       credentials: 'include'
     })
       .then(async (r) => {
         if (!r.ok) throw new Error('Unable to generate shipping slip');
         return r.text();
       })
       .then((html) => {
         w.document.open();
         w.document.write(html);
         w.document.close();
         w.focus();
       })
       .catch(() => { w.document.body.innerText = 'Failed to load shipping slip.'; });
   } catch {}
 }
 
 function LoginForm() {
  const { login } = useAuth();
  const [credentials, setCredentials] = useState({ username: '', password: '', remember: true });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(credentials.username, credentials.password, credentials.remember);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Admin Login</h1>
          <p className="text-gray-600 mt-2">Sign in to access the admin panel</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              type="text"
              required
              value={credentials.username}
              onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              required
              value={credentials.password}
              onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
            <input
              type="checkbox"
              checked={credentials.remember}
              onChange={(e) => setCredentials(prev => ({ ...prev, remember: e.target.checked }))}
              disabled={loading}
            />
            Remember this device
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && <Icon.Loading className="w-4 h-4" />}
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}


function Dashboard({ onNavigate = () => {} }) {
  const [analytics, setAnalytics] = useState(null);
  const [observability, setObservability] = useState(null);
  const [observabilityError, setObservabilityError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { request } = useApi();
  const { addToast } = useToasts();

  // Stock alerts merged into dashboard
  //const [stockAlerts, setStockAlerts] = useState([]);
  //const [stockAlertsLoading, setStockAlertsLoading] = useState(true);

  // Current discounts/promotions
  const [activePromotions, setActivePromotions] = useState([]);
  const [promosLoading, setPromosLoading] = useState(true);

  const loadObservability = React.useCallback(async () => {
    try {
      const observabilityData = await request('/observability');
      setObservability(observabilityData);
      setObservabilityError(null);
    } catch (err) {
      console.warn('Failed to load observability metrics:', err);
      setObservabilityError('Failed to load observability data');
    }
  }, [request]);

  const loadAnalytics = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const analyticsData = await request('/analytics');
      setAnalytics(analyticsData);
      await loadObservability();
    } catch (err) {
      setError('Failed to load analytics');
      addToast('Failed to load analytics', 'error');
    } finally {
      setLoading(false);
    }
  }, [request, addToast, loadObservability]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadObservability();
    }, 15000);
    return () => clearInterval(interval);
  }, [loadObservability]);

  const loadPromotions = React.useCallback(async () => {
    try {
      setPromosLoading(true);
      const list = await request('/deals');
      const now = new Date();
      const actives = (Array.isArray(list) ? list : []).filter(p => {
        if (p.active === false) return false;
        const starts = p.start_date ? new Date(p.start_date) : null;
        const ends = p.end_date ? new Date(p.end_date) : null;
        if (starts && starts > now) return false;
        if (ends && ends < now) return false;
        return true;
      });
      setActivePromotions(actives.slice(0, 5));
    } catch (e) {
      // ignore on dashboard
    } finally {
      setPromosLoading(false);
    }
  }, [request]);

  useEffect(() => { loadPromotions(); }, [loadPromotions]);

  const formatBytes = (value) => {
    if (!Number.isFinite(value) || value < 0) return '—';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let bytes = value;
    let unitIndex = 0;
    while (bytes >= 1024 && unitIndex < units.length - 1) {
      bytes /= 1024;
      unitIndex += 1;
    }
    const formatted = bytes >= 100 ? bytes.toFixed(0) : bytes >= 10 ? bytes.toFixed(1) : bytes.toFixed(2);
    return `${formatted} ${units[unitIndex]}`;
  };

  const formatPercent = (value) => {
    if (!Number.isFinite(value)) return '—';
    return `${value.toFixed(1)}%`;
  };

  const formatDuration = (seconds) => {
    if (!Number.isFinite(seconds)) return '—';
    if (seconds < 60) return `${Math.floor(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ${Math.floor(seconds % 60)}s`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ${minutes % 60}m`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  };

  const ObservabilityStat = ({ title, primary, secondary, caption, icon }) => (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500">{title}</p>
          <p className="mt-2 text-xl font-semibold text-gray-900">{primary}</p>
          {secondary && <p className="text-xs text-gray-500 mt-1">{secondary}</p>}
          {caption && <p className="text-xs text-gray-400 mt-1">{caption}</p>}
        </div>
        {icon}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon.Loading className="w-6 h-6 text-blue-600 mr-2" />
        <span>Loading dashboard...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">
        {error}
        <button onClick={loadAnalytics} className="ml-4 px-3 py-1 bg-blue-600 text-white rounded">Retry</button>
      </div>
    );
  }

  if (!analytics) {
    return <div className="text-center py-12 text-gray-500">No analytics data available</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Overview of your store performance</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div onClick={() => onNavigate('orders')} className="cursor-pointer bg-white rounded-lg shadow-sm p-6 hover:bg-gray-50">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">{currency(analytics.totalRevenue)}</p>
              <p className="text-xs text-gray-500">Average: {currency(analytics.averageOrderValue)}/order</p>
            </div>
            <Icon.Tag className="w-8 h-8 text-green-600" />
          </div>
        </div>
        <div onClick={() => onNavigate('orders')} className="cursor-pointer bg-white rounded-lg shadow-sm p-6 hover:bg-gray-50">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">This Month Revenue</p>
              <p className="text-2xl font-bold text-gray-900">{currency(analytics.thisMonthRevenue)}</p>
              <p className="text-xs text-gray-500">Orders: {analytics.thisMonthOrders}</p>
            </div>
            <Icon.Tag className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <div onClick={() => onNavigate('orders')} className="cursor-pointer bg-white rounded-lg shadow-sm p-6 hover:bg-gray-50">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{analytics.totalOrders}</p>
              <p className="text-xs text-gray-500">Pending: {analytics.pendingOrders}</p>
            </div>
            <Icon.ShoppingCart className="w-8 h-8 text-purple-600" />
          </div>
        </div>
        <div onClick={() => onNavigate('products')} className="cursor-pointer bg-white rounded-lg shadow-sm p-6 hover:bg-gray-50">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Total Products</p>
              <p className="text-2xl font-bold text-gray-900">{analytics.totalProducts}</p>
              <p className="text-xs text-gray-500">Low stock: {analytics.lowStock}</p>
            </div>
            <Icon.Package className="w-8 h-8 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Order Status Breakdown */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Status Breakdown</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div onClick={() => { try { sessionStorage.setItem('pv_admin_orders_status','pending'); } catch {} onNavigate('orders'); }} className="cursor-pointer text-center p-4 bg-yellow-50 rounded-lg hover:bg-yellow-100">
            <p className="text-2xl font-bold text-yellow-700">{analytics.pendingOrders}</p>
            <p className="text-xs text-yellow-600 mt-1">Pending</p>
          </div>
          <div onClick={() => { try { sessionStorage.setItem('pv_admin_orders_status','confirmed'); } catch {} onNavigate('orders'); }} className="cursor-pointer text-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100">
            <p className="text-2xl font-bold text-blue-700">{analytics.confirmedOrders}</p>
            <p className="text-xs text-blue-600 mt-1">Confirmed</p>
          </div>
          <div onClick={() => { try { sessionStorage.setItem('pv_admin_orders_status','processing'); } catch {} onNavigate('orders'); }} className="cursor-pointer text-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100">
            <p className="text-2xl font-bold text-purple-700">{analytics.processingOrders}</p>
            <p className="text-xs text-purple-600 mt-1">Processing</p>
          </div>
          <div onClick={() => { try { sessionStorage.setItem('pv_admin_orders_status','shipped'); } catch {} onNavigate('orders'); }} className="cursor-pointer text-center p-4 bg-indigo-50 rounded-lg hover:bg-indigo-100">
            <p className="text-2xl font-bold text-indigo-700">{analytics.shippedOrders}</p>
            <p className="text-xs text-indigo-600 mt-1">Shipped</p>
          </div>
          <div onClick={() => { try { sessionStorage.setItem('pv_admin_orders_status','delivered'); } catch {} onNavigate('orders'); }} className="cursor-pointer text-center p-4 bg-green-50 rounded-lg hover:bg-green-100">
            <p className="text-2xl font-bold text-green-700">{analytics.deliveredOrders}</p>
            <p className="text-xs text-green-600 mt-1">Delivered</p>
          </div>
          <div onClick={() => { try { sessionStorage.setItem('pv_admin_orders_status','cancelled'); } catch {} onNavigate('orders'); }} className="cursor-pointer text-center p-4 bg-red-50 rounded-lg hover:bg-red-100">
            <p className="text-2xl font-bold text-red-700">{analytics.cancelledOrders}</p>
            <p className="text-xs text-red-600 mt-1">Cancelled</p>
          </div>
        </div>
      </div>

      {/* Low Stock Alert */}
      {analytics.lowStock > 0 && (
        <div onClick={() => onNavigate('products')} className="cursor-pointer bg-orange-50 border-l-4 border-orange-400 p-4 rounded hover:bg-orange-100">
          <div className="flex items-center">
            <Icon.Alert className="w-5 h-5 text-orange-400 mr-3" />
            <div>
              <p className="text-sm font-medium text-orange-800">
                {analytics.lowStock} product{analytics.lowStock !== 1 ? 's' : ''} running low on stock
              </p>
              <p className="text-xs text-orange-700 mt-1">Check the Products page to restock items with less than 5 units.</p>
            </div>
          </div>
        </div>
      )}

      {/* Recent Orders Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Recent Orders</h2>
          <button type="button" onClick={() => onNavigate('orders')} className="text-sm text-blue-600 underline">View all</button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analytics.recentOrders && analytics.recentOrders.length > 0 ? (
                analytics.recentOrders.map(order => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {order.display_order_id || order.id.slice(0, 8)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {order.customer?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {currency(order.total)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                        order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'processing' ? 'bg-purple-100 text-purple-800' :
                        order.status === 'confirmed' ? 'bg-indigo-100 text-indigo-800' :
                        order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    No orders yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Old stats preserved below for reference */}
      <div style={{ display: 'none' }} className="bg-white rounded-lg shadow-sm p-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Low Stock Items</p>
              <p className="text-2xl font-bold text-gray-900">{analytics.lowStock}</p>
            </div>
            <Icon.Package className="w-8 h-8 text-red-600" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Server Health</h2>
            <p className="text-sm text-gray-600">Live snapshot of VPS performance and reliability</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            {observability ? `Last update: ${new Date(observability.timestamp).toLocaleTimeString()}` : ''}
            <button type="button" onClick={() => onNavigate('settings')} className="text-blue-600 underline">Settings</button>
          </div>
        </div>
        <div className="p-6 space-y-6">
          {observabilityError && (
            <div className="rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-700 flex items-start justify-between gap-3">
              <span>{observabilityError}</span>
              <button
                type="button"
                onClick={loadAnalytics}
                className="rounded-full border border-yellow-400 px-3 py-1 text-xs font-semibold hover:bg-yellow-100"
              >
                Retry
              </button>
            </div>
          )}

          {observability && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <ObservabilityStat
                  title="CPU load"
                  primary={formatPercent(
                    Number.isFinite(observability.cpu?.systemPercent)
                      ? observability.cpu.systemPercent
                      : ((observability.cpu?.load1 ?? 0) / Math.max(1, observability.cpu?.cores || 1) * 100)
                  )}
                  secondary={`Avg (5m): ${observability.cpu?.load5?.toFixed(2) ?? '—'}`}
                  caption={`Process: ${formatPercent(observability.cpu?.processPercent)}`}
                  icon={<Icon.Settings className="w-5 h-5 text-blue-600" />}
                />
                <ObservabilityStat
                  title="Memory usage"
                  primary={formatPercent(observability.memory?.usedPercent)}
                  secondary={`${formatBytes(observability.memory?.used)} / ${formatBytes(observability.memory?.total)}`}
                  caption={`Process RSS: ${formatBytes(observability.memory?.processRss)}`}
                  icon={<Icon.Package className="w-5 h-5 text-purple-600" />}
                />
                <ObservabilityStat
                  title="Disk usage"
                  primary={formatPercent(observability.disk?.usedPercent)}
                  secondary={`${formatBytes(observability.disk?.used)} / ${formatBytes(observability.disk?.total)}`}
                  caption={observability.disk?.mount ? `Mount: ${observability.disk.mount}` : ''}
                  icon={<Icon.Download className="w-5 h-5 text-amber-600" />}
                />
                <ObservabilityStat
                  title="Network"
                  primary={`${formatBytes(observability.network?.inboundRate)} /s`}
                  secondary={`Outbound: ${formatBytes(observability.network?.outboundRate)} /s`}
                  caption={observability.network?.interfaceCount ? `${observability.network.interfaceCount} interfaces monitored` : ''}
                  icon={<Icon.Tag className="w-5 h-5 text-green-600" />}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                  <div className="font-semibold text-gray-900 mb-2">Recent API performance</div>
                  <p className="text-sm">{observability.requests?.count || 0} API requests in the last {observability.requests?.windowSeconds || 300}s</p>
                  <p className="mt-1 text-sm">Average: {observability.requests?.averageMs || 0} ms · p95: {observability.requests?.p95Ms || 0} ms</p>
                  <p className="mt-1 text-sm">Server errors: {observability.requests?.errorCount || 0} ({Number(observability.requests?.errorRate || 0).toFixed(1)}%)</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                  <div className="font-semibold text-gray-900 mb-2">Uptime</div>
                  <p className="text-sm">Process: {formatDuration(observability.uptime?.processSeconds)}</p>
                  <p className="text-sm">System: {formatDuration(observability.uptime?.systemSeconds)}</p>
                  {observability.temperature && (
                    <p className="mt-2 text-sm">
                      Temperature: {observability.temperature.celsius.toFixed(1)}°C
                    </p>
                  )}
                </div>
                {observability.alerts?.length ? (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    <div className="font-semibold text-red-800 mb-2">Active alerts</div>
                    <ul className="space-y-1">
                      {observability.alerts.map((alert, idx) => (
                        <li key={idx}>• {alert.message}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
                    <div className="font-semibold text-emerald-800 mb-2">All clear</div>
                    <p>No health alerts at the moment.</p>
                  </div>
                )}
              </div>
            </>
          )}

          {!observability && !observabilityError && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
              Observability data is not yet available. Please try again shortly.
            </div>
          )}
        </div>
      </div>


      {/* Current Discounts */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Current Discounts</h2>
            <p className="text-sm text-gray-600">Live promotions and discount codes</p>
          </div>
          <button type="button" onClick={() => onNavigate('campaigns')} className="text-sm text-blue-600 underline">Manage</button>
        </div>
        {promosLoading ? (
          <div className="p-6 text-sm text-gray-500">Loading promotions...</div>
        ) : activePromotions.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No active discounts right now</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ends</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activePromotions.map(promo => (
                  <tr key={promo.id}>
                    <td className="px-6 py-3 text-sm text-gray-900">{promo.title}</td>
                    <td className="px-6 py-3 text-sm text-gray-700">{promo.type}</td>
                    <td className="px-6 py-3 text-sm text-gray-700">{promo.type === 'percentage' ? `${promo.value}%` : `${promo.value}?`}</td>
                    <td className="px-6 py-3 text-sm text-gray-700">{promo.code || '-'}</td>
                    <td className="px-6 py-3 text-sm text-gray-500">{promo.end_date ? new Date(promo.end_date).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ProductsManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updated_desc');
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 250);
  const hasLoadedRef = useRef(false);
  const { request, downloadFile } = useApi();
  const { addToast } = useToasts();

  const loadProducts = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent && !hasLoadedRef.current) setLoading(true);
      const data = await request('/products');
      const rows = Array.isArray(data) ? data : [];
      const normalized = rows.map((product) => ({
        ...summarizeProductForAdmin(product),
        _gallery: parseStringJsonArray(product?.images)
      }));
      setProducts(normalized);
      hasLoadedRef.current = true;
    } catch (err) {
      if (!silent) addToast('Failed to load products', 'error');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [request, addToast]);

  useEffect(() => {
    loadProducts();
    const interval = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        loadProducts({ silent: true });
      }
    }, 90000);
    return () => clearInterval(interval);
  }, [loadProducts]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    (products || []).forEach((product) => {
      if (product._category) set.add(product._category);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [products]);

  const productStats = useMemo(() => {
    const total = products.length;
    const active = products.filter((product) => product._isActive).length;
    const lowStock = products.filter((product) => {
      const stock = Number(product._displayStock || 0);
      return stock > 0 && stock <= 5;
    }).length;
    const outOfStock = products.filter((product) => Number(product._displayStock || 0) <= 0).length;
    return { total, active, lowStock, outOfStock };
  }, [products]);

  const filteredProducts = useMemo(() => {
    const query = debouncedSearchTerm.trim().toLowerCase();
    let list = products;
    if (query) {
      list = list.filter((product) => String(product._searchHaystack || '').includes(query));
    }
    if (statusFilter !== 'all') {
      list = list.filter((product) => (statusFilter === 'active' ? product._isActive : !product._isActive));
    }
    if (categoryFilter !== 'all') {
      list = list.filter((product) => product._category === categoryFilter);
    }
    if (stockFilter !== 'all') {
      list = list.filter((product) => {
        const stock = Number(product._displayStock || 0);
        if (stockFilter === 'in') return stock > 5;
        if (stockFilter === 'low') return stock > 0 && stock <= 5;
        if (stockFilter === 'out') return stock <= 0;
        return true;
      });
    }
    const sorted = [...list];
    sorted.sort((a, b) => {
      if (sortBy === 'title_asc') return a._title.localeCompare(b._title);
      if (sortBy === 'price_high') return Number(b._displayPrice || 0) - Number(a._displayPrice || 0);
      if (sortBy === 'price_low') return Number(a._displayPrice || 0) - Number(b._displayPrice || 0);
      if (sortBy === 'stock_high') return Number(b._displayStock || 0) - Number(a._displayStock || 0);
      if (sortBy === 'stock_low') return Number(a._displayStock || 0) - Number(b._displayStock || 0);
      const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
      const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
      return dateB - dateA;
    });
    return sorted;
  }, [products, debouncedSearchTerm, statusFilter, stockFilter, categoryFilter, sortBy]);

  const isInteractiveProductRowTarget = useCallback((target) => {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest('button, a, input, select, textarea, label, [role="button"], [data-product-row-ignore="true"]'));
  }, []);

  const openProductEditor = useCallback((product) => {
    setEditingProduct(product);
    setShowForm(true);
  }, []);

  const openProductFromRow = useCallback((event, product) => {
    if (isInteractiveProductRowTarget(event.target)) return;
    openProductEditor(product);
  }, [isInteractiveProductRowTarget, openProductEditor]);

  const handleProductRowKeyDown = useCallback((event, product) => {
    if (isInteractiveProductRowTarget(event.target)) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openProductEditor(product);
    }
  }, [isInteractiveProductRowTarget, openProductEditor]);

  const handleDelete = async (productId) => {
    const shouldDelete = window.confirm('Are you sure you want to delete this product?');
    if (!shouldDelete) return;

    try {
      await request(`/products/${productId}`, { method: 'DELETE' });
      setProducts((prev) => prev.filter((product) => product.id !== productId));
      addToast('Product deleted successfully', 'success');
    } catch (err) {
      addToast('Failed to delete product', 'error');
    }
  };

  const toggleProductStatus = async (product) => {
    const nextActive = !product._isActive;
    try {
      const formData = new FormData();
      formData.append('active', String(nextActive));
      
      await request(`/products/${product.id}`, {
        method: 'PUT',
        body: formData
      });
      setProducts((prev) => prev.map((item) => (
        item.id === product.id ? { ...item, _isActive: nextActive, active: nextActive } : item
      )));
      addToast(`Product ${nextActive ? 'activated' : 'deactivated'}`, 'success');
    } catch (err) {
      addToast('Failed to update product status', 'error');
      loadProducts({ silent: true });
    }
  };

  const handleExport = async () => {
    try {
      await downloadFile('/products.csv', `products_${new Date().toISOString().split('T')[0]}.csv`);
      addToast('Products exported successfully', 'success');
    } catch (err) {
      addToast('Failed to export products', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon.Loading className="w-6 h-6 text-blue-600 mr-2" />
        <span>Loading products...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <p className="text-gray-600 mt-1">Catalog control with fast filters and one-click editing.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleExport}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <Icon.Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            type="button"
            onClick={() => {
              setEditingProduct(null);
              setShowForm(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Icon.Plus className="w-4 h-4" />
            Add Product
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500">Total</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{productStats.total}</div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <div className="text-xs uppercase tracking-wide text-emerald-700">Active</div>
          <div className="text-2xl font-semibold text-emerald-900 mt-1">{productStats.active}</div>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="text-xs uppercase tracking-wide text-amber-700">Low Stock</div>
          <div className="text-2xl font-semibold text-amber-900 mt-1">{productStats.lowStock}</div>
        </div>
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4">
          <div className="text-xs uppercase tracking-wide text-rose-700">Out of Stock</div>
          <div className="text-2xl font-semibold text-rose-900 mt-1">{productStats.outOfStock}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="space-y-2 border-b border-gray-200 bg-gray-50/70 px-3 py-2">
          <div className="relative">
            <Icon.Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by title, category, color, or custom ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md">
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md">
              <option value="all">All stock levels</option>
              <option value="in">In stock ({'>'}5)</option>
              <option value="low">Low stock (1-5)</option>
              <option value="out">Out of stock</option>
            </select>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md">
              <option value="all">All categories</option>
              {categoryOptions.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md">
              <option value="updated_desc">Newest first</option>
              <option value="title_asc">Title A-Z</option>
              <option value="price_high">Price high to low</option>
              <option value="price_low">Price low to high</option>
              <option value="stock_high">Stock high to low</option>
              <option value="stock_low">Stock low to high</option>
            </select>
          </div>
          <div className="text-xs text-gray-500">
            Showing {filteredProducts.length} of {products.length} products
          </div>
        </div>

        <div className="md:hidden space-y-3 p-3">
          {filteredProducts.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-10">
              No products match the current filters.
            </div>
          ) : (
            filteredProducts.map((product) => {
              const stock = Number(product._displayStock || 0);
              return (
              <div
                key={product.id}
                role="button"
                tabIndex={0}
                onClick={(e) => openProductFromRow(e, product)}
                onKeyDown={(e) => handleProductRowKeyDown(e, product)}
                className="border rounded-lg p-3 bg-white shadow-sm cursor-pointer hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <div className="flex items-start gap-3">
                  <img
                    src={product._primaryImage}
                    alt={product._title}
                    className="w-14 h-14 rounded-lg object-cover bg-gray-100"
                    onError={applyProductImageFallback}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{product._title}</div>
                    {product.dna_id && <div className="text-xs text-gray-500 truncate">ID: {product.dna_id}</div>}
                    <div className="text-xs text-gray-500 truncate">{product._category}</div>
                    <div className="mt-1">
                      <AdminPriceDisplay
                        price={product._displayPrice}
                        originalPrice={product._displayOriginalPrice}
                        showFrom={product._variantCount > 0}
                        compact
                      />
                    </div>
                    <div className="text-xs mt-1">
                      <span className={stock <= 0 ? 'text-red-600' : stock <= 5 ? 'text-amber-600' : 'text-emerald-600'}>Stock: {stock}</span>
                    </div>
                    {product._variantCount > 0 && (
                      <div className="text-xs text-blue-600 mt-1">{product._variantCount} variant{product._variantCount === 1 ? '' : 's'}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    data-product-row-ignore="true"
                    onClick={() => toggleProductStatus(product)}
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      product._isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {product._isActive ? 'Active' : 'Inactive'}
                  </button>
                </div>
                <div className="mt-3 flex items-center gap-3 text-sm">
                  <button
                    type="button"
                    data-product-row-ignore="true"
                    onClick={() => openProductEditor(product)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    data-product-row-ignore="true"
                    onClick={() => handleDelete(product.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );})
          )}
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inventory</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Updated</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No products match the current filters.
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product) => {
                  const stock = Number(product._displayStock || 0);
                  return (
                  <tr
                    key={product.id}
                    role="button"
                    tabIndex={0}
                    onClick={(e) => openProductFromRow(e, product)}
                    onKeyDown={(e) => handleProductRowKeyDown(e, product)}
                    className="hover:bg-gray-50 cursor-pointer focus:outline-none focus:bg-blue-50"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <img
                          src={product._primaryImage}
                          alt={product._title}
                          className="w-12 h-12 rounded-lg object-cover bg-gray-100"
                          onError={applyProductImageFallback}
                        />
                        <div className="ml-4 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{product._title}</div>
                          {product.dna_id && <div className="text-xs text-gray-500 truncate">ID: {product.dna_id}</div>}
                          <div className="text-sm text-gray-500 truncate">{product._color}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{product._category}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <AdminPriceDisplay
                        price={product._displayPrice}
                        originalPrice={product._displayOriginalPrice}
                        showFrom={product._variantCount > 0}
                      />
                      {product._variantCount > 0 && (
                        <div className="text-xs text-blue-600">{product._variantCount} variant{product._variantCount === 1 ? '' : 's'}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={stock <= 0 ? 'text-red-600' : stock <= 5 ? 'text-amber-600' : 'text-emerald-600'}>{stock}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        type="button"
                        data-product-row-ignore="true"
                        onClick={() => toggleProductStatus(product)}
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          product._isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {product._isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.updated_at ? new Date(product.updated_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                      <div className="inline-flex items-center gap-3">
                        <button
                          type="button"
                          data-product-row-ignore="true"
                          onClick={() => openProductEditor(product)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <Icon.Edit className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          data-product-row-ignore="true"
                          onClick={() => handleDelete(product.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Icon.Trash className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );})
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <ProductForm
          product={editingProduct}
          categories={categoryOptions}
          onClose={() => {
            setShowForm(false);
            setEditingProduct(null);
          }}
          onSave={() => {
            setShowForm(false);
            setEditingProduct(null);
            loadProducts();
          }}
        />
      )}
    </div>
  );
}

function ProductForm({ product, categories = [], onClose, onSave }) {
  const initialFormData = useMemo(() => ({
    title: product?.title || '',
    description: product?.description || '',
    price: product?.basePrice ?? product?.price ?? '',
    color: product?.color || '',
    category: product?.category || '',
    stock: product?.baseStock ?? product?.stock ?? 0,
    hideBaseModel: product?.hideBaseModel === true || product?.hide_base_model === 1 || product?.hide_base_model === true,
    variantDisplayMode: String(product?.variantDisplayMode ?? product?.variant_display_mode ?? '').toLowerCase() === 'grid' ? 'grid' : 'list',
    limitPurchaseQuantity: product?.limitPurchaseQuantity === true || product?.limit_purchase_quantity === 1 || product?.limit_purchase_quantity === true,
    maxPurchaseQuantity: Math.max(1, parseInt(product?.maxPurchaseQuantity ?? product?.max_purchase_quantity ?? 1, 10) || 1),
    hidePrice: product?.hidePrice === true || product?.hide_price === 1 || product?.hide_price === true,
    hidePriceVisibleDigits: Math.max(1, Math.min(12, parseInt(product?.hidePriceVisibleDigits ?? product?.hide_price_visible_digits ?? 1, 10) || 1)),
    prePurchase: product?.prePurchase === true || product?.pre_purchase === 1 || product?.pre_purchase === true || product?.pre_purchase === 'true',
    active: product?.active !== false,
    imageUrl: product?.img || '',
    dnaId: product?.dna_id || ''
  }), [product]);
  const [formData, setFormData] = useState(initialFormData);
  const initialVariants = useMemo(() => parseProductVariants(product?.variants), [product]);
  const [variants, setVariants] = useState(initialVariants);

  const initialGalleryItems = useMemo(() => {
    try {
      if (!product?.images) return [];
      const imgs = Array.isArray(product.images) ? product.images : JSON.parse(product.images);
      return Array.isArray(imgs) ? imgs.map((src) => String(src || '').trim()).filter(Boolean) : [];
    } catch { return []; }
  }, [product]);
  const [galleryItems, setGalleryItems] = useState(initialGalleryItems);
  const [galleryInputUrl, setGalleryInputUrl] = useState('');
  const [galleryPreview, setGalleryPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const categoryListId = React.useId();
  
  const { addToast } = useToasts();
  const { request } = useApi();

  const isDirty = useMemo(() => {
    return hasFormChanges(formData, initialFormData)
      || hasFormChanges(galleryItems, initialGalleryItems)
      || hasFormChanges(variants, initialVariants)
      || String(galleryInputUrl || '').trim().length > 0;
  }, [formData, initialFormData, galleryItems, initialGalleryItems, variants, initialVariants, galleryInputUrl]);

  const requestClose = useCallback(() => {
    if (!confirmDiscardChanges(isDirty)) return;
    onClose();
  }, [isDirty, onClose]);

  const slugify = useCallback((value) => String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80), []);

  const addGalleryItems = useCallback((incoming) => {
    setGalleryItems((prev) => {
      const next = [...prev];
      const known = new Set(prev.map((url) => String(url || '').trim()));
      (incoming || []).forEach((entry) => {
        const url = String(entry || '').trim();
        if (!url || known.has(url)) return;
        known.add(url);
        next.push(url);
      });
      return next;
    });
  }, []);

  const addGalleryUrl = useCallback(() => {
    const url = galleryInputUrl.trim();
    if (!url) return;
    addGalleryItems([url]);
    setGalleryInputUrl('');
  }, [galleryInputUrl, addGalleryItems]);

  const moveGalleryItem = useCallback((index, direction) => {
    setGalleryItems((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next;
    });
  }, []);

  const addVariant = useCallback(() => {
    setVariants((prev) => ([
      ...prev,
      {
        id: `variant_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        label: '',
        sku: '',
        description: '',
        price: Number(formData.price || 0) || 0,
        stock: 0,
        active: true,
        img: ''
      }
    ]));
  }, [formData.price]);

  const updateVariant = useCallback((variantId, patch) => {
    setVariants((prev) => prev.map((variant) => (
      variant.id === variantId ? { ...variant, ...patch } : variant
    )));
  }, []);

  const removeVariant = useCallback((variantId) => {
    setVariants((prev) => prev.filter((variant) => variant.id !== variantId));
  }, []);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append('title', String(formData.title || '').trim());
      submitData.append('description', String(formData.description || ''));
      submitData.append('price', String(Number(formData.price || 0)));
      submitData.append('color', String(formData.color || '').trim());
      submitData.append('category', String(formData.category || '').trim());
      submitData.append('stock', String(Math.max(0, parseInt(formData.stock, 10) || 0)));
      submitData.append('hideBaseModel', String(!!formData.hideBaseModel));
      submitData.append('variantDisplayMode', formData.variantDisplayMode === 'grid' ? 'grid' : 'list');
      submitData.append('limitPurchaseQuantity', String(!!formData.limitPurchaseQuantity));
      submitData.append('maxPurchaseQuantity', String(Math.max(1, parseInt(formData.maxPurchaseQuantity, 10) || 1)));
      submitData.append('hidePrice', String(!!formData.hidePrice));
      submitData.append('hidePriceVisibleDigits', String(Math.max(1, Math.min(12, parseInt(formData.hidePriceVisibleDigits, 10) || 1))));
      submitData.append('prePurchase', String(!!formData.prePurchase));
      submitData.append('active', String(!!formData.active));
      submitData.append('dnaId', String(formData.dnaId || '').trim());
      if (formData.imageUrl) submitData.append('imageUrl', formData.imageUrl);
      submitData.append('imagesJson', JSON.stringify(galleryItems || []));
      submitData.append('variantsJson', JSON.stringify((variants || []).map((variant) => ({
        id: String(variant.id || '').trim(),
        label: String(variant.label || '').trim(),
        sku: String(variant.sku || '').trim(),
        description: String(variant.description || '').trim(),
        price: Math.max(0, Number(variant.price) || 0),
        stock: Math.max(0, parseInt(variant.stock, 10) || 0),
        active: variant.active !== false,
        img: String(variant.img || '').trim() || null
      }))));
      submitData.append('variantOptionsJson', JSON.stringify([]));

      
      if (product) {
        await request(`/products/${product.id}`, {
          method: 'PUT',
          body: submitData
        });
        addToast('Product updated successfully', 'success');
      } else {
        await request('/products', {
          method: 'POST',
          body: submitData
        });
        addToast('Product created successfully', 'success');
      }
      
      onSave();
    } catch (err) {
      addToast(`Failed to ${product ? 'update' : 'create'} product`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/55 flex items-start sm:items-center justify-center z-[210] p-4"
      onClick={requestClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 border-b border-gray-200 p-4 sm:px-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {product ? 'Edit Product' : 'Add New Product'}
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              form="product-editor-form"
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading && <Icon.Loading className="h-4 w-4" />}
              {loading ? 'Saving...' : (product ? 'Update' : 'Create')}
            </button>
            <button
              type="button"
              onClick={requestClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
              aria-label="Close product editor"
            >
              <Icon.X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form id="product-editor-form" onSubmit={handleSubmit} className="min-h-0 space-y-6 overflow-y-auto p-4 sm:p-6">
          
          <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-start">
            <div className="md:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">{variants.length > 0 ? 'Base Price / fallback (৳)' : 'Price (৳)'}</label>
              <input
                type="number"
                min="0"
                step="1"
                required
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <label className="mt-2 flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={!!formData.hidePrice}
                  onChange={(e) => setFormData(prev => ({ ...prev, hidePrice: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="font-medium text-gray-900">Mask storefront price</span>
              </label>
              {formData.hidePrice && (
                <div className="mt-2 flex items-center gap-2">
                  <label className="whitespace-nowrap text-xs font-medium text-gray-600">Visible digits</label>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    step="1"
                    value={formData.hidePriceVisibleDigits}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      hidePriceVisibleDigits: Math.max(1, Math.min(12, parseInt(e.target.value, 10) || 1))
                    }))}
                    className="w-20 rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
              <input
                type="text"
                required
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <input
                type="text"
                required
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                list={categoryListId}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <datalist id={categoryListId}>
                {(categories || []).map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              {categories.length > 0 && (
                <p className="mt-1 text-xs text-gray-500">Start typing to use existing categories.</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 md:col-span-12 md:grid-cols-[10rem_11rem] md:items-start">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stock</label>
                <input
                  type="number"
                  min="0"
                  value={formData.stock}
                  onChange={(e) => setFormData(prev => ({ ...prev, stock: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {variants.length > 0 && (
                  <p className="mt-1 text-xs text-gray-500">Total storefront stock adds active variant stock on top of this.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.active ? 'active' : 'inactive'}
                  onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.value === 'active' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,0.72fr)_minmax(18rem,0.58fr)]">
            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Type</label>
            <div className="inline-flex rounded-full border border-gray-200 bg-gray-50 p-1 text-sm">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, prePurchase: false }))}
                className={`rounded-full px-4 py-2 font-semibold transition ${!formData.prePurchase ? 'bg-gray-950 text-white shadow-sm ring-1 ring-gray-950' : 'text-gray-500 hover:text-gray-900'}`}
                aria-pressed={!formData.prePurchase}
              >
                Normal product
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, prePurchase: true }))}
                className={`rounded-full px-4 py-2 font-semibold transition ${formData.prePurchase ? 'bg-gray-950 text-white shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                aria-pressed={!!formData.prePurchase}
              >
                Pre-purchase
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Normal products use stock for checkout. Pre-purchase products show the Messenger handoff regardless of stock.
            </p>
            </div>

            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={'Product description (optional). Add inline media with [image:/shopapi/uploads/example.jpg]'}
            />
            </div>
          </div>

          {/* Product ID (human-friendly) - optional */}
          <div className="max-w-xl">
            <label className="block text-sm font-medium text-gray-700 mb-1">Custom Product ID (optional)</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.dnaId}
                onChange={(e) => setFormData(prev => ({ ...prev, dnaId: e.target.value }))}
                placeholder="e.g. classic-black-tote"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, dnaId: slugify(prev.title) }))}
                className="px-3 py-2 text-xs border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap"
              >
                Use title
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">If left blank, it’s auto-generated from the title.</p>
          </div>

          <div className="space-y-4 rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-gray-900">Variants</div>
              </div>
              <div className="flex items-center gap-2">
                <label className={`inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-xs font-medium ${variants.length > 0 ? 'cursor-pointer text-gray-700' : 'cursor-not-allowed text-gray-400'}`}>
                  <input
                    type="checkbox"
                    checked={!formData.hideBaseModel}
                    disabled={variants.length === 0}
                    onChange={(e) => setFormData(prev => ({ ...prev, hideBaseModel: !e.target.checked }))}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Show base model
                </label>
                <label className="text-xs font-medium text-gray-700">Storefront view
                  <select value={formData.variantDisplayMode || 'list'} onChange={(e) => setFormData((prev) => ({ ...prev, variantDisplayMode: e.target.value === 'grid' ? 'grid' : 'list' }))} className="ml-2 rounded-md border border-gray-300 bg-white px-2 py-2 text-xs">
                    <option value="list">List view</option>
                    <option value="grid">Grid view</option>
                  </select>
                </label>
                {variants.length > 0 && (
                  <button
                    type="button"
                    onClick={() => { setVariants([]); setFormData(prev => ({ ...prev, hideBaseModel: false })); }}
                    className="px-3 py-2 text-xs border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Remove all
                  </button>
                )}
                <button
                  type="button"
                  onClick={addVariant}
                  className="px-3 py-2 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Add variant
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3">
              <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={!!formData.limitPurchaseQuantity}
                  onChange={(e) => setFormData((prev) => ({ ...prev, limitPurchaseQuantity: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Limit quantity per customer order
              </label>
              {formData.limitPurchaseQuantity && (
                <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-700">
                  Maximum units
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={formData.maxPurchaseQuantity}
                    onChange={(e) => setFormData((prev) => ({ ...prev, maxPurchaseQuantity: Math.max(1, parseInt(e.target.value, 10) || 1) }))}
                    className="w-20 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                  />
                </label>
              )}
              <p className="text-xs text-gray-500">Disabled by default. When disabled, customers can buy up to the available stock.</p>
            </div>

            {variants.length === 0 ? (
              <div className="text-sm text-gray-500">No variants added. This product will behave like a normal single-option product.</div>
            ) : (
              <div className="space-y-3">
                {variants.map((variant, index) => (
                  <div key={variant.id || index} className="rounded-lg border border-gray-200 p-4 space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-gray-900">Variant {index + 1}</div>
                      <button
                        type="button"
                        onClick={() => removeVariant(variant.id)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Label</label>
                        <input
                          type="text"
                          value={variant.label}
                          onChange={(e) => updateVariant(variant.id, { label: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="Black / Low Light"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">SKU</label>
                        <input
                          type="text"
                          value={variant.sku}
                          onChange={(e) => updateVariant(variant.id, { sku: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                              placeholder="SKU-BLK-LOW"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Price (৳)</label>
                        <input
                          type="number"
                          min="0"
                          value={variant.price}
                          onChange={(e) => updateVariant(variant.id, { price: Number(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Stock</label>
                        <input
                          type="number"
                          min="0"
                          value={variant.stock}
                          onChange={(e) => updateVariant(variant.id, { stock: parseInt(e.target.value, 10) || 0 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Variant Description</label>
                      <textarea
                        rows={2}
                        value={variant.description || ''}
                        onChange={(e) => updateVariant(variant.id, { description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder={'Optional storefront description for this option. Add inline media with [image:/shopapi/uploads/example.jpg]'}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-4 items-end">
                      <MediaPicker
                        label="Variant Image"
                        value={variant.img}
                        onChange={(url) => updateVariant(variant.id, { img: url })}
                        helpText="Optional image override for this specific variant"
                        ratioHint="1:1 square"
                      />
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={variant.active !== false}
                          onChange={(e) => updateVariant(variant.id, { active: e.target.checked })}
                        />
                        Active
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <MediaPicker
              label="Product Image"
              value={formData.imageUrl}
              onChange={(url) => setFormData(prev => ({ ...prev, imageUrl: url }))}
              helpText="Main product image"
              ratioHint="1:1 square"
            />
          </div>

          {/* Gallery images */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Gallery Media</label>
            <p className="text-xs text-gray-500 mb-3">Add multiple images, videos, or 3D models. Display ratio: 1:1 square.</p>
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
              <input
                type="text"
                value={galleryInputUrl}
                onChange={(e) => setGalleryInputUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addGalleryUrl();
                  }
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Paste media URL..."
              />
              <button
                type="button"
                onClick={addGalleryUrl}
                className="px-4 py-2 border rounded-md"
              >
                Add URL
              </button>
            </div>
              <MediaPicker
                label="Add from Media Library"
                value=""
                onChange={(url) => addGalleryItems(url ? [url] : [])}
                helpText="Pick media to add to gallery"
                accept="*/*"
                ratioHint="1:1 square"
              />
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {galleryItems.map((u, i) => {
                const kind = getMediaKind(u);
                const isVideo = kind === 'video';
                const isModel = kind === 'model';
                return (
                  <div key={`${u}-${i}`} className="border rounded-lg overflow-hidden group bg-white">
                    <div className="h-24 bg-gray-50 flex items-center justify-center relative">
                      {isVideo ? (
                        <video
                          src={u}
                          className="w-full h-full object-cover"
                          muted
                          playsInline
                          preload="metadata"
                          controls={false}
                          controlsList="nodownload noplaybackrate"
                          disablePictureInPicture
                        />
                      ) : isModel ? (
                        <div className="text-xs text-gray-600">3D Model</div>
                      ) : (
                        <img src={u} alt="" className="w-full h-full object-cover" onError={(e)=>{e.currentTarget.style.display='none';}} />
                      )}
                      <span className="absolute left-2 top-2 px-2 py-0.5 rounded bg-black/60 text-white text-[10px] uppercase">{kind}</span>
                      <button
                        type="button"
                        onClick={() => setGalleryPreview(u)}
                        className="absolute top-2 right-2 px-2 py-1 rounded bg-white/90 text-[10px] text-gray-700 border shadow"
                      >
                        Preview
                      </button>
                    </div>
                    <div className="p-2 space-y-2">
                      <div className="text-[11px] text-gray-600 truncate" title={u}>{u}</div>
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => moveGalleryItem(i, -1)}
                            disabled={i === 0}
                            className="px-2 py-1 border border-gray-300 rounded disabled:opacity-40"
                          >
                            Up
                          </button>
                          <button
                            type="button"
                            onClick={() => moveGalleryItem(i, 1)}
                            disabled={i === galleryItems.length - 1}
                            className="px-2 py-1 border border-gray-300 rounded disabled:opacity-40"
                          >
                            Down
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => setGalleryItems(prev => prev.filter((_, idx) => idx !== i))}
                          className="text-red-600"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {galleryItems.length === 0 && (
              <div className="mt-2 text-xs text-gray-500">No gallery items yet.</div>
            )}
          </div>
          {galleryPreview && (
            <MediaPreviewModal item={galleryPreview} onClose={() => setGalleryPreview(null)} />
          )}

          )}
        </form>
      </div>
    </div>
  );
  return modalContent;
}

// Continue with the rest of the components...
function UsersManager() {
  const { request } = useApi();
  const { addToast } = useToasts();
  const { user } = useAuth();


  const canManageAdmins = isPrivilegedRole(user?.role);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: 'staff',
    permissions: getRolePresetPermissions('staff')
  });
  const [editingUser, setEditingUser] = useState(null);
  const [editingUserInitial, setEditingUserInitial] = useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await request('/users');
      setUsers((Array.isArray(data) ? data : []).map((u) => {
        const normalizedRole = USER_ROLE_OPTIONS.includes(String(u.role || '').toLowerCase())
          ? String(u.role).toLowerCase()
          : 'staff';
        return {
          ...u,
          role: normalizedRole,
          permissions: normalizePermissions(u.permissions, normalizedRole)
        };
      }));
    } catch (err) {
      addToast('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  }, [request, addToast]);

  useEffect(() => { load(); }, [load]);

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const role = USER_ROLE_OPTIONS.includes(newUser.role) ? newUser.role : 'staff';
      const payload = {
        username: String(newUser.username || '').trim(),
        email: String(newUser.email || '').trim(),
        password: String(newUser.password || ''),
        role,
        permissions: normalizePermissions(newUser.permissions, role)
      };
      await request('/users', { method: 'POST', body: JSON.stringify(payload) });
      setNewUser({
        username: '',
        email: '',
        password: '',
        role: 'staff',
        permissions: getRolePresetPermissions('staff')
      });
      addToast('User created', 'success');
      load();
    } catch (err) {
      addToast(err?.message || 'Failed to create user', 'error');
    } finally {
      setCreating(false);
    }
  };

  const openUserEditor = (u) => {
    if (!canManageAdmins && isPrivilegedRole(u.role)) {
      addToast('Only admins can manage admin users', 'error');
      return;
    }
    const role = USER_ROLE_OPTIONS.includes(String(u.role || '').toLowerCase())
      ? String(u.role).toLowerCase()
      : 'staff';
    const nextEditingUser = {
      id: u.id,
      username: String(u.username || ''),
      email: String(u.email || ''),
      role,
      permissions: normalizePermissions(u.permissions, role),
      password: ''
    };
    setEditingUser(nextEditingUser);
    setEditingUserInitial(nextEditingUser);
  };

  const closeUserEditor = () => {
    const isDirty = !!editingUser && !!editingUserInitial && hasFormChanges(editingUser, editingUserInitial);
    if (!confirmDiscardChanges(isDirty)) return;
    setEditingUser(null);
    setEditingUserInitial(null);
  };

  const updateEditingRole = (role) => {
    if (!editingUser) return;
    const nextRole = USER_ROLE_OPTIONS.includes(role) ? role : 'staff';
    setEditingUser(prev => prev ? {
      ...prev,
      role: nextRole,
      permissions: getRolePresetPermissions(nextRole)
    } : prev);
  };

  const toggleEditingPermission = (permissionKey) => {
    setEditingUser(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [permissionKey]: !prev.permissions?.[permissionKey]
        }
      };
    });
  };

  const saveUser = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      const role = USER_ROLE_OPTIONS.includes(editingUser.role) ? editingUser.role : 'staff';
      const payload = {
        username: String(editingUser.username || '').trim(),
        email: String(editingUser.email || '').trim(),
        role,
        permissions: normalizePermissions(editingUser.permissions, role)
      };
      const nextPassword = String(editingUser.password || '').trim();
      if (nextPassword) payload.password = nextPassword;
      await request(`/users/${editingUser.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      addToast('User updated', 'success');
      setEditingUser(null);
      setEditingUserInitial(null);
      load();
    } catch (err) {
      addToast('Failed to update user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async () => {
    if (!editingUser) return;
    if (!window.confirm(`Delete user "${editingUser.username}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await request(`/users/${editingUser.id}`, { method: 'DELETE' });
      addToast('User deleted', 'success');
      setEditingUser(null);
      setEditingUserInitial(null);
      load();
    } catch (err) {
      addToast('Failed to delete user', 'error');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Icon.Loading className="w-6 h-6 text-blue-600 mr-2" />
        <span>Loading users...</span>
      </div>
    );
  }

  const filteredUsers = users.filter((u) => {
    const normalizedRole = String(u.role || '').toLowerCase();
    const roleMatch = roleFilter === 'all' || normalizedRole === roleFilter;
    const q = searchTerm.trim().toLowerCase();
    const queryMatch = !q || String(u.username || '').toLowerCase().includes(q) || String(u.email || '').toLowerCase().includes(q);
    return roleMatch && queryMatch;
  });

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-600 mt-1">Manage admin and staff access</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <h2 className="text-lg font-semibold text-gray-900">Add User</h2>
        <form onSubmit={createUser} className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          <input className="border rounded px-3 py-2" placeholder="Username" value={newUser.username} onChange={e => setNewUser({ ...newUser, username: e.target.value })} required />
          <input className="border rounded px-3 py-2" placeholder="Email" type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} required />
          <input className="border rounded px-3 py-2" placeholder="Password" type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} required />
          <select
            className="border rounded px-3 py-2"
            value={newUser.role}
            onChange={e => setNewUser(prev => ({
              ...prev,
              role: e.target.value,
              permissions: getRolePresetPermissions(e.target.value)
            }))}
          >
            {canManageAdmins && <option value="admin">Admin</option>}
            <option value="manager">Manager</option>
            <option value="staff">Staff</option>
          </select>
          <div className="md:col-span-4">
            <p className="text-xs text-gray-500 mb-3">
              Role presets are applied automatically. Use Manage on a user row to customize granular permissions.
            </p>
            <button type="submit" disabled={creating} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">{creating ? 'Creating...' : 'Create User'}</button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900">Existing Users</h2>
          <div className="flex gap-2">
            <input
              className="border rounded px-3 py-2 text-sm"
              placeholder="Search username/email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select className="border rounded px-3 py-2 text-sm" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="staff">Staff</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto mt-4">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((u) => (
                <tr key={u.id}>
                  <td className="px-6 py-4 text-sm text-gray-900">{u.username}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{u.email}</td>
                  <td className="px-6 py-4 text-sm">
                    <button onClick={() => openUserEditor(u)} className="px-3 py-1.5 border rounded-md text-gray-700 hover:bg-gray-50">
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td className="px-6 py-8 text-sm text-gray-500 text-center" colSpan={3}>No users found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editingUser && (
        <div
          className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4"
          onClick={closeUserEditor}
        >
          <div
            className="bg-white w-full max-w-5xl rounded-lg shadow-xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Manage User</h3>
                <p className="text-sm text-gray-500">Role presets auto-apply; you can then fine-tune individual permissions.</p>
              </div>
              <button onClick={closeUserEditor} className="text-gray-500 hover:text-gray-800">
                <Icon.X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                  <input
                    className="border rounded px-3 py-2 w-full"
                    value={editingUser.username}
                    onChange={e => setEditingUser(prev => prev ? { ...prev, username: e.target.value } : prev)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    className="border rounded px-3 py-2 w-full"
                    type="email"
                    value={editingUser.email}
                    onChange={e => setEditingUser(prev => prev ? { ...prev, email: e.target.value } : prev)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select className="border rounded px-3 py-2 w-full" value={editingUser.role} onChange={e => updateEditingRole(e.target.value)}>
                    {canManageAdmins && <option value="admin">Admin</option>}
                    <option value="manager">Manager</option>
                    <option value="staff">Staff</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password (optional)</label>
                  <input
                    className="border rounded px-3 py-2 w-full"
                    type="password"
                    placeholder="Leave blank to keep current password"
                    value={editingUser.password || ''}
                    onChange={e => setEditingUser(prev => prev ? { ...prev, password: e.target.value } : prev)}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900">Permissions</h4>
                <button
                  type="button"
                  onClick={() => setEditingUser(prev => prev ? { ...prev, permissions: getRolePresetPermissions(prev.role) } : prev)}
                  className="text-xs px-3 py-1.5 border rounded-md hover:bg-gray-50"
                >
                  Reset to Role Preset
                </button>
              </div>

              <div className="space-y-3">
                {USER_PERMISSION_GROUPS.map(group => (
                  <div key={group.id} className="border rounded-lg p-4">
                    <h5 className="text-sm font-semibold text-gray-800">{group.title}</h5>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                      {group.permissions.map(permissionKey => (
                        <label key={permissionKey} className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={!!editingUser.permissions?.[permissionKey]}
                            onChange={() => toggleEditingPermission(permissionKey)}
                          />
                          <span>{prettyPermissionLabel(permissionKey)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="px-6 py-4 border-t flex items-center justify-between gap-3">
              <div>
                {!isPrivilegedRole(editingUser.role) && (
                  <button
                    type="button"
                    onClick={deleteUser}
                    disabled={deleting}
                    className="px-4 py-2 bg-red-600 text-white rounded-md disabled:opacity-50"
                  >
                    {deleting ? 'Deleting...' : 'Delete User'}
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={closeUserEditor} className="px-4 py-2 border rounded-md">Cancel</button>
                <button type="button" onClick={saveUser} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Admin error boundary caught:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="max-w-lg w-full bg-white border border-red-200 text-red-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-2">Something went wrong</h2>
            <p className="text-sm">{String(this.state.error)}</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function AdminDashboard() {
  return (
    <AuthProvider>
      <ApiProvider>
        <ToastsProvider>
          <ErrorBoundary>
            <AdminDashboardContent />
          </ErrorBoundary>
        </ToastsProvider>
      </ApiProvider>
    </AuthProvider>
  );
}

function AdminDashboardContent() {
  const { request } = useApi();
  const { user, loading: authLoading, logout } = useAuth();
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { toasts, addToast, removeToast } = useToasts();
  const urlInitRef = useRef(false);
  const notificationAudioRef = useRef(null);
  const [notificationPermission, setNotificationPermission] = useState('unknown');
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [notificationSoundPreference, setNotificationSoundPreference] = useState(() => readAdminNotificationSound());
  const [notificationSoundUrl, setNotificationSoundUrl] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');
  const [settingsSaveControl, setSettingsSaveControl] = useState(null);
  const autoEnableAttemptedRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const settings = await request('/settings');
        if (!mounted) return;
        setNotificationSoundUrl(String(settings?.notificationSoundUrl || '').trim());
        setFaviconUrl(String(settings?.faviconUrl || '').trim());
      } catch {}
    })();
    return () => { mounted = false; };
  }, [request]);

  useEffect(() => {
    applyFaviconHref(String(faviconUrl || '').trim() || '/logo.svg');
  }, [faviconUrl]);

  useEffect(() => {
    try {
      localStorage.setItem(ADMIN_NOTIFICATION_SOUND_KEY, notificationSoundPreference);
    } catch {}
  }, [notificationSoundPreference]);

  const playNotificationSound = useCallback(() => {
    if (notificationSoundPreference === 'off') {
      setAudioEnabled(false);
      return false;
    }
    if (notificationSoundPreference === 'beep') {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) {
          setAudioEnabled(false);
          return false;
        }
        const ctx = new AudioCtx();
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();
        oscillator.type = 'sine';
        oscillator.frequency.value = 880;
        gain.gain.value = 0.08;
        oscillator.connect(gain);
        gain.connect(ctx.destination);
        oscillator.start();
        oscillator.stop(ctx.currentTime + 0.12);
        oscillator.onended = () => {
          try { ctx.close(); } catch {}
        };
        setAudioEnabled(true);
        return true;
      } catch {
        setAudioEnabled(false);
        return false;
      }
    }
    const audio = notificationAudioRef.current;
    if (!audio) return false;
    if (notificationSoundPreference === 'custom' && !String(notificationSoundUrl || '').trim()) {
      setAudioEnabled(false);
      return false;
    }
    try {
      audio.volume = 0.9;
      audio.currentTime = 0;
      const maybePromise = audio.play();
      if (maybePromise && typeof maybePromise.then === 'function') {
        maybePromise
          .then(() => setAudioEnabled(true))
          .catch(() => setAudioEnabled(false));
      } else {
        setAudioEnabled(true);
      }
      return true;
    } catch {}
    setAudioEnabled(false);
    return false;
  }, [notificationSoundPreference, notificationSoundUrl, setAudioEnabled]);

  const enableAlerts = useCallback(async (silent = false) => {
    let notifOk = notificationPermission === 'granted';
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'granted') {
        notifOk = true;
        setNotificationPermission('granted');
      } else {
        try {
          const permission = await Notification.requestPermission();
          notifOk = permission === 'granted';
          setNotificationPermission(permission);
        } catch {
          notifOk = false;
          setNotificationPermission(Notification.permission || 'default');
        }
      }
    } else {
      setNotificationPermission('unsupported');
    }

    let audioOk = audioEnabled;
    if (notificationSoundPreference === 'off') {
      audioOk = false;
    } else if (!silent) {
      audioOk = playNotificationSound();
    }
    setAudioEnabled(audioOk);

    if (!silent) {
      if (!notifOk) {
        addToast('Desktop notifications are blocked. Enable them in your browser settings.', 'info');
      }
      if (!audioOk) {
        addToast('Sound alerts are blocked. Click the page and try again.', 'info');
      }
    }
  }, [notificationPermission, audioEnabled, addToast, setNotificationPermission, setAudioEnabled, notificationSoundPreference, playNotificationSound]);

  useEffect(() => {
    if (!user || !hasUserPermission(user, 'admin.stream:read')) return;
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setNotificationPermission(Notification.permission);
    } else {
      setNotificationPermission('unsupported');
    }
    const autoEnable = () => {
      if (autoEnableAttemptedRef.current) return;
      autoEnableAttemptedRef.current = true;
      enableAlerts(true);
    };
    window.addEventListener('click', autoEnable, { once: true });
    return () => window.removeEventListener('click', autoEnable);
  }, [user, enableAlerts]);

  const showDesktopNotification = useCallback((title, body, targetPage = null) => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') {
      setNotificationPermission(Notification.permission || 'default');
      return;
    }
    try {
      const notification = new Notification(title, { body });
      if (targetPage) {
        notification.onclick = () => {
          try { window.focus(); } catch {}
          setCurrentPage(targetPage);
          try { notification.close(); } catch {}
        };
      }
      setNotificationPermission('granted');
    } catch {}
  }, [setNotificationPermission, setCurrentPage]);

  const handleAdminEvent = useCallback((eventType, payload = {}) => {
    let title = 'New activity';
    let body = '';
    let toastMessage = 'New activity';
    let targetPage = null;
    if (eventType === 'order.created') {
      const displayId = payload.displayOrderId || payload.orderId || '';
      const itemCount = payload.itemsCount != null ? `${payload.itemsCount} item${payload.itemsCount === 1 ? '' : 's'}` : '';
      const total = payload.total != null ? `Total ${payload.total}` : '';
      title = 'New order received';
      body = [displayId && `Order ${displayId}`, itemCount, total].filter(Boolean).join(' • ');
      toastMessage = displayId ? `New order received (${displayId})` : 'New order received';
      targetPage = hasUserPermission(user, 'orders:read') ? 'orders' : null;
    } else if (eventType === 'review.created') {
      title = 'New review submitted';
      if (payload.productTitle) {
        body = `Product: ${payload.productTitle}`;
        toastMessage = `New review: ${payload.productTitle}`;
      } else if (payload.productSlug) {
        body = `Product: ${payload.productSlug}`;
        toastMessage = `New review: ${payload.productSlug}`;
      } else if (payload.productId) {
        body = `Product ${String(payload.productId).slice(0, 8)}...`;
        toastMessage = 'New review submitted';
      } else {
        body = 'New product review';
        toastMessage = 'New review submitted';
      }
      targetPage = hasUserPermission(user, 'reviews:read') ? 'reviews' : null;
    } else if (eventType === 'help.created') {
      title = 'New support ticket';
      body = payload.topic ? `Topic: ${payload.topic}` : 'New help request';
      toastMessage = 'New support ticket';
      targetPage = hasUserPermission(user, 'help:read') ? 'help' : null;
    }
    playNotificationSound();
    addToast(toastMessage, 'info', targetPage ? { targetPage } : undefined);
    showDesktopNotification(title, body, targetPage);
  }, [addToast, playNotificationSound, showDesktopNotification, user]);

  useEffect(() => {
    if (!user || !hasUserPermission(user, 'admin.stream:read')) return;
    let source = null;
    let closed = false;
    let retryTimer = null;

    const getStreamUrl = () => {
      const token = getStoredAdminToken();
      if (token) {
        return `/shopapi/admin/stream?access_token=${encodeURIComponent(token)}`;
      }
      return '/shopapi/admin/stream';
    };

    const connect = () => {
      if (closed) return;
      try {
        source = new EventSource(getStreamUrl(), { withCredentials: true });
      } catch {
        return;
      }

      const onEvent = (type) => (event) => {
        let payload = {};
        try { payload = JSON.parse(event.data || '{}'); } catch {}
        handleAdminEvent(type, payload);
      };

      source.addEventListener('order.created', onEvent('order.created'));
      source.addEventListener('review.created', onEvent('review.created'));
      source.addEventListener('help.created', onEvent('help.created'));

      source.onerror = async () => {
        if (source) {
          source.close();
        }
        if (closed) return;
        // If no token is available and cookie auth is unavailable, stop retrying to avoid 401 spam.
        if (!getStoredAdminToken()) {
          try {
            const cookieProbe = await fetch(`${API_BASE}/auth/me`, { credentials: 'include' });
            if (!cookieProbe.ok) return;
          } catch {
            return;
          }
        }
        retryTimer = window.setTimeout(connect, 3000);
      };
    };

    connect();
    return () => {
      closed = true;
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
      if (source) {
        source.close();
      }
    };
  }, [user, handleAdminEvent]);

  // Overlay scrollbar (invisible until scrolling) – mirrors client behavior
  useEffect(() => {
    if (typeof document === 'undefined') return;
    let styleTag = document.getElementById('pv-admin-scrollbar-style');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'pv-admin-scrollbar-style';
      styleTag.innerHTML = `
html{scrollbar-gutter:stable both-edges}html,body{scrollbar-width:none}body::-webkit-scrollbar{width:0;height:0}
.pv-admin-sidebar-scroll{scrollbar-width:none;-ms-overflow-style:none;-webkit-overflow-scrolling:touch}
.pv-admin-sidebar-scroll::-webkit-scrollbar{display:none;width:0;height:0}
#pv-scrollbar-admin{position:fixed;right:10px;top:10px;bottom:10px;width:10px;pointer-events:none;touch-action:none;z-index:1000;opacity:0;transform:translateX(10px);transition:opacity .24s ease-out,transform .24s ease-out}
#pv-scrollbar-admin.show{opacity:1;transform:translateX(0)}
#pv-scrollbar-admin .thumb{position:absolute;left:0;width:10px;border-radius:9999px;background:rgba(17,24,39,.35);box-shadow:0 2px 8px rgba(0,0,0,.12)}
@media(hover:hover) and (pointer:fine){#pv-scrollbar-admin.show{pointer-events:auto}#pv-scrollbar-admin .thumb{cursor:grab}#pv-scrollbar-admin .thumb:hover{background:rgba(17,24,39,.5)}#pv-scrollbar-admin.dragging .thumb{cursor:grabbing;background:rgba(17,24,39,.58)}}`;
      document.head.appendChild(styleTag);
    }

    let bar = document.getElementById('pv-scrollbar-admin');
    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'pv-scrollbar-admin';
      const t = document.createElement('div');
      t.className = 'thumb';
      bar.appendChild(t);
      document.body.appendChild(bar);
    }
    const thumb = bar.querySelector('.thumb');

    let hideTimer = null;
    let dragging = false;
    let dragPointerId = null;
    let dragStartY = 0;
    let dragStartScroll = 0;
    const showBar = () => {
      bar.classList.add('show');
      if (hideTimer) window.clearTimeout(hideTimer);
      if (!dragging) hideTimer = window.setTimeout(() => bar.classList.remove('show'), 700);
    };
    const getScrollMetrics = () => {
      const doc = document.documentElement;
      const body = document.body;
      const scrollTop = window.scrollY || doc.scrollTop || body.scrollTop || 0;
      const scrollHeight = Math.max(body.scrollHeight, doc.scrollHeight, body.offsetHeight, doc.offsetHeight, body.clientHeight, doc.clientHeight);
      const vh = window.innerHeight;
      const maxScroll = Math.max(1, scrollHeight - vh);
      return { scrollTop, scrollHeight, vh, maxScroll };
    };
    const updateThumb = () => {
      const { scrollTop, scrollHeight, vh, maxScroll } = getScrollMetrics();
      if (scrollHeight <= vh + 1) {
        bar.style.display = 'none';
        return;
      }
      bar.style.display = 'block';
      const minThumb = 28;
      const thumbH = Math.max(minThumb, Math.round((vh * vh) / scrollHeight));
      const ratio = Math.min(1, Math.max(0, scrollTop / maxScroll));
      const top = Math.round(Math.max(0, bar.clientHeight - thumbH) * ratio);
      thumb.style.height = thumbH + 'px';
      thumb.style.transform = `translateY(${top}px)`;
    };
    const onThumbPointerDown = (event) => {
      if (event.button !== 0) return;
      dragging = true;
      dragPointerId = event.pointerId;
      dragStartY = event.clientY;
      dragStartScroll = getScrollMetrics().scrollTop;
      bar.classList.add('dragging', 'show');
      if (hideTimer) window.clearTimeout(hideTimer);
      try { thumb.setPointerCapture(event.pointerId); } catch {}
      event.preventDefault();
      event.stopPropagation();
    };
    const onPointerMove = (event) => {
      if (!dragging || event.pointerId !== dragPointerId) return;
      const { maxScroll } = getScrollMetrics();
      const trackTravel = Math.max(1, bar.clientHeight - thumb.offsetHeight);
      window.scrollTo(0, Math.max(0, Math.min(maxScroll, dragStartScroll + ((event.clientY - dragStartY) / trackTravel) * maxScroll)));
      event.preventDefault();
    };
    const endDrag = (event) => {
      if (!dragging || (event?.pointerId !== undefined && event.pointerId !== dragPointerId)) return;
      dragging = false;
      dragPointerId = null;
      bar.classList.remove('dragging');
      updateThumb();
      showBar();
    };
    const onTrackPointerDown = (event) => {
      if (event.button !== 0 || event.target !== bar) return;
      const { maxScroll } = getScrollMetrics();
      const trackTravel = Math.max(1, bar.clientHeight - thumb.offsetHeight);
      const targetTop = event.clientY - bar.getBoundingClientRect().top - (thumb.offsetHeight / 2);
      window.scrollTo(0, Math.max(0, Math.min(maxScroll, (targetTop / trackTravel) * maxScroll)));
      updateThumb();
      showBar();
      event.preventDefault();
    };
    const onScroll = () => { updateThumb(); showBar(); };
    const onResize = () => { updateThumb(); };
    updateThumb();
    thumb.addEventListener('pointerdown', onThumbPointerDown);
    bar.addEventListener('pointerdown', onTrackPointerDown);
    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      if (hideTimer) window.clearTimeout(hideTimer);
      thumb.removeEventListener('pointerdown', onThumbPointerDown);
      bar.removeEventListener('pointerdown', onTrackPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      const inst = document.getElementById('pv-scrollbar-admin');
      if (inst && inst.isConnected) inst.remove();
    };
  }, []);

  const navigation = React.useMemo(() => {
    const can = (perm) => hasUserPermission(user, perm);
    const canManagePromotions = can('campaigns:read') || can('promotions:read');
    // Reordered navigation: remove Newsletter and merge stock alerts into dashboard.
    return [
      can('dashboard:view') ? { id: 'dashboard', name: 'Dashboard', icon: Icon.Dashboard } : null,
      can('analytics:read') ? { id: 'visitors', name: 'Analytics', icon: Icon.Activity } : null,
      can('products:read') ? { id: 'products', name: 'Products', icon: Icon.Package } : null,
      can('orders:read') ? { id: 'orders', name: 'Orders', icon: Icon.ShoppingCart } : null,
      canManagePromotions ? { id: 'campaigns', name: 'Promotions', icon: Icon.Tag } : null,
      // Requests moved under Settings submenu
      can('reviews:read') ? { id: 'reviews', name: 'Reviews', icon: Icon.Eye } : null,
      can('audit:read') ? { id: 'audit', name: 'Audit Logs', icon: Icon.Activity } : null,
      can('help:read') ? { id: 'help', name: 'Help', icon: Icon.Palette } : null,
      can('media:read') ? { id: 'media', name: 'Media', icon: Icon.Upload } : null,
      (can('settings:read') || can('settings:write') || can('users:read') || can('backup:manage') || can('legal:write'))
        ? { id: 'settings', name: 'Settings', icon: Icon.Settings }
        : null,
    ].filter(Boolean);
  }, [user]);

  useEffect(() => {
    if (navigation.length && !navigation.find(n => n.id === currentPage)) {
      setCurrentPage(navigation[0].id);
    }
  }, [currentPage, navigation]);

  useEffect(() => {
    if (urlInitRef.current) return;
    if (typeof window === 'undefined') return;
    if (!navigation.length) return;
    const params = new URLSearchParams(window.location.search);
    const page = params.get('page');
    if (page && navigation.find(n => n.id === page)) {
      setCurrentPage(page);
    }
    urlInitRef.current = true;
  }, [navigation]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!urlInitRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('page') !== currentPage) {
      params.set('page', currentPage);
      params.delete('tab');
      params.delete('status');
      const qs = params.toString();
      const nextUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      window.history.replaceState({}, '', nextUrl);
    }
  }, [currentPage]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Icon.Loading className="w-8 h-8 text-blue-600" />
      </div>
    );
  }

  if (!user) {
    return <LoginForm />;
  }

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard onNavigate={setCurrentPage} />;
      case 'products':
        return <ProductsManager />;
      case 'visitors':
        return <VisitorsAnalyticsManager />;
      case 'orders':
        return <OrdersManager />;
      case 'campaigns':
        return <PromotionsManager />;
      case 'reviews':
        return <ReviewsManager storefrontBaseUrl={getStorefrontBaseUrl()} />;
      case 'audit':
        return <AuditLogsManager />;
      case 'help':
        return <HelpNotesManager />;
      case 'media':
        return <MediaLibrary />;
      case 'settings':
        return (
          <SettingsManager
            notificationSoundPreference={notificationSoundPreference}
            onNotificationSoundPreferenceChange={setNotificationSoundPreference}
            notificationSoundUrl={notificationSoundUrl}
            onNotificationSoundUrlChange={setNotificationSoundUrl}
            onFaviconUrlChange={setFaviconUrl}
            onSaveControlChange={setSettingsSaveControl}
            onPreviewNotificationSound={() => {
              const ok = playNotificationSound();
              if (!ok) {
                addToast('Could not play preview sound. Browser might be blocking audio.', 'info');
              }
            }}
          />
        );
      default:
        return <Dashboard />;
    }
  };

  const canUseMobileSettingsSave = currentPage === 'settings' && settingsSaveControl?.visible && !settingsSaveControl?.disabled;

  return (
    <div className="min-h-screen bg-gray-50">
      <audio
        ref={notificationAudioRef}
        src={notificationSoundPreference === 'custom' && String(notificationSoundUrl || '').trim() ? notificationSoundUrl : notificationSound}
        preload="auto"
      />
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 h-14 bg-gray-900 text-gray-100 flex items-center justify-between px-4 shadow">
        <button
          onClick={() => setSidebarOpen(true)}
          className="inline-flex items-center gap-2 text-sm"
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
          Menu
        </button>
        <div className="text-sm font-semibold">Admin Panel</div>
        {currentPage === 'settings' ? (
          <button
            type="button"
            onClick={() => settingsSaveControl?.save?.()}
            disabled={!canUseMobileSettingsSave}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-gray-100 hover:bg-white/10 hover:text-white disabled:opacity-40"
            aria-label="Save"
            title="Save"
          >
            {settingsSaveControl?.saving ? (
              <Icon.Loading className="h-7 w-7" />
            ) : (
              <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
                <path d="M17 21v-8H7v8" />
                <path d="M7 3v5h8" />
              </svg>
            )}
          </button>
        ) : (
          <div className="h-10 w-10" />
        )}
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 w-64 bg-gray-900 text-gray-100 shadow-xl z-50 transform transition-transform duration-200 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-800">
          <h1 className="text-xl font-bold">Admin Panel</h1>
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden text-gray-400 hover:text-gray-200"
            aria-label="Close sidebar"
          >
            <Icon.X className="w-5 h-5" />
          </button>
        </div>
        <nav className="pv-admin-sidebar-scroll mt-4 flex-1 overflow-y-auto overscroll-contain pb-24">
          {navigation.map((item) => (
            <div key={item.id}>
              <button
                onClick={() => {
                  setCurrentPage(item.id);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center px-6 py-3 text-left transition-colors ${
                  currentPage === item.id
                    ? 'bg-gray-800 text-white border-r-2 border-blue-400'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5 mr-3" />
                {item.name}
              </button>
            </div>
          ))}
        </nav>
        <div className="mt-auto p-6 border-t border-gray-800">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-300">
              {user.username}
            </div>
            <button
              onClick={logout}
              className="text-gray-400 hover:text-red-400"
              title="Logout"
            >
              <Icon.LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
      {/* Main content */}
      <div className="md:ml-64">
        <main className="p-4 md:p-8 pt-20 md:pt-8">
          {hasUserPermission(user, 'admin.stream:read') && notificationPermission === 'denied' && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                Enable desktop and sound alerts for realtime updates.
              </div>
              <button
                type="button"
                onClick={() => enableAlerts(false)}
                className="px-3 py-1.5 rounded-md border border-amber-300 bg-white hover:bg-amber-100"
              >
                Enable alerts
              </button>
            </div>
          )}
          {renderCurrentPage()}
        </main>
      </div>
      {/* Toast notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => {
          const baseTone =
            toast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
            toast.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
            'bg-blue-50 text-blue-800 border border-blue-200';
          return (
            <button
              key={toast.id}
              type="button"
              onClick={() => {
                if (toast.targetPage) {
                  setCurrentPage(toast.targetPage);
                  setSidebarOpen(false);
                }
                removeToast(toast.id);
              }}
              className={`px-4 py-3 rounded-lg shadow-lg max-w-sm text-left transition cursor-pointer hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 ${baseTone}`}
            >
              <span>{toast.message}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function OrdersManager() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(() => {
    try {
      const value = new URLSearchParams(window.location.search).get('status') || 'all';
      return ['all', 'pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'].includes(value) ? value : 'all';
    } catch { return 'all'; }
  });
  const [bulkStatusValue, setBulkStatusValue] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [fulfillmentStatusFilter, setFulfillmentStatusFilter] = useState('all');
  const [bkashOnly, setBkashOnly] = useState(false);
  const [bkashPendingOnly, setBkashPendingOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 400);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [hasMore, setHasMore] = useState(false);
  const [selected, setSelected] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showCustomOrderForm, setShowCustomOrderForm] = useState(false);
  const { request, downloadFile } = useApi();
  const { addToast } = useToasts();

  useEffect(() => {
    try {
      const preset = sessionStorage.getItem('pv_admin_orders_status');
      if (preset) {
        setStatusFilter(preset);
        sessionStorage.removeItem('pv_admin_orders_status');
      }
    } catch {}
  }, []);

  const loadOrders = React.useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      params.set('offset', String((page - 1) * pageSize));
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (paymentStatusFilter !== 'all') params.set('paymentStatus', paymentStatusFilter);
      if (fulfillmentStatusFilter !== 'all') params.set('fulfillmentStatus', fulfillmentStatusFilter);
      if (debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());
      const data = await request(`/orders?${params.toString()}`);
      setOrders(data);
      setHasMore(Array.isArray(data) && data.length === pageSize);
    } catch (err) {
      addToast('Failed to load orders', 'error');
    } finally {
      setLoading(false);
    }
  }, [request, addToast, page, pageSize, statusFilter, paymentStatusFilter, fulfillmentStatusFilter, debouncedSearchTerm]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, paymentStatusFilter, fulfillmentStatusFilter, searchTerm, pageSize]);

  const isInteractiveOrderRowTarget = useCallback((target) => {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest('button, a, input, select, textarea, label, [role="button"], [data-order-row-ignore="true"]'));
  }, []);

  const openOrderDetailsFromRow = useCallback((event, order) => {
    if (isInteractiveOrderRowTarget(event.target)) return;
    setSelectedOrder(order);
  }, [isInteractiveOrderRowTarget]);

  const handleOrderRowKeyDown = useCallback((event, order) => {
    if (isInteractiveOrderRowTarget(event.target)) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setSelectedOrder(order);
    }
  }, [isInteractiveOrderRowTarget]);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await request(`/orders/${orderId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      addToast('Order status updated', 'success');
      loadOrders();
    } catch (err) {
      addToast('Failed to update order status', 'error');
    }
  };

  const handleExport = async () => {
    try {
      const selectedIds = filteredOrders.filter((order) => selected[order.id]).map((order) => order.id);
      const query = selectedIds.length > 0
        ? `?selectedIds=${encodeURIComponent(selectedIds.join(','))}`
        : '';
      await downloadFile(`/export/orders${query}`, `orders_steadfast_${new Date().toISOString().split('T')[0]}.xlsx`);
      addToast('Orders exported successfully', 'success');
    } catch (err) {
      addToast('Failed to export orders', 'error');
    }
  };

  const filteredOrders = useMemo(() => {
    let list = orders;
    if (bkashOnly) list = list.filter(o => (o.payment?.method === 'bkash'));
    if (bkashPendingOnly) list = list.filter(o => (o.payment?.method === 'bkash' && o.status === 'pending'));
    return list;
  }, [orders, bkashOnly, bkashPendingOnly]);

  const allSelected = useMemo(() => filteredOrders.length > 0 && filteredOrders.every(o => selected[o.id]), [filteredOrders, selected]);
  const selectedCount = useMemo(() => filteredOrders.filter((order) => selected[order.id]).length, [filteredOrders, selected]);
  const toggleSelectAll = () => {
    if (allSelected) {
      const next = { ...selected };
      filteredOrders.forEach(o => { delete next[o.id]; });
      setSelected(next);
    } else {
      const next = { ...selected };
      filteredOrders.forEach(o => { next[o.id] = true; });
      setSelected(next);
    }
  };

  const bulkTogglePaymentStatus = async () => {
    const targets = filteredOrders.filter(o => selected[o.id]);
    if (targets.length === 0) { addToast('Select orders first', 'error'); return; }
    try {
      for (const order of targets) {
        const current = order.payment_status || (order.payment?.method === 'bkash' ? 'pending' : 'unpaid');
        const next = current === 'paid' ? 'unpaid' : 'paid';
        await request(`/orders/${order.id}`, { method: 'PUT', body: JSON.stringify({ paymentStatus: next }) });
      }
      addToast(`Updated payment status for ${targets.length} order(s)`, 'success');
      setSelected({});
      loadOrders();
    } catch (err) {
      addToast('Bulk update failed', 'error');
    }
  };

  const bulkUpdateOrderStatus = async () => {
    const orderIds = filteredOrders.filter((order) => selected[order.id]).map((order) => order.id);
    if (orderIds.length === 0) { addToast('Select orders first', 'error'); return; }
    if (!bulkStatusValue) { addToast('Choose a status first', 'error'); return; }
    try {
      const result = await request('/orders/bulk-status', {
        method: 'PUT',
        body: JSON.stringify({ orderIds, status: bulkStatusValue })
      });
      const updatedCount = Number(result?.updatedCount || orderIds.length);
      const autoMarkedPaidCount = Number(result?.autoMarkedPaidCount || 0);
      addToast(
        autoMarkedPaidCount > 0
          ? `Updated ${updatedCount} order(s); ${autoMarkedPaidCount} delivered order(s) were also marked paid`
          : `Updated status for ${updatedCount} order(s)`,
        'success'
      );
      setSelected({});
      setBulkStatusValue('');
      loadOrders();
    } catch (err) {
      addToast('Failed to bulk update order status', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon.Loading className="w-6 h-6 text-blue-600 mr-2" />
        <span>Loading orders...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
          <p className="text-gray-600 mt-1">Manage customer orders</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowCustomOrderForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Icon.Plus className="w-4 h-4" />
            Create
          </button>
          <button
            onClick={handleExport}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <Icon.Download className="w-4 h-4" />
            Export Steadfast XLSX
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-200">
        <div className="flex flex-wrap items-center gap-2">
          {['all', 'pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1 text-sm rounded-full ${
                statusFilter === status
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
          <label className="ml-2 inline-flex items-center gap-1 text-sm text-gray-700">
            <input type="checkbox" checked={bkashOnly} onChange={(e) => setBkashOnly(e.target.checked)} /> bKash only
          </label>
          <label className="inline-flex items-center gap-1 text-sm text-gray-700">
            <input type="checkbox" checked={bkashPendingOnly} onChange={(e) => setBkashPendingOnly(e.target.checked)} /> Pending bKash
          </label>
          <select
            value={paymentStatusFilter}
            onChange={(e) => setPaymentStatusFilter(e.target.value)}
            className="px-2 py-1 text-sm border border-gray-300 rounded-md min-w-[170px]"
          >
            <option value="all">Payment: All</option>
            <option value="unpaid">Payment: Unpaid</option>
            <option value="pending">Payment: Pending</option>
            <option value="paid">Payment: Paid</option>
            <option value="refunded">Payment: Refunded</option>
            <option value="partial_refund">Payment: Partial Refund</option>
          </select>
          <select
            value={fulfillmentStatusFilter}
            onChange={(e) => setFulfillmentStatusFilter(e.target.value)}
            className="px-2 py-1 text-sm border border-gray-300 rounded-md min-w-[180px]"
          >
            <option value="all">Fulfillment: All</option>
            <option value="unfulfilled">Fulfillment: Unfulfilled</option>
            <option value="processing">Fulfillment: Processing</option>
            <option value="shipped">Fulfillment: Shipped</option>
            <option value="delivered">Fulfillment: Delivered</option>
            <option value="returned">Fulfillment: Returned</option>
            <option value="cancelled">Fulfillment: Cancelled</option>
          </select>
          <select
            value={bulkStatusValue}
            onChange={(e) => setBulkStatusValue(e.target.value)}
            className="px-2 py-1 text-sm border border-gray-300 rounded-md min-w-[170px]"
            title="Choose the order status to apply to selected orders"
          >
            <option value="">Set status (selected)</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button
            onClick={bulkUpdateOrderStatus}
            disabled={!bulkStatusValue || selectedCount === 0}
            className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
            title="Apply the chosen status to selected orders"
          >
            Apply status ({selectedCount})
          </button>
          <button
            onClick={bulkTogglePaymentStatus}
            className="ml-auto bg-gray-900 text-white px-3 py-1 rounded-md hover:bg-gray-800 text-sm"
            title="Toggle payment status for selected orders"
          >
            Toggle payment (selected)
          </button>
        </div>
        {/* Search */}
        <div className="mt-3 relative">
          <Icon.Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search orders by ID, name, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="text-sm text-gray-600">Page {page}</div>
          <button
            type="button"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 text-sm border rounded-md disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage(p => p + 1)}
            disabled={!hasMore}
            className="px-3 py-1.5 text-sm border rounded-md disabled:opacity-50"
          >
            Next
          </button>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm text-gray-600">Rows</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value) || 50)}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md"
            >
              {[25, 50, 100, 200].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>
        </div>

        <div className="md:hidden space-y-3 p-3">
          {filteredOrders.map(order => (
            <div
              key={order.id}
              role="button"
              tabIndex={0}
              onClick={(e) => openOrderDetailsFromRow(e, order)}
              onKeyDown={(e) => handleOrderRowKeyDown(e, order)}
              className="border rounded-lg p-3 bg-white shadow-sm cursor-pointer hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-mono text-gray-700">{order.display_order_id || order.id.slice(0, 8) + '...'}</div>
                  <div className="text-sm font-medium text-gray-900">
                    {order.customer.name || `${order.customer.firstName || ''} ${order.customer.lastName || ''}`.trim() || '-'}
                  </div>
                  <div className="text-xs text-gray-500">{order.customer.email}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold">{currency(order.total)}</div>
                  {order.discount_amount > 0 && (
                    <div className="text-xs text-green-600">-{currency(order.discount_amount)} discount</div>
                  )}
                  {Number(order.manual_discount_amount || 0) > 0 && (
                    <div className="text-xs text-green-600">-{currency(order.manual_discount_amount)} special</div>
                  )}
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-600">
                {order.items.length} items • {new Date(order.created_at).toLocaleDateString()}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <select
                  value={order.status}
                  onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                  className="text-xs font-semibold rounded-full px-2 py-1 border"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <button
                  onClick={() => setSelectedOrder(order)}
                  className="text-xs px-3 py-1 rounded border"
                >
                  View
                </button>
              </div>
            </div>
          ))}
          {filteredOrders.length === 0 && (
            <div className="text-sm text-gray-500 text-center py-6">No orders found</div>
          )}
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3"><input type="checkbox" checked={allSelected} onChange={toggleSelectAll} /></th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOrders.map(order => (
                <tr
                  key={order.id}
                  tabIndex={0}
                  onClick={(e) => openOrderDetailsFromRow(e, order)}
                  onKeyDown={(e) => handleOrderRowKeyDown(e, order)}
                  className={`${order.status === 'pending' ? 'bg-orange-50 hover:bg-orange-100 focus:bg-orange-100' : 'hover:bg-gray-50 focus:bg-gray-50'} cursor-pointer focus:outline-none`}
                >
                  <td className="px-3 py-4"><input type="checkbox" checked={!!selected[order.id]} onChange={(e) => setSelected(prev => ({ ...prev, [order.id]: e.target.checked }))} /></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {order.display_order_id || order.id.slice(0, 8) + '...'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {order.customer.name || `${order.customer.firstName || ''} ${order.customer.lastName || ''}`.trim() || '-'}
                    </div>
                    <div className="text-sm text-gray-500">{order.customer.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {order.items.length} items
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {currency(order.total)}
                    {order.discount_amount > 0 && (
                      <div className="text-xs text-green-600">-{currency(order.discount_amount)} discount</div>
                    )}
                    {Number(order.manual_discount_amount || 0) > 0 && (
                      <div className="text-xs text-green-600">-{currency(order.manual_discount_amount)} special</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="text-gray-900">{order.payment?.method?.toUpperCase() || '-'}</div>
                    <div className="mt-1">
                      {(() => {
                        const ps = order.payment_status || (order.payment?.method === 'bkash' ? 'pending' : 'unpaid');
                        const cls =
                          ps === 'paid' ? 'bg-green-100 text-green-800' :
                          ps === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          ps === 'refunded' ? 'bg-red-100 text-red-800' :
                          ps === 'partial_refund' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-700';
                        return (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
                            Payment: {ps.replace('_', ' ')}
                          </span>
                        );
                      })()}
                    </div>
                    {order.payment?.method === 'bkash' && (
                      <>
                        <div className="text-xs text-gray-600">Txn: {order.payment?.bkashTxn || '-'}</div>
                        {order.payment?.bkashScreenshotUrl && (
                          <div className="mt-1">
                            <a
                              href={(String(order.payment.bkashScreenshotUrl).startsWith('/uploads') ? `/shopapi${order.payment.bkashScreenshotUrl}` : order.payment.bkashScreenshotUrl)}
                              target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center text-xs text-blue-600 underline"
                              title="Open bKash receipt"
                            >
                              View receipt
                            </a>
                          </div>
                        )}
                      </>
                      )}
                    <div className="mt-1 space-x-1">
                      {order.payment?.method === 'bkash' && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${order.status === 'pending' ? 'bg-pink-100 text-pink-800' : 'bg-pink-200 text-pink-900'}`}>
                          {order.status === 'pending' ? 'Pending verify' : 'Verified'}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={order.status}
                      onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                      className={`text-xs font-semibold rounded-full px-2 py-1 border-0 ${
                        order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                        order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'confirmed' ? 'bg-yellow-100 text-yellow-800' :
                        order.status === 'pending' ? 'bg-orange-100 text-orange-800' :
                        order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <div className="mt-1 text-xs text-gray-500">
                      Fulfillment: {order.fulfillment_status || 'unfulfilled'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(order.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex items-center gap-3">
                    <button
                      onClick={() => setSelectedOrder(order)}
                      className="text-blue-600 hover:text-blue-900"
                      title="View Details"
                    >
                      <Icon.Eye className="w-4 h-4" />
                    </button>
                    {(order.payment?.method === 'bkash' && order.status === 'pending') && (
                      <button
                        onClick={() => request(`/orders/${order.id}`, { method: 'PUT', body: JSON.stringify({ status: 'confirmed', paymentStatus: 'paid' }) }).then(loadOrders)}
                        className="text-pink-700 hover:text-pink-900"
                        title="Mark as paid (bKash)"
                      >
                        Mark paid
                      </button>
                    )}
                    <button
                      onClick={() => openInvoice(order.id)}
                      className="text-gray-700 hover:text-gray-900"
                      title="Print Invoice"
                    >
                      Invoice
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    {/* Order Details Modal */}
    {selectedOrder && (
      <OrderDetailsModal 
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
        onUpdate={(updatedOrder) => {
          setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
          setSelectedOrder(updatedOrder);
        }}
      />
    )}
    
    {/* Custom Order Form Modal */}
    {showCustomOrderForm && (
      <CustomOrderForm
        onClose={() => setShowCustomOrderForm(false)}
        onCreated={() => {
          loadOrders();
          setShowCustomOrderForm(false);
        }}
      />
    )}
  </div>
);
}

function CustomOrderForm({ onClose, onCreated }) {
  const { request } = useApi();
  const { addToast } = useToasts();
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({
    shippingRates: { inside: 60, outside: 120 },
    bkashCashoutEnabled: false,
    bkashCashoutAmount: 20
  });
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    address: '',
    city: '',
    items: [],
    shippingLocation: 'inside',
    paymentMethod: 'cod',
    specialDiscount: 0,
    notes: ''
  });
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedVariant, setSelectedVariant] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const isDirty = useMemo(() => {
    return Boolean(
      String(formData.customerName || '').trim()
      || String(formData.customerEmail || '').trim()
      || String(formData.customerPhone || '').trim()
      || String(formData.address || '').trim()
      || String(formData.city || '').trim()
      || String(formData.notes || '').trim()
      || (Array.isArray(formData.items) && formData.items.length > 0)
      || String(selectedProduct || '').trim()
      || String(selectedVariant || '').trim()
      || Number(quantity || 1) !== 1
      || formData.shippingLocation !== 'inside'
      || formData.paymentMethod !== 'cod'
      || Number(formData.specialDiscount || 0) !== 0
    );
  }, [formData, selectedProduct, selectedVariant, quantity]);

  const requestClose = useCallback(() => {
    if (!confirmDiscardChanges(isDirty)) return;
    onClose();
  }, [isDirty, onClose]);

  useEffect(() => {
    request('/products').then(data => setProducts((Array.isArray(data) ? data : []).filter(p => p.active).map((product) => summarizeProductForAdmin(product)))).catch(() => addToast('Failed to load products', 'error'));
    request('/settings').then(setSettings).catch(() => {});
  }, [request, addToast]);

  const selectedProductRecord = useMemo(
    () => products.find((product) => product.id === selectedProduct) || null,
    [products, selectedProduct]
  );

  const selectableVariants = useMemo(
    () => (selectedProductRecord?.variants || []).filter((variant) => variant.active !== false),
    [selectedProductRecord]
  );

  const selectedBasePrice = useMemo(
    () => Number(
      selectedProductRecord?.baseDisplayPrice
      ?? selectedProductRecord?.basePrice
      ?? selectedProductRecord?.price
      ?? 0
    ) || 0,
    [selectedProductRecord]
  );

  const selectedBaseOriginalPrice = useMemo(() => {
    const original = Number(selectedProductRecord?.baseOriginalPrice);
    return original > selectedBasePrice ? original : null;
  }, [selectedBasePrice, selectedProductRecord]);

  useEffect(() => {
    if (!selectedProductRecord) {
      setSelectedVariant('');
      return;
    }
    if (!selectedVariant) return;
    if (selectableVariants.some((variant) => variant.id === selectedVariant)) return;
    setSelectedVariant('');
  }, [selectedProductRecord, selectableVariants, selectedVariant]);

  const addItem = () => {
    const product = selectedProductRecord;
    if (!product) return;
    const variant = selectableVariants.find((entry) => entry.id === selectedVariant) || null;

    const itemKey = variant ? `${product.id}:${variant.id}` : product.id;
    const existing = formData.items.find(i => i.itemKey === itemKey);
    const lineName = variant ? `${product.title} - ${variant.label}` : product.title;
    const linePrice = variant ? variant.price : selectedBasePrice;
    if (existing) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.map(i => i.itemKey === itemKey ? { ...i, quantity: i.quantity + quantity } : i)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, {
          itemKey,
          productId: product.id,
          variantId: variant?.id || null,
          variantLabel: variant?.label || (selectableVariants.length > 0 ? 'Base model' : null),
          variantSku: variant?.sku || null,
          name: lineName,
          price: linePrice,
          originalPrice: variant
            ? (Number(variant.originalPrice) > Number(variant.price) ? Number(variant.originalPrice) : null)
            : (Number(selectedBaseOriginalPrice) > Number(selectedBasePrice) ? Number(selectedBaseOriginalPrice) : null),
          quantity,
          img: variant?.img || product.img || null
        }]
      }));
    }
    setSelectedProduct('');
    setSelectedVariant('');
    setQuantity(1);
  };

  const removeItem = (itemKey) => {
    setFormData(prev => ({ ...prev, items: prev.items.filter(i => i.itemKey !== itemKey) }));
  };

  const calculateTotal = () => {
    if (formData.items.length === 0) return 0;
    const itemsTotal = formData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const shipping = formData.shippingLocation === 'inside' ? (settings.shippingRates?.inside || 60) : (settings.shippingRates?.outside || 120);
    const bkashCharge = formData.paymentMethod === 'bkash' && settings.bkashCashoutEnabled
      ? Math.max(0, Number(settings.bkashCashoutAmount) || 0)
      : 0;
    const beforeDiscount = itemsTotal + shipping + bkashCharge;
    return Math.max(0, beforeDiscount - Math.max(0, Number(formData.specialDiscount) || 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      addToast('Please add at least one product', 'error');
      return;
    }
    
    try {
      setSubmitting(true);
      const orderData = {
        customerName: formData.customerName,
        email: formData.customerEmail,
        phone: formData.customerPhone,
        address: formData.address,
        city: formData.city,
        items: formData.items,
        paymentMethod: formData.paymentMethod,
        shippingCost: formData.shippingLocation === 'inside' ? (settings.shippingRates?.inside || 60) : (settings.shippingRates?.outside || 120),
        specialDiscount: Math.max(0, Number(formData.specialDiscount) || 0),
        total: calculateTotal(),
        notes: formData.notes,
        source: 'admin_custom'
      };
      
      await request('/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData)
      });
      
      addToast('Custom order created successfully', 'success');
      onCreated();
    } catch (err) {
      console.error('Create order error:', err);
      const errorMessage = err.error || err.message || 'Failed to create order';
      addToast('Failed to create order: ' + errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4"
      onClick={requestClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 border-b p-4 sm:px-6">
          <div><h2 className="text-xl font-semibold">Create Order</h2><p className="mt-0.5 text-xs text-gray-500">Customer, products, delivery, and payment in one view.</p></div>
          <div className="flex items-center gap-2">
            <button type="submit" form="custom-order-form" disabled={submitting || formData.items.length === 0} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-gray-400">
              {submitting && <Icon.Loading className="h-4 w-4" />}{submitting ? 'Creating...' : 'Create'}
            </button>
            <button type="button" onClick={requestClose} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100" aria-label="Close custom order editor"><Icon.X className="h-5 w-5" /></button>
          </div>
        </div>
        
        <form id="custom-order-form" onSubmit={handleSubmit} className="grid min-h-0 grid-cols-1 gap-5 overflow-y-auto p-4 sm:p-6 lg:grid-cols-12 lg:items-start">
          <section className="lg:col-span-5">
            <h3 className="mb-3 text-base font-semibold text-gray-900">Customer</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input type="text" required value={formData.customerName} onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input type="tel" required value={formData.customerPhone} onChange={(e) => setFormData(prev => ({ ...prev, customerPhone: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
                <input type="email" value={formData.customerEmail} onChange={(e) => setFormData(prev => ({ ...prev, customerEmail: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                <input type="text" required value={formData.city} onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                <textarea required rows={2} value={formData.address} onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
            </div>
          </section>

          <section className="lg:col-span-7">
            <h3 className="mb-3 text-base font-semibold text-gray-900">Products</h3>
            <div className="mb-4 flex flex-wrap gap-2">
              <select value={selectedProduct} onChange={(e) => setSelectedProduct(e.target.value)} className="min-w-[14rem] flex-[2_1_18rem] rounded-md border border-gray-300 px-3 py-2">
                <option value="">Select a product...</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title} - {currency(p._displayPrice)}
                    {Number(p._displayOriginalPrice) > Number(p._displayPrice) ? ` (was ${currency(p._displayOriginalPrice)})` : ''}
                  </option>
                ))}
              </select>
              {selectableVariants.length > 0 && (
                <select value={selectedVariant} onChange={(e) => setSelectedVariant(e.target.value)} className="min-w-[12rem] flex-1 rounded-md border border-gray-300 px-3 py-2">
                  <option value="">
                    Base model - {currency(selectedBasePrice)}
                    {Number(selectedBaseOriginalPrice) > Number(selectedBasePrice) ? ` (was ${currency(selectedBaseOriginalPrice)})` : ''}
                  </option>
                  {selectableVariants.map((variant) => (
                    <option key={variant.id} value={variant.id}>
                      {variant.label} - {currency(variant.price)}
                      {Number(variant.originalPrice) > Number(variant.price) ? ` (was ${currency(variant.originalPrice)})` : ''}
                    </option>
                  ))}
                </select>
              )}
              <input type="number" min="1" value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value) || 1)} className="w-20 px-3 py-2 border border-gray-300 rounded-md" />
              <button type="button" onClick={addItem} disabled={!selectedProduct} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400">Add</button>
            </div>

            {formData.items.length > 0 && (
              <div className="border border-gray-200 rounded-md overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Product</th>
                      <th className="px-4 py-2 text-center text-sm font-medium text-gray-700">Qty</th>
                      <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">Price</th>
                      <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">Total</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                    <tbody>
                      {formData.items.map(item => (
                      <tr key={item.itemKey || `${item.productId}:${item.variantId || 'base'}`} className="border-t">
                        <td className="px-4 py-2 text-sm">{item.name}</td>
                        <td className="px-4 py-2 text-sm text-center">{item.quantity}</td>
                        <td className="px-4 py-2 text-sm text-right">৳{item.price}</td>
                        <td className="px-4 py-2 text-sm text-right font-medium">৳{item.price * item.quantity}</td>
                        <td className="px-4 py-2 text-right">
                          <button type="button" onClick={() => removeItem(item.itemKey || `${item.productId}:${item.variantId || 'base'}`)} className="text-red-600 hover:text-red-800 text-sm">Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:col-span-7">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Shipping Location</label>
              <select value={formData.shippingLocation} onChange={(e) => setFormData(prev => ({ ...prev, shippingLocation: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                <option value="inside">Inside Dhaka (৳{settings.shippingRates?.inside || 60})</option>
                <option value="outside">Outside Dhaka (৳{settings.shippingRates?.outside || 120})</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
              <select value={formData.paymentMethod} onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                <option value="cod">Cash on Delivery</option>
                <option value="bkash">bKash</option>
                <option value="bank">Bank Transfer</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Special Discount (৳)</label>
              <input
                type="number"
                min="0"
                step="1"
                value={formData.specialDiscount}
                onChange={(e) => setFormData(prev => ({ ...prev, specialDiscount: Math.max(0, Number(e.target.value) || 0) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="0"
              />
              <p className="mt-1 text-xs text-gray-500">Subtracts directly from this order without changing product prices.</p>
            </div>
          </section>

          <section className="lg:col-span-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">Order Notes</label>
            <textarea rows={3} value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Special instructions or notes..." />
          </section>

          <div className="border-t pt-4 lg:col-span-12">
              <div className="flex justify-between text-lg font-semibold">
                <span>Total:</span>
                <span>৳{calculateTotal()}</span>
              </div>
              {formData.items.length > 0 && formData.paymentMethod === 'bkash' && settings.bkashCashoutEnabled && (
                <div className="mt-2 text-xs text-gray-500 text-right">
                  Includes bKash cashout charge: ৳{Math.max(0, Number(settings.bkashCashoutAmount) || 0)}
                </div>
              )}
          </div>

        </form>
      </div>
    </div>
  );
}

function PromotionsManager() {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => {
    try {
      const value = new URLSearchParams(window.location.search).get('status') || 'all';
      return ['all', 'active', 'scheduled', 'inactive', 'expired'].includes(value) ? value : 'all';
    } catch { return 'all'; }
  });
  const [scopeFilter, setScopeFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [viewingPromotion, setViewingPromotion] = useState(null);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 250);
  const hasLoadedRef = useRef(false);
  const { request } = useApi();
  const { addToast } = useToasts();

  const loadPromotions = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent && !hasLoadedRef.current) setLoading(true);
      const data = await request('/deals');
      setPromotions(Array.isArray(data) ? data : []);
      hasLoadedRef.current = true;
    } catch (err) {
      if (!silent) addToast('Failed to load promotions', 'error');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [request, addToast]);

  useEffect(() => {
    loadPromotions();
  }, [loadPromotions]);

  const normalizedPromotions = useMemo(() => {
    return promotions.map((promo) => {
      let productCount = 0;
      try {
        if (promo.product_ids) {
          const ids = JSON.parse(promo.product_ids);
          productCount = Array.isArray(ids) ? ids.length : 0;
        }
      } catch {
        productCount = 0;
      }
      const hasCategory = Boolean(String(promo.category || '').trim());
      const scopeType = productCount > 0 ? 'products' : (hasCategory ? 'category' : 'all');
      const now = Date.now();
      const startsAt = promo.start_date ? new Date(promo.start_date).getTime() : null;
      const endsAt = promo.end_date ? new Date(promo.end_date).getTime() : null;
      const isScheduled = startsAt && startsAt > now;
      const isExpired = endsAt && endsAt < now;
      const activeNow = !!promo.active && !isScheduled && !isExpired;
      const status = activeNow ? 'active' : (isScheduled ? 'scheduled' : (promo.active ? 'expired' : 'inactive'));
      const showOnOffers = Number(promo.show_on_offers ?? 1) === 1;
      const showAsPopup = Number(promo.show_as_popup ?? 0) === 1;
      const showAsHeader = Number(promo.show_as_header ?? 0) === 1;
      const hasImage = Boolean(String(promo.image_url || '').trim());
      let displayPages = [];
      try {
        const parsed = promo.display_pages ? JSON.parse(promo.display_pages) : [];
        displayPages = Array.isArray(parsed) ? parsed : [];
      } catch {
        displayPages = [];
      }
      return {
        ...promo,
        _productCount: productCount,
        _scopeType: scopeType,
        _status: status,
        _showOnOffers: showOnOffers,
        _showAsPopup: showAsPopup,
        _showAsHeader: showAsHeader,
        _hasImage: hasImage,
        _displayPages: displayPages,
        _scopeLabel: productCount > 0
          ? `${productCount} product(s)`
          : (hasCategory ? `Category • ${promo.category}` : 'All items'),
        _searchHaystack: `${promo.title || ''} ${promo.description || ''} ${promo.code || ''} ${promo.category || ''} ${promo.header_text || ''} ${promo.offer_details || ''}`.toLowerCase()
      };
    });
  }, [promotions]);

  const promotionStats = useMemo(() => ({
    total: normalizedPromotions.length,
    active: normalizedPromotions.filter((promo) => promo._status === 'active').length,
    scheduled: normalizedPromotions.filter((promo) => promo._status === 'scheduled').length,
    inactive: normalizedPromotions.filter((promo) => promo._status === 'inactive' || promo._status === 'expired').length
  }), [normalizedPromotions]);

  const filteredPromotions = useMemo(() => {
    const query = debouncedSearchTerm.trim().toLowerCase();
    let list = normalizedPromotions;
    if (query) {
      list = list.filter((promo) => promo._searchHaystack.includes(query));
    }
    if (statusFilter !== 'all') {
      list = list.filter((promo) => promo._status === statusFilter);
    }
    if (scopeFilter === 'all_items') {
      list = list.filter((promo) => promo._scopeType === 'all');
    } else if (scopeFilter !== 'all') {
      list = list.filter((promo) => promo._scopeType === scopeFilter);
    }
    if (typeFilter !== 'all') {
      list = list.filter((promo) => String(promo.type || '') === typeFilter);
    }
    return list;
  }, [normalizedPromotions, debouncedSearchTerm, statusFilter, scopeFilter, typeFilter]);

  const isInteractivePromoRowTarget = useCallback((target) => {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest('button, a, input, select, textarea, label, [role="button"], [data-promo-row-ignore="true"]'));
  }, []);

  const openPromotionEditor = useCallback((promo) => {
    setEditingPromotion(promo);
    setShowForm(true);
  }, []);

  const openPromotionFromRow = useCallback((event, promo) => {
    if (isInteractivePromoRowTarget(event.target)) return;
    setViewingPromotion(promo);
  }, [isInteractivePromoRowTarget]);

  const handlePromotionRowKeyDown = useCallback((event, promo) => {
    if (isInteractivePromoRowTarget(event.target)) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setViewingPromotion(promo);
    }
  }, [isInteractivePromoRowTarget]);

  const handleDelete = async (promotionId) => {
    const shouldDelete = window.confirm('Are you sure you want to delete this promotion?');
    if (!shouldDelete) return;

    try {
      await request(`/deals/${promotionId}`, { method: 'DELETE' });
      setPromotions((prev) => prev.filter((promo) => promo.id !== promotionId));
      addToast('Promotion deleted successfully', 'success');
    } catch (err) {
      addToast('Failed to delete promotion', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon.Loading className="w-6 h-6 text-blue-600 mr-2" />
        <span>Loading promotions...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Promotions</h1>
          <p className="text-gray-600 mt-1">Manage discount rules and storefront placements from one section.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              setEditingPromotion(null);
              setShowForm(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Icon.Plus className="w-4 h-4" />
            Add
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500">Total</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{promotionStats.total}</div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <div className="text-xs uppercase tracking-wide text-emerald-700">Active</div>
          <div className="text-2xl font-semibold text-emerald-900 mt-1">{promotionStats.active}</div>
        </div>
        <div className="rounded-lg border border-sky-200 bg-sky-50 p-4">
          <div className="text-xs uppercase tracking-wide text-sky-700">Scheduled</div>
          <div className="text-2xl font-semibold text-sky-900 mt-1">{promotionStats.scheduled}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="text-xs uppercase tracking-wide text-gray-700">Inactive/Expired</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{promotionStats.inactive}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="space-y-2 border-b border-gray-200 bg-gray-50/70 px-3 py-2">
          <div className="relative">
            <Icon.Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by title, description, code, or scope..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md">
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="scheduled">Scheduled</option>
              <option value="inactive">Inactive</option>
              <option value="expired">Expired</option>
            </select>
            <select value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md">
              <option value="all">All scopes</option>
              <option value="all_items">All items</option>
              <option value="category">Category</option>
              <option value="products">Specific products</option>
            </select>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md">
              <option value="all">All types</option>
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed amount</option>
              <option value="bogo">Buy 1 Get 1 Free</option>
              <option value="free_delivery">Free delivery</option>
            </select>
          </div>
          <div className="text-xs text-gray-500">
            Showing {filteredPromotions.length} of {normalizedPromotions.length} promotions
          </div>
        </div>
        <div className="md:hidden space-y-3 p-3">
          {filteredPromotions.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-8">No promotions match the current filters.</div>
          ) : filteredPromotions.map((promo) => (
            <div
              key={promo.id}
              role="button"
              tabIndex={0}
              onClick={(e) => openPromotionFromRow(e, promo)}
              onKeyDown={(e) => handlePromotionRowKeyDown(e, promo)}
              className="border rounded-lg p-3 bg-white shadow-sm cursor-pointer hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <div className="text-sm font-medium text-gray-900">{promo.title}</div>
              <div className="text-xs text-gray-500 mt-1">{promo.description}</div>
              <div className="text-xs text-gray-400 mt-1">Scope: {promo._scopeLabel}</div>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800">{promo.type}</span>
                <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-800">
                  {promo.type === 'bogo' ? 'Buy 1 Get 1 Free' : promo.type === 'free_delivery' ? 'Free delivery' : promo.type === 'percentage' ? `${promo.value}%` : currency(promo.value)}
                </span>
                <span className={`px-2 py-1 rounded-full ${
                  promo._status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : promo._status === 'scheduled'
                      ? 'bg-sky-100 text-sky-800'
                      : promo._status === 'expired'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-gray-100 text-gray-800'
                }`}>
                  {promo._status}
                </span>
                <span className={`px-2 py-1 rounded-full ${promo._showOnOffers ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'}`}>
                  {promo._showOnOffers ? 'Public offers: on' : 'Public offers: off'}
                </span>
                <span className={`px-2 py-1 rounded-full ${promo._showAsPopup ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-700'}`}>
                  {promo._showAsPopup ? 'Popup: on' : 'Popup: off'}
                </span>
                <span className={`px-2 py-1 rounded-full ${promo._showAsHeader ? 'bg-cyan-100 text-cyan-800' : 'bg-gray-100 text-gray-700'}`}>
                  {promo._showAsHeader ? 'Header: on' : 'Header: off'}
                </span>
                <span className={`px-2 py-1 rounded-full ${promo._hasImage ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700'}`}>
                  {promo._hasImage ? 'Image: set' : 'Image: missing'}
                </span>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                Pages: {promo._displayPages.length > 0 ? promo._displayPages.join(', ') : 'all pages'}
              </div>
              <div className="mt-2 text-xs text-gray-600">
                Usage: {promo.used_count || 0}{promo.max_uses && ` / ${promo.max_uses}`}
                {promo.max_items && <div>{promo.items_used || 0} / {promo.max_items} items</div>}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  data-promo-row-ignore="true"
                  onClick={() => openPromotionEditor(promo)}
                  className="text-blue-600 hover:text-blue-900 text-sm"
                >
                  Edit
                </button>
                <button
                  type="button"
                  data-promo-row-ignore="true"
                  onClick={() => handleDelete(promo.id)}
                  className="text-red-600 hover:text-red-900 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPromotions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">No promotions match the current filters.</td>
                </tr>
              ) : filteredPromotions.map((promo) => (
                <tr
                  key={promo.id}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => openPromotionFromRow(e, promo)}
                  onKeyDown={(e) => handlePromotionRowKeyDown(e, promo)}
                  className="hover:bg-gray-50 cursor-pointer focus:outline-none focus:bg-blue-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{promo.title}</div>
                    <div className="text-sm text-gray-500">{promo.description}</div>
                    <div className="text-xs text-gray-400 mt-1">Scope: {promo._scopeLabel}</div>
                    <div className={`text-xs mt-1 ${promo._showOnOffers ? 'text-emerald-600' : 'text-gray-400'}`}>
                      Public offers page: {promo._showOnOffers ? 'Shown' : 'Hidden'}
                    </div>
                    <div className="text-xs mt-1 text-gray-500">
                      Popup: {promo._showAsPopup ? 'Shown' : 'Hidden'} • Header: {promo._showAsHeader ? 'Shown' : 'Hidden'}
                    </div>
                    <div className="text-xs mt-1 text-gray-500">
                      Pages: {promo._displayPages.length > 0 ? promo._displayPages.join(', ') : 'All pages'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {promo.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {promo.type === 'bogo' ? 'Buy 1 Get 1 Free' : promo.type === 'free_delivery' ? 'Free delivery' : promo.type === 'percentage' ? `${promo.value}%` : currency(promo.value)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {promo.code ? (
                      <code className="bg-gray-100 px-2 py-1 rounded">{promo.code}</code>
                    ) : (
                      <span className="text-gray-400">Auto-apply</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {promo.used_count || 0}
                    {promo.max_uses && ` / ${promo.max_uses}`}
                    {promo.max_items && (
                      <div>{promo.items_used || 0} / {promo.max_items} items</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      promo._status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : promo._status === 'scheduled'
                          ? 'bg-sky-100 text-sky-800'
                          : promo._status === 'expired'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-gray-100 text-gray-800'
                    }`}>
                      {promo._status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        data-promo-row-ignore="true"
                        onClick={() => openPromotionEditor(promo)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Icon.Edit className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        data-promo-row-ignore="true"
                        onClick={() => handleDelete(promo.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Icon.Trash className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <PromotionForm
          promotion={editingPromotion}
          onClose={() => {
            setShowForm(false);
            setEditingPromotion(null);
          }}
          onSave={() => {
            setShowForm(false);
            setEditingPromotion(null);
            loadPromotions();
          }}
        />
      )}
      {viewingPromotion && (
        <PromotionDetailsModal
          promotion={viewingPromotion}
          onClose={() => setViewingPromotion(null)}
          onEdit={() => {
            const promotion = viewingPromotion;
            setViewingPromotion(null);
            openPromotionEditor(promotion);
          }}
        />
      )}
    </div>
  );
}

function PromotionDetailsModal({ promotion, onClose, onEdit }) {
  const placements = [
    Number(promotion?._showOnOffers ?? promotion?.show_on_offers ?? 0) === 1 ? 'Offers page' : '',
    Number(promotion?._showAsPopup ?? promotion?.show_as_popup ?? 0) === 1 ? 'Popup' : '',
    Number(promotion?._showAsHeader ?? promotion?.show_as_header ?? 0) === 1 ? 'Header bar' : ''
  ].filter(Boolean);
  const detailRows = [
    ['Status', promotion?._status || (promotion?.active ? 'active' : 'inactive')],
    ['Type', promotion?.type || '-'],
    ['Value', promotion?.type === 'bogo' ? 'Buy 1 Get 1 Free — once per order' : promotion?.type === 'free_delivery' ? 'Delivery fee waived' : promotion?.type === 'percentage' ? `${promotion?.value || 0}%` : currency(promotion?.value || 0)],
    ...(promotion?.type === 'bogo' ? [['Free product', promotion.reward_product_id], ['Free product option', promotion.reward_variant_id || 'Base model']] : []),
    ['Code', promotion?.code || 'Auto-apply'],
    ['Scope', promotion?._scopeLabel || promotion?.category || 'All items'],
    ['Minimum order', currency(promotion?.min_order_amount || 0)],
    ['Usage', `${promotion?.used_count || 0}${promotion?.max_uses ? ` / ${promotion.max_uses}` : ''}`],
    ['Schedule', `${promotion?.start_date ? new Date(promotion.start_date).toLocaleDateString() : 'Immediate'} - ${promotion?.end_date ? new Date(promotion.end_date).toLocaleDateString() : 'No end date'}`]
  ];
  return createPortal(
    <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/50 p-4" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-5 py-4">
          <div className="min-w-0"><h2 className="truncate text-xl font-semibold text-gray-900">{promotion?.title || 'Promotion Details'}</h2><p className="mt-0.5 text-xs text-gray-500">Promotion #{promotion?.id}</p></div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onEdit} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"><Icon.Edit className="h-4 w-4" />Edit</button>
            <button type="button" onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100" aria-label="Close promotion details"><Icon.X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="min-h-0 overflow-y-auto p-5">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{detailRows.map(([label, value]) => <div key={label} className="rounded-lg border border-gray-200 bg-gray-50 p-3"><div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</div><div className="mt-1 break-words text-sm font-medium text-gray-900">{value}</div></div>)}</div>
              {(promotion?.description || promotion?.offer_details) && <div className="rounded-lg border border-gray-200 p-4"><h3 className="text-sm font-semibold text-gray-900">Customer-facing content</h3>{promotion.description && <p className="mt-2 text-sm text-gray-600">{promotion.description}</p>}{promotion.offer_details && <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700">{promotion.offer_details}</p>}</div>}
              <div className="rounded-lg border border-gray-200 p-4"><h3 className="text-sm font-semibold text-gray-900">Placement</h3><div className="mt-2 flex flex-wrap gap-2">{placements.length ? placements.map(label => <span key={label} className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">{label}</span>) : <span className="text-sm text-gray-500">No storefront placement</span>}</div><div className="mt-3 text-xs text-gray-500">Pages: {promotion?._displayPages?.length ? promotion._displayPages.join(', ') : 'All pages'}</div>{promotion?.target_url && <div className="mt-1 break-all text-xs text-blue-600">{promotion.target_url}</div>}</div>
            </div>
            <aside className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              {promotion?.image_url ? <img src={promotion.image_url} alt="" className="aspect-square w-full rounded-lg bg-white object-contain" /> : <div className="flex aspect-square items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white text-sm text-gray-400">No promotion image</div>}
              {promotion?.header_text && <div className="mt-3 rounded-lg bg-gray-900 p-3 text-sm text-white">{promotion.header_text}</div>}
            </aside>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function PromotionForm({ promotion, onClose, onSave }) {
  const availablePromotionPages = [
    'home', 'catalog', 'product', 'offers', 'track', 'help', 'cart', 'checkout', 'thanks', 'terms', 'privacy', 'cookies'
  ];
  const initialProductIds = (() => {
    try {
      if (promotion?.product_ids) return JSON.parse(promotion.product_ids);
      if (promotion?.productIds) return Array.isArray(promotion.productIds) ? promotion.productIds : [];
      return [];
    } catch {
      return [];
    }
  })();
  const initialScope = (() => {
    if (initialProductIds.length > 0) return 'products';
    if (promotion?.category) return 'category';
    return 'all';
  })();
  const initialDisplayPages = (() => {
    try {
      if (!promotion?.display_pages) return [];
      const parsed = JSON.parse(promotion.display_pages);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();
  const initialFormData = useMemo(() => ({
    title: promotion?.title || '',
    description: promotion?.description || '',
    type: promotion?.type || 'percentage',
    rewardProductId: promotion?.reward_product_id || '',
    rewardVariantId: promotion?.reward_variant_id || '',
    value: promotion?.value || '',
    code: promotion?.code || '',
    minOrderAmount: promotion?.min_order_amount || 0,
    maxUses: promotion?.max_uses || '',
    maxItems: promotion?.max_items || '',
    startDate: promotion?.start_date ? promotion.start_date.split('T')[0] : '',
    endDate: promotion?.end_date ? promotion.end_date.split('T')[0] : '',
    scope: initialScope,
    category: promotion?.category || '',
    productIds: initialProductIds,
    imageUrl: promotion?.image_url || '',
    targetUrl: promotion?.target_url || '',
    offerDetails: promotion?.offer_details || '',
    headerText: promotion?.header_text || '',
    showOnOffers: Number(promotion?.show_on_offers ?? 1) === 1,
    showAsPopup: Number(promotion?.show_as_popup ?? 0) === 1,
    showAsHeader: Number(promotion?.show_as_header ?? 0) === 1,
    displayPages: initialDisplayPages,
    active: Number(promotion?.active ?? 1) === 1
  }), [promotion, initialScope, initialProductIds, initialDisplayPages]);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  const { request } = useApi();
  const { addToast } = useToasts();
  const isDirty = useMemo(() => hasFormChanges(formData, initialFormData), [formData, initialFormData]);
  const hasVisualPlacement = formData.showOnOffers || formData.showAsPopup;
  const hasHeaderPlacement = formData.showAsHeader;
  const hasAnyPlacement = hasVisualPlacement || hasHeaderPlacement;
  const isGiftOrDelivery = ['bogo', 'free_delivery'].includes(formData.type);
  const giftProduct = products.find(product => product.id === formData.rewardProductId);
  const giftVariants = (() => {
    try {
      const variants = Array.isArray(giftProduct?.variants) ? giftProduct.variants : JSON.parse(giftProduct?.variants || '[]');
      return variants.filter(variant => variant.active !== false);
    } catch { return []; }
  })();
  const requestClose = useCallback(() => {
    if (!confirmDiscardChanges(isDirty)) return;
    onClose();
  }, [isDirty, onClose]);
  const categoryOptions = useMemo(() => {
    const set = new Set();
    (products || []).forEach(p => {
      const c = (p?.category || '').trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [products]);
  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return products;
    return (products || []).filter(p => {
      const title = (p?.title || '').toLowerCase();
      const cat = (p?.category || '').toLowerCase();
      return title.includes(q) || cat.includes(q);
    });
  }, [products, productSearch]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingProducts(true);
        const data = await request('/products');
        if (!mounted) return;
        setProducts(Array.isArray(data) ? data : []);
      } catch {
        if (mounted) setProducts([]);
      } finally {
        if (mounted) setLoadingProducts(false);
      }
    })();
    return () => { mounted = false; };
  }, [request]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (formData.scope === 'category' && !formData.category.trim()) {
        addToast('Choose a category for this discount scope', 'error');
        setLoading(false);
        return;
      }
      if (formData.scope === 'products' && (!formData.productIds || formData.productIds.length === 0)) {
        addToast('Select at least one product for this discount scope', 'error');
        setLoading(false);
        return;
      }
      const targetUrl = String(formData.targetUrl || '').trim();
      if (hasVisualPlacement && !targetUrl) {
        addToast('Target URL is required for offers or popup placement', 'error');
        setLoading(false);
        return;
      }
      if (hasVisualPlacement && !targetUrl.startsWith('/') && !/^https?:\/\//i.test(targetUrl)) {
        addToast('Target URL must start with / or http(s)://', 'error');
        setLoading(false);
        return;
      }
      if (hasVisualPlacement && !String(formData.imageUrl || '').trim()) {
        addToast('Set an image for offers or popup placement', 'error');
        setLoading(false);
        return;
      }
      if (hasVisualPlacement && !String(formData.offerDetails || '').trim()) {
        addToast('Offer details are required for offers or popup placement', 'error');
        setLoading(false);
        return;
      }
      if (hasHeaderPlacement && !String(formData.headerText || '').trim()) {
        addToast('Header text is required when header placement is enabled', 'error');
        setLoading(false);
        return;
      }
      const submitData = {
        title: formData.title,
        description: formData.description,
        type: formData.type,
        value: isGiftOrDelivery ? 0 : parseFloat(formData.value),
        rewardProductId: formData.type === 'bogo' ? formData.rewardProductId : null,
        rewardVariantId: formData.type === 'bogo' ? (formData.rewardVariantId || null) : null,
        code: formData.code || null,
        minOrderAmount: parseInt(formData.minOrderAmount) || 0,
        maxUses: formData.maxUses ? parseInt(formData.maxUses) : null,
        maxItems: formData.maxItems ? parseInt(formData.maxItems) : null,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
        imageUrl: hasVisualPlacement ? (formData.imageUrl || null) : null,
        targetUrl: hasVisualPlacement ? targetUrl : null,
        offerDetails: hasVisualPlacement ? (formData.offerDetails || null) : null,
        headerText: hasHeaderPlacement ? (formData.headerText || null) : null,
        showOnOffers: !!formData.showOnOffers,
        showAsPopup: !!formData.showAsPopup,
        showAsHeader: !!formData.showAsHeader,
        displayPages: hasAnyPlacement && Array.isArray(formData.displayPages) ? formData.displayPages : [],
        active: formData.active
      };
      if (formData.scope === 'category') {
        submitData.category = formData.category.trim();
        submitData.productIds = null;
      } else if (formData.scope === 'products') {
        submitData.productIds = formData.productIds || [];
        submitData.category = null;
      } else {
        submitData.category = null;
        submitData.productIds = null;
      }

      if (promotion) {
        await request(`/deals/${promotion.id}`, {
          method: 'PUT',
          body: JSON.stringify(submitData)
        });
        addToast('Promotion updated successfully', 'success');
      } else {
        await request('/deals', {
          method: 'POST',
          body: JSON.stringify(submitData)
        });
        addToast('Promotion created successfully', 'success');
      }
      
      onSave();
    } catch (err) {
      addToast(err.message || `Failed to ${promotion ? 'update' : 'create'} promotion`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4"
      onClick={requestClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-7xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-5 py-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {promotion ? 'Edit Promotion' : 'Add New Promotion'}
          </h2>
          <div className="flex items-center gap-2">
            <button type="submit" form="promotion-editor-form" disabled={loading} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">{loading && <Icon.Loading className="h-4 w-4" />}{loading ? 'Saving...' : (promotion ? 'Update' : 'Create')}</button>
            <button type="button" onClick={requestClose} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100" aria-label="Close promotion editor"><Icon.X className="h-5 w-5" /></button>
          </div>
        </div>

        <form id="promotion-editor-form" onSubmit={handleSubmit} className="min-h-0 space-y-5 overflow-y-auto p-5">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-start">
            <div className="md:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="percentage">Percentage Discount</option>
                <option value="fixed">Fixed Amount Discount</option>
                <option value="bogo">Buy 1 Get 1 Free</option>
                <option value="free_delivery">Free Delivery</option>
                <option value="delivery_percentage">Delivery Percentage Discount</option>
                <option value="delivery_fixed">Delivery Amount Discount</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Discount Scope</label>
              <select
                value={formData.scope}
                onChange={(e) => {
                  const nextScope = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    scope: nextScope,
                    category: nextScope === 'category' ? prev.category : '',
                    productIds: nextScope === 'products' ? (prev.productIds || []) : []
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All items (default)</option>
                <option value="category">Specific category</option>
                <option value="products">Specific products</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {formData.type === 'percentage' ? 'Discount Percentage (%)' : 'Discount Amount (৳)'}
              </label>
              <input
                type="number"
                min="0"
                max={formData.type.includes('percentage') ? 100 : undefined}
                step={formData.type === 'percentage' ? 1 : 0.01}
                required={!isGiftOrDelivery}
                disabled={isGiftOrDelivery}
                value={isGiftOrDelivery ? 0 : formData.value}
                onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Promo Code (Optional)</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Leave empty for auto-apply"
              />
            </div>

            <p className="md:col-span-12 text-sm text-gray-600">
              One coupon per order. Fixed discounts and Buy 1 Get 1 Free apply once per order. Sale items can use a delivery coupon only.
            </p>
            {formData.type === 'bogo' && (
              <div className="md:col-span-12 grid gap-3 sm:grid-cols-2 rounded-lg bg-green-50 p-4">
                <label className="text-sm font-medium text-gray-700">Product the customer gets free (1 unit)
                  <select required value={formData.rewardProductId} onChange={event => setFormData(prev => ({ ...prev, rewardProductId: event.target.value, rewardVariantId: '' }))} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2">
                    <option value="">Choose the free product</option>
                    {products.filter(product => product.active && !product.pre_purchase && !product.prePurchase).map(product => <option key={product.id} value={product.id}>{product.title}</option>)}
                  </select>
                </label>
                {giftVariants.length > 0 && <label className="text-sm font-medium text-gray-700">Free product option
                  <select required={!!giftProduct?.hideBaseModel} value={formData.rewardVariantId} onChange={event => setFormData(prev => ({ ...prev, rewardVariantId: event.target.value }))} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2">
                    <option value="">{giftProduct?.hideBaseModel ? 'Choose an option' : 'Base model'}</option>
                    {giftVariants.map(variant => <option key={variant.id} value={variant.id}>{variant.label || variant.id}</option>)}
                  </select>
                </label>}
                <p className="sm:col-span-2 text-xs text-gray-600">Buy 1 qualifying product and get 1 unit of the selected product free, while stock lasts. The scope above selects qualifying purchases. Applies once per order, even when buying multiple units.</p>
              </div>
            )}
            {formData.scope === 'category' && (
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  list="promo-category-options"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Bags, Wallets, Accessories"
                />
                <datalist id="promo-category-options">
                  {(categoryOptions || []).map(c => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
                {categoryOptions.length > 0 && (
                  <p className="mt-1 text-xs text-gray-500">Start typing to use an existing category.</p>
                )}
              </div>
            )}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Order Amount (৳)</label>
              <input
                type="number"
                min="0"
                value={formData.minOrderAmount}
                onChange={(e) => setFormData(prev => ({ ...prev, minOrderAmount: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses (Optional)</label>
              <input
                type="number"
                min="1"
                value={formData.maxUses}
                onChange={(e) => setFormData(prev => ({ ...prev, maxUses: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Unlimited"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Redemption Cap (Optional)</label>
              <input
                type="number"
                min="1"
                value={formData.maxItems}
                onChange={(e) => setFormData(prev => ({ ...prev, maxItems: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Counts once per order"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.active ? 'active' : 'inactive'}
                onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.value === 'active' }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="md:col-span-12">
              <label className="block text-sm font-medium text-gray-700 mb-2">Placements</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <label className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.showOnOffers}
                    onChange={(e) => setFormData(prev => ({ ...prev, showOnOffers: e.target.checked }))}
                  />
                  Show on offers page
                </label>
                <label className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.showAsPopup}
                    onChange={(e) => setFormData(prev => ({ ...prev, showAsPopup: e.target.checked }))}
                  />
                  Show as popup
                </label>
                <label className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.showAsHeader}
                    onChange={(e) => setFormData(prev => ({ ...prev, showAsHeader: e.target.checked }))}
                  />
                  Show in header bar
                </label>
              </div>
            </div>

            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date (Optional)</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date (Optional)</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
                       </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Promotion description (optional)"
            />
          </div>

          {formData.scope === 'products' && (
            <div className="rounded-lg border border-gray-200 p-4 bg-gray-50">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-medium text-gray-900">Select products</div>
                  <div className="text-xs text-gray-500">{formData.productIds.length} selected</div>
                </div>
                {loadingProducts && <span className="text-xs text-gray-500">Loading...</span>}
              </div>
              <div className="mt-3">
                <div className="relative">
                  <Icon.Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    placeholder="Search products by name or category..."
                    className="pl-9 pr-3 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="mt-3 max-h-60 overflow-auto rounded-md border border-gray-200 bg-white">
                {filteredProducts.length === 0 ? (
                  <div className="p-3 text-sm text-gray-500">No products match your search.</div>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {filteredProducts.map((p) => {
                      const checked = formData.productIds.includes(p.id);
                      return (
                        <li key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = new Set(formData.productIds || []);
                              if (e.target.checked) next.add(p.id); else next.delete(p.id);
                              setFormData(prev => ({ ...prev, productIds: Array.from(next) }));
                            }}
                          />
                          <div className="min-w-0">
                            <div className="text-sm text-gray-900 truncate">{p.title}</div>
                            <div className="text-xs text-gray-500 truncate">{p.category || 'Uncategorized'}</div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}

          {hasVisualPlacement && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <MediaPicker
                    label="Promotion Image"
                    value={formData.imageUrl}
                    onChange={(url) => setFormData(prev => ({ ...prev, imageUrl: url }))}
                    helpText="Required for popup and offers placements."
                    ratioHint="16:3 wide or 1:1 square"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target URL <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.targetUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, targetUrl: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="/offers or https://example.com"
                    required={hasVisualPlacement}
                  />
                  <p className="mt-1 text-xs text-gray-500">Required. Must start with `/` or `http(s)://`.</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Offer Details</label>
                <textarea
                  rows={4}
                  value={formData.offerDetails}
                  onChange={(e) => setFormData(prev => ({ ...prev, offerDetails: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Explain how the offer works and how customers can avail it."
                  required={hasVisualPlacement}
                />
              </div>
            </>
          )}

          {hasHeaderPlacement && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Header Bar Text</label>
              <input
                type="text"
                value={formData.headerText}
                onChange={(e) => setFormData(prev => ({ ...prev, headerText: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Short text for the top header bar"
                required={hasHeaderPlacement}
              />
            </div>
          )}

          {hasAnyPlacement && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Show On Pages</label>
              <p className="text-xs text-gray-500 mb-2">Leave all unchecked to show on every page.</p>
              <div className="flex flex-wrap gap-2">
                {availablePromotionPages.map((pageKey) => {
                  const checked = formData.displayPages.includes(pageKey);
                  return (
                    <label key={pageKey} className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-xs text-gray-700">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setFormData((prev) => {
                            const next = new Set(prev.displayPages || []);
                            if (e.target.checked) next.add(pageKey);
                            else next.delete(pageKey);
                            return { ...prev, displayPages: Array.from(next) };
                          });
                        }}
                      />
                      <span className="uppercase tracking-wide">{pageKey}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

        </form>
      </div>
    </div>
  );
}


// CustomizationManager removed


function ReviewsManager({ storefrontBaseUrl = '' }) {
  const { request } = useApi();
  const { addToast } = useToasts();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedReview, setSelectedReview] = useState(null);
  
  const load = React.useCallback(async () => { 
    try { 
      setLoading(true); 
      const rows = await request(`/reviews/admin?status=${statusFilter}`); 
      setReviews(rows); 
    } catch { 
      addToast('Failed to load reviews','error'); 
    } finally { 
      setLoading(false);
    } 
  }, [request, addToast, statusFilter]);
  
  useEffect(() => { load(); }, [load]);
  
  const approve = async (id) => { 
    try { 
      await request(`/reviews/${id}/approve`, { method: 'PUT' }); 
      addToast('Review approved','success'); 
      load(); 
    } catch { 
      addToast('Failed to approve','error'); 
    } 
  };
  
  const remove = async (id) => { 
    if (!window.confirm('Are you sure you want to delete this review?')) return; 
    try { 
      await request(`/reviews/${id}`, { method: 'DELETE' }); 
      addToast('Review deleted','success');
      load(); 
    } catch { 
      addToast('Failed to delete','error');
    } 
  };

  const getReviewProductHref = useCallback((review) => {
    const base = String(storefrontBaseUrl || '').replace(/\/+$/, '');
    const slug = String(review?.product_slug || '').trim();
    if (slug) return `${base}/product/${encodeURIComponent(slug)}`;
    const productId = String(review?.product_id || '').trim();
    if (productId) return `${base}/product/${encodeURIComponent(productId)}`;
    return '';
  }, [storefrontBaseUrl]);

  const renderReviewProductLink = useCallback((review) => {
    const label = review?.product_title || review?.product_slug || review?.product_id || 'Unknown product';
    const href = getReviewProductHref(review);
    if (!href) return <span className="text-sm text-gray-700">{label}</span>;
    return (
      <a href={href} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
        {label}
      </a>
    );
  }, [getReviewProductHref]);
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reviews</h1>
          <p className="text-gray-600">Manage customer reviews and ratings</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setStatusFilter('pending')}
            className={`px-4 py-2 rounded-lg ${statusFilter === 'pending' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            Pending
          </button>
          <button
            onClick={() => setStatusFilter('approved')}
            className={`px-4 py-2 rounded-lg ${statusFilter === 'approved' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            Approved
          </button>
        </div>
      </div>
      
      <div className="bg-white rounded shadow-sm">
        {loading ? (
          <div className="p-6 text-center">
            <Icon.Loading className="w-6 h-6 inline-block text-blue-600 mr-2" />
            Loading reviews…
          </div>
        ) : reviews.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No {statusFilter} reviews found
          </div>
        ) : (
          <>
            <div className="md:hidden space-y-3 p-3">
              {reviews.map(r => (
                <div key={r.id} className="border rounded-lg p-3 bg-white shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-xs text-gray-500">Product</div>
                      <div>{renderReviewProductLink(r)}</div>
                    </div>
                    <div className="text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="mt-2 text-sm text-gray-900 line-clamp-2">{r.text}</div>
                  <div className="mt-2 text-xs text-gray-600">Rating: {r.rating}</div>
                  <div className="mt-3 flex items-center gap-2">
                    {statusFilter === 'pending' && (
                      <button
                        onClick={() => approve(r.id)}
                        className="px-3 py-1 rounded-md bg-green-600 text-white text-xs"
                      >
                        Approve
                      </button>
                    )}
                    <button
                      onClick={() => setSelectedReview(r)}
                      className="px-3 py-1 rounded-md border text-xs"
                    >
                      View
                    </button>
                    <button
                      onClick={() => remove(r.id)}
                      className="px-3 py-1 rounded-md border border-red-300 text-red-600 text-xs"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Review</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Media</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {reviews.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{renderReviewProductLink(r)}</td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-1">
                        {Array.from({ length: r.rating }).map((_, i) => (
                          <svg key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                        <span className="ml-1 text-gray-500">({r.rating})</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm max-w-md">
                      <p className="line-clamp-2">{r.text}</p>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {r.media_url ? (
                        <button
                          onClick={() => setSelectedReview(r)}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <Icon.Eye className="w-4 h-4" />
                          View
                        </button>
                      ) : (
                        <span className="text-gray-400">No media</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <div className="flex items-center justify-center gap-2">
                        {statusFilter === 'pending' && (
                          <button 
                            onClick={() => approve(r.id)} 
                            className="px-3 py-1 rounded-md bg-green-600 text-white hover:bg-green-700"
                          >
                            Approve
                          </button>
                        )}
                        <button 
                          onClick={() => setSelectedReview(r)} 
                          className="px-3 py-1 rounded-md border border-gray-300 hover:bg-gray-50"
                        >
                          <Icon.Eye className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => remove(r.id)} 
                          className="px-3 py-1 rounded-md border border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <Icon.Trash className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      
      {/* Review Details Modal */}
      {selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedReview(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Review Details</h2>
              <button onClick={() => setSelectedReview(null)} className="text-gray-400 hover:text-gray-600">
                <Icon.X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Product ID</label>
                <p className="mt-1">{renderReviewProductLink(selectedReview)}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Rating</label>
                <div className="mt-1 flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <svg key={i} className={`w-5 h-5 ${i < selectedReview.rating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                  <span className="ml-2 text-sm text-gray-600">({selectedReview.rating}/5)</span>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Review Text</label>
                <p className="mt-1 text-sm text-gray-900 leading-relaxed">{selectedReview.text}</p>
              </div>
              
              {selectedReview.media_url && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Media</label>
                  <div className="mt-2">
                    <img 
                      src={resolveMediaUrl(selectedReview.media_url)} 
                      alt="Review media" 
                      className="w-full rounded-lg border border-gray-200"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div 
                      className="hidden w-full h-48 items-center justify-center bg-gray-100 rounded-lg border border-gray-200"
                    >
                      <p className="text-sm text-gray-500">Image failed to load</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <p className="mt-1 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    selectedReview.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {selectedReview.status}
                  </span>
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Submitted</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(selectedReview.created_at).toLocaleString()}
                </p>
              </div>
              
              <div className="pt-4 border-t flex flex-col sm:flex-row sm:justify-end gap-2">
                {selectedReview.status === 'pending' && (
                  <button
                    onClick={() => {
                      approve(selectedReview.id);
                      setSelectedReview(null);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Approve Review
                  </button>
                )}
                <button
                  onClick={() => {
                    remove(selectedReview.id);
                    setSelectedReview(null);
                  }}
                  className="px-4 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50"
                >
                  Delete Review
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VisitorsAnalyticsManager() {
  const { request } = useApi();
  const { addToast } = useToasts();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [selected, setSelected] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loadingOrderId, setLoadingOrderId] = useState('');
  const [query, setQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [deviceFilter, setDeviceFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const next = await request(`/visitor-analytics?days=${encodeURIComponent(days)}`);
      setData(next);
    } catch (err) {
      addToast('Failed to load visitor analytics', 'error');
    } finally {
      setLoading(false);
    }
  }, [request, addToast, days]);

  useEffect(() => { load(); }, [load]);

  const chartColors = ['#f472b6', '#38bdf8', '#a78bfa', '#34d399', '#fbbf24', '#fb7185', '#818cf8', '#2dd4bf'];
  const visitors = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = Array.isArray(data?.visitors) ? data.visitors : [];
    return list.filter((visitor) => {
      if (sourceFilter !== 'all' && String(visitor.source || 'Direct') !== sourceFilter) return false;
      if (deviceFilter !== 'all' && String(visitor.deviceType || 'unknown') !== deviceFilter) return false;
      if (!q) return true;
      return [
        visitor.id,
        visitor.ip,
        visitor.browser,
        visitor.os,
        visitor.deviceType,
        visitor.source,
        visitor.landingPage,
        ...(visitor.referrers || []),
        ...(visitor.orders || []).map(order => order.orderId)
      ].join(' ').toLowerCase().includes(q);
    });
  }, [data, query, sourceFilter, deviceFilter]);
  const timeline = Array.isArray(data?.timeline) ? data.timeline : [];
  const topPages = Array.isArray(data?.topPages) ? data.topPages : [];
  const landingPages = Array.isArray(data?.landingPages) ? data.landingPages : [];
  const acquisitionSources = Array.isArray(data?.acquisitionSources) ? data.acquisitionSources : [];
  const devices = Array.isArray(data?.devices) ? data.devices : [];
  const browsers = Array.isArray(data?.browsers) ? data.browsers : [];
  const referrers = Array.isArray(data?.topReferrers) ? data.topReferrers : [];
  const eventsByType = useMemo(() => Array.isArray(data?.eventsByType) ? data.eventsByType : [], [data?.eventsByType]);
  const eventCount = useCallback((...names) => eventsByType
    .filter(row => names.includes(String(row.label || '').toLowerCase()))
    .reduce((sum, row) => sum + Number(row.count || 0), 0), [eventsByType]);
  const totalVisitors = Number(data?.totals?.visitors || 0);
  const totalPageViews = Number(data?.totals?.pageViews || 0);
  const totalPurchases = Number(data?.totals?.purchases || 0);
  const totalRevenue = Number(data?.totals?.revenue || 0);
  const pagesPerVisitor = totalVisitors > 0 ? (totalPageViews / totalVisitors).toFixed(1) : '0.0';
  const averageOrderValue = totalPurchases > 0 ? totalRevenue / totalPurchases : 0;
  const conversionRate = totalVisitors > 0 ? (totalPurchases / totalVisitors) * 100 : 0;
  const sourceOptions = useMemo(() => Array.from(new Set((data?.visitors || []).map(visitor => String(visitor.source || 'Direct')))).sort(), [data]);
  const deviceOptions = useMemo(() => Array.from(new Set((data?.visitors || []).map(visitor => String(visitor.deviceType || 'unknown')))).sort(), [data]);
  const commerceFunnel = useMemo(() => [
    { label: 'Product views', value: eventCount('view_item') || totalPageViews },
    { label: 'Added to cart', value: eventCount('add_to_cart') },
    { label: 'Checkout started', value: eventCount('initiate_checkout') },
    { label: 'Purchases', value: totalPurchases }
  ], [eventCount, totalPageViews, totalPurchases]);
  const maxPage = Math.max(1, ...topPages.map(row => Number(row.count || 0)), ...landingPages.map(row => Number(row.count || 0)));
  const formatWhen = (value) => value ? new Date(value).toLocaleString() : '-';
  const percentOf = (value, total) => total > 0 ? `${Math.round((Number(value || 0) / total) * 100)}%` : '0%';
  const webVitals = data?.webVitals || { samples: 0, metrics: {} };
  const clientErrors = data?.clientErrors || { total: 0, groups: [] };
  const webVitalLabels = { lcp: 'LCP', inp: 'INP', cls: 'CLS', fcp: 'FCP', ttfb: 'TTFB' };
  const formatWebVital = (name, metric) => {
    if (!metric?.samples) return 'No samples';
    return name === 'cls' ? Number(metric.p75 || 0).toFixed(3) : `${Math.round(metric.p75 || 0)} ms`;
  };
  const webVitalTone = (rating) => rating === 'good'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
    : rating === 'needs-improvement'
      ? 'border-amber-200 bg-amber-50 text-amber-800'
      : rating === 'poor'
        ? 'border-rose-200 bg-rose-50 text-rose-800'
        : 'border-gray-200 bg-gray-50 text-gray-600';

  const MetricCard = ({ label, value, note, tone = 'gray' }) => {
    const toneMap = {
      pink: 'bg-pink-50 text-pink-700 ring-pink-100',
      blue: 'bg-sky-50 text-sky-700 ring-sky-100',
      purple: 'bg-violet-50 text-violet-700 ring-violet-100',
      green: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
      gray: 'bg-gray-50 text-gray-700 ring-gray-100'
    };
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
            <div className="mt-2 text-2xl font-bold text-gray-900">{value}</div>
            {note && <div className="mt-1 text-xs text-gray-500">{note}</div>}
          </div>
          <span className={`h-9 w-9 rounded-lg ring-1 ${toneMap[tone] || toneMap.gray}`} />
        </div>
      </div>
    );
  };

  const ChartCard = ({ title, subtitle, children, className = '', id }) => (
    <section id={id} className={`scroll-mt-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm ${className}`}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
        </div>
      </div>
      {children}
    </section>
  );

  const EmptyState = ({ label = 'No data yet' }) => (
    <div className="flex h-full min-h-40 items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 text-sm text-gray-500">{label}</div>
  );

  const BarRow = ({ label, value, max }) => (
    <div className="grid grid-cols-[minmax(110px,1fr)_minmax(120px,2fr)_44px] items-center gap-2 text-xs">
      <div className="truncate font-medium text-gray-700" title={label}>{label || 'Unknown'}</div>
      <div className="h-2 overflow-hidden rounded-full bg-gray-100">
        <div className="h-full rounded-full bg-gray-900" style={{ width: `${Math.max(4, Math.round((Number(value || 0) / Math.max(1, max)) * 100))}%` }} />
      </div>
      <div className="text-right font-semibold text-gray-600">{value}</div>
    </div>
  );

  const tooltipStyle = { borderRadius: 10, border: '1px solid #e5e7eb', boxShadow: '0 12px 30px rgba(15,23,42,0.12)' };

  const openVisitorOrder = useCallback(async (orderSummary) => {
    const orderId = String(orderSummary?.orderId || '').trim();
    if (!orderId) return;
    setLoadingOrderId(orderId);
    try {
      const rows = await request(`/orders?limit=10&search=${encodeURIComponent(orderId)}`);
      const order = (Array.isArray(rows) ? rows : []).find(row => String(row.display_order_id || row.id) === orderId)
        || (Array.isArray(rows) ? rows[0] : null);
      if (!order) throw new Error('Order not found');
      setSelected(null);
      setSelectedOrder(order);
    } catch (err) {
      addToast(err.message || 'Could not open the linked order', 'error');
    } finally {
      setLoadingOrderId('');
    }
  }, [request, addToast]);

  useEffect(() => {
    if (loading || typeof window === 'undefined' || !window.location.hash) return;
    const id = window.location.hash.slice(1);
    window.requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }));
  }, [loading]);

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Icon.Loading className="mr-2 h-5 w-5 text-blue-600" />Loading visitor analytics...</div>;
  }

  return (
    <div id="analytics-overview" className="scroll-mt-6 space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="mt-1 text-sm text-gray-600">First-party acquisition, behavior, conversion, revenue, technology, and order attribution.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search visitor, source, page, IP..." className="h-9 min-w-[260px] rounded-lg border border-gray-300 px-3 text-sm" />
          <select value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)} className="h-9 rounded-lg border border-gray-300 bg-white px-3 text-sm">
            <option value="all">All sources</option>
            {sourceOptions.map(source => <option key={source} value={source}>{source}</option>)}
          </select>
          <select value={deviceFilter} onChange={(event) => setDeviceFilter(event.target.value)} className="h-9 rounded-lg border border-gray-300 bg-white px-3 text-sm">
            <option value="all">All devices</option>
            {deviceOptions.map(device => <option key={device} value={device}>{device}</option>)}
          </select>
          <select value={days} onChange={(event) => setDays(Number(event.target.value))} className="h-9 rounded-lg border border-gray-300 bg-white px-3 text-sm">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 365 days</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 xl:grid-cols-8">
        <MetricCard label="Visitors" value={data?.totals?.visitors || 0} note="Unique browser IDs" tone="pink" />
        <MetricCard label="Page Views" value={data?.totals?.pageViews || 0} note={`${data?.days || days} day window`} tone="blue" />
        <MetricCard label="Events" value={data?.totals?.events || 0} note="Clicks, carts, checkout" tone="purple" />
        <MetricCard label="Purchases" value={data?.totals?.purchases || 0} note={`${percentOf(data?.totals?.purchases || 0, data?.totals?.visitors || 0)} of visitors`} tone="green" />
        <MetricCard label="Revenue" value={currency(data?.totals?.revenue || 0)} note="Tracked purchase value" />
        <MetricCard label="Conversion" value={`${conversionRate.toFixed(1)}%`} note="Visitor to purchase" tone="green" />
        <MetricCard label="Avg. Order" value={currency(averageOrderValue)} note="Revenue per purchase" tone="blue" />
        <MetricCard label="Pages / Visitor" value={pagesPerVisitor} note="Content depth" tone="purple" />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(360px,0.75fr)]">
        <ChartCard title="Traffic and Revenue Trend" subtitle="Daily page views, purchases, and revenue.">
          {timeline.length ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timeline} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="visitorVisits" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f472b6" stopOpacity={0.32} />
                      <stop offset="95%" stopColor="#f472b6" stopOpacity={0.03} />
                    </linearGradient>
                    <linearGradient id="visitorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.24} />
                      <stop offset="95%" stopColor="#38bdf8" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(value) => String(value).slice(5)} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={34} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={52} tickFormatter={(value) => `৳${value}`} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(value, name) => name === 'revenue' ? currency(value) : value} />
                  <Legend />
                  <Area yAxisId="left" type="monotone" dataKey="visits" name="Page views" stroke="#ec4899" fill="url(#visitorVisits)" strokeWidth={2} />
                  <Area yAxisId="left" type="monotone" dataKey="purchases" name="Purchases" stroke="#10b981" fill="#10b98122" strokeWidth={2} />
                  <Area yAxisId="right" type="monotone" dataKey="revenue" name="Revenue" stroke="#0284c7" fill="url(#visitorRevenue)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyState label="No traffic events in this date range" />}
        </ChartCard>

        <ChartCard id="analytics-acquisition" title="Acquisition Mix" subtitle="Where visitors came from.">
          {acquisitionSources.length ? (
            <div className="grid gap-3 lg:grid-cols-[160px_minmax(0,1fr)] xl:grid-cols-1">
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={acquisitionSources} dataKey="count" nameKey="label" innerRadius={54} outerRadius={76} paddingAngle={3}>
                      {acquisitionSources.map((entry, index) => <Cell key={entry.label} fill={chartColors[index % chartColors.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2">
                {acquisitionSources.slice(0, 8).map((row, index) => (
                  <div key={row.label} className="flex items-center justify-between gap-2 text-xs">
                    <span className="flex min-w-0 items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: chartColors[index % chartColors.length] }} /><span className="truncate text-gray-700" title={row.label}>{row.label}</span></span>
                    <span className="font-semibold text-gray-900">{row.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <EmptyState label="No acquisition data yet" />}
        </ChartCard>
      </div>

      <ChartCard title="Commerce Funnel" subtitle="Progress from product discovery to completed purchase.">
        <div className="grid gap-3 md:grid-cols-4">
          {commerceFunnel.map((step, index) => {
            const previous = index > 0 ? commerceFunnel[index - 1].value : step.value;
            const rate = previous > 0 ? Math.round((step.value / previous) * 100) : 0;
            return (
              <div key={step.label} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{step.label}</div>
                <div className="mt-1 flex items-end justify-between gap-2"><span className="text-2xl font-bold text-gray-900">{step.value}</span>{index > 0 && <span className="text-xs font-semibold text-gray-500">{rate}% retained</span>}</div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200"><div className="h-full rounded-full bg-blue-600" style={{ width: `${Math.max(4, Math.min(100, index === 0 ? 100 : rate))}%` }} /></div>
              </div>
            );
          })}
        </div>
      </ChartCard>

      <ChartCard title="Storefront Experience" subtitle="Sampled real-user Web Vitals and client-side errors. Values are p75 for the selected period.">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
          {Object.keys(webVitalLabels).map((name) => {
            const metric = webVitals.metrics?.[name];
            return (
              <div key={name} className={`rounded-lg border p-3 ${webVitalTone(metric?.rating)}`}>
                <div className="text-xs font-semibold uppercase tracking-wide">{webVitalLabels[name]}</div>
                <div className="mt-1 text-xl font-bold">{formatWebVital(name, metric)}</div>
                <div className="mt-1 text-xs opacity-80">{metric?.samples || 0} sample{metric?.samples === 1 ? '' : 's'} · {String(metric?.rating || 'unknown').replace('-', ' ')}</div>
              </div>
            );
          })}
          <div className={`rounded-lg border p-3 ${clientErrors.total ? 'border-rose-200 bg-rose-50 text-rose-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>
            <div className="text-xs font-semibold uppercase tracking-wide">Client errors</div>
            <div className="mt-1 text-xl font-bold">{clientErrors.total || 0}</div>
            <div className="mt-1 text-xs opacity-80">Consent-aware browser reports</div>
          </div>
        </div>
        {clientErrors.groups?.length > 0 && (
          <div className="mt-3 grid gap-2 md:grid-cols-2">
            {clientErrors.groups.slice(0, 4).map((entry) => (
              <div key={`${entry.kind}:${entry.message}:${entry.source}`} className="rounded-lg border border-rose-100 bg-white px-3 py-2 text-xs text-gray-700">
                <div className="flex items-center justify-between gap-3"><span className="font-semibold text-rose-700">{entry.kind}</span><span>{entry.count}x</span></div>
                <div className="mt-1 truncate" title={entry.message}>{entry.message}</div>
                {entry.source && <div className="mt-0.5 truncate text-gray-400" title={entry.source}>{entry.source}</div>}
              </div>
            ))}
          </div>
        )}
      </ChartCard>

      <div className="grid gap-4 xl:grid-cols-2">
        <ChartCard id="analytics-content" title="Landing Pages" subtitle="First page visitors entered through.">
          {landingPages.length ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={landingPages.slice(0, 10)} layout="vertical" margin={{ top: 0, right: 12, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={130} tickFormatter={(value) => String(value).slice(0, 22)} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="count" name="Entrances" radius={[0, 6, 6, 0]} fill="#a78bfa" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyState label="No landing pages yet" />}
        </ChartCard>

        <ChartCard title="Top Content" subtitle="Most viewed pages in the selected window.">
          <div className="space-y-2">
            {topPages.length ? topPages.slice(0, 10).map((row) => <BarRow key={row.label} label={row.label} value={row.count} max={maxPage} />) : <EmptyState label="No page views yet" />}
          </div>
        </ChartCard>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <ChartCard title="Devices" subtitle="Desktop, mobile, and tablet split.">
          <div className="space-y-2">{devices.length ? devices.map(row => <BarRow key={row.label} label={row.label} value={row.count} max={Math.max(1, data?.totals?.events || 1)} />) : <EmptyState label="No device data yet" />}</div>
        </ChartCard>
        <ChartCard title="Browsers" subtitle="Browser mix across tracked events.">
          <div className="space-y-2">{browsers.length ? browsers.map(row => <BarRow key={row.label} label={row.label} value={row.count} max={Math.max(1, data?.totals?.events || 1)} />) : <EmptyState label="No browser data yet" />}</div>
        </ChartCard>
        <ChartCard title="Top Referrers" subtitle="Specific referring URLs where available.">
          <div className="space-y-2">{referrers.length ? referrers.slice(0, 7).map(row => <BarRow key={row.label} label={row.label} value={row.count} max={Math.max(1, referrers[0]?.count || 1)} />) : <div className="text-sm text-gray-500">Direct, app, or blocked referrer traffic</div>}</div>
        </ChartCard>
      </div>

      <section id="analytics-visitors" className="scroll-mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-2 border-b border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Visitor Explorer</h2>
            <p className="text-xs text-gray-500">Click a visitor for source, landing page, event stream, and purchase detail.</p>
          </div>
          <span className="text-xs font-semibold text-gray-500">{visitors.length} shown</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr><th className="px-4 py-3 text-left">Visitor</th><th className="px-4 py-3 text-left">Source</th><th className="px-4 py-3 text-left">Landing Page</th><th className="px-4 py-3 text-left">Last Seen</th><th className="px-4 py-3 text-left">Device</th><th className="px-4 py-3 text-left">Purchases</th><th className="px-4 py-3 text-left">IP</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {visitors.length ? visitors.map(visitor => (
                <tr key={visitor.id} onClick={() => setSelected(visitor)} className="cursor-pointer hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-900">{String(visitor.id).slice(0, 18)}...</td>
                  <td className="px-4 py-3 text-gray-700">{visitor.source || 'Direct'}</td>
                  <td className="max-w-[240px] truncate px-4 py-3 text-gray-600" title={visitor.landingPage}>{visitor.landingPage || '-'}</td>
                  <td className="px-4 py-3 text-gray-600">{formatWhen(visitor.lastSeen)}</td>
                  <td className="px-4 py-3 text-gray-600">{visitor.deviceType} · {visitor.browser}</td>
                  <td className="px-4 py-3 text-gray-700">{visitor.purchases} / {currency(visitor.revenue || 0)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{visitor.ip || '-'}</td>
                </tr>
              )) : <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">No visitors match this view</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <ChartCard id="analytics-events" title="Event Stream" subtitle="Recent tracked actions across the storefront.">
        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {(data?.recentEvents || []).slice(0, 18).map(event => (
            <button key={event.id} type="button" onClick={() => setSelected((data?.visitors || []).find(visitor => visitor.id === event.visitorId) || null)} className="rounded-lg border border-gray-200 px-3 py-2 text-left hover:border-gray-300 hover:bg-gray-50">
              <div className="flex items-center justify-between gap-2"><span className="text-sm font-semibold text-gray-900">{event.type}</span><span className="text-xs text-gray-400">{formatWhen(event.at)}</span></div>
              <div className="mt-1 truncate text-xs text-gray-500">{event.path || event.title || event.orderId || 'No detail'}</div>
            </button>
          ))}
        </div>
      </ChartCard>

      {selected && createPortal(
        <div className="fixed inset-0 z-[260] flex items-center justify-center bg-black/45 p-4" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelected(null); }}>
          <div className="max-h-[88vh] w-full max-w-5xl overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <div><h3 className="text-lg font-semibold text-gray-900">Visitor Details</h3><p className="font-mono text-xs text-gray-500">{selected.id}</p></div>
              <button type="button" onClick={() => setSelected(null)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"><Icon.X className="h-5 w-5" /></button>
            </div>
            <div className="grid max-h-[calc(88vh-64px)] gap-4 overflow-y-auto p-4 lg:h-[min(680px,calc(88vh-64px))] lg:grid-cols-[280px_minmax(0,1fr)] lg:overflow-hidden">
              <aside className="space-y-3 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
                <div><div className="text-xs font-semibold uppercase text-gray-500">Seen</div><div>{formatWhen(selected.firstSeen)} - {formatWhen(selected.lastSeen)}</div></div>
                <div><div className="text-xs font-semibold uppercase text-gray-500">Acquisition</div><div>{selected.source || 'Direct'}</div></div>
                <div><div className="text-xs font-semibold uppercase text-gray-500">Landing Page</div><div className="break-all text-xs">{selected.landingPage || '-'}</div></div>
                <div><div className="text-xs font-semibold uppercase text-gray-500">Device</div><div>{selected.deviceType} · {selected.browser} · {selected.os}</div></div>
                <div><div className="text-xs font-semibold uppercase text-gray-500">IP</div><div className="break-all font-mono text-xs">{selected.ip || '-'}</div></div>
                <div><div className="text-xs font-semibold uppercase text-gray-500">Revenue</div><div>{selected.purchases} purchase(s), {currency(selected.revenue || 0)}</div></div>
                <div><div className="text-xs font-semibold uppercase text-gray-500">Referrers</div><div className="space-y-1">{(selected.referrers || []).length ? selected.referrers.map(ref => <div key={ref} className="break-all text-xs text-gray-600">{ref}</div>) : '-'}</div></div>
              </aside>
              <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="rounded-lg border border-gray-200 p-3"><h4 className="mb-2 text-sm font-semibold text-gray-900">Top Pages</h4><div className="space-y-2">{(selected.topPages || []).map(row => <BarRow key={row.label} label={row.label} value={row.count} max={Math.max(1, selected.topPages?.[0]?.count || 1)} />)}</div></div>
                  <div className="rounded-lg border border-gray-200 p-3"><h4 className="mb-2 text-sm font-semibold text-gray-900">Orders</h4>{(selected.orders || []).length ? selected.orders.map(order => <button type="button" key={order.orderId} onClick={() => openVisitorOrder(order)} disabled={loadingOrderId === order.orderId} className="mb-1.5 flex w-full items-center justify-between rounded-md bg-gray-50 px-2 py-2 text-left text-xs transition hover:bg-blue-50 hover:text-blue-700 disabled:opacity-60"><span className="font-mono">{order.orderId}</span><span>{loadingOrderId === order.orderId ? 'Opening...' : currency(order.total || 0)}</span></button>) : <div className="text-sm text-gray-500">No tracked purchases</div>}</div>
                </div>
                <div className="rounded-lg border border-gray-200 p-3"><div className="mb-2 flex items-center justify-between"><h4 className="text-sm font-semibold text-gray-900">Timeline</h4><span className="text-xs text-gray-400">{(selected.events || []).length} events</span></div><div className="max-h-[252px] space-y-2 overflow-y-auto pr-1">{(selected.events || []).map(event => <div key={event.id} className="grid grid-cols-[128px_112px_minmax(0,1fr)] gap-2 rounded-md bg-gray-50 px-2 py-1.5 text-xs"><span className="text-gray-400">{formatWhen(event.at)}</span><span className="font-semibold text-gray-700">{event.type}</span><span className="truncate text-gray-600">{event.path || event.orderId || event.title || '-'}</span></div>)}</div></div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onUpdate={(updatedOrder) => setSelectedOrder(updatedOrder)}
        />
      )}
    </div>
  );
}
function AuditLogsManager() {
  const { request } = useApi();
  const { addToast } = useToasts();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [actionFilter, setActionFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [undoingId, setUndoingId] = useState(null);
  const pageSize = 25;

  const truncateText = useCallback((value, max = 96) => {
    const s = String(value ?? '');
    return s.length > max ? `${s.slice(0, max - 1)}…` : s;
  }, []);

  const parseMeta = useCallback((raw) => {
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, []);

  const formatActionLabel = useCallback((action) => {
    return String(action || '')
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }, []);

  const normalizeMetaValue = useCallback((value) => {
    if (value === null || typeof value === 'undefined' || value === '') return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '-';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? '' : 's'}`;
    if (typeof value === 'object') return `${Object.keys(value).length} field${Object.keys(value).length === 1 ? '' : 's'}`;
    return String(value);
  }, []);

  const collectDiffKeys = useCallback((before, after) => {
    if (!before || !after || typeof before !== 'object' || typeof after !== 'object' || Array.isArray(before) || Array.isArray(after)) return [];
    const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
    return keys.filter((key) => {
      try {
        return JSON.stringify(before[key]) !== JSON.stringify(after[key]);
      } catch {
        return String(before[key]) !== String(after[key]);
      }
    });
  }, []);

  const summarizeMeta = useCallback((meta) => {
    if (!meta || typeof meta !== 'object' || Array.isArray(meta)) {
      return { keyFacts: [], diffKeys: [], diffCount: 0, topLevelCount: 0, hasRawMeta: false };
    }

    const before = meta.before;
    const after = meta.after;
    const diffKeys = collectDiffKeys(before, after);

    const hiddenKeys = new Set(['before', 'after']);
    const knownFactKeys = [
      'target_action',
      'status',
      'paymentStatus',
      'fulfillmentStatus',
      'edited',
      'reason',
      'error',
      'path',
      'name',
      'result'
    ];

    const keyFacts = [];
    if (typeof meta.from !== 'undefined' || typeof meta.to !== 'undefined') {
      keyFacts.push({
        label: 'Transition',
        value: `${normalizeMetaValue(meta.from)} -> ${normalizeMetaValue(meta.to)}`
      });
      hiddenKeys.add('from');
      hiddenKeys.add('to');
    }

    for (const key of knownFactKeys) {
      if (typeof meta[key] === 'undefined') continue;
      keyFacts.push({
        label: key === 'target_action'
          ? 'Target action'
          : key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()),
        value: normalizeMetaValue(meta[key])
      });
      hiddenKeys.add(key);
    }

    const extraKeys = Object.keys(meta).filter((key) => !hiddenKeys.has(key));
    for (const key of extraKeys.slice(0, 4)) {
      keyFacts.push({
        label: key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
        value: normalizeMetaValue(meta[key])
      });
    }

    return {
      keyFacts: keyFacts.slice(0, 6),
      diffKeys: diffKeys.slice(0, 8),
      diffCount: diffKeys.length,
      topLevelCount: Object.keys(meta).length,
      hasRawMeta: true
    };
  }, [collectDiffKeys, normalizeMetaValue]);

  const preparedLogs = useMemo(() => {
    return logs.map((log) => {
      const metaObj = parseMeta(log.metadata);
      const summary = summarizeMeta(metaObj);
      const entityLabel = log.entity_type
        ? `${log.entity_type}${log.entity_id ? `:${String(log.entity_id).slice(0, 10)}` : ''}`
        : '-';
      const undoLabel =
        log.undo_status === 'success'
          ? 'Undone'
          : log.undo_status === 'pending'
            ? 'Undo in progress'
            : (log.undoable ? 'Undo available' : (log.undo_reason || 'Not undoable'));

      return {
        log,
        metaObj,
        summary,
        actionLabel: formatActionLabel(log.action),
        entityLabel,
        undoLabel,
        timeLabel: new Date(log.created_at).toLocaleString()
      };
    });
  }, [logs, parseMeta, summarizeMeta, formatActionLabel]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      params.set('offset', String(page * pageSize));
      if (actionFilter.trim()) params.set('action', actionFilter.trim());
      if (actorFilter.trim()) params.set('actor', actorFilter.trim());
      if (entityFilter.trim()) params.set('entityType', entityFilter.trim());
      if (fromDate) params.set('from', fromDate);
      if (toDate) params.set('to', toDate);
      const rows = await request(`/audit?${params.toString()}`);
      setLogs(rows);
      setHasMore(rows.length === pageSize);
    } catch (err) {
      addToast('Failed to load audit logs', 'error');
    } finally {
      setLoading(false);
    }
  }, [request, addToast, page, pageSize, actionFilter, actorFilter, entityFilter, fromDate, toDate]);

  useEffect(() => {
    setPage(0);
  }, [actionFilter, actorFilter, entityFilter, fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUndo = async (log) => {
    if (!log?.id) return;
    if (!log.undoable) {
      addToast(log.undo_reason || 'Undo not available', 'error');
      return;
    }
    const ok = window.confirm('Undo this action? This will revert changes from this log.');
    if (!ok) return;
    try {
      setUndoingId(log.id);
      await request(`/audit/${log.id}/undo`, { method: 'POST' });
      addToast('Undo completed', 'success');
      load();
    } catch (err) {
      addToast(err.message || 'Undo failed', 'error');
    } finally {
      setUndoingId(null);
    }
  };

  const clearFilters = useCallback(() => {
    setActionFilter('');
    setActorFilter('');
    setEntityFilter('');
    setFromDate('');
    setToDate('');
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-gray-600">Track admin actions and critical system changes</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {
              const params = new URLSearchParams();
              if (actionFilter.trim()) params.set('action', actionFilter.trim());
              if (actorFilter.trim()) params.set('actor', actorFilter.trim());
              if (entityFilter.trim()) params.set('entityType', entityFilter.trim());
              if (fromDate) params.set('from', fromDate);
              if (toDate) params.set('to', toDate);
              window.open(`/shopapi/audit/export?${params.toString()}`, '_blank');
            }}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="bg-white rounded shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="text-xs uppercase text-gray-500">Action</label>
            <input
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              placeholder="order_update, review_approve..."
              className="mt-1 w-full border rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs uppercase text-gray-500">Actor</label>
            <input
              value={actorFilter}
              onChange={(e) => setActorFilter(e.target.value)}
              placeholder="admin username"
              className="mt-1 w-full border rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs uppercase text-gray-500">Entity Type</label>
            <input
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              placeholder="order, review, settings..."
              className="mt-1 w-full border rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs uppercase text-gray-500">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="mt-1 w-full border rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs uppercase text-gray-500">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="mt-1 w-full border rounded px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={clearFilters}
            className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50"
          >
            Clear filters
          </button>
          <div className="text-xs text-gray-500">
            Showing up to {pageSize} logs per page
          </div>
        </div>
      </div>

      <div className="bg-white rounded shadow-sm">
        {loading ? (
          <div className="p-6 text-center">
            <Icon.Loading className="w-6 h-6 inline-block text-blue-600 mr-2" />
            Loading audit logs…
          </div>
        ) : preparedLogs.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No audit logs found</div>
        ) : (
          <div className="p-3 space-y-3">
            {preparedLogs.map((entry) => (
              <article key={entry.log.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                        {entry.actionLabel || entry.log.action}
                      </span>
                      <span className="text-xs text-gray-500">{entry.timeLabel}</span>
                    </div>
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">{entry.log.actor_username || '-'}</span>
                      <span className="text-gray-400"> · </span>
                      <span>{entry.entityLabel}</span>
                      <span className="text-gray-400"> · </span>
                      <span>{entry.log.ip || '-'}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedLog(entry)}
                      className="px-3 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-50"
                    >
                      View details
                    </button>
                    {entry.log.undoable && entry.log.undo_status !== 'success' ? (
                      <button
                        type="button"
                        onClick={() => handleUndo(entry.log)}
                        disabled={undoingId === entry.log.id}
                        className="px-3 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {undoingId === entry.log.id ? 'Undoing…' : 'Undo'}
                      </button>
                    ) : (
                      <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-600 border border-gray-200">
                        {entry.undoLabel}
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 lg:grid-cols-[1.2fr_2fr] gap-3">
                  <div className="rounded border border-gray-200 bg-gray-50 p-3">
                    <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Change Summary</div>
                    <div className="text-sm text-gray-700">
                      {entry.summary.diffCount > 0
                        ? `${entry.summary.diffCount} field${entry.summary.diffCount === 1 ? '' : 's'} changed`
                        : (entry.summary.topLevelCount > 0
                          ? `${entry.summary.topLevelCount} metadata field${entry.summary.topLevelCount === 1 ? '' : 's'}`
                          : 'No metadata details')}
                    </div>
                  </div>
                  <div className="rounded border border-gray-200 bg-white p-3">
                    <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Key Details</div>
                    {entry.summary.keyFacts.length === 0 && entry.summary.diffKeys.length === 0 ? (
                      <div className="text-sm text-gray-500">No structured metadata available</div>
                    ) : (
                      <div className="space-y-2">
                        {entry.summary.keyFacts.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {entry.summary.keyFacts.map((fact, idx) => (
                              <span key={`${fact.label}-${idx}`} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-gray-50 border border-gray-200 text-gray-700">
                                <span className="font-medium text-gray-600">{fact.label}:</span>
                                <span>{truncateText(fact.value, 72)}</span>
                              </span>
                            ))}
                          </div>
                        )}
                        {entry.summary.diffKeys.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {entry.summary.diffKeys.map((key) => (
                              <span key={key} className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-50 border border-blue-200 text-blue-700">
                                {key}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <button
            className="px-3 py-1 rounded border disabled:opacity-50"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            Prev
          </button>
          <div className="text-xs text-gray-500">Page {page + 1}</div>
          <button
            className="px-3 py-1 rounded border disabled:opacity-50"
            onClick={() => setPage(p => p + 1)}
            disabled={!hasMore}
          >
            Next
          </button>
        </div>
      </div>

      {selectedLog && (
        <div
          className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4"
          onClick={() => setSelectedLog(null)}
        >
          <div
            className="bg-white rounded-lg shadow-lg w-full max-w-5xl max-h-[92vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{selectedLog.actionLabel || selectedLog.log.action}</h2>
                <p className="text-sm text-gray-500 mt-1">{selectedLog.timeLabel}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedLog(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon.X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto max-h-[calc(92vh-148px)] space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Actor</div>
                  <div className="text-sm text-gray-800 mt-1">{selectedLog.log.actor_username || '-'}</div>
                </div>
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Entity</div>
                  <div className="text-sm text-gray-800 mt-1 break-all">{selectedLog.entityLabel}</div>
                </div>
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-xs uppercase tracking-wide text-gray-500">IP</div>
                  <div className="text-sm text-gray-800 mt-1 break-all">{selectedLog.log.ip || '-'}</div>
                </div>
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Undo</div>
                  <div className="text-sm text-gray-800 mt-1">{selectedLog.undoLabel}</div>
                </div>
              </div>

              <div className="rounded border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Metadata Highlights</h3>
                {selectedLog.summary.keyFacts.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {selectedLog.summary.keyFacts.map((fact, idx) => (
                      <div key={`${fact.label}-${idx}`} className="text-sm text-gray-700">
                        <span className="font-medium text-gray-600">{fact.label}:</span>{' '}
                        <span>{fact.value}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">No highlight fields</div>
                )}
              </div>

              <div className="rounded border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Changed Fields</h3>
                {selectedLog.summary.diffKeys.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedLog.summary.diffKeys.map((key) => (
                      <span key={key} className="px-2 py-1 rounded text-xs bg-blue-50 border border-blue-200 text-blue-700">
                        {key}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">No before/after diff data in this log</div>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Before Snapshot</h3>
                  <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 max-h-64 overflow-auto whitespace-pre-wrap break-all">
                    {selectedLog.metaObj?.before ? JSON.stringify(selectedLog.metaObj.before, null, 2) : 'No before snapshot'}
                  </pre>
                </div>
                <div className="rounded border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">After Snapshot</h3>
                  <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 max-h-64 overflow-auto whitespace-pre-wrap break-all">
                    {selectedLog.metaObj?.after ? JSON.stringify(selectedLog.metaObj.after, null, 2) : 'No after snapshot'}
                  </pre>
                </div>
              </div>

              <div className="rounded border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Raw Metadata JSON</h3>
                <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 max-h-80 overflow-auto whitespace-pre-wrap break-all">
                  {selectedLog.summary.hasRawMeta ? JSON.stringify(selectedLog.metaObj, null, 2) : 'No metadata'}
                </pre>
              </div>
            </div>
            <div className="border-t border-gray-200 p-4 flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedLog(null)}
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function HelpNotesManager() {
  const { request } = useApi();
  const { addToast } = useToasts();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [selected, setSelected] = useState(null);

  const load = React.useCallback(async () => {
    try { setLoading(true); const rows = await request('/help'); setTickets(Array.isArray(rows) ? rows : []); }
    catch { addToast('Failed to load tickets','error'); }
    finally { setLoading(false);} 
  }, [request, addToast]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    try {
      const preset = sessionStorage.getItem('pv_admin_tickets_status');
      if (preset) {
        setStatusFilter(preset);
        sessionStorage.removeItem('pv_admin_tickets_status');
      }
    } catch {}
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await request(`/help/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
      addToast(status === 'closed' ? 'Ticket closed' : 'Ticket reopened', 'success');
      if (selected?.id === id) setSelected(prev => prev ? { ...prev, status } : prev);
      load();
    } catch { addToast('Failed to update ticket', 'error'); }
  };

  const filtered = tickets.filter(t => statusFilter === 'all' ? true : (t.status || 'open') === statusFilter);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Support Tickets</h1>
          <p className="text-gray-600">Open and manage customer support tickets</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-700">Status</label>
          <select value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)} className="border rounded px-2 py-1 text-sm">
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6">Loading.</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-sm text-gray-600">No tickets found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">From</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Topic</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(t => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm text-gray-900">{t.name} <span className="text-gray-500">({t.email || t.contact || '-'})</span></td>
                    <td className="px-4 py-2 text-sm">{t.topic || '-'}</td>
                    <td className="px-4 py-2 text-sm max-w-[420px] truncate">{t.message}</td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ (t.status || 'open') === 'open' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800' }`}>
                        {t.status || 'open'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-500">{t.created_at ? new Date(t.created_at).toLocaleString() : '-'}</td>
                    <td className="px-4 py-2 text-sm text-right">
                      <button onClick={() => setSelected(t)} className="px-3 py-1 rounded border mr-2">View</button>
                      {(t.status || 'open') === 'open' ? (
                        <button onClick={() => updateStatus(t.id, 'closed')} className="px-3 py-1 rounded border">Close</button>
                      ) : (
                        <button onClick={() => updateStatus(t.id, 'open')} className="px-3 py-1 rounded border">Reopen</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl" onClick={(e)=>e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Ticket Details</h2>
                <p className="text-sm text-gray-500">ID: {selected.id}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-500 hover:text-gray-700"><Icon.X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-sm text-gray-500">Customer</div>
                  <div className="text-gray-900">{selected.name || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Contact</div>
                  <div className="text-gray-900 flex items-center gap-2">
                    <span>{selected.email || selected.contact || '-'}</span>
                    {(selected.email || selected.contact) && (
                      <button onClick={() => { navigator.clipboard?.writeText(selected.email || selected.contact); addToast('Copied', 'success'); }} className="text-xs px-2 py-0.5 border rounded">Copy</button>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Order</div>
                  <div className="text-gray-900">{selected.order_id || '-'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Status</div>
                  <div className="text-gray-900">{selected.status || 'open'}</div>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Topic</div>
                <div className="text-gray-900">{selected.topic || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Message</div>
                <div className="text-gray-900 whitespace-pre-wrap bg-gray-50 p-3 rounded border">{selected.message}</div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-between items-center">
              <div className="text-xs text-gray-500">Created: {selected.created_at ? new Date(selected.created_at).toLocaleString() : '-'}</div>
              <div className="flex gap-2">
                {(selected.status || 'open') === 'open' ? (
                  <button onClick={() => updateStatus(selected.id, 'closed')} className="px-4 py-2 bg-blue-600 text-white rounded">Close Ticket</button>
                ) : (
                  <button onClick={() => updateStatus(selected.id, 'open')} className="px-4 py-2 bg-blue-600 text-white rounded">Reopen Ticket</button>
                )}
                <button onClick={() => setSelected(null)} className="px-4 py-2 border rounded">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function BackupManager() {
  const { request, downloadFile } = useApi();
  const { addToast } = useToasts();
  const [backups, setBackups] = useState([]);
  const [localBackups, setLocalBackups] = useState([]);
  const [backupConfig, setBackupConfig] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [creating, setCreating] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreMode, setRestoreMode] = useState('full');
  const [restoreUploads, setRestoreUploads] = useState(true);
  const [restoreScopes, setRestoreScopes] = useState(['products', 'orders', 'promotions', 'reviews', 'settings', 'audit', 'support', 'notifications']);
  const [restoring, setRestoring] = useState(false);
  const [operation, setOperation] = useState({
    active: false,
    label: '',
    received: 0,
    total: 0,
    indeterminate: false
  });

  const restoreScopeOptions = useMemo(() => ([
    { id: 'products', label: 'Products' },
    { id: 'orders', label: 'Orders' },
    { id: 'promotions', label: 'Promotions & banners' },
    { id: 'reviews', label: 'Reviews' },
    { id: 'settings', label: 'Settings, customization, legal pages' },
    { id: 'audit', label: 'Audit logs' },
    { id: 'support', label: 'Help messages & newsletter' },
    { id: 'notifications', label: 'Notification logs' },
    { id: 'users', label: 'Admin/user accounts' }
  ]), []);

  const weekdayOptions = useMemo(() => ([
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ]), []);

  const showOperation = useCallback((label, { indeterminate = true } = {}) => {
    setOperation({
      active: true,
      label: String(label || 'Working...'),
      received: 0,
      total: 0,
      indeterminate
    });
  }, []);

  const updateOperation = useCallback((received, total) => {
    const r = Number(received || 0);
    const t = Number(total || 0);
    setOperation((prev) => ({
      ...prev,
      received: r,
      total: t,
      indeterminate: !(t > 0)
    }));
  }, []);

  const hideOperation = useCallback(() => {
    setOperation({ active: false, label: '', received: 0, total: 0, indeterminate: false });
  }, []);

  const loadBackups = useCallback(async () => {
    try {
      setLoading(true);
      const data = await request('/backup/list');
      setBackups(data.backups || []);
      setConfigured(!data.message);
    } catch {
      addToast('Failed to load backups', 'error');
      setConfigured(false);
    } finally {
      setLoading(false);
    }
  }, [request, addToast]);

  const loadBackupConfig = useCallback(async () => {
    try {
      setConfigLoading(true);
      const data = await request('/backup/config');
      setBackupConfig(data?.config || null);
      setScheduler(data?.scheduler || null);
      setLocalBackups(Array.isArray(data?.localBackups) ? data.localBackups : []);
    } catch {
      addToast('Failed to load backup configuration', 'error');
    } finally {
      setConfigLoading(false);
    }
  }, [request, addToast]);

  useEffect(() => {
    loadBackups();
    loadBackupConfig();
  }, [loadBackups, loadBackupConfig]);

  const handleCreateBackup = async () => {
    if (!window.confirm('Create a new backup? This will upload your database and files to Google Drive.')) return;
    try {
      showOperation('Creating and uploading Google Drive backup...', { indeterminate: true });
      setCreating(true);
      await request('/backup/create', { method: 'POST' });
      addToast('Backup created successfully', 'success');
      await Promise.all([loadBackups(), loadBackupConfig()]);
    } catch (err) {
      addToast(err.message || 'Failed to create backup', 'error');
    } finally {
      setCreating(false);
      hideOperation();
    }
  };

  const handleDownload = async (fileId, fileName) => {
    try {
      showOperation(`Downloading ${fileName || 'backup'}...`, { indeterminate: true });
      await downloadFile(`/backup/download/${fileId}`, fileName || `backup_${Date.now()}.zip`, {
        method: 'POST',
        onProgress: (received, total) => updateOperation(received, total)
      });
      addToast('Backup downloaded', 'success');
    } catch {
      addToast('Failed to download backup', 'error');
    } finally {
      hideOperation();
    }
  };

  const handleDelete = async (fileId, fileName) => {
    if (!window.confirm(`Delete backup: ${fileName}?`)) return;
    try {
      await request(`/backup/${fileId}`, { method: 'DELETE' });
      addToast('Backup deleted', 'success');
      await loadBackups();
    } catch {
      addToast('Failed to delete backup', 'error');
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleLocalBackupDownload = async () => {
    try {
      showOperation('Preparing and downloading local backup...', { indeterminate: true });
      await downloadFile('/backup/local', `shopstarter_backup_${new Date().toISOString().split('T')[0]}.zip`, {
        onProgress: (received, total) => updateOperation(received, total)
      });
      addToast('Local backup downloaded successfully', 'success');
    } catch (err) {
      addToast('Failed to download local backup: ' + err.message, 'error');
    } finally {
      hideOperation();
    }
  };

  const toggleRestoreScope = useCallback((scopeId) => {
    setRestoreScopes((prev) => (
      prev.includes(scopeId)
        ? prev.filter((item) => item !== scopeId)
        : [...prev, scopeId]
    ));
  }, []);

  const canSubmitSelectiveRestore = restoreMode === 'full' || restoreScopes.length > 0 || restoreUploads;

  const handleRestoreSubmit = async (e) => {
    e.preventDefault();
    if (!restoreFile) return;
    if (!canSubmitSelectiveRestore) {
      addToast('Select at least one restore scope or media files', 'error');
      return;
    }
    const fullRestore = restoreMode === 'full';
    const confirmText = fullRestore
      ? 'WARNING: Full restore will overwrite all current data and media. Continue?'
      : 'WARNING: Selective restore will overwrite selected sections. Continue?';
    if (!window.confirm(confirmText)) return;

    try {
      showOperation(fullRestore ? 'Restoring full backup...' : 'Restoring selected scopes...', { indeterminate: true });
      setRestoring(true);
      const formData = new FormData();
      formData.append('backup', restoreFile);
      formData.append('mode', restoreMode);
      formData.append('scopes', JSON.stringify(restoreScopes));
      formData.append('restoreUploads', String(restoreUploads));
      await request('/backup/restore', { method: 'POST', body: formData });
      addToast('Backup restored successfully! Reloading...', 'success');
      setRestoreModalOpen(false);
      setRestoreFile(null);
      setTimeout(() => window.location.reload(), 2000);
    } catch (err) {
      addToast(err.message || 'Failed to restore backup', 'error');
    } finally {
      setRestoring(false);
      hideOperation();
    }
  };

  const handleSaveConfig = async () => {
    if (!backupConfig) return;
    try {
      setSavingConfig(true);
      const data = await request('/backup/config', {
        method: 'PUT',
        body: JSON.stringify(backupConfig)
      });
      setBackupConfig(data?.config || backupConfig);
      addToast('Backup schedule updated', 'success');
      await loadBackupConfig();
    } catch (err) {
      addToast(err.message || 'Failed to save backup schedule', 'error');
    } finally {
      setSavingConfig(false);
    }
  };

  const operationPercent = operation.total > 0
    ? Math.max(0, Math.min(100, Math.round((operation.received / operation.total) * 100)))
    : 0;

  const setLocalConfigField = (field, value) => {
    setBackupConfig((prev) => ({
      ...(prev || {}),
      localSchedule: {
        ...(prev?.localSchedule || {}),
        [field]: value
      }
    }));
  };

  const setGoogleConfigField = (field, value) => {
    setBackupConfig((prev) => ({
      ...(prev || {}),
      googleSchedule: {
        ...(prev?.googleSchedule || {}),
        [field]: value
      }
    }));
  };

  const formatScheduleTime = (hour, minute) => {
    const h = String(hour).padStart(2, '0');
    const m = String(minute).padStart(2, '0');
    return `${h}:${m}`;
  };

  const schedulerLastRun = scheduler?.lastRunAt ? new Date(scheduler.lastRunAt).toLocaleString() : 'Not run yet';

  if (configLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <Icon.Loading className="w-8 h-8 mx-auto text-blue-600" />
        <p className="text-sm text-gray-600 mt-2">Loading backup tools...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Backup & Restore</h1>
          <p className="text-gray-600 mt-1">Comprehensive backup, scoped restore, and automated retention</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 w-full md:w-auto">
          <button
            onClick={handleLocalBackupDownload}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2 justify-center"
          >
            <Icon.Download className="w-4 h-4" />
            Download Local
          </button>
          <button
            onClick={() => setRestoreModalOpen(true)}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition flex items-center gap-2 justify-center"
          >
            <Icon.Upload className="w-4 h-4" />
            Restore
          </button>
          <button
            onClick={handleCreateBackup}
            disabled={creating || !configured}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 justify-center"
          >
            {creating ? <Icon.Loading className="w-4 h-4" /> : <Icon.Upload className="w-4 h-4" />}
            {creating ? 'Creating...' : 'Google Backup'}
          </button>
        </div>
      </div>

      {operation.active && (
        <div className="bg-white rounded-lg shadow p-4 border border-blue-100">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-medium text-gray-900">{operation.label}</div>
            <div className="text-xs text-gray-500">
              {operation.indeterminate ? 'Working...' : `${operationPercent}%`}
            </div>
          </div>
          <div className="mt-2 h-2 rounded-full bg-gray-200 overflow-hidden">
            <div
              className={`h-full rounded-full ${operation.indeterminate ? 'animate-pulse bg-blue-500 w-1/2' : 'bg-blue-600'}`}
              style={operation.indeterminate ? undefined : { width: `${operationPercent}%` }}
            />
          </div>
          {!operation.indeterminate && operation.total > 0 && (
            <div className="mt-1 text-xs text-gray-500">
              {formatBytes(operation.received)} / {formatBytes(operation.total)}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Local VPS Automation</h2>
            <p className="text-sm text-gray-600 mt-1">Auto backup to local VPS storage with retention trimming.</p>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={!!backupConfig?.localSchedule?.enabled}
              onChange={(e) => setLocalConfigField('enabled', e.target.checked)}
            />
            Enable scheduled local backups
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Day</label>
              <select
                value={Number(backupConfig?.localSchedule?.dayOfWeek ?? 0)}
                onChange={(e) => setLocalConfigField('dayOfWeek', Number(e.target.value))}
                className="w-full border rounded-md px-3 py-2 text-sm"
              >
                {weekdayOptions.map((day) => (
                  <option key={day.value} value={day.value}>{day.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Hour (24h)</label>
              <input
                type="number"
                min="0"
                max="23"
                value={Number(backupConfig?.localSchedule?.hour ?? 5)}
                onChange={(e) => setLocalConfigField('hour', Number(e.target.value))}
                className="w-full border rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Minute</label>
              <input
                type="number"
                min="0"
                max="59"
                value={Number(backupConfig?.localSchedule?.minute ?? 0)}
                onChange={(e) => setLocalConfigField('minute', Number(e.target.value))}
                className="w-full border rounded-md px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Keep last local backups</label>
            <input
              type="number"
              min="1"
              max="500"
              value={Number(backupConfig?.localSchedule?.keepLast ?? 10)}
              onChange={(e) => setLocalConfigField('keepLast', Number(e.target.value))}
              className="w-full border rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div className="text-xs text-gray-600 bg-gray-50 rounded-md p-3">
            Next rule: every <strong>{weekdayOptions.find((d) => d.value === Number(backupConfig?.localSchedule?.dayOfWeek ?? 0))?.label || 'Sunday'}</strong> at <strong>{formatScheduleTime(Number(backupConfig?.localSchedule?.hour ?? 5), Number(backupConfig?.localSchedule?.minute ?? 0))}</strong>.
            Local snapshots on VPS: <strong>{localBackups.length}</strong>.
          </div>
          {localBackups.length > 0 && (
            <div className="border rounded-md p-3">
              <div className="text-xs font-medium text-gray-700 mb-2">Recent local backups</div>
              <div className="space-y-2">
                {localBackups.slice(0, 5).map((entry) => (
                  <div key={entry.name} className="text-xs text-gray-600 flex items-center justify-between gap-2">
                    <span className="truncate">{entry.name}</span>
                    <span className="shrink-0">{formatBytes(entry.size)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Google Drive Automation</h2>
            <p className="text-sm text-gray-600 mt-1">Optional cloud copy of scheduled backups.</p>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={!!backupConfig?.googleSchedule?.enabled}
              onChange={(e) => setGoogleConfigField('enabled', e.target.checked)}
            />
            Enable Google upload for scheduled runs
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={!!backupConfig?.googleSchedule?.keepAll}
              onChange={(e) => setGoogleConfigField('keepAll', e.target.checked)}
            />
            Keep all Google backups (default)
          </label>
          {!backupConfig?.googleSchedule?.keepAll && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Keep last Google backups</label>
              <input
                type="number"
                min="1"
                max="500"
                value={Number(backupConfig?.googleSchedule?.keepLast ?? 50)}
                onChange={(e) => setGoogleConfigField('keepLast', Number(e.target.value))}
                className="w-full border rounded-md px-3 py-2 text-sm"
              />
            </div>
          )}
          {!configured && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              Google Drive is not configured. Add credentials.json and token.json in backend to enable cloud backup.
            </div>
          )}
          <div className="text-xs text-gray-600 bg-gray-50 rounded-md p-3">
            Scheduler status: <strong>{scheduler?.running ? 'Running' : 'Idle'}</strong><br />
            Last run: <strong>{schedulerLastRun}</strong>
            {scheduler?.lastError && (
              <>
                <br />
                Last error: <strong className="text-red-600">{scheduler.lastError}</strong>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSaveConfig}
          disabled={savingConfig || !backupConfig}
          className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-black disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {savingConfig && <Icon.Loading className="w-4 h-4" />}
          {savingConfig ? 'Saving...' : 'Save Backup Settings'}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Google Drive Backups</h2>
            <p className="text-sm text-gray-600">Manual and scheduled cloud backups.</p>
          </div>
        </div>
        {loading ? (
          <div className="text-center py-10">
            <Icon.Loading className="w-8 h-8 mx-auto text-blue-600" />
            <p className="text-gray-600 mt-2">Loading backups...</p>
          </div>
        ) : backups.length === 0 ? (
          <div className="text-sm text-gray-500 py-8 text-center">No Google backups found.</div>
        ) : (
          <div className="mt-4 space-y-3">
            {backups.map((backup) => (
              <div key={backup.id} className="border rounded-lg p-4 bg-white flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 break-all">{backup.name}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    {formatBytes(backup.size)} · {new Date(backup.createdTime).toLocaleString()}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => handleDownload(backup.id, backup.name)}
                    className="px-3 py-1.5 border rounded-md text-sm hover:bg-gray-50"
                    type="button"
                  >
                    Download
                  </button>
                  <button
                    onClick={() => handleDelete(backup.id, backup.name)}
                    className="px-3 py-1.5 border border-red-200 text-red-700 rounded-md text-sm hover:bg-red-50"
                    type="button"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900 mb-2">Backup Scope</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Backups package the full database plus all uploaded media files.</li>
          <li>• Full restore can recover a blank instance to prior state in one operation.</li>
          <li>• Selective restore can overwrite only chosen scopes (orders/products/reviews/etc.).</li>
          <li>• Every restore creates an automatic safety backup first.</li>
        </ul>
      </div>

      {restoreModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]"
          onClick={() => setRestoreModalOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-lg w-full max-w-2xl m-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Restore Backup</h2>
              <button onClick={() => setRestoreModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <Icon.X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleRestoreSubmit} className="p-6 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
                Restore overwrites existing data in selected scopes. A safety backup is created before restore.
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Restore mode</label>
                <label className="flex items-center gap-2 text-sm text-gray-800">
                  <input
                    type="radio"
                    name="restoreMode"
                    value="full"
                    checked={restoreMode === 'full'}
                    onChange={(e) => {
                      const next = e.target.value;
                      setRestoreMode(next);
                      if (next === 'full') setRestoreUploads(true);
                    }}
                  />
                  Full site restore (all tables + uploads)
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-800">
                  <input
                    type="radio"
                    name="restoreMode"
                    value="selective"
                    checked={restoreMode === 'selective'}
                    onChange={(e) => setRestoreMode(e.target.value)}
                  />
                  Selective restore (choose sections)
                </label>
              </div>

              {restoreMode === 'selective' && (
                <div className="space-y-3 border rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-800">Selectable scopes</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {restoreScopeOptions.map((scope) => (
                      <label key={scope.id} className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={restoreScopes.includes(scope.id)}
                          onChange={() => toggleRestoreScope(scope.id)}
                        />
                        {scope.label}
                      </label>
                    ))}
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700 pt-1 border-t">
                    <input
                      type="checkbox"
                      checked={restoreUploads}
                      onChange={(e) => setRestoreUploads(e.target.checked)}
                    />
                    Restore uploaded media files
                  </label>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Backup File (.zip)
                </label>
                <input
                  type="file"
                  accept=".zip"
                  onChange={(e) => setRestoreFile(e.target.files[0])}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  required
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-end gap-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setRestoreModalOpen(false);
                    setRestoreFile(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={restoring}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!restoreFile || restoring || !canSubmitSelectiveRestore}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {restoring ? (
                    <>
                      <Icon.Loading className="w-4 h-4" />
                      Restoring...
                    </>
                  ) : (
                    <>
                      <Icon.Upload className="w-4 h-4" />
                      Restore Backup
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
function LegalPagesManager() {
  const { request } = useApi();
  const { addToast } = useToasts();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);
  const [formData, setFormData] = useState({
    page_key: '',
    title: '',
    content: '',
    active: true
  });

  const loadPages = useCallback(async () => {
    try {
      setLoading(true);
      const data = await request('/legal-pages');
      setPages(data);
    } catch {
      addToast('Failed to load legal pages', 'error');
    } finally {
      setLoading(false);
    }
  }, [request, addToast]);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPage) {
        await request(`/legal-pages/${editingPage.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData),
          headers: { 'Content-Type': 'application/json' }
        });
        addToast('Legal page updated', 'success');
      } else {
        await request('/legal-pages', {
          method: 'POST',
          body: JSON.stringify(formData),
          headers: { 'Content-Type': 'application/json' }
        });
        addToast('Legal page created', 'success');
      }
      setShowForm(false);
      setEditingPage(null);
      setFormData({ page_key: '', title: '', content: '', active: true });
      loadPages();
    } catch {
      addToast('Failed to save legal page', 'error');
    }
  };

  const handleEdit = (page) => {
    const next = {
      page_key: page.page_key,
      title: page.title,
      content: page.content,
      active: page.active
    };
    setEditingPage(page);
    setFormData(next);
    setInitialFormData(next);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this legal page?')) return;
    try {
      await request(`/legal-pages/${id}`, { method: 'DELETE' });
      addToast('Legal page deleted', 'success');
      loadPages();
    } catch {
      addToast('Failed to delete', 'error');
    }
  };

  const handleNewPage = () => {
    const next = { page_key: '', title: '', content: '', active: true };
    setEditingPage(null);
    setFormData(next);
    setInitialFormData(next);
    setShowForm(true);
  };

  const closeForm = useCallback(() => {
    const isDirty = showForm && initialFormData ? hasFormChanges(formData, initialFormData) : false;
    if (!confirmDiscardChanges(isDirty)) return;
    setShowForm(false);
  }, [showForm, formData, initialFormData]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Legal Pages</h1>
          <p className="text-gray-600">Manage privacy policy, terms, refund & shipping policies</p>
        </div>
        <button
          onClick={handleNewPage}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Icon.Plus className="w-4 h-4 inline mr-2" />
          Add Legal Page
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Icon.Loading className="w-8 h-8 mx-auto text-blue-600" />
          <p className="text-gray-600 mt-2">Loading...</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Page Key</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pages.map(page => (
                <tr key={page.id}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{page.page_key}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{page.title}</td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs ${page.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {page.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-sm space-x-2">
                    <button onClick={() => handleEdit(page)} className="text-blue-600 hover:text-blue-800">Edit</button>
                    <button onClick={() => handleDelete(page.id)} className="text-red-600 hover:text-red-800">Delete</button>
                  </td>
                </tr>
              ))}
              {pages.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                    No legal pages yet. Click "Add Legal Page" to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]"
          onClick={closeForm}
        >
          <div
            className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold">{editingPage ? 'Edit Legal Page' : 'New Legal Page'}</h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                <Icon.X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Page Key</label>
                <input
                  type="text"
                  required
                  value={formData.page_key}
                  onChange={(e) => setFormData(prev => ({ ...prev, page_key: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="e.g., privacy-policy, terms-conditions"
                  disabled={!!editingPage}
                />
                <p className="text-xs text-gray-500 mt-1">URL slug for this page (cannot be changed after creation)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="e.g., Privacy Policy"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                <textarea
                  required
                  rows={15}
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                  placeholder={'HTML content, or [img]https://example.com/image.jpg[/img] for an inline image...'}
                />
                <p className="text-xs text-gray-500 mt-1">Supports safe HTML. Add images with <code>[img]https://example.com/image.jpg[/img]</code>; optional alt text: <code>[img alt=&quot;Description&quot;]https://...[/img]</code>.</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                  id="active-checkbox"
                />
                <label htmlFor="active-checkbox" className="text-sm font-medium text-gray-700">Active (visible to users)</label>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-end gap-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingPage ? 'Update' : 'Create'} Page
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}


function MediaLibrary() {
  const { request } = useApi();
  const auth = useAuth();
  const { addToast } = useToasts();

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalEntries, setTotalEntries] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');
  const [viewMode, setViewMode] = useState('list');

  const [dragActive, setDragActive] = useState(false);
  const [currentPath, setCurrentPath] = useState('');
  const [selectedPaths, setSelectedPaths] = useState([]);
  const [previewItem, setPreviewItem] = useState(null);

  const [uploadDedupeMode, setUploadDedupeMode] = useState('reuse');
  const [uploadOptimizeMode, setUploadOptimizeMode] = useState('auto');
  const [showLibrarySettings, setShowLibrarySettings] = useState(false);

  const [dialog, setDialog] = useState(null);
  const [dialogBusy, setDialogBusy] = useState(false);

  const fileInputRef = useRef(null);
  const offsetRef = useRef(0);
  const debouncedSearch = useDebouncedValue(searchTerm, 220);
  const MEDIA_PAGE_SIZE = 120;

  const getEntryPath = useCallback((entry) => String(entry?.path || entry?.name || ''), []);
  const encodeMediaPath = useCallback((relPath) => String(relPath || '').split('/').map((part) => encodeURIComponent(part)).join('/'), []);

  const formatFileSize = useCallback((bytes) => {
    const value = Number(bytes || 0);
    if (!Number.isFinite(value) || value <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = value;
    let unit = 0;
    while (size >= 1024 && unit < units.length - 1) {
      size /= 1024;
      unit += 1;
    }
    return `${Math.round(size * 100) / 100} ${units[unit]}`;
  }, []);

  const formatDate = useCallback((epochMs) => {
    const value = Number(epochMs || 0);
    if (!Number.isFinite(value) || value <= 0) return '-';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return '-';
    }
  }, []);

  const getEntryKind = useCallback((entry) => {
    if (!entry || entry.type === 'dir') return 'folder';
    const backendType = String(entry.media_type || '').toLowerCase();
    if (['image', 'video', 'audio', 'model', 'document'].includes(backendType)) return backendType;
    const inferred = getMediaKind(entry.url || entry.name || '');
    if (['image', 'video', 'audio', 'model', 'document'].includes(inferred)) return inferred;
    return 'other';
  }, []);

  const getCopyPathSuggestion = useCallback((inputPath) => {
    const fullPath = String(inputPath || '').trim();
    if (!fullPath) return '';
    const slashIndex = fullPath.lastIndexOf('/');
    const dirPrefix = slashIndex >= 0 ? `${fullPath.slice(0, slashIndex + 1)}` : '';
    const leaf = slashIndex >= 0 ? fullPath.slice(slashIndex + 1) : fullPath;
    const dotIndex = leaf.lastIndexOf('.');
    if (dotIndex > 0) {
      return `${dirPrefix}${leaf.slice(0, dotIndex)}-copy${leaf.slice(dotIndex)}`;
    }
    return `${dirPrefix}${leaf}-copy`;
  }, []);

  const load = React.useCallback(async ({ reset = true } = {}) => {
    const nextOffset = reset ? 0 : offsetRef.current;
    try {
      if (reset) setLoading(true);
      else setLoadingMore(true);

      const params = new URLSearchParams();
      params.set('includeMeta', '1');
      params.set('limit', String(MEDIA_PAGE_SIZE));
      params.set('offset', String(nextOffset));
      params.set('sort', sortBy || 'name_asc');
      if (currentPath) params.set('path', currentPath);
      if (debouncedSearch) params.set('q', debouncedSearch);
      if (typeFilter) params.set('type', typeFilter);

      const data = await request(`/media?${params.toString()}`);
      const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
      const total = Number.isFinite(Number(data?.total)) ? Number(data.total) : items.length;
      const nextHasMore = typeof data?.hasMore === 'boolean'
        ? data.hasMore
        : (items.length >= MEDIA_PAGE_SIZE);

      offsetRef.current = nextOffset + items.length;
      setFiles((prev) => reset ? items : [...prev, ...items]);
      setTotalEntries(total);
      setHasMore(nextHasMore);
    } catch {
      addToast('Failed to load media', 'error');
      if (reset) {
        setFiles([]);
        setTotalEntries(0);
        setHasMore(false);
      }
    } finally {
      if (reset) setLoading(false);
      else setLoadingMore(false);
    }
  }, [request, addToast, currentPath, debouncedSearch, typeFilter, sortBy]);

  useEffect(() => {
    offsetRef.current = 0;
    load({ reset: true });
  }, [load]);

  useEffect(() => {
    const available = new Set((files || []).map(getEntryPath));
    setSelectedPaths((prev) => prev.filter((pathValue) => available.has(pathValue)));
  }, [files, getEntryPath]);

  const copyUrl = async (url) => {
    if (!url) return;
    const resolved = resolveMediaUrl(url);
    const absoluteUrl = /^https?:\/\//i.test(resolved) ? resolved : `${window.location.origin}${resolved.startsWith('/') ? '' : '/'}${resolved}`;
    try {
      await navigator.clipboard.writeText(absoluteUrl);
      addToast('Secure URL copied to clipboard', 'success');
    } catch {
      addToast('Failed to copy URL', 'error');
    }
  };

  const selectedEntries = useMemo(() => {
    const selected = new Set(selectedPaths);
    return (files || []).filter((entry) => selected.has(getEntryPath(entry)));
  }, [files, selectedPaths, getEntryPath]);

  const singleSelectedEntry = selectedEntries.length === 1 ? selectedEntries[0] : null;
  const singleSelectedPath = singleSelectedEntry ? getEntryPath(singleSelectedEntry) : '';
  const singleSelectedKind = singleSelectedEntry ? getEntryKind(singleSelectedEntry) : '';
  const canOptimizeSelection = !!(
    singleSelectedEntry &&
    singleSelectedEntry.type !== 'dir' &&
    singleSelectedKind === 'image'
  );

  const toggleSelection = useCallback((entryPath, forceChecked) => {
    const pathValue = String(entryPath || '');
    if (!pathValue) return;
    setSelectedPaths((prev) => {
      const exists = prev.includes(pathValue);
      const shouldInclude = typeof forceChecked === 'boolean' ? forceChecked : !exists;
      if (shouldInclude && !exists) return [...prev, pathValue];
      if (!shouldInclude && exists) return prev.filter((item) => item !== pathValue);
      return prev;
    });
  }, []);

  const openEntry = useCallback((entry) => {
    if (!entry) return;
    if (entry.type === 'dir') {
      setCurrentPath(getEntryPath(entry));
      setSelectedPaths([]);
      return;
    }
    setPreviewItem(entry);
  }, [getEntryPath]);

  const handleEntryClick = useCallback((entry, event) => {
    const pathValue = getEntryPath(entry);
    if (!pathValue) return;
    if (event?.metaKey || event?.ctrlKey) {
      toggleSelection(pathValue);
      return;
    }
    setSelectedPaths([pathValue]);
  }, [getEntryPath, toggleSelection]);

  const deleteSelection = useCallback(async () => {
    if (!selectedPaths.length) return;
    if (!window.confirm(`Delete ${selectedPaths.length} selected item(s)?`)) return;
    try {
      const results = await Promise.allSettled(
        selectedPaths.map((pathValue) => request(`/media/${encodeMediaPath(pathValue)}`, { method: 'DELETE' }))
      );
      const success = results.filter((result) => result.status === 'fulfilled').length;
      const failed = results.length - success;
      if (success > 0) addToast(`Deleted ${success} item(s)`, 'success');
      if (failed > 0) addToast(`${failed} item(s) failed to delete`, 'error');
      setSelectedPaths([]);
      load({ reset: true });
    } catch {
      addToast('Failed to delete selection', 'error');
    }
  }, [selectedPaths, request, encodeMediaPath, addToast, load]);

  const emptyTrash = useCallback(async () => {
    if (!window.confirm('Permanently delete all files currently in the media trash?')) return;
    try {
      const result = await request('/media-trash', { method: 'DELETE' });
      const removedCount = Number(result?.removedCount || 0);
      addToast(removedCount > 0 ? `Emptied trash (${removedCount} item${removedCount === 1 ? '' : 's'})` : 'Trash was already empty', 'success');
      load({ reset: true });
    } catch (err) {
      addToast(err.message || 'Failed to empty trash', 'error');
    }
  }, [request, addToast, load]);

  const openDialog = useCallback((nextDialog) => {
    setDialogBusy(false);
    setDialog(nextDialog);
  }, []);

  const closeDialog = useCallback(() => {
    if (dialogBusy) return;
    setDialog(null);
  }, [dialogBusy]);

  const openNewFolderDialog = useCallback(() => {
    openDialog({
      type: 'mkdir',
      value: currentPath ? `${currentPath}/new-folder` : 'new-folder'
    });
  }, [currentPath, openDialog]);

  const openRenameDialog = useCallback(() => {
    if (!singleSelectedEntry) return;
    openDialog({
      type: 'rename',
      from: singleSelectedPath,
      value: singleSelectedPath
    });
  }, [singleSelectedEntry, singleSelectedPath, openDialog]);

  const openCopyDialog = useCallback(() => {
    if (!singleSelectedEntry || singleSelectedEntry.type === 'dir') return;
    openDialog({
      type: 'copy',
      from: singleSelectedPath,
      value: getCopyPathSuggestion(singleSelectedPath)
    });
  }, [singleSelectedEntry, singleSelectedPath, getCopyPathSuggestion, openDialog]);

  const openOptimizeDialog = useCallback(() => {
    if (!canOptimizeSelection || !singleSelectedEntry) return;
    openDialog({
      type: 'optimize',
      path: singleSelectedPath,
      maxWidth: 1920,
      quality: 84,
      format: 'keep',
      cropMode: 'inside',
      crop: { unit: '%', x: 10, y: 10, width: 80, height: 80 },
      cropPixels: null,
      url: singleSelectedEntry.url || '',
      replace: false,
      onlyIfSmaller: true
    });
  }, [canOptimizeSelection, singleSelectedEntry, singleSelectedPath, openDialog]);

  const shareSelection = useCallback(async () => {
    if (!singleSelectedEntry || singleSelectedEntry.type === 'dir' || !singleSelectedEntry.url) return;
    const resolved = resolveMediaUrl(singleSelectedEntry.url);
    const absoluteUrl = /^https?:\/\//i.test(resolved) ? resolved : `${window.location.origin}${resolved.startsWith('/') ? '' : '/'}${resolved}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: singleSelectedEntry.name || 'Media file', url: absoluteUrl });
        addToast('Share panel opened', 'success');
      } else {
        await navigator.clipboard.writeText(absoluteUrl);
        addToast('Secure URL copied to clipboard', 'success');
      }
    } catch (err) {
      if (err?.name !== 'AbortError') addToast('Failed to share media', 'error');
    }
  }, [singleSelectedEntry, addToast]);

  const submitDialog = useCallback(async (event) => {
    event.preventDefault();
    if (!dialog) return;
    try {
      setDialogBusy(true);
      if (dialog.type === 'mkdir') {
        const name = String(dialog.value || '').trim();
        if (!name) throw new Error('Folder path is required');
        await request('/media/mkdir', {
          method: 'POST',
          body: JSON.stringify({ name })
        });
        addToast('Folder created', 'success');
      } else if (dialog.type === 'rename') {
        const from = String(dialog.from || '').trim();
        const to = String(dialog.value || '').trim();
        if (!from || !to) throw new Error('Source and destination are required');
        await request('/media/rename', {
          method: 'POST',
          body: JSON.stringify({ from, to })
        });
        setSelectedPaths([to]);
        addToast('Updated path', 'success');
      } else if (dialog.type === 'copy') {
        const from = String(dialog.from || '').trim();
        const to = String(dialog.value || '').trim();
        if (!from || !to) throw new Error('Source and destination are required');
        await request('/media/copy', {
          method: 'POST',
          body: JSON.stringify({ from, to })
        });
        setSelectedPaths([to]);
        addToast('Copy created', 'success');
      } else if (dialog.type === 'optimize') {
        const targetPath = String(dialog.path || '').trim();
        if (!targetPath) throw new Error('File path is required');
        const payload = await request('/media/optimize', {
          method: 'POST',
          body: JSON.stringify({
            path: targetPath,
            maxWidth: Number(dialog.maxWidth || 1920),
            quality: Number(dialog.quality || 84),
            format: String(dialog.format || 'keep'),
            cropMode: String(dialog.cropMode || 'inside'),
            crop: dialog.cropMode === 'free' ? { ...(dialog.cropPixels || {}), unit: 'pixels' } : undefined,
            replace: !!dialog.replace,
            onlyIfSmaller: !!dialog.onlyIfSmaller
          })
        });
        if (payload?.skipped) {
          addToast('Optimization skipped because output was not smaller', 'info');
        } else {
          addToast(`Optimized successfully (saved ${formatFileSize(payload?.savedBytes || 0)})`, 'success');
          if (payload?.file?.path) setSelectedPaths([payload.file.path]);
        }
      }
      setDialog(null);
      load({ reset: true });
    } catch (err) {
      addToast(err?.message || 'Action failed', 'error');
    } finally {
      setDialogBusy(false);
    }
  }, [dialog, request, addToast, formatFileSize, load]);

  const uploadFiles = async (fileList) => {
    if (!fileList || !fileList.length) return;
    setUploading(true);
    setUploadProgress(0);

    const fd = new FormData();
    Array.from(fileList).forEach((file) => fd.append('files', file));
    fd.append('dedupe', uploadDedupeMode);
    fd.append('optimize', uploadOptimizeMode);

    try {
      const responseText = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (event) => {
          if (!event.lengthComputable) return;
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentComplete);
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.responseText || '{}');
          } else {
            let message = 'Upload failed';
            try {
              const parsed = JSON.parse(xhr.responseText || '{}');
              if (parsed?.error) message = parsed.error;
            } catch {}
            reject(new Error(message));
          }
        });
        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.open('POST', '/shopapi/media');
        xhr.withCredentials = true;
        try {
          const headers = auth?.getAuthHeaders ? auth.getAuthHeaders() : {};
          if (currentPath) headers['X-Upload-Folder'] = currentPath;
          Object.entries(headers).forEach(([key, value]) => xhr.setRequestHeader(key, value));
        } catch {}
        xhr.send(fd);
      });

      let payload = {};
      try {
        payload = JSON.parse(responseText || '{}');
      } catch {}

      const count = Number(payload?.count || fileList.length);
      const duplicateCount = Number(payload?.duplicateCount || 0);
      if (duplicateCount > 0) {
        addToast(`Uploaded ${count} item(s). ${duplicateCount} duplicate(s) reused.`, 'success');
      } else {
        addToast(`Uploaded ${count} item(s).`, 'success');
      }
      await load({ reset: true });
    } catch (err) {
      addToast(err?.message || 'Failed to upload', 'error');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === 'dragenter' || event.type === 'dragover') {
      setDragActive(true);
    } else if (event.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      uploadFiles(event.dataTransfer.files);
    }
  };

  const visibleEntries = useMemo(() => files || [], [files]);

  const breadcrumbs = useMemo(() => {
    const segments = currentPath ? currentPath.split('/').filter(Boolean) : [];
    let cursor = '';
    return segments.map((segment) => {
      cursor = cursor ? `${cursor}/${segment}` : segment;
      return { name: segment, path: cursor };
    });
  }, [currentPath]);

  const folderCount = useMemo(() => files.filter((entry) => entry.type === 'dir').length, [files]);
  const fileCount = useMemo(() => files.filter((entry) => entry.type !== 'dir').length, [files]);
  const totalFileBytes = useMemo(() => files.filter((entry) => entry.type !== 'dir').reduce((sum, entry) => sum + Number(entry.size || 0), 0), [files]);

  const renderThumb = (entry, sizeClass = 'h-8 w-8') => {
    if (entry.type === 'dir') {
      return (
        <div className={`${sizeClass} rounded bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-700`}>
          <Icon.Folder className="w-4 h-4" />
        </div>
      );
    }

    const kind = getEntryKind(entry);
    if (kind === 'video') {
      return (
        <div className={`${sizeClass} rounded border bg-gray-100 text-gray-700 text-[10px] font-semibold flex items-center justify-center`}>
          VIDEO
        </div>
      );
    }

    if (kind === 'image') {
      const src = resolveMediaUrl(entry.url);
      return (
        <img
          src={src}
          alt={entry.name}
          className={`${sizeClass} rounded object-cover bg-gray-100`}
          loading="lazy"
          decoding="async"
          onError={(event) => {
            event.currentTarget.style.visibility = 'hidden';
          }}
        />
      );
    }

    if (kind === 'audio') {
      return <div className={`${sizeClass} rounded border bg-purple-50 text-purple-700 text-[10px] font-semibold flex items-center justify-center`}>AUDIO</div>;
    }

    if (kind === 'document') {
      return <div className={`${sizeClass} rounded border bg-blue-50 text-blue-700 text-[10px] font-semibold flex items-center justify-center`}>DOC</div>;
    }

    if (kind === 'model') {
      return <div className={`${sizeClass} rounded border bg-gray-100 text-gray-700 text-[10px] font-semibold flex items-center justify-center`}>3D</div>;
    }

    return <div className={`${sizeClass} rounded border bg-gray-100 text-gray-500 text-[10px] font-semibold flex items-center justify-center`}>FILE</div>;
  };

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-gray-200 bg-white px-3 py-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
              <Icon.Folder className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <h1 className="text-lg font-bold text-gray-900">Media Library</h1>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">{fileCount} files</span>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">{folderCount} folders</span>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">{formatFileSize(totalFileBytes)}</span>
              </div>
              <p className="truncate text-xs text-gray-500">Files, folders, sharing, copy/move, and image optimization in one workspace.</p>
          </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {[
              { label: 'Settings', icon: Icon.Settings, action: () => setShowLibrarySettings(true) },
              { label: 'New folder', icon: Icon.Folder, action: openNewFolderDialog }
            ].map((item) => {
              const ActionIcon = item.icon;
              return (
                <button key={item.label} type="button" onClick={item.action} title={item.label} aria-label={item.label} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 transition hover:bg-gray-100">
                  <ActionIcon className="h-5 w-5" />
                </button>
              );
            })}
            <button type="button" onClick={emptyTrash} title="Empty trash" aria-label="Empty trash" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 bg-white text-red-600 transition hover:bg-red-50">
              <Icon.Trash className="h-5 w-5" />
            </button>
            <button type="button" onClick={() => fileInputRef.current?.click?.()} disabled={uploading} className="inline-flex h-9 items-center gap-2 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:bg-blue-400">
              <Icon.Upload className="h-4 w-4" />
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
            <input ref={fileInputRef} type="file" className="hidden" multiple accept="*/*" onChange={(event) => uploadFiles(event.target.files)} disabled={uploading} />
          </div>
        </div>
        <div className="space-y-2 border-b border-gray-200 bg-gray-50/70 px-3 py-2">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <button
              type="button"
              onClick={() => {
                setCurrentPath('');
                setSelectedPaths([]);
              }}
              className={`px-2 py-1 rounded ${currentPath ? 'text-blue-700 hover:bg-blue-50' : 'bg-blue-50 text-blue-700 font-semibold'}`}
            >
              Uploads
            </button>
            {breadcrumbs.map((crumb) => (
              <React.Fragment key={crumb.path}>
                <span className="text-gray-400">/</span>
                <button
                  type="button"
                  onClick={() => {
                    setCurrentPath(crumb.path);
                    setSelectedPaths([]);
                  }}
                  className={`px-2 py-1 rounded ${crumb.path === currentPath ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-blue-700 hover:bg-blue-50'}`}
                >
                  {crumb.name}
                </button>
              </React.Fragment>
            ))}
            <button
              type="button"
              onClick={() => {
                if (!currentPath) return;
                const upPath = currentPath.split('/').slice(0, -1).join('/');
                setCurrentPath(upPath);
                setSelectedPaths([]);
              }}
              disabled={!currentPath}
              className="ml-auto px-3 py-1.5 rounded border border-gray-300 text-xs sm:text-sm hover:bg-gray-50 disabled:opacity-50"
            >
              Up
            </button>
          </div>

          <div className="grid grid-cols-1 gap-2 xl:grid-cols-[minmax(220px,1fr)_160px_150px_auto] xl:items-center">
            <label className="flex h-9 min-w-0 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
              <Icon.Search className="h-4 w-4 shrink-0 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or path..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm outline-none"
              />
            </label>
            <select
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
              className="h-9 rounded-lg border border-gray-300 bg-white px-2 text-sm"
            >
              <option value="all">All Types</option>
              <option value="folders">Folders</option>
              <option value="image">Images</option>
              <option value="video">Videos</option>
              <option value="audio">Audio</option>
              <option value="model">3D Models</option>
              <option value="document">Documents</option>
              <option value="other">Other Files</option>
            </select>
            <select
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value)}
              className="h-9 rounded-lg border border-gray-300 bg-white px-2 text-sm"
            >
              <option value="name_asc">Name A-Z</option>
              <option value="name_desc">Name Z-A</option>
              <option value="recent">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="size_desc">Largest</option>
              <option value="size_asc">Smallest</option>
            </select>
            <div className="inline-flex h-9 overflow-hidden rounded-lg border border-gray-300 bg-white">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`px-3 text-sm font-medium ${viewMode === 'list' ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                List
              </button>
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`px-3 text-sm font-medium ${viewMode === 'grid' ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                Grid
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-600">
            <span><strong className="text-gray-900">Folder:</strong> {currentPath || '/'}</span>
            <span><strong className="text-gray-900">Shown:</strong> {folderCount} folder(s), {fileCount} file(s)</span>
            <span><strong className="text-gray-900">Total:</strong> {totalEntries}</span>
            <span><strong className="text-gray-900">Size:</strong> {formatFileSize(totalFileBytes)}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_300px]">
          <div
            className={`min-h-[420px] border-r border-gray-200 p-3 ${dragActive ? 'bg-blue-50' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {uploading && (
              <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
                <div className="flex items-center justify-between text-xs text-blue-900 mb-1">
                  <span>Uploading files</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-blue-100 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-center py-16">
                <Icon.Loading className="w-7 h-7 mx-auto text-blue-600" />
                <p className="text-gray-600 mt-2">Loading media...</p>
              </div>
            ) : visibleEntries.length === 0 ? (
              <div className="text-center py-16 text-gray-500 border-2 border-dashed rounded-lg">
                <p className="font-medium">No items found</p>
                <p className="text-sm mt-1">{searchTerm ? 'Try another search or filter.' : 'Upload files or create a folder to get started.'}</p>
              </div>
            ) : viewMode === 'list' ? (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="w-10 px-3 py-2 text-left"></th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Name</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Type</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-700">Size</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Modified</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {visibleEntries.map((entry) => {
                      const pathValue = getEntryPath(entry);
                      const selected = selectedPaths.includes(pathValue);
                      const kind = getEntryKind(entry);
                      return (
                        <tr
                          key={pathValue}
                          onClick={(event) => handleEntryClick(entry, event)}
                          onDoubleClick={() => openEntry(entry)}
                          className={`cursor-pointer ${selected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                          title={entry.type === 'dir' ? 'Double click to open folder' : 'Double click to preview file'}
                        >
                          <td className="px-3 py-2" onClick={(event) => event.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={(event) => toggleSelection(pathValue, event.target.checked)}
                              className="rounded border-gray-300"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2 min-w-[240px]">
                              {renderThumb(entry)}
                              <div className="min-w-0">
                                <div className="font-medium text-gray-900 truncate">{entry.name}</div>
                                <div className="text-xs text-gray-500 truncate">{pathValue}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-gray-700 capitalize">{entry.type === 'dir' ? 'Folder' : kind}</td>
                          <td className="px-3 py-2 text-right text-gray-700">{entry.type === 'dir' ? '-' : formatFileSize(entry.size)}</td>
                          <td className="px-3 py-2 text-gray-700">{formatDate(entry.modified_at || entry.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-3">
                {visibleEntries.map((entry) => {
                  const pathValue = getEntryPath(entry);
                  const selected = selectedPaths.includes(pathValue);
                  const kind = getEntryKind(entry);
                  return (
                    <div
                      key={pathValue}
                      onClick={(event) => handleEntryClick(entry, event)}
                      onDoubleClick={() => openEntry(entry)}
                      className={`border rounded-lg p-2 cursor-pointer transition ${
                        selected ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50' : 'border-gray-200 hover:shadow-sm'
                      }`}
                      title={entry.type === 'dir' ? 'Double click to open folder' : 'Double click to preview file'}
                    >
                      <div className="h-28 rounded bg-gray-50 flex items-center justify-center overflow-hidden">
                        {entry.type === 'dir' ? (
                          <Icon.Folder className="w-10 h-10 text-amber-500" />
                        ) : (
                          renderThumb(entry, 'h-full w-full')
                        )}
                      </div>
                      <div className="pt-2">
                        <div className="font-medium text-sm text-gray-900 truncate">{entry.name}</div>
                        <div className="text-xs text-gray-500 capitalize">{entry.type === 'dir' ? 'Folder' : kind}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {hasMore && (
              <div className="pt-3 text-center">
                <button
                  type="button"
                  onClick={() => load({ reset: false })}
                  disabled={loadingMore}
                  className="px-3 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  {loadingMore ? 'Loading more...' : `Load more (${visibleEntries.length}/${totalEntries})`}
                </button>
              </div>
            )}
          </div>

          <aside className="space-y-3 border-t bg-gray-50/70 p-3 xl:border-l xl:border-t-0">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-3 py-2">
                <div className="text-sm font-semibold text-gray-900">Selection</div>
                <div className="text-xs text-gray-500">{selectedEntries.length} selected</div>
              </div>
              <div className="p-3">

              {selectedEntries.length === 0 ? (
                <div className="text-sm text-gray-500">Select an item. Double click opens folders or previews files.</div>
              ) : (
                <div className="space-y-3">
                  {singleSelectedEntry ? (
                    <div className="rounded border border-gray-200 bg-gray-50 p-2">
                      <div className="flex items-center gap-2 mb-2">
                        {renderThumb(singleSelectedEntry)}
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{singleSelectedEntry.name}</div>
                          <div className="text-xs text-gray-500 truncate">{singleSelectedPath}</div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 space-y-1">
                        <div>Type: {singleSelectedEntry.type === 'dir' ? 'folder' : singleSelectedKind}</div>
                        <div>Size: {singleSelectedEntry.type === 'dir' ? '-' : formatFileSize(singleSelectedEntry.size)}</div>
                        <div>Modified: {formatDate(singleSelectedEntry.modified_at || singleSelectedEntry.created_at)}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-600">Multi-select is active. Bulk delete is available.</div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'Open', icon: Icon.Eye, onClick: () => singleSelectedEntry && openEntry(singleSelectedEntry), disabled: !singleSelectedEntry },
                      { label: 'Copy link', icon: Icon.Link, onClick: () => singleSelectedEntry?.url && copyUrl(singleSelectedEntry.url), disabled: !singleSelectedEntry || singleSelectedEntry.type === 'dir' },
                      { label: 'Share', icon: Icon.Share, onClick: shareSelection, disabled: !singleSelectedEntry || singleSelectedEntry.type === 'dir' },
                      { label: 'Move / Cut', icon: Icon.Scissors, onClick: openRenameDialog, disabled: !singleSelectedEntry },
                      { label: 'Copy file', icon: Icon.Copy, onClick: openCopyDialog, disabled: !singleSelectedEntry || singleSelectedEntry.type === 'dir' },
                      { label: 'Edit image', icon: Icon.Crop, onClick: openOptimizeDialog, disabled: !canOptimizeSelection }
                    ].map((action) => {
                      const ActionIcon = action.icon;
                      return (
                        <button key={action.label} type="button" onClick={action.onClick} disabled={action.disabled} className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                          <ActionIcon className="h-4 w-4" />
                          {action.label}
                        </button>
                      );
                    })}
                    <button type="button" onClick={deleteSelection} className="col-span-2 inline-flex items-center justify-center gap-2 rounded-lg border border-red-300 bg-red-50 px-2 py-2 text-sm font-semibold text-red-700 hover:bg-red-100">
                      <Icon.Trash className="h-4 w-4" />
                      Delete selected
                    </button>
                  </div>
                </div>
              )}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {previewItem && (
        <MediaPreviewModal item={previewItem} onClose={() => setPreviewItem(null)} />
      )}

      {showLibrarySettings && (
        <div className="fixed inset-0 z-[240] bg-black/45 flex items-center justify-center p-4" onClick={() => setShowLibrarySettings(false)}>
          <div className="w-full max-w-xl bg-white rounded-xl shadow-xl border border-gray-200" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Media Library Settings</h3>
              <button
                type="button"
                onClick={() => setShowLibrarySettings(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon.X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                <div className="text-sm font-semibold text-gray-900 mb-3">Upload Defaults</div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duplicate handling</label>
                    <select
                      value={uploadDedupeMode}
                      onChange={(event) => setUploadDedupeMode(event.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                    >
                      <option value="reuse">Reuse identical files (recommended)</option>
                      <option value="allow">Allow duplicates</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Auto optimize on upload</label>
                    <select
                      value={uploadOptimizeMode}
                      onChange={(event) => setUploadOptimizeMode(event.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                    >
                      <option value="off">Off (original only)</option>
                      <option value="auto">Automatic (recommended)</option>
                      <option value="aggressive">Smaller WebP variants</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                <div className="text-sm font-semibold text-gray-900 mb-3">Library Preferences</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Default view</label>
                    <select
                      value={viewMode}
                      onChange={(event) => setViewMode(event.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                    >
                      <option value="list">List</option>
                      <option value="grid">Grid</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Default sort</label>
                    <select
                      value={sortBy}
                      onChange={(event) => setSortBy(event.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                    >
                      <option value="name_asc">Name A-Z</option>
                      <option value="name_desc">Name Z-A</option>
                      <option value="recent">Newest</option>
                      <option value="oldest">Oldest</option>
                      <option value="size_desc">Largest</option>
                      <option value="size_asc">Smallest</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Original files are preserved by default. Dedupe avoids storing identical copies.
                </p>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                onClick={() => setShowLibrarySettings(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {dialog && (
        <div className="fixed inset-0 z-[250] bg-black/50 flex items-center justify-center p-4" onClick={closeDialog}>
          <form
            onSubmit={submitDialog}
            className={dialog.type === 'optimize' ? 'flex max-h-[calc(100vh-2rem)] w-full max-w-7xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl' : 'w-full max-w-lg bg-white rounded-xl shadow-xl border border-gray-200'}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {dialog.type === 'mkdir' && 'Create Folder'}
                {dialog.type === 'rename' && 'Rename / Move'}
                {dialog.type === 'copy' && 'Duplicate File'}
                {dialog.type === 'optimize' && 'Edit Image'}
              </h3>
              {dialog.type === 'optimize' ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={closeDialog}
                    disabled={dialogBusy}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={dialogBusy}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-blue-400"
                  >
                    {dialogBusy && <Icon.Loading className="h-4 w-4" />}
                    Apply
                  </button>
                </div>
              ) : (
                <button type="button" onClick={closeDialog} disabled={dialogBusy} className="text-gray-400 hover:text-gray-600">
                  <Icon.X className="w-5 h-5" />
                </button>
              )}
            </div>

            <div className={dialog.type === "optimize" ? "min-h-0 flex-1 overflow-y-auto p-3" : "p-4 space-y-3"}>
              {(dialog.type === 'mkdir' || dialog.type === 'rename' || dialog.type === 'copy') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {dialog.type === 'mkdir' ? 'Folder path (relative to uploads)' : 'Target path (relative to uploads)'}
                  </label>
                  <input
                    type="text"
                    value={dialog.value || ''}
                    onChange={(event) => setDialog((prev) => ({ ...prev, value: event.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="products/new-folder"
                    autoFocus
                  />
                  {dialog.type !== 'mkdir' && (
                    <div className="text-xs text-gray-500 mt-1">From: {dialog.from}</div>
                  )}
                </div>
              )}

              {dialog.type === 'optimize' && (() => {
                const cropAspect = dialog.cropMode === 'square'
                  ? 1
                  : dialog.cropMode === 'portrait'
                    ? 4 / 5
                    : dialog.cropMode === 'landscape'
                      ? 16 / 9
                      : undefined;
                const isCropActive = dialog.cropMode !== 'inside';
                const cropModes = [
                  ['inside', 'Fit'],
                  ['free', 'Free'],
                  ['square', '1:1'],
                  ['portrait', '4:5'],
                  ['landscape', '16:9']
                ];
                return (
                  <div className="grid min-h-0 gap-3 xl:grid-cols-[minmax(0,1fr)_340px]">
                    <div className="min-w-0 rounded-xl border border-gray-200 bg-gray-950 p-2">
                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-gray-300">
                        <span className="truncate">{dialog.path}</span>
                        {isCropActive && dialog.cropPixels && <span>{Math.round(dialog.cropPixels.width)} x {Math.round(dialog.cropPixels.height)}px</span>}
                      </div>
                      {dialog.url && (isCropActive ? (
                        <ReactCrop
                          crop={dialog.crop}
                          aspect={cropAspect}
                          keepSelection
                          ruleOfThirds
                          minWidth={24}
                          minHeight={24}
                          onChange={(pixelCrop, percentCrop) => setDialog(prev => ({ ...prev, crop: percentCrop }))}
                          onComplete={(pixelCrop) => setDialog(prev => ({ ...prev, cropPixels: pixelCrop }))}
                          className="mx-auto flex max-h-[calc(100vh-13rem)] max-w-full justify-center"
                        >
                          <img src={resolveMediaUrl(dialog.url)} alt="Crop source" className="mx-auto block max-h-[calc(100vh-14rem)] max-w-full object-contain" />
                        </ReactCrop>
                      ) : (
                        <img src={resolveMediaUrl(dialog.url)} alt="Preview" className="mx-auto block max-h-[calc(100vh-14rem)] max-w-full object-contain" />
                      ))}
                    </div>

                    <aside className="space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Crop</div>
                        <div className="mt-2 grid grid-cols-5 gap-1">
                          {cropModes.map(([mode, label]) => (
                            <button
                              key={mode}
                              type="button"
                              onClick={() => setDialog((prev) => ({ ...prev, cropMode: mode, crop: { unit: '%', x: 10, y: 10, width: 80, height: 80 }, cropPixels: null }))}
                              className={`rounded-lg border px-2 py-1.5 text-xs font-semibold ${dialog.cropMode === mode ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'}`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        <p className="mt-2 text-xs text-gray-500">Drag the crop box on the image. Ratio buttons lock the crop like a normal editor.</p>
                      </div>

                      {isCropActive && dialog.cropPixels && (
                        <dl className="grid grid-cols-4 gap-1 rounded-lg bg-white p-2 text-[11px]">
                          <div><dt className="text-gray-400">X</dt><dd className="font-semibold text-gray-800">{Math.round(dialog.cropPixels.x)}</dd></div>
                          <div><dt className="text-gray-400">Y</dt><dd className="font-semibold text-gray-800">{Math.round(dialog.cropPixels.y)}</dd></div>
                          <div><dt className="text-gray-400">W</dt><dd className="font-semibold text-gray-800">{Math.round(dialog.cropPixels.width)}</dd></div>
                          <div><dt className="text-gray-400">H</dt><dd className="font-semibold text-gray-800">{Math.round(dialog.cropPixels.height)}</dd></div>
                        </dl>
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        <label className="text-xs font-medium text-gray-600">Max Width<input type="number" min={320} max={4096} value={dialog.maxWidth ?? 1920} onChange={(event) => setDialog((prev) => ({ ...prev, maxWidth: event.target.value }))} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm" /></label>
                        <label className="text-xs font-medium text-gray-600">Quality<input type="number" min={45} max={96} value={dialog.quality ?? 84} onChange={(event) => setDialog((prev) => ({ ...prev, quality: event.target.value }))} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm" /></label>
                      </div>

                      <label className="text-xs font-medium text-gray-600">Format<select value={dialog.format || 'keep'} onChange={(event) => setDialog((prev) => ({ ...prev, format: event.target.value }))} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm"><option value="keep">Keep existing format</option><option value="webp">WebP</option><option value="jpeg">JPEG</option><option value="png">PNG</option><option value="avif">AVIF</option></select></label>

                      <div className="space-y-2 rounded-lg bg-white p-2">
                        <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={!!dialog.onlyIfSmaller} onChange={(event) => setDialog((prev) => ({ ...prev, onlyIfSmaller: event.target.checked }))} className="rounded border-gray-300" />Save only if smaller</label>
                        <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={!!dialog.replace} onChange={(event) => setDialog((prev) => ({ ...prev, replace: event.target.checked }))} className="rounded border-gray-300" />Replace original file</label>
                      </div>
                    </aside>
                  </div>
                );
              })()}
            </div>

            {dialog.type !== 'optimize' && (
              <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closeDialog}
                  disabled={dialogBusy}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={dialogBusy}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 inline-flex items-center gap-2"
                >
                  {dialogBusy && <Icon.Loading className="w-4 h-4" />}
                  {dialog.type === 'mkdir' && 'Create'}
                  {dialog.type === 'rename' && 'Save'}
                  {dialog.type === 'copy' && 'Copy'}
                </button>
              </div>
            )}
          </form>
        </div>
      )}
    </div>
  );
}


function DesignSection({ title, description, preview, actions, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h4 className="text-base font-semibold text-gray-900">{title}</h4>
          {description && <p className="mt-1 text-xs text-gray-500">{description}</p>}
        </div>
        {(preview || actions) && <div className="flex shrink-0 items-center gap-2">{preview}{actions}</div>}
      </div>
      {children}
    </section>
  );
}

function DesignGrid({ children, className = '' }) {
  return <div className={`grid grid-cols-1 gap-2 sm:grid-cols-[repeat(auto-fit,minmax(160px,1fr))] ${className}`}>{children}</div>;
}

function DesignField({ label, valueLabel, help, children }) {
  return (
    <label className="block text-sm font-medium text-gray-700">
      <span className="flex min-h-5 items-center justify-between gap-2">
        <span>{label}</span>
        {valueLabel !== undefined && valueLabel !== null && <span className="text-xs font-medium text-gray-400">{valueLabel}</span>}
      </span>
      <div className="mt-1">{children}</div>
      {help && <p className="mt-1 text-xs font-normal text-gray-500">{help}</p>}
    </label>
  );
}

function PersistentColorInput({ value, onChange, fallback = '#000000', className = '', ...props }) {
  const normalizedValue = normalizeCssColorToHex(value) || fallback;
  const [draftValue, setDraftValue] = React.useState(normalizedValue);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (document.activeElement !== inputRef.current) setDraftValue(normalizedValue);
  }, [normalizedValue]);

  const commitValue = React.useCallback((nextValue) => {
    const normalizedNext = normalizeCssColorToHex(nextValue) || fallback;
    setDraftValue(normalizedNext);
    if (normalizedNext !== normalizedValue) onChange(normalizedNext);
  }, [fallback, normalizedValue, onChange]);

  return (
    <input
      {...props}
      ref={inputRef}
      type="color"
      value={draftValue}
      onInput={(event) => setDraftValue(event.currentTarget.value)}
      onBlur={(event) => commitValue(event.currentTarget.value)}
      className={`admin-color-input ${className}`}
    />
  );
}

function ColorControl({ label, value, onChange, fallback = '#000000', help }) {
  const safeValue = normalizeCssColorToHex(value) || fallback;
  return (
    <DesignField label={label} valueLabel={safeValue} help={help}>
      <div className="flex items-center gap-1.5">
        <PersistentColorInput value={safeValue} onChange={onChange} fallback={fallback} className="h-8 w-9 shrink-0 rounded-md border border-gray-300 p-0" />
        <input type="text" value={value || ''} onChange={(event) => onChange(event.target.value)} placeholder={fallback} className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm" />
      </div>
    </DesignField>
  );
}

function TextControl({ label, value, onChange, placeholder = '', help }) {
  return (
    <DesignField label={label} help={help}>
      <input type="text" value={value || ''} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm" />
    </DesignField>
  );
}

function RangeControl({ label, value, onChange, min = 0, max = 100, step = 1, unit = '', help }) {
  const numericValue = Number(value ?? min) || 0;
  return (
    <DesignField label={label} valueLabel={`${numericValue}${unit}`} help={help}>
      <input type="range" min={min} max={max} step={step} value={numericValue} onChange={(event) => onChange(Number(event.target.value))} className="w-full" />
    </DesignField>
  );
}

function ToggleControl({ label, checked, onChange, help }) {
  return (
    <label className="flex min-h-9 items-start gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm text-gray-700">
      <input type="checkbox" checked={!!checked} onChange={(event) => onChange(event.target.checked)} className="mt-0.5 rounded border-gray-300" />
      <span className="min-w-0">
        <span className="block font-semibold text-gray-900">{label}</span>
        {help && <span className="mt-0.5 block text-xs leading-snug text-gray-500">{help}</span>}
      </span>
    </label>
  );
}

function SelectControl({ label, value, onChange, options, help }) {
  return (
    <DesignField label={label} help={help}>
      <select value={value} onChange={(event) => onChange(event.target.value)} className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm">
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </DesignField>
  );
}

function ControlGroup({ title, description, enabled, onEnabledChange, children, className = '' }) {
  return (
    <div className={`min-w-0 rounded-lg border border-gray-100 bg-gray-50/80 p-2 ${className}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <div className="min-w-0 flex-1">
          <div className="inline-flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900">
            <span>{title}</span>
            {typeof enabled === 'boolean' && (
              <label className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600">
                <input type="checkbox" checked={enabled} onChange={(event) => onEnabledChange?.(event.target.checked)} className="rounded border-gray-300" />
                Enabled
              </label>
            )}
          </div>
          {description && <p className="mt-0.5 text-[11px] leading-snug text-gray-500">{description}</p>}
        </div>
      </div>
      {(enabled === undefined || enabled) && <div className="mt-2">{children}</div>}
    </div>
  );
}
function SettingsManager({ notificationSoundPreference = DEFAULT_ADMIN_NOTIFICATION_SOUND, onNotificationSoundPreferenceChange, notificationSoundUrl = '', onNotificationSoundUrlChange, onPreviewNotificationSound, onFaviconUrlChange, onSaveControlChange }) {
  const [settings, setSettings] = useState({
    siteName: '',
    siteTitle: DEFAULT_SITE_TITLE,
    tagline: DEFAULT_SITE_TAGLINE,
    currency: '৳',
    facebookUrl: '',
    messengerUrl: '',
    whatsappUrl: '',
    instagramUrl: '',
    supportEmail: '',
    helpFaqs: DEFAULT_HELP_FAQS,
    homeHeroDescription: DEFAULT_HOME_HERO_DESCRIPTION,
    homeHeroDisplayMode: 'standard',
    homeHeroIntervalSeconds: 3.6,
    homeHeroManualPauseSeconds: 10,
    homepageHeaderMode: 'flush',
    homepageHeaderTopShadowColor: '#111827',
    homepageHeaderTopShadowOpacity: 0.2,
    homepageHeaderTopShadowBlur: 18,
    homepageHeaderTopShadowSpread: 0,
    homepageHeaderTopShadowRadius: 32,
    shippingRates: { inside: 60, outside: 120 },
    bkashNumber: '',
    bkashQrUrl: '',
    bkashCashoutEnabled: false,
    bkashCashoutAmount: 20,
    orderIdPrefix: 'PV',
    orderIdSuffix: '',
    enableCampaignPopup: true,
    campaignPopupDelaySeconds: 3,
    notificationSoundUrl: '',
    logoUrl: '/logo.svg',
    logoAccentUrl: '',
    headerLogoFrameEnabled: false,
    headerLogoBackgroundColor: 'transparent',
    headerLogoBorderEnabled: false,
    headerLogoBorderColor: '#ffffff',
    headerLogoShadowEnabled: false,
    headerLogoShadowColor: '#111827',
    headerLogoShadowOpacity: 0.16,
    headerLogoShadowBlur: 18,
    headerLogoCornerRadius: 16,
    titleSvgUrl: '',
    headerTitleSvgColorizeEnabled: false,
    headerTitleFontFamily: '',
    headerTitleFontUrl: '',
    headerTitleFillColor: '#000000',
    headerTitleStrokeEnabled: true,
    headerTitleStrokeColor: '#ffffff',
    headerTitleStrokeWidth: 3,
    headerTitleShadowEnabled: false,
    headerTitleShadowColor: '#ffffff',
    headerTitleShadowX: 0,
    headerTitleShadowY: 0,
    headerTitleShadowBlur: 0,
    headerTitleShadowOpacity: 0,
    headerTitleGradientEnabled: true,
    headerTitleGradientColors: ['#ffffff', '#f9a8d4', '#bbf7d0', '#d8b4fe'],
    headerTitleGradientPattern: 'linear',
    headerTitleGradientDuration: 5.5,
    headerTitleGradientAngle: 135,
    headerTitleGradientOffsetEnabled: true,
    headerTitleGradientStagger: 0.12,
    faviconUrl: '',
    shareImageUrl: '',
    metaPixelId: DEFAULT_META_PIXEL_ID,
    accentColor: '#d3763b',
    secondaryAccentColor: DEFAULT_SECONDARY_ACCENT_COLOR,
    heroBanners: [],
    homepageBanners: [],
    homepageRails: DEFAULT_HOME_RAILS,
    homepageShortcutsEnabled: true,
    homepageShortcutsLayout: 'row',
    homepageShortcuts: DEFAULT_HOME_SHORTCUTS,
    homepageClosingCtaEnabled: true,
    homepageClosingCtaEyebrow: 'Ready to order',
    homepageClosingCtaTitle: 'Add in-stock products to cart or message for pre-purchases.',
    homepageClosingCtaDescription: 'Checkout stays available for live inventory, while zero-stock items route customers to Messenger.',
    homepageClosingCtaPrimaryLabel: 'Track an order',
    homepageClosingCtaPrimaryTarget: 'track',
    homepageClosingCtaSecondaryLabel: 'Contact support',
    homepageClosingCtaSecondaryTarget: 'help',
    deliveryEstimateInStock: '2-3 days',
    deliveryEstimatePreorder: '3-4 weeks',
    priceBreakdownInStock: 'This price covers the item cost, handling, warehouse processing, and international shipping. Delivery fees and promo adjustments (if any) are calculated at checkout.',
    priceBreakdownPreorder: 'Pre-order pricing is indicative. Final availability, delivery timing, and any required advance payment are confirmed in Messenger.',
    demoMode: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [brandingSubTab, setBrandingSubTab] = useState('identity');
  const [openHeroStyleControls, setOpenHeroStyleControls] = useState({});
  const [restartingServices, setRestartingServices] = useState(false);
  const [restartConfirmText, setRestartConfirmText] = useState('');
  
  const { request } = useApi();
  const { addToast } = useToasts();
  const { user } = useAuth();
  const [shortcutCategories, setShortcutCategories] = useState([]);

  useEffect(() => {
    let mounted = true;
    request('/products')
      .then((data) => {
        if (!mounted) return;
        const categories = Array.from(new Set((Array.isArray(data) ? data : [])
          .map((product) => String(product?.category || '').trim())
          .filter(Boolean)))
          .sort((a, b) => a.localeCompare(b));
        setShortcutCategories(categories);
      })
      .catch(() => { if (mounted) setShortcutCategories([]); });
    return () => { mounted = false; };
  }, [request]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const id = 'peyada-admin-header-title-font';
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const rawUrl = String(settings?.headerTitleFontUrl || '').trim();
    if (!rawUrl) return undefined;
    const url = resolveMediaUrl(rawUrl).replace(/\\/g, '/').replace(/"/g, '\\"');
    const ext = url.split('?')[0].split('#')[0].split('.').pop()?.toLowerCase();
    const format = ext === 'woff2' ? 'woff2' : ext === 'woff' ? 'woff' : ext === 'ttf' ? 'truetype' : ext === 'otf' ? 'opentype' : 'woff2';
    const styleEl = document.createElement('style');
    styleEl.id = id;
    styleEl.textContent = '@font-face{font-family:"PeyadaAdminHeaderTitleCustom";src:url("' + url + '") format("' + format + '");font-display:swap;}';
    document.head.appendChild(styleEl);
    return () => { if (styleEl.isConnected) styleEl.remove(); };
  }, [settings?.headerTitleFontUrl]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await request('/settings');
        const shippingInside = Number(data?.shippingRates?.inside ?? 60);
        const shippingOutside = Number(data?.shippingRates?.outside ?? 120);
        setSettings({
          siteName: data.siteName || '',
          siteTitle: typeof data.siteTitle === 'string' && data.siteTitle.trim().length > 0 ? data.siteTitle : DEFAULT_SITE_TITLE,
          tagline: typeof data.tagline === 'string' ? data.tagline : DEFAULT_SITE_TAGLINE,
          currency: data.currency || '৳',
          facebookUrl: data.facebookUrl || '',
          messengerUrl: data.messengerUrl || '',
          whatsappUrl: data.whatsappUrl || '',
          instagramUrl: data.instagramUrl || '',
          supportEmail: data.supportEmail || '',
          helpFaqs: normalizeHelpFaqs(data.helpFaqs),
          homeHeroDescription: typeof data.homeHeroDescription === 'string' ? data.homeHeroDescription : DEFAULT_HOME_HERO_DESCRIPTION,
          homeHeroDisplayMode: ['immersive', 'standard'].includes(String(data.homeHeroDisplayMode || '')) ? String(data.homeHeroDisplayMode) : 'standard',
          homeHeroIntervalSeconds: Math.max(2.5, Math.min(30, Number(data.homeHeroIntervalSeconds ?? 3.6) || 3.6)),
          homeHeroManualPauseSeconds: Math.max(3, Math.min(90, Number(data.homeHeroManualPauseSeconds ?? 10) || 10)),
          homepageHeaderMode: ['floating', 'flush'].includes(String(data.homepageHeaderMode || '')) ? String(data.homepageHeaderMode) : 'flush',
          homepageHeaderTopShadowColor: normalizeCssColorToHex(data.homepageHeaderTopShadowColor) || '#111827',
          homepageHeaderTopShadowOpacity: Math.max(0, Math.min(1, Number(data.homepageHeaderTopShadowOpacity ?? 0.2) || 0)),
          homepageHeaderTopShadowBlur: Math.max(0, Math.min(80, Number(data.homepageHeaderTopShadowBlur ?? 18) || 0)),
          homepageHeaderTopShadowSpread: Math.max(-20, Math.min(40, Number(data.homepageHeaderTopShadowSpread ?? 0) || 0)),
          homepageHeaderTopShadowRadius: Math.max(0, Math.min(80, Number(data.homepageHeaderTopShadowRadius ?? 32) || 0)),
          shippingRates: { inside: shippingInside, outside: shippingOutside },
          bkashNumber: data.bkashNumber || '',
          bkashQrUrl: data.bkashQrUrl || '',
          bkashCashoutEnabled: data.bkashCashoutEnabled ?? false,
          bkashCashoutAmount: Math.max(0, Number(data.bkashCashoutAmount ?? 20) || 20),
          orderIdPrefix: data.orderIdPrefix || 'PV',
          orderIdSuffix: data.orderIdSuffix || '',
          enableCampaignPopup: data.enableCampaignPopup ?? true,
          campaignPopupDelaySeconds: typeof data.campaignPopupDelaySeconds === 'number' ? data.campaignPopupDelaySeconds : 3,
          notificationSoundUrl: data.notificationSoundUrl || '',
          logoUrl: data.logoUrl || '/logo.svg',
          logoAccentUrl: data.logoAccentUrl || '',
          headerLogoFrameEnabled: data.headerLogoFrameEnabled === true,
          headerLogoBackgroundColor: data.headerLogoBackgroundColor || 'transparent',
          headerLogoBorderEnabled: data.headerLogoBorderEnabled === true,
          headerLogoBorderColor: normalizeCssColorToHex(data.headerLogoBorderColor) || '#ffffff',
          headerLogoShadowEnabled: data.headerLogoShadowEnabled === true,
          headerLogoShadowColor: normalizeCssColorToHex(data.headerLogoShadowColor) || '#111827',
          headerLogoShadowOpacity: Math.max(0, Math.min(1, Number(data.headerLogoShadowOpacity ?? 0.16) || 0)),
          headerLogoShadowBlur: Math.max(0, Math.min(60, Number(data.headerLogoShadowBlur ?? 18) || 0)),
          headerLogoCornerRadius: Math.max(0, Math.min(28, Number(data.headerLogoCornerRadius ?? 16) || 0)),
          titleSvgUrl: data.titleSvgUrl || '',
          headerTitleSvgColorizeEnabled: data.headerTitleSvgColorizeEnabled === true,
          headerTitleFontFamily: typeof data.headerTitleFontFamily === 'string' ? data.headerTitleFontFamily : '',
          headerTitleFontUrl: typeof data.headerTitleFontUrl === 'string' ? data.headerTitleFontUrl : '',
          headerTitleFillColor: normalizeCssColorToHex(data.headerTitleFillColor) || '#000000',
          headerTitleStrokeEnabled: data.headerTitleStrokeEnabled !== false,
          headerTitleStrokeColor: normalizeCssColorToHex(data.headerTitleStrokeColor) || '#ffffff',
          headerTitleStrokeWidth: Math.max(0, Math.min(8, Number(data.headerTitleStrokeWidth ?? 3) || 0)),
          headerTitleShadowEnabled: data.headerTitleShadowEnabled === true,
          headerTitleShadowColor: normalizeCssColorToHex(data.headerTitleShadowColor) || '#ffffff',
          headerTitleShadowX: Math.max(-24, Math.min(24, Number(data.headerTitleShadowX ?? 0) || 0)),
          headerTitleShadowY: Math.max(-24, Math.min(24, Number(data.headerTitleShadowY ?? 0) || 0)),
          headerTitleShadowBlur: Math.max(0, Math.min(40, Number(data.headerTitleShadowBlur ?? 0) || 0)),
          headerTitleShadowOpacity: Math.max(0, Math.min(1, Number(data.headerTitleShadowOpacity ?? 0) || 0)),
          headerTitleGradientEnabled: data.headerTitleGradientEnabled !== false,
          headerTitleGradientColors: (Array.isArray(data.headerTitleGradientColors) && data.headerTitleGradientColors.length ? data.headerTitleGradientColors : ['#ffffff', '#f9a8d4', '#bbf7d0', '#d8b4fe']).map(color => normalizeCssColorToHex(color) || '#ffffff').slice(0, 8),
          headerTitleGradientPattern: ['linear', 'radial', 'repeating', 'conic', 'mesh', 'fluid'].includes(String(data.headerTitleGradientPattern || '')) ? String(data.headerTitleGradientPattern) : 'linear',
          headerTitleGradientDuration: Math.max(1, Math.min(30, Number(data.headerTitleGradientDuration ?? 5.5) || 5.5)),
          headerTitleGradientAngle: Math.max(0, Math.min(360, Number(data.headerTitleGradientAngle ?? 135) || 135)),
          headerTitleGradientOffsetEnabled: data.headerTitleGradientOffsetEnabled !== false,
          headerTitleGradientStagger: Math.max(0, Math.min(0.8, Number(data.headerTitleGradientStagger ?? 0.12) || 0.12)),
          faviconUrl: data.faviconUrl || '',
          shareImageUrl: data.shareImageUrl || '',
          metaPixelId: data.metaPixelId || DEFAULT_META_PIXEL_ID,
          accentColor: normalizeCssColorToHex(data.accentColor) || '#d3763b',
          secondaryAccentColor: normalizeCssColorToHex(data.secondaryAccentColor) || DEFAULT_SECONDARY_ACCENT_COLOR,
          heroBanners: normalizeHeroBanners(data.heroBanners),
          homepageBanners: normalizeHomepageBanners(data.homepageBanners),
          homepageRails: normalizeHomepageRails(data.homepageRails),
          homepageShortcutsEnabled: data.homepageShortcutsEnabled !== false,
          homepageShortcutsLayout: ['row', 'grid', 'stack'].includes(String(data.homepageShortcutsLayout || '')) ? String(data.homepageShortcutsLayout) : 'row',
          homepageShortcuts: normalizeHomepageShortcuts(data.homepageShortcuts),
          homepageClosingCtaEnabled: data.homepageClosingCtaEnabled !== false,
          homepageClosingCtaEyebrow: typeof data.homepageClosingCtaEyebrow === 'string' ? data.homepageClosingCtaEyebrow : 'Ready to order',
          homepageClosingCtaTitle: typeof data.homepageClosingCtaTitle === 'string' ? data.homepageClosingCtaTitle : 'Add in-stock products to cart or message for pre-purchases.',
          homepageClosingCtaDescription: typeof data.homepageClosingCtaDescription === 'string' ? data.homepageClosingCtaDescription : 'Checkout stays available for live inventory, while zero-stock items route customers to Messenger.',
          homepageClosingCtaPrimaryLabel: typeof data.homepageClosingCtaPrimaryLabel === 'string' ? data.homepageClosingCtaPrimaryLabel : 'Track an order',
          homepageClosingCtaPrimaryTarget: typeof data.homepageClosingCtaPrimaryTarget === 'string' ? data.homepageClosingCtaPrimaryTarget : 'track',
          homepageClosingCtaSecondaryLabel: typeof data.homepageClosingCtaSecondaryLabel === 'string' ? data.homepageClosingCtaSecondaryLabel : 'Contact support',
          homepageClosingCtaSecondaryTarget: typeof data.homepageClosingCtaSecondaryTarget === 'string' ? data.homepageClosingCtaSecondaryTarget : 'help',
          deliveryEstimateInStock: typeof data.deliveryEstimateInStock === 'string' ? data.deliveryEstimateInStock : '2-3 days',
          deliveryEstimatePreorder: typeof data.deliveryEstimatePreorder === 'string' ? data.deliveryEstimatePreorder : '3-4 weeks',
          priceBreakdownInStock: typeof data.priceBreakdownInStock === 'string' ? data.priceBreakdownInStock : 'This price covers the item cost, handling, warehouse processing, and international shipping. Delivery fees and promo adjustments (if any) are calculated at checkout.',
          priceBreakdownPreorder: typeof data.priceBreakdownPreorder === 'string' ? data.priceBreakdownPreorder : 'Pre-order pricing is indicative. Final availability, delivery timing, and any required advance payment are confirmed in Messenger.',
          demoMode: data.demoMode ?? false
        });
      } catch (err) {
        addToast('Failed to load settings', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [request, addToast]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!hasUserPermission(user, 'settings:write')) {
      addToast('You do not have permission to update settings', 'error');
      return;
    }
    setSaving(true);

    try {
      const sanitizedShipping = {
        inside: Number(settings.shippingRates?.inside ?? 60),
        outside: Number(settings.shippingRates?.outside ?? 120)
      };
      const timestamp = Date.now();
      const sanitizedHeroBanners = (settings.heroBanners || []).map((banner, idx) => {
        const rawImg = (banner?.image ?? '').toString().trim();
        const cleanImg = rawImg.replace(/^['"]|['"]$/g, '').trim();
        return {
          id: banner?.id && banner.id.toString().trim().length > 0 ? banner.id : `hero_${timestamp}_${idx}`,
          contentEnabled: banner?.contentEnabled === true,
          headline: banner?.headline?.trim() || '',
          subheadline: banner?.subheadline?.trim() || '',
          image: cleanImg,
          ctaLabel: banner?.ctaLabel?.trim() || '',
          href: banner?.href?.trim() || banner?.imageHref?.trim() || '',
          imageHref: '',
          glowColor: normalizeCssColorToHex(banner?.glowColor) || '',
          titleColor: normalizeCssColorToHex(banner?.titleColor) || '#ffffff',
          descriptionColor: normalizeCssColorToHex(banner?.descriptionColor) || '#ffffff',
          titleGlowColor: normalizeCssColorToHex(banner?.titleGlowColor) || '',
          descriptionGlowColor: normalizeCssColorToHex(banner?.descriptionGlowColor) || '',
          titleGlowBlur: Math.max(0, Math.min(48, Number(banner?.titleGlowBlur ?? banner?.titleGlowAmount ?? 10) || 10)),
          descriptionGlowBlur: Math.max(0, Math.min(48, Number(banner?.descriptionGlowBlur ?? banner?.descriptionGlowAmount ?? 8) || 8)),
          titleGlowSpread: Math.max(0, Math.min(8, Number(banner?.titleGlowSpread ?? 2) || 2)),
          descriptionGlowSpread: Math.max(0, Math.min(8, Number(banner?.descriptionGlowSpread ?? 1.5) || 1.5)),
          titleGlowOpacity: Math.max(0, Math.min(1, Number(banner?.titleGlowOpacity ?? 0.58))),
          descriptionGlowOpacity: Math.max(0, Math.min(1, Number(banner?.descriptionGlowOpacity ?? 0.48))),
          active: banner?.active === false ? false : true
        };
      });
      const sanitizedHomepageBanners = (settings.homepageBanners || []).map((banner, idx) => {
        const rawImg = (banner?.image ?? '').toString().trim();
        const cleanImg = rawImg.replace(/^['"]|['"]$/g, '').trim();
        const pos = (banner?.position || '').toString().trim();
        const allowed = HOMEPAGE_BANNER_POSITIONS;
        const position = allowed.includes(pos) ? pos : 'between_featured';
        return {
          id: banner?.id && banner.id.toString().trim().length > 0 ? banner.id : `home_banner_${timestamp}_${idx}`,
          image: cleanImg,
          href: banner?.href?.trim() || '',
          position,
          paddingTop: Math.max(0, Math.min(64, Number(banner?.paddingTop ?? 8) || 0)),
          paddingBottom: Math.max(0, Math.min(64, Number(banner?.paddingBottom ?? 8) || 0)),
          active: banner?.active === false ? false : true
        };
      });
      const sanitizedHomepageRails = normalizeHomepageRails(settings.homepageRails).map((rail, idx) => ({
        ...rail,
        id: rail.id || `rail_${timestamp}_${idx}`,
        title: String(rail.title || rail.category || 'Products').trim(),
        subtitle: String(rail.subtitle || '').trim(),
        category: String(rail.category || '').trim()
      }));
      const sanitizedHomepageShortcuts = normalizeHomepageShortcuts(settings.homepageShortcuts).map((shortcut, idx) => ({
        ...shortcut,
        id: shortcut.id || `shortcut_${timestamp}_${idx}`,
        label: String(shortcut.label || 'Shortcut').trim() || 'Shortcut',
        targetValue: String(shortcut.targetValue || '').trim(),
        iconImage: String(shortcut.iconImage || '').trim(),
        color: normalizeCssColorToHex(shortcut.color) || '#f9a8d4'
      }));
      const incompleteShortcut = sanitizedHomepageShortcuts.find(shortcut => shortcut.active && !shortcut.targetValue);
      if (incompleteShortcut) {
        throw new Error(`Choose a destination for the shortcut "${incompleteShortcut.label}" before saving.`);
      }
      const payload = {
        ...settings,
        siteTitle: String(settings.siteTitle || '').trim(),
        tagline: String(settings.tagline || '').trim(),
        supportEmail: String(settings.supportEmail || '').trim(),
        messengerUrl: String(settings.messengerUrl || '').trim(),
        whatsappUrl: String(settings.whatsappUrl || '').trim(),
        helpFaqs: normalizeHelpFaqs(settings.helpFaqs)
          .map((item) => ({
            ...item,
            question: String(item.question || '').trim(),
            answer: String(item.answer || '').trim()
          }))
          .filter((item) => item.question && item.answer),
        homeHeroDescription: String(settings.homeHeroDescription || '').trim(),
        homeHeroDisplayMode: ['immersive', 'standard'].includes(String(settings.homeHeroDisplayMode || '')) ? String(settings.homeHeroDisplayMode) : 'standard',
        homeHeroIntervalSeconds: Math.max(2.5, Math.min(30, Number(settings.homeHeroIntervalSeconds ?? 3.6) || 3.6)),
        homeHeroManualPauseSeconds: Math.max(3, Math.min(90, Number(settings.homeHeroManualPauseSeconds ?? 10) || 10)),
        homepageHeaderMode: ['floating', 'flush'].includes(String(settings.homepageHeaderMode || '')) ? String(settings.homepageHeaderMode) : 'flush',
        homepageHeaderTopShadowColor: normalizeCssColorToHex(settings.homepageHeaderTopShadowColor) || '#111827',
        homepageHeaderTopShadowOpacity: Math.max(0, Math.min(1, Number(settings.homepageHeaderTopShadowOpacity ?? 0.2) || 0)),
        homepageHeaderTopShadowBlur: Math.max(0, Math.min(80, Number(settings.homepageHeaderTopShadowBlur ?? 18) || 0)),
        homepageHeaderTopShadowSpread: Math.max(-20, Math.min(40, Number(settings.homepageHeaderTopShadowSpread ?? 0) || 0)),
        homepageHeaderTopShadowRadius: Math.max(0, Math.min(80, Number(settings.homepageHeaderTopShadowRadius ?? 32) || 0)),
        titleSvgUrl: String(settings.titleSvgUrl || '').trim(),
        headerTitleSvgColorizeEnabled: settings.headerTitleSvgColorizeEnabled === true,
        headerLogoFrameEnabled: settings.headerLogoFrameEnabled === true,
        headerLogoBackgroundColor: settings.headerLogoBackgroundColor || 'transparent',
        headerLogoBorderEnabled: settings.headerLogoBorderEnabled === true,
        headerLogoBorderColor: normalizeCssColorToHex(settings.headerLogoBorderColor) || '#ffffff',
        headerLogoShadowEnabled: settings.headerLogoShadowEnabled === true,
        headerLogoShadowColor: normalizeCssColorToHex(settings.headerLogoShadowColor) || '#111827',
        headerLogoShadowOpacity: Math.max(0, Math.min(1, Number(settings.headerLogoShadowOpacity ?? 0.16) || 0)),
        headerLogoShadowBlur: Math.max(0, Math.min(60, Number(settings.headerLogoShadowBlur ?? 18) || 0)),
        headerLogoCornerRadius: Math.max(0, Math.min(28, Number(settings.headerLogoCornerRadius ?? 16) || 0)),
        headerTitleFontFamily: String(settings.headerTitleFontFamily || '').trim(),
        headerTitleFontUrl: String(settings.headerTitleFontUrl || '').trim(),
        headerTitleFillColor: normalizeCssColorToHex(settings.headerTitleFillColor) || '#000000',
        headerTitleStrokeEnabled: settings.headerTitleStrokeEnabled !== false,
        headerTitleStrokeColor: normalizeCssColorToHex(settings.headerTitleStrokeColor) || '#ffffff',
        headerTitleStrokeWidth: Math.max(0, Math.min(8, Number(settings.headerTitleStrokeWidth ?? 3) || 0)),
        headerTitleShadowEnabled: settings.headerTitleShadowEnabled === true,
        headerTitleShadowColor: normalizeCssColorToHex(settings.headerTitleShadowColor) || '#ffffff',
        headerTitleShadowX: Math.max(-24, Math.min(24, Number(settings.headerTitleShadowX ?? 0) || 0)),
        headerTitleShadowY: Math.max(-24, Math.min(24, Number(settings.headerTitleShadowY ?? 0) || 0)),
        headerTitleShadowBlur: Math.max(0, Math.min(40, Number(settings.headerTitleShadowBlur ?? 0) || 0)),
        headerTitleShadowOpacity: Math.max(0, Math.min(1, Number(settings.headerTitleShadowOpacity ?? 0) || 0)),
        headerTitleGradientEnabled: settings.headerTitleGradientEnabled !== false,
        headerTitleGradientColors: (Array.isArray(settings.headerTitleGradientColors) && settings.headerTitleGradientColors.length ? settings.headerTitleGradientColors : ['#ffffff', '#f9a8d4', '#bbf7d0', '#d8b4fe']).map(color => normalizeCssColorToHex(color) || '#ffffff').slice(0, 8),
        headerTitleGradientPattern: ['linear', 'radial', 'repeating', 'conic', 'mesh', 'fluid'].includes(String(settings.headerTitleGradientPattern || '')) ? String(settings.headerTitleGradientPattern) : 'linear',
        headerTitleGradientDuration: Math.max(1, Math.min(30, Number(settings.headerTitleGradientDuration ?? 5.5) || 5.5)),
        headerTitleGradientAngle: Math.max(0, Math.min(360, Number(settings.headerTitleGradientAngle ?? 135) || 135)),
        headerTitleGradientOffsetEnabled: settings.headerTitleGradientOffsetEnabled !== false,
        headerTitleGradientStagger: Math.max(0, Math.min(0.8, Number(settings.headerTitleGradientStagger ?? 0.12) || 0.12)),
        faviconUrl: String(settings.faviconUrl || '').trim(),
        shareImageUrl: String(settings.shareImageUrl || '').trim(),
        metaPixelId: String(settings.metaPixelId || '').trim(),
        accentColor: normalizeCssColorToHex(settings.accentColor) || '#d3763b',
        secondaryAccentColor: normalizeCssColorToHex(settings.secondaryAccentColor) || DEFAULT_SECONDARY_ACCENT_COLOR,
        shippingRates: sanitizedShipping,
        bkashCashoutEnabled: settings.bkashCashoutEnabled === true,
        bkashCashoutAmount: Math.max(0, Number(settings.bkashCashoutAmount ?? 20) || 20),
        campaignPopupDelaySeconds: Math.max(0, Number(settings.campaignPopupDelaySeconds ?? 0)),
        deliveryEstimateInStock: String(settings.deliveryEstimateInStock || '').trim() || '2-3 days',
        deliveryEstimatePreorder: String(settings.deliveryEstimatePreorder || '').trim() || '3-4 weeks',
        priceBreakdownInStock: String(settings.priceBreakdownInStock || '').trim(),
        priceBreakdownPreorder: String(settings.priceBreakdownPreorder || '').trim(),
        heroBanners: sanitizedHeroBanners,
        homepageBanners: sanitizedHomepageBanners,
        homepageRails: sanitizedHomepageRails,
        homepageShortcutsEnabled: settings.homepageShortcutsEnabled !== false,
        homepageShortcutsLayout: ['row', 'grid', 'stack'].includes(String(settings.homepageShortcutsLayout || '')) ? String(settings.homepageShortcutsLayout) : 'row',
        homepageShortcuts: sanitizedHomepageShortcuts,
        homepageClosingCtaEnabled: settings.homepageClosingCtaEnabled !== false,
        homepageClosingCtaEyebrow: String(settings.homepageClosingCtaEyebrow || '').trim(),
        homepageClosingCtaTitle: String(settings.homepageClosingCtaTitle || '').trim(),
        homepageClosingCtaDescription: String(settings.homepageClosingCtaDescription || '').trim(),
        homepageClosingCtaPrimaryLabel: String(settings.homepageClosingCtaPrimaryLabel || '').trim(),
        homepageClosingCtaPrimaryTarget: String(settings.homepageClosingCtaPrimaryTarget || '').trim(),
        homepageClosingCtaSecondaryLabel: String(settings.homepageClosingCtaSecondaryLabel || '').trim(),
        homepageClosingCtaSecondaryTarget: String(settings.homepageClosingCtaSecondaryTarget || '').trim()
      };

      await request('/settings', {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      if (typeof onNotificationSoundUrlChange === 'function') {
        onNotificationSoundUrlChange(String(payload.notificationSoundUrl || '').trim());
      }
      if (typeof onFaviconUrlChange === 'function') {
        onFaviconUrlChange(String(payload.faviconUrl || '').trim());
      }
      addToast('Settings updated successfully', 'success');
    } catch (err) {
      addToast(err.message || 'Failed to update settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const can = useCallback((permissionKey) => hasUserPermission(user, permissionKey), [user]);
  const canWriteSettings = can('settings:write');
  const saveableSettingsTab = !['users', 'legal', 'backup', 'data'].includes(activeTab);
  const settingsSaveRef = useRef(null);
  settingsSaveRef.current = () => handleSave({ preventDefault() {} });
  const canRestartSystem = can('system:restart');
  const isRestartArmed = restartConfirmText.trim().toUpperCase() === 'RESTART';
  const handleRestartServices = useCallback(async () => {
    if (!canRestartSystem || restartingServices || !isRestartArmed) return;
    const ok = window.confirm('Confirm backend container restart? API and admin requests will disconnect briefly.');
    if (!ok) return;
    try {
      setRestartingServices(true);
      const result = await request('/system/restart', {
        method: 'POST',
        body: JSON.stringify({})
      });
      setRestartConfirmText('');
      addToast(result?.message || 'Restart initiated. Services should recover shortly.', 'success');
    } catch (err) {
      addToast(err?.message || 'Failed to start restart', 'error');
    } finally {
      setRestartingServices(false);
    }
  }, [canRestartSystem, restartingServices, isRestartArmed, request, addToast]);
  useEffect(() => {
    if (typeof onSaveControlChange !== 'function') return undefined;
    onSaveControlChange({
      visible: saveableSettingsTab,
      saving,
      disabled: saving || !canWriteSettings || !saveableSettingsTab,
      save: () => settingsSaveRef.current?.()
    });
    return () => onSaveControlChange(null);
  }, [onSaveControlChange, saveableSettingsTab, saving, canWriteSettings]);

  const tabs = useMemo(() => ([
    { id: 'general', name: 'General', icon: Icon.Settings, enabled: can('settings:read') || can('settings:write') },
    { id: 'branding', name: 'Branding', icon: Icon.Palette, enabled: can('settings:read') || can('settings:write') },
    { id: 'payment', name: 'Payment & Shipping', icon: Icon.Tag, enabled: can('settings:read') || can('settings:write') },
    { id: 'features', name: 'Features', icon: Icon.Package, enabled: can('settings:read') || can('settings:write') },
    { id: 'users', name: 'Users', icon: Icon.User, enabled: can('users:read') },
    { id: 'legal', name: 'Legal Pages', icon: Icon.Package, enabled: can('legal:write') },
    { id: 'backup', name: 'Backup', icon: Icon.Download, enabled: can('backup:manage') || can('backup:restore') },
    { id: 'data', name: 'Data management', icon: Icon.Trash, enabled: String(user?.role || '').toLowerCase() === 'admin' }
  ].filter(tab => tab.enabled)), [can, user?.role]);
  const tabIds = useMemo(() => tabs.map(tab => tab.id), [tabs]);
  const tabInitRef = useRef(false);

  useEffect(() => {
    if (!tabIds.length) return;
    const fallbackTab = tabIds[0];
    if (!tabInitRef.current) {
      let desiredTab = fallbackTab;
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        const urlTab = params.get('tab');
        if (urlTab && tabIds.includes(urlTab)) {
          desiredTab = urlTab;
        }
      }
      tabInitRef.current = true;
      if (desiredTab !== activeTab) {
        setActiveTab(desiredTab);
      }
      return;
    }
    if (!tabIds.includes(activeTab)) {
      setActiveTab(fallbackTab);
    }
  }, [tabIds, activeTab]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!tabInitRef.current) return;
    if (!tabIds.length || !tabIds.includes(activeTab)) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('page') !== 'settings' || params.get('tab') !== activeTab) {
      params.set('page', 'settings');
      params.set('tab', activeTab);
      const qs = params.toString();
      const nextUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      window.history.replaceState({}, '', nextUrl);
    }
  }, [activeTab, tabIds]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon.Loading className="w-6 h-6 text-blue-600 mr-2" />
        <span>Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">Settings & Customization</h1>
          <p className="text-gray-600 mt-1">Configure your store appearance and functionality</p>
        </div>
        {saveableSettingsTab && (
          <button
            type="button"
            onClick={(e) => handleSave(e)}
            disabled={saving || !canWriteSettings}
            className="hidden h-10 shrink-0 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 md:inline-flex"
            title={canWriteSettings ? 'Save' : 'Read Only'}
          >
            {saving ? <Icon.Loading className="h-4 w-4" /> : <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" /><path d="M17 21v-8H7v8" /><path d="M7 3v5h8" /></svg>}
            <span>{saving ? 'Saving...' : (canWriteSettings ? 'Save' : 'Read Only')}</span>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="border-b border-gray-200">
          <div className="flex items-center">
            <nav className="flex min-w-0 flex-1 -mb-px overflow-x-auto flex-nowrap">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={'flex items-center gap-2 px-4 py-4 text-sm font-medium border-b-2 transition whitespace-nowrap ' + (
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  )}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {activeTab === 'users' && can('users:read') && (<div className="p-6"><UsersManager /></div>)}
        {activeTab === 'legal' && can('legal:write') && (<div className="p-6"><LegalPagesManager /></div>)}
        {activeTab === 'backup' && (can('backup:manage') || can('backup:restore')) && (<div className="p-6"><BackupManager /></div>)}
        {activeTab === 'data' && String(user?.role || '').toLowerCase() === 'admin' && (<div className="p-6"><DataManagementPanel request={request} addToast={addToast} currency={currency} /></div>)}
        {(activeTab !== 'users' && activeTab !== 'legal' && activeTab !== 'backup' && activeTab !== 'data') && (
        <form onSubmit={handleSave} className="p-6">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="space-y-3">
              <DesignSection
                title="Store Profile"
                description="Core storefront identity, contact channels, and customer-facing metadata."
                preview={<span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600">{settings.currency || '৳'} {settings.siteName || 'Store'}</span>}
              >
                <DesignGrid className="xl:grid-cols-6">
                  <TextControl label="Store Name" value={settings.siteName} placeholder="Your Store" onChange={(value) => setSettings(prev => ({ ...prev, siteName: value }))} />
                  <TextControl label="SEO Title" value={settings.siteTitle} placeholder={DEFAULT_SITE_TITLE} onChange={(value) => setSettings(prev => ({ ...prev, siteTitle: value }))} />
                  <TextControl label="Currency" value={settings.currency} placeholder="৳" onChange={(value) => setSettings(prev => ({ ...prev, currency: value }))} />
                  <TextControl label="Support Email" value={settings.supportEmail} placeholder="support@yourdomain.com" onChange={(value) => setSettings(prev => ({ ...prev, supportEmail: value }))} />
                  <TextControl label="Facebook URL" value={settings.facebookUrl} placeholder="https://facebook.com/yourpage" onChange={(value) => setSettings(prev => ({ ...prev, facebookUrl: value }))} />
                  <TextControl label="Instagram URL" value={settings.instagramUrl} placeholder="https://instagram.com/yourhandle" onChange={(value) => setSettings(prev => ({ ...prev, instagramUrl: value }))} />
                  <TextControl label="Messenger URL" value={settings.messengerUrl} placeholder="https://m.me/yourpage" onChange={(value) => setSettings(prev => ({ ...prev, messengerUrl: value }))} help="Used by pre-order buttons when a product has zero stock." />
                  <TextControl label="WhatsApp URL" value={settings.whatsappUrl} placeholder="https://wa.me/8801..." onChange={(value) => setSettings(prev => ({ ...prev, whatsappUrl: value }))} />
                  <TextControl label="Meta Pixel ID" value={settings.metaPixelId} placeholder="123456789012345" onChange={(value) => setSettings(prev => ({ ...prev, metaPixelId: value }))} help="Enables storefront analytics events for Meta ads." />
                </DesignGrid>
              </DesignSection>

              <DesignSection title="Store Copy" description="Short pieces of storefront copy used for SEO, previews, and homepage messaging.">
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <DesignField label="SEO Description" help="Used for search snippets, link previews, and marketing copy.">
                    <textarea value={settings.tagline} onChange={(event) => setSettings(prev => ({ ...prev, tagline: event.target.value }))} rows={3} className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm" placeholder={DEFAULT_SITE_TAGLINE} />
                  </DesignField>
                  <DesignField label="Homepage Intro" help="Optional supporting text for the storefront homepage.">
                    <textarea value={settings.homeHeroDescription} onChange={(event) => setSettings(prev => ({ ...prev, homeHeroDescription: event.target.value }))} rows={3} className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm" placeholder={DEFAULT_HOME_HERO_DESCRIPTION} />
                  </DesignField>
                </div>
              </DesignSection>

              <DesignSection
                title="Help Questions"
                description="Questions and answers shown as dropdowns on the customer Help page."
                preview={<span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600">{normalizeHelpFaqs(settings.helpFaqs).filter((item) => item.active !== false).length} visible</span>}
              >
                <div className="space-y-2">
                  {normalizeHelpFaqs(settings.helpFaqs).map((faq, index, faqs) => (
                    <div key={faq.id} className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 bg-gray-50/70 p-2 lg:grid-cols-[minmax(180px,0.85fr)_minmax(280px,1.65fr)_auto] lg:items-start">
                      <label className="text-xs font-medium text-gray-600">
                        Question
                        <input
                          type="text"
                          maxLength={240}
                          value={faq.question}
                          onChange={(event) => {
                            const next = normalizeHelpFaqs(settings.helpFaqs);
                            next[index] = { ...next[index], question: event.target.value };
                            setSettings((prev) => ({ ...prev, helpFaqs: next }));
                          }}
                          className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm"
                        />
                      </label>
                      <label className="text-xs font-medium text-gray-600">
                        Answer
                        <textarea
                          rows={2}
                          maxLength={2000}
                          value={faq.answer}
                          onChange={(event) => {
                            const next = normalizeHelpFaqs(settings.helpFaqs);
                            next[index] = { ...next[index], answer: event.target.value };
                            setSettings((prev) => ({ ...prev, helpFaqs: next }));
                          }}
                          className="mt-1 w-full resize-y rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm"
                        />
                      </label>
                      <div className="flex items-center gap-1 lg:pt-5">
                        <label className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 text-xs font-medium text-gray-700">
                          <input
                            type="checkbox"
                            checked={faq.active !== false}
                            onChange={(event) => {
                              const next = normalizeHelpFaqs(settings.helpFaqs);
                              next[index] = { ...next[index], active: event.target.checked };
                              setSettings((prev) => ({ ...prev, helpFaqs: next }));
                            }}
                          />
                          Visible
                        </label>
                        <button type="button" disabled={index === 0} onClick={() => setSettings((prev) => ({ ...prev, helpFaqs: moveListItem(normalizeHelpFaqs(prev.helpFaqs), index, index - 1) }))} className="h-8 rounded-md border border-gray-200 bg-white px-2 text-xs disabled:opacity-35">Up</button>
                        <button type="button" disabled={index === faqs.length - 1} onClick={() => setSettings((prev) => ({ ...prev, helpFaqs: moveListItem(normalizeHelpFaqs(prev.helpFaqs), index, index + 1) }))} className="h-8 rounded-md border border-gray-200 bg-white px-2 text-xs disabled:opacity-35">Down</button>
                        <button type="button" onClick={() => setSettings((prev) => ({ ...prev, helpFaqs: normalizeHelpFaqs(prev.helpFaqs).filter((_, itemIndex) => itemIndex !== index) }))} className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-red-200 bg-white text-red-600" aria-label={`Remove question ${index + 1}`} title="Remove question"><Icon.Trash className="h-4 w-4" /></button>
                      </div>
                    </div>
                  ))}
                  <button type="button" disabled={normalizeHelpFaqs(settings.helpFaqs).length >= 20} onClick={() => setSettings((prev) => ({ ...prev, helpFaqs: [...normalizeHelpFaqs(prev.helpFaqs), createHelpFaq()] }))} className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40">
                    <Icon.Plus className="h-4 w-4" /> Add Question
                  </button>
                </div>
              </DesignSection>

              <DesignSection
                title="Order Numbering"
                description="Optional order ID prefix/suffix. Keep this stable once customers start receiving order IDs."
                preview={<code className="rounded bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-800">{settings.orderIdPrefix}83425190{settings.orderIdSuffix}</code>}
              >
                <DesignGrid className="xl:grid-cols-4">
                  <TextControl label="Prefix" value={settings.orderIdPrefix} placeholder="PV" onChange={(value) => setSettings(prev => ({ ...prev, orderIdPrefix: value }))} />
                  <TextControl label="Suffix" value={settings.orderIdSuffix} placeholder="Optional" onChange={(value) => setSettings(prev => ({ ...prev, orderIdSuffix: value }))} />
                </DesignGrid>
              </DesignSection>
            </div>
          )}
          {/* Branding Tab */}
          {activeTab === 'branding' && (
            <div className="space-y-3">
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px overflow-x-auto flex-nowrap">
                  {[
                    ['identity', 'Identity'],
                    ['header', 'Header'],
                    ['hero', 'Hero Banners'],
                    ['sections', 'Product Sections'],
                    ['banners', 'Full-Width Banners']
                  ].map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setBrandingSubTab(id)}
                      className={'flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition ' + (brandingSubTab === id ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700')}
                    >
                      {label}
                    </button>
                  ))}
                </nav>
              </div>

              {brandingSubTab === 'identity' && (
              <>
              <DesignSection
                title="Theme"
                description="Global storefront timing and brand color tokens. These should be reused before adding one-off colors elsewhere."
                preview={(
                  <div className="flex items-center gap-2 rounded-lg border border-gray-200 px-2 py-1">
                    <span className="h-5 w-5 rounded" style={{ backgroundColor: settings.accentColor || '#d3763b' }} />
                    <span className="h-5 w-5 rounded" style={{ backgroundColor: settings.secondaryAccentColor || DEFAULT_SECONDARY_ACCENT_COLOR }} />
                  </div>
                )}
              >
                <DesignGrid>
                  <ColorControl label="Primary Accent" value={settings.accentColor} fallback="#d3763b" onChange={(value) => setSettings(prev => ({ ...prev, accentColor: value }))} />
                  <ColorControl label="Secondary Accent" value={settings.secondaryAccentColor} fallback={DEFAULT_SECONDARY_ACCENT_COLOR} onChange={(value) => setSettings(prev => ({ ...prev, secondaryAccentColor: value }))} />
                  <SelectControl
                    label="Hero Layout"
                    value={settings.homeHeroDisplayMode || 'standard'}
                    onChange={(value) => setSettings(prev => ({ ...prev, homeHeroDisplayMode: value }))}
                    options={[
                      { value: 'standard', label: 'Responsive Content Banner' },
                      { value: 'immersive', label: 'Immersive Cover Hero' }
                    ]}
                    help="Standard starts after the header and fits wide banners by their horizontal edges without crop or stretch. Recommended range: 16:9 to 16:4."
                  />
                  <RangeControl label="Hero Autoplay" value={settings.homeHeroIntervalSeconds} min={2.5} max={30} step={0.1} unit="s" onChange={(value) => setSettings(prev => ({ ...prev, homeHeroIntervalSeconds: Math.max(2.5, Math.min(30, Number(value) || 3.6)) }))} />
                  <RangeControl label="Interaction Pause" value={settings.homeHeroManualPauseSeconds} min={3} max={90} step={1} unit="s" onChange={(value) => setSettings(prev => ({ ...prev, homeHeroManualPauseSeconds: Math.max(3, Math.min(90, Number(value) || 10)) }))} />
                </DesignGrid>
              </DesignSection>
              <DesignSection title="Brand Assets" description="Logo, wordmark, social preview, and the reusable logo container token.">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
                  <MediaPicker label="Primary Logo" value={settings.logoUrl} onChange={(url) => setSettings(prev => ({ ...prev, logoUrl: url }))} helpText="Header logo" ratioHint="1:1" />
                  <MediaPicker label="Accent Logo" value={settings.logoAccentUrl} onChange={(url) => setSettings(prev => ({ ...prev, logoAccentUrl: url }))} helpText="Optional alternate logo" ratioHint="1:1" />
                  <MediaPicker label="Wordmark SVG" value={settings.titleSvgUrl || ''} onChange={(url) => setSettings(prev => ({ ...prev, titleSvgUrl: url }))} helpText="Optional SVG wordmark used instead of the generated text wordmark" accept="image/svg+xml,.svg" ratioHint="Wordmark" />
                  <MediaPicker label="Share Image" value={settings.shareImageUrl} onChange={(url) => setSettings(prev => ({ ...prev, shareImageUrl: url }))} helpText="Social preview fallback" ratioHint="1200 x 630" />
                  <MediaPicker label="Favicon" value={settings.faviconUrl} onChange={(url) => { setSettings(prev => ({ ...prev, faviconUrl: url })); if (typeof onFaviconUrlChange === 'function') onFaviconUrlChange(String(url || '').trim()); }} helpText="Browser tab icon" accept="image/*,.ico" ratioHint="1:1" />
                </div>
                <ControlGroup
                  title="Logo Container"
                  description="Reusable container token for the header logo. Keep disabled for a clean logo-only presentation."
                  enabled={settings.headerLogoFrameEnabled === true}
                  onEnabledChange={(checked) => setSettings(prev => ({ ...prev, headerLogoFrameEnabled: checked }))}
                >
                  <div className="mb-4 flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-3">
                    <span className="flex h-14 w-14 items-center justify-center overflow-hidden" style={{ background: settings.headerLogoFrameEnabled === true ? (settings.headerLogoBackgroundColor || 'rgba(255,255,255,0.82)') : 'transparent', border: settings.headerLogoBorderEnabled === true ? `1px solid ${normalizeCssColorToHex(settings.headerLogoBorderColor) || '#ffffff'}` : '0 solid transparent', borderRadius: `${Math.max(0, Math.min(28, Number(settings.headerLogoCornerRadius ?? 16) || 0))}px`, boxShadow: settings.headerLogoShadowEnabled === true ? `0 10px ${Math.max(0, Math.min(60, Number(settings.headerLogoShadowBlur ?? 18) || 0))}px rgba(17,24,39,${Math.max(0, Math.min(1, Number(settings.headerLogoShadowOpacity ?? 0.16) || 0))})` : 'none' }}>
                      <img src={settings.logoUrl ? resolveMediaUrl(settings.logoUrl) : '/logo.svg'} alt="Header logo preview" className="h-11 w-11 rounded-2xl object-contain" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900">Container preview</div>
                      <div className="text-xs text-gray-500">Fill, radius, stroke, and effects use the same controls as other surfaces.</div>
                    </div>
                  </div>
                  <DesignGrid className="xl:grid-cols-5">
                    <TextControl label="Fill" value={settings.headerLogoBackgroundColor || 'transparent'} placeholder="transparent or #ffffff" onChange={(value) => setSettings(prev => ({ ...prev, headerLogoBackgroundColor: value }))} />
                    <RangeControl label="Radius" value={settings.headerLogoCornerRadius ?? 16} min={0} max={28} step={1} unit="px" onChange={(value) => setSettings(prev => ({ ...prev, headerLogoCornerRadius: value }))} />
                    <ToggleControl label="Stroke" checked={settings.headerLogoBorderEnabled === true} onChange={(checked) => setSettings(prev => ({ ...prev, headerLogoBorderEnabled: checked }))} />
                    {settings.headerLogoBorderEnabled === true && <ColorControl label="Stroke Color" value={settings.headerLogoBorderColor} fallback="#ffffff" onChange={(value) => setSettings(prev => ({ ...prev, headerLogoBorderColor: value }))} />}
                    <ToggleControl label="Drop Shadow" checked={settings.headerLogoShadowEnabled === true} onChange={(checked) => setSettings(prev => ({ ...prev, headerLogoShadowEnabled: checked }))} />
                  </DesignGrid>
                  {settings.headerLogoShadowEnabled === true && (
                    <DesignGrid className="mt-3 xl:grid-cols-3">
                      <ColorControl label="Shadow Color" value={settings.headerLogoShadowColor} fallback="#111827" onChange={(value) => setSettings(prev => ({ ...prev, headerLogoShadowColor: value }))} />
                      <RangeControl label="Blur" value={settings.headerLogoShadowBlur ?? 18} min={0} max={60} step={1} unit="px" onChange={(value) => setSettings(prev => ({ ...prev, headerLogoShadowBlur: value }))} />
                      <RangeControl label="Opacity" value={settings.headerLogoShadowOpacity ?? 0.16} min={0} max={1} step={0.05} onChange={(value) => setSettings(prev => ({ ...prev, headerLogoShadowOpacity: value }))} />
                    </DesignGrid>
                  )}
                </ControlGroup>
              </DesignSection>
              </>
              )}

              {brandingSubTab === 'header' && (
                <section className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h4 className="text-base font-semibold text-gray-900">Homepage Header</h4>
                      <p className="mt-0.5 text-xs text-gray-500">Controls the top-of-homepage header shell and brand wordmark.</p>
                    </div>
                    <div className="hidden h-9 w-24 rounded-full border border-gray-200 bg-white shadow-[0_0_14px_rgba(17,24,39,0.14)] sm:block" />
                  </div>
                  <ControlGroup
                    title="Header Shell"
                    description="Homepage top surface only. Scrolled state keeps the normal floating shell."
                  >
                    <DesignGrid className="sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
                      <SelectControl
                        label="Top Style"
                        value={settings.homepageHeaderMode || 'flush'}
                        onChange={(value) => setSettings(prev => ({ ...prev, homepageHeaderMode: value }))}
                        options={[
                          { value: 'flush', label: 'No Shell at Top' },
                          { value: 'floating', label: 'Floating Glass Widget' }
                        ]}
                        help="Used only while the homepage is at the top."
                      />
                      <ColorControl label="Shadow Color" value={settings.homepageHeaderTopShadowColor} fallback="#111827" onChange={(value) => setSettings(prev => ({ ...prev, homepageHeaderTopShadowColor: value }))} />
                      <RangeControl label="Opacity" value={settings.homepageHeaderTopShadowOpacity ?? 0.2} min={0} max={1} step={0.05} onChange={(value) => setSettings(prev => ({ ...prev, homepageHeaderTopShadowOpacity: value }))} />
                      <RangeControl label="Blur" value={settings.homepageHeaderTopShadowBlur ?? 18} min={0} max={80} step={1} unit="px" onChange={(value) => setSettings(prev => ({ ...prev, homepageHeaderTopShadowBlur: value }))} />
                      <RangeControl label="Spread" value={settings.homepageHeaderTopShadowSpread ?? 0} min={-20} max={40} step={1} unit="px" onChange={(value) => setSettings(prev => ({ ...prev, homepageHeaderTopShadowSpread: value }))} />
                      <RangeControl label="Radius" value={settings.homepageHeaderTopShadowRadius ?? 32} min={0} max={80} step={1} unit="px" onChange={(value) => setSettings(prev => ({ ...prev, homepageHeaderTopShadowRadius: value }))} />
                    </DesignGrid>
                  </ControlGroup>
                  <div className="mt-4 border-t border-gray-100 pt-4">
                    <div className="grid gap-3 2xl:grid-cols-[minmax(0,1fr)_340px] 2xl:items-start">
                      <div className="min-w-0 space-y-3">
                        <div>
                          <h4 className="text-base font-semibold text-gray-900">Wordmark</h4>
                          <p className="mt-0.5 text-xs text-gray-500">Text and SVG wordmarks share fill, gradient, stroke, and shadow controls.</p>
                        </div>


                    {settings.titleSvgUrl && (
                      <label className="inline-flex max-w-full items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm font-semibold text-gray-800">
                        <input
                          type="checkbox"
                          checked={settings.headerTitleSvgColorizeEnabled === true}
                          onChange={(event) => setSettings(prev => ({ ...prev, headerTitleSvgColorizeEnabled: event.target.checked }))}
                          className="rounded border-gray-300"
                        />
                        <span>Colorize SVG</span>
                        <span className="text-xs font-normal text-gray-500">Use shared effects</span>
                      </label>
                    )}

                    <DesignGrid className="md:grid-cols-2 2xl:grid-cols-3">
                      <TextControl
                        label="Font Family"
                        value={settings.headerTitleFontFamily || ''}
                        placeholder="Inter, Poppins, system-ui"
                        onChange={(value) => setSettings(prev => ({ ...prev, headerTitleFontFamily: value }))}
                        help="Generated text only."
                      />
                      <div>
                        <MediaPicker label="Font File" value={settings.headerTitleFontUrl || ''} onChange={(url) => setSettings(prev => ({ ...prev, headerTitleFontUrl: url }))} helpText="WOFF/TTF/OTF" accept=".woff,.woff2,.ttf,.otf,font/*" ratioHint="Font" compact />
                      </div>
                      <ColorControl label="Fill" value={settings.headerTitleFillColor} fallback="#000000" onChange={(value) => setSettings(prev => ({ ...prev, headerTitleFillColor: value }))} help="Used when gradient is off." />
                    </DesignGrid>

                    <div className="grid min-w-0 grid-cols-1 gap-3 xl:grid-cols-2">
                      <ControlGroup
                        title="Stroke"
                        description="Outside contrast edge."
                        enabled={settings.headerTitleStrokeEnabled !== false}
                        onEnabledChange={(checked) => setSettings(prev => ({ ...prev, headerTitleStrokeEnabled: checked }))}
                      >
                        <DesignGrid className="sm:grid-cols-2 xl:grid-cols-2">
                        <ColorControl label="Stroke Color" value={settings.headerTitleStrokeColor} fallback="#ffffff" onChange={(value) => setSettings(prev => ({ ...prev, headerTitleStrokeColor: value }))} />
                        <RangeControl label="Stroke Width" value={settings.headerTitleStrokeWidth ?? 3} min={0} max={8} step={0.25} unit="px" onChange={(value) => setSettings(prev => ({ ...prev, headerTitleStrokeWidth: value }))} />
                      </DesignGrid>
                      </ControlGroup>

                      <ControlGroup
                        title="Drop Shadow"
                        description="Readable depth effect."
                        enabled={settings.headerTitleShadowEnabled === true}
                        onEnabledChange={(checked) => setSettings(prev => ({ ...prev, headerTitleShadowEnabled: checked }))}
                      >
                        <DesignGrid className="xl:grid-cols-2">
                          <ColorControl label="Shadow Color" value={settings.headerTitleShadowColor} fallback="#ffffff" onChange={(value) => setSettings(prev => ({ ...prev, headerTitleShadowColor: value }))} />
                          <RangeControl label="Offset X" value={settings.headerTitleShadowX ?? 0} min={-24} max={24} step={1} unit="px" onChange={(value) => setSettings(prev => ({ ...prev, headerTitleShadowX: value }))} />
                          <RangeControl label="Offset Y" value={settings.headerTitleShadowY ?? 0} min={-24} max={24} step={1} unit="px" onChange={(value) => setSettings(prev => ({ ...prev, headerTitleShadowY: value }))} />
                          <RangeControl label="Blur" value={settings.headerTitleShadowBlur ?? 0} min={0} max={40} step={1} unit="px" onChange={(value) => setSettings(prev => ({ ...prev, headerTitleShadowBlur: value }))} />
                          <RangeControl label="Opacity" value={settings.headerTitleShadowOpacity ?? 0} min={0} max={1} step={0.05} onChange={(value) => setSettings(prev => ({ ...prev, headerTitleShadowOpacity: value }))} />
                        </DesignGrid>
                      </ControlGroup>

                      <ControlGroup
                        title="Gradient Fill"
                        description="Animated fill. Drag swatches to reorder."
                        enabled={settings.headerTitleGradientEnabled !== false}
                        onEnabledChange={(checked) => setSettings(prev => ({ ...prev, headerTitleGradientEnabled: checked }))}
                        className="xl:col-span-2"
                      >
                        {(() => {
                          const colors = (Array.isArray(settings.headerTitleGradientColors) && settings.headerTitleGradientColors.length ? settings.headerTitleGradientColors : ['#ffffff', '#f9a8d4', '#bbf7d0', '#d8b4fe']).slice(0, 8);
                          return (
                            <div className="space-y-3">
                              <div className="flex flex-wrap items-center gap-2">
                                {colors.map((color, colorIndex) => (
                                  <div
                                    key={`gradient-color-${colorIndex}`}
                                    draggable
                                    onDragStart={(e) => e.dataTransfer.setData('text/plain', String(colorIndex))}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      const fromIndex = Number(e.dataTransfer.getData('text/plain'));
                                      if (!Number.isFinite(fromIndex) || fromIndex === colorIndex) return;
                                      setSettings(prev => {
                                        const next = [...colors];
                                        const [moved] = next.splice(fromIndex, 1);
                                        next.splice(colorIndex, 0, moved);
                                        return { ...prev, headerTitleGradientColors: next };
                                      });
                                    }}
                                    className="flex cursor-grab items-center gap-1 rounded-md border border-gray-200 bg-white px-1.5 py-1 active:cursor-grabbing"
                                    title="Drag to reorder"
                                  >
                                    <span className="text-[10px] font-bold text-gray-400">::</span>
                                    <PersistentColorInput value={color} fallback="#ffffff" onChange={(nextColor) => setSettings(prev => { const next = [...colors]; next[colorIndex] = nextColor; return { ...prev, headerTitleGradientColors: next }; })} className="admin-gradient-swatch h-8 w-8 shrink-0 appearance-none rounded-md border border-gray-200 p-0" />
                                    {colors.length > 2 && <button type="button" onClick={() => setSettings(prev => ({ ...prev, headerTitleGradientColors: colors.filter((_, idx) => idx !== colorIndex) }))} className="text-xs font-bold text-gray-400 hover:text-red-500">x</button>}
                                  </div>
                                ))}
                                {colors.length < 8 && <button type="button" onClick={() => setSettings(prev => ({ ...prev, headerTitleGradientColors: [...colors, '#ffffff'] }))} className="rounded-md border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-white">Add color</button>}
                              </div>
                              <DesignGrid className="sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
                                <SelectControl
                                  label="Pattern"
                                  value={settings.headerTitleGradientPattern || 'linear'}
                                  onChange={(value) => setSettings(prev => ({ ...prev, headerTitleGradientPattern: value }))}
                                  options={[
                                    { value: 'linear', label: 'Linear' },
                                    { value: 'radial', label: 'Radial' },
                                    { value: 'repeating', label: 'Repeating' },
                                    { value: 'conic', label: 'Conic' },
                                    { value: 'mesh', label: 'Mesh' },
                                    { value: 'fluid', label: 'Fluid' }
                                  ]}
                                />
                                <RangeControl label="Duration" value={settings.headerTitleGradientDuration ?? 5.5} min={1} max={30} step={0.1} unit="s" onChange={(value) => setSettings(prev => ({ ...prev, headerTitleGradientDuration: value }))} />
                                <RangeControl label="Angle" value={settings.headerTitleGradientAngle ?? 135} min={0} max={360} step={1} unit="deg" onChange={(value) => setSettings(prev => ({ ...prev, headerTitleGradientAngle: value }))} />
                                <ToggleControl label="Character Offset" checked={settings.headerTitleGradientOffsetEnabled !== false} onChange={(checked) => setSettings(prev => ({ ...prev, headerTitleGradientOffsetEnabled: checked }))} help="Stagger the gradient across individual characters." />
                                {settings.headerTitleGradientOffsetEnabled !== false && <RangeControl label="Offset Delay" value={settings.headerTitleGradientStagger ?? 0.12} min={0} max={0.8} step={0.01} unit="s" onChange={(value) => setSettings(prev => ({ ...prev, headerTitleGradientStagger: value }))} />}
                              </DesignGrid>
                            </div>
                          );
                        })()}
                      </ControlGroup>
                    </div>
                      </div>
                      {(() => {
                        const titleText = settings.siteName || 'PEYADA';
                        const fill = normalizeCssColorToHex(settings.headerTitleFillColor) || '#000000';
                        const stroke = normalizeCssColorToHex(settings.headerTitleStrokeColor) || '#ffffff';
                        const strokeEnabled = settings.headerTitleStrokeEnabled !== false;
                        const strokeWidth = strokeEnabled ? Math.max(0, Math.min(8, Number(settings.headerTitleStrokeWidth ?? 3) || 0)) : 0;
                        const shadowColor = normalizeCssColorToHex(settings.headerTitleShadowColor) || '#ffffff';
                        const shadowOpacity = Math.max(0, Math.min(1, Number(settings.headerTitleShadowOpacity ?? 0) || 0));
                        const hex = shadowColor.replace('#', '');
                        const shadowRgb = `${parseInt(hex.slice(0, 2), 16)}, ${parseInt(hex.slice(2, 4), 16)}, ${parseInt(hex.slice(4, 6), 16)}`;
                        const shadow = settings.headerTitleShadowEnabled ? `${Math.max(-24, Math.min(24, Number(settings.headerTitleShadowX ?? 0) || 0))}px ${Math.max(-24, Math.min(24, Number(settings.headerTitleShadowY ?? 0) || 0))}px ${Math.max(0, Math.min(40, Number(settings.headerTitleShadowBlur ?? 0) || 0))}px rgba(${shadowRgb}, ${shadowOpacity})` : 'none';
                        const gradientColors = (Array.isArray(settings.headerTitleGradientColors) && settings.headerTitleGradientColors.length ? settings.headerTitleGradientColors : ['#ffffff', '#f9a8d4', '#bbf7d0', '#d8b4fe']).map(color => normalizeCssColorToHex(color) || '#ffffff').slice(0, 8);
                        const gradientAngle = Math.max(0, Math.min(360, Number(settings.headerTitleGradientAngle ?? 135) || 135));
                        const gradientPattern = ['linear', 'radial', 'repeating', 'conic', 'mesh', 'fluid'].includes(String(settings.headerTitleGradientPattern || '')) ? String(settings.headerTitleGradientPattern) : 'linear';
                        const gradientPreview = (() => {
                          if (gradientPattern === 'radial') return `radial-gradient(circle at 25% 20%, ${gradientColors.join(', ')})`;
                          if (gradientPattern === 'repeating') return `repeating-linear-gradient(${gradientAngle}deg, ${gradientColors.map((color, index) => `${color} ${index * 18}px, ${color} ${(index + 1) * 18}px`).join(', ')})`;
                          if (gradientPattern === 'conic') return `conic-gradient(from ${gradientAngle}deg, ${gradientColors.join(', ')}, ${gradientColors[0] || '#fff'})`;
                          if (gradientPattern === 'mesh') return `radial-gradient(circle at 15% 20%, ${gradientColors[1] || gradientColors[0]} 0 18%, transparent 36%), radial-gradient(circle at 80% 30%, ${gradientColors[2] || gradientColors[0]} 0 20%, transparent 40%), radial-gradient(circle at 45% 85%, ${gradientColors[3] || gradientColors[0]} 0 18%, transparent 38%), linear-gradient(${gradientAngle}deg, ${gradientColors.join(', ')})`;
                          if (gradientPattern === 'fluid') return `radial-gradient(ellipse at 20% 50%, ${gradientColors[1] || gradientColors[0]} 0 22%, transparent 48%), radial-gradient(ellipse at 75% 45%, ${gradientColors[2] || gradientColors[0]} 0 24%, transparent 52%), linear-gradient(${gradientAngle}deg, ${gradientColors.join(', ')})`;
                          return `linear-gradient(${gradientAngle}deg, ${gradientColors.join(', ')})`;
                        })();
                        const wordmarkSvgSrc = settings.titleSvgUrl ? resolveMediaUrl(settings.titleSvgUrl) : '';
                        const previewStyle = {
                          '--admin-title-fill': fill,
                          '--admin-title-stroke': stroke,
                          '--admin-title-stroke-width': `${strokeWidth}px`,
                          '--admin-title-shadow': shadow,
                          '--admin-title-image-shadow': shadow === 'none' ? 'none' : `drop-shadow(${shadow})`,
                          '--admin-title-gradient': gradientPreview,
                          '--admin-title-gradient-duration': `${Math.max(1, Math.min(30, Number(settings.headerTitleGradientDuration ?? 5.5) || 5.5))}s`,
                          '--admin-title-gradient-stagger': settings.headerTitleGradientOffsetEnabled !== false ? `${Math.max(0, Math.min(0.8, Number(settings.headerTitleGradientStagger ?? 0.12) || 0.12))}s` : '0s',
                          '--admin-title-svg-url': wordmarkSvgSrc ? `url("${String(wordmarkSvgSrc).replace(/"/g, '\\"')}")` : 'none',
                          fontFamily: settings.headerTitleFontUrl ? '"PeyadaAdminHeaderTitleCustom", ui-sans-serif, system-ui, sans-serif' : (settings.headerTitleFontFamily || undefined)
                        };
                        return (
                          <div className="order-first w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm xl:order-none xl:sticky xl:top-3">
                            <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-3 py-2">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Live Preview</div>
                              <div className="text-[11px] text-gray-400">Checkerboard contrast</div>
                            </div>
                            <div className="admin-checkerboard flex min-h-[96px] items-center justify-center px-3 py-4">
                              {wordmarkSvgSrc && settings.headerTitleSvgColorizeEnabled === true ? (
                                <span className={(settings.headerTitleGradientEnabled !== false ? 'admin-title-svg-mask is-gradient' : 'admin-title-svg-mask')} style={previewStyle} role="img" aria-label={`${titleText} wordmark preview`} />
                              ) : wordmarkSvgSrc ? (
                                <img src={wordmarkSvgSrc} alt={`${titleText} wordmark preview`} className="max-h-16 max-w-full object-contain" style={{ filter: shadow === 'none' ? 'none' : `drop-shadow(${shadow})` }} />
                              ) : (
                                <div className={(settings.headerTitleGradientEnabled !== false ? 'admin-title-preview is-gradient' : 'admin-title-preview') + ' text-3xl font-black uppercase leading-none tracking-wide sm:text-4xl'} style={previewStyle} aria-label={titleText}>
                                  {Array.from(titleText).map((char, charIndex) => (
                                    <span key={charIndex} className="admin-title-preview-char" style={{ '--char-index': settings.headerTitleGradientOffsetEnabled !== false ? charIndex : 0, '--char-offset': settings.headerTitleGradientOffsetEnabled !== false ? `${charIndex * -56}px` : '0px' }} aria-hidden="true">{char === ' ' ? '\u00A0' : char}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </section>
              )}

              {brandingSubTab === 'hero' && (
              <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h4 className="text-base font-semibold text-gray-900">Hero Banners</h4>
                    <p className="text-xs text-gray-500">Image-first linked banners with an optional text and call-to-action overlay.</p>
                  </div>
                  <button type="button" onClick={() => setSettings(prev => ({ ...prev, heroBanners: [...(prev.heroBanners || []), createHeroBanner()] }))} className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-gray-800"><Icon.Plus className="h-4 w-4" />Add Banner</button>
                </div>
                <div className="space-y-3">
                  {(settings.heroBanners || []).length === 0 ? (
                    <p className="rounded-lg border-2 border-dashed border-gray-200 py-6 text-center text-sm text-gray-500">No hero banners yet.</p>
                  ) : (settings.heroBanners || []).map((banner, index) => {
                    const updateBanner = (patch) => {
                      const newBanners = [...settings.heroBanners];
                      newBanners[index] = { ...newBanners[index], ...patch };
                      setSettings(prev => ({ ...prev, heroBanners: newBanners }));
                    };
                    const accent = normalizeCssColorToHex(settings.accentColor) || '#d3763b';
                    const titleGlow = banner.titleGlowColor || banner.glowColor || accent;
                    const descGlow = banner.descriptionGlowColor || banner.glowColor || accent;
                    const titleShadowColor = banner.titleShadowColor || 'rgba(0,0,0,0.62)';
                    const descShadowColor = banner.descriptionShadowColor || 'rgba(0,0,0,0.58)';
                    const titleShadow = '0 ' + (banner.titleGlowSpread ?? 2) + 'px ' + ((banner.titleGlowSpread ?? 2) + 2) + 'px ' + titleShadowColor + ', 0 0 ' + (banner.titleGlowBlur ?? 10) + 'px color-mix(in srgb, ' + titleGlow + ' ' + Math.round((banner.titleGlowOpacity ?? 0.58) * 100) + '%, transparent)';
                    const descShadow = '0 ' + (banner.descriptionGlowSpread ?? 1.5) + 'px ' + ((banner.descriptionGlowSpread ?? 1.5) + 2) + 'px ' + descShadowColor + ', 0 0 ' + (banner.descriptionGlowBlur ?? 8) + 'px color-mix(in srgb, ' + descGlow + ' ' + Math.round((banner.descriptionGlowOpacity ?? 0.48) * 100) + '%, transparent)';
                    const styleControlKey = banner.id || 'hero-' + index;
                    const showStyleControls = !!openHeroStyleControls[styleControlKey];
                    return (
                      <div key={banner.id} className="rounded-lg border border-gray-200 p-3">
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-semibold text-gray-800">Banner {index + 1}</span>
                            <label className="inline-flex items-center gap-1.5 text-xs text-gray-600"><input type="checkbox" checked={banner.active !== false} onChange={(e) => updateBanner({ active: e.target.checked })} className="rounded" />Active</label>
                          </div>
                          <button type="button" onClick={() => setSettings(prev => ({ ...prev, heroBanners: prev.heroBanners.filter((_, i) => i !== index) }))} className="text-red-600 hover:text-red-800"><Icon.Trash className="h-4 w-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1.45fr)_minmax(420px,0.85fr)]">
                          <div className="space-y-3">
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(260px,0.85fr)_minmax(0,1.15fr)] md:items-end">
                              <MediaPicker label="Banner Image" value={banner.image || ''} onChange={(url) => updateBanner({ image: url })} helpText="Displayed at its natural wide ratio without crop or stretch" ratioHint="16:9 to 16:4 wide" />
                              <label className="text-sm font-medium text-gray-700">
                                Destination URL
                                <input
                                  type="text"
                                  value={banner.href || banner.imageHref || ''}
                                  onChange={(e) => updateBanner({ href: e.target.value, imageHref: '' })}
                                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                  placeholder="/catalog or https://..."
                                />
                                <span className="mt-1 block text-xs font-normal text-gray-500">The entire banner opens this destination.</span>
                              </label>
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                              <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={banner.contentEnabled === true}
                                  onChange={(e) => updateBanner({ contentEnabled: e.target.checked })}
                                  className="rounded"
                                />
                                Add text and call-to-action overlay
                              </label>
                              <span className="text-xs text-gray-500">Optional. Image-only is the recommended default.</span>
                            </div>

                            {banner.contentEnabled === true && (
                              <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                  <label className="text-sm font-medium text-gray-700">Headline<input type="text" maxLength={120} value={banner.headline || ''} onChange={(e) => updateBanner({ headline: e.target.value })} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm" /></label>
                                  <label className="text-sm font-medium text-gray-700">Description<input type="text" maxLength={220} value={banner.subheadline || ''} onChange={(e) => updateBanner({ subheadline: e.target.value })} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm" /></label>
                                </div>
                                <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(180px,0.45fr)_minmax(0,1fr)] md:items-center">
                                  <label className="text-sm font-medium text-gray-700">Call-to-action Label<input type="text" value={banner.ctaLabel || ''} onChange={(e) => updateBanner({ ctaLabel: e.target.value })} className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm" placeholder="Optional" /></label>
                                  <label className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-gray-700 md:mt-0 md:self-end md:pb-2">
                                    <input
                                      type="checkbox"
                                      checked={showStyleControls}
                                      onChange={(e) => setOpenHeroStyleControls(prev => ({ ...prev, [styleControlKey]: e.target.checked }))}
                                      className="rounded"
                                    />
                                    Customize overlay appearance
                                  </label>
                                </div>
                              </div>
                            )}
                            {banner.contentEnabled === true && showStyleControls && (
                              <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                                  {[
                                    ['titleColor', 'Title', '#ffffff'],
                                    ['descriptionColor', 'Description', '#ffffff'],
                                    ['titleGlowColor', 'Title Glow Color', accent],
                                    ['descriptionGlowColor', 'Description Glow Color', accent],
                                    ['titleShadowColor', 'Title Shadow Color', '#000000'],
                                    ['descriptionShadowColor', 'Description Shadow Color', '#000000']
                                  ].map(([field, label, fallback]) => (
                                    <label key={field} className="text-xs font-medium text-gray-600">{label}<PersistentColorInput value={banner[field]} fallback={fallback} onChange={(nextColor) => updateBanner({ [field]: nextColor })} className="mt-1 h-9 w-full rounded-md border border-gray-300 bg-white p-0" /></label>
                                  ))}
                                </div>
                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                                  {[
                                    ['titleGlowBlur', 'Title Glow Blur', 10, 0, 48, 1],
                                    ['descriptionGlowBlur', 'Description Glow Blur', 8, 0, 48, 1],
                                    ['titleGlowSpread', 'Title Shadow Spread', 2, 0, 8, 0.1],
                                    ['descriptionGlowSpread', 'Description Shadow Spread', 1.5, 0, 8, 0.1],
                                    ['titleGlowOpacity', 'Title Glow Opacity', 0.58, 0, 1, 0.05],
                                    ['descriptionGlowOpacity', 'Description Glow Opacity', 0.48, 0, 1, 0.05]
                                  ].map(([field, label, fallback, min, max, step]) => (
                                    <label key={field} className="text-xs font-medium text-gray-600"><span className="flex justify-between gap-2"><span>{label}</span><span>{banner[field] ?? fallback}</span></span><input type="range" min={min} max={max} step={step} value={banner[field] ?? fallback} onChange={(e) => updateBanner({ [field]: Number(e.target.value) })} className="mt-1 w-full" /></label>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="overflow-hidden rounded-xl bg-gray-900 p-0" style={{ aspectRatio: '16 / 9', backgroundImage: banner.image ? 'linear-gradient(rgba(0,0,0,.14), rgba(0,0,0,.14)), url(' + banner.image + ')' : 'linear-gradient(135deg, ' + accent + ', #111827)', backgroundSize: banner.image ? 'contain' : 'cover', backgroundRepeat: 'no-repeat', backgroundPosition: 'center' }}>
                            {banner.contentEnabled === true && (
                              <div className="flex h-full flex-col justify-center p-6">
                                {banner.headline && <div className="text-3xl font-bold leading-tight md:whitespace-nowrap" style={{ color: banner.titleColor || '#ffffff', textShadow: titleShadow }}>{banner.headline}</div>}
                                {banner.subheadline && <div className="mt-2 text-sm leading-relaxed lg:whitespace-nowrap" style={{ color: banner.descriptionColor || '#ffffff', textShadow: descShadow }}>{banner.subheadline}</div>}
                                {banner.ctaLabel && <span className="mt-4 inline-flex w-max rounded-full bg-white px-4 py-2 text-xs font-semibold text-gray-950">{banner.ctaLabel}</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
              )}

              {brandingSubTab === 'sections' && (
              <>
              <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <div><h4 className="text-base font-semibold text-gray-900">Homepage Product Sections</h4><p className="text-xs text-gray-500">Product rails shown under the hero.</p></div>
                  <button type="button" onClick={() => setSettings(prev => ({ ...prev, homepageRails: [...normalizeHomepageRails(prev.homepageRails), createHomepageRail()] }))} className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-gray-800"><Icon.Plus className="h-4 w-4" />Add Section</button>
                </div>
                <div className="space-y-2">
                  {normalizeHomepageRails(settings.homepageRails).map((rail, index, rails) => (
                    <div key={rail.id} className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 p-3 md:grid-cols-[auto_140px_minmax(120px,1fr)_minmax(120px,1fr)_minmax(160px,1fr)_auto] md:items-end">
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700 md:pb-2"><input type="checkbox" checked={rail.active !== false} onChange={(e) => { const next = normalizeHomepageRails(settings.homepageRails); next[index] = { ...next[index], active: e.target.checked }; setSettings(prev => ({ ...prev, homepageRails: next })); }} className="rounded" />Active</label>
                      <label className="text-xs font-medium text-gray-600">Type<select value={rail.type} onChange={(e) => { const next = normalizeHomepageRails(settings.homepageRails); next[index] = { ...next[index], type: e.target.value }; setSettings(prev => ({ ...prev, homepageRails: next })); }} className="mt-1 w-full rounded-md border border-gray-300 px-2 py-2 text-sm"><option value="sale">On sale</option><option value="stock">In stock</option><option value="prepurchase">Pre-purchase</option><option value="category">Category</option><option value="newest">Newest</option><option value="all">All products</option></select></label>
                      <label className="text-xs font-medium text-gray-600">Title<input type="text" value={rail.title || ''} onChange={(e) => { const next = normalizeHomepageRails(settings.homepageRails); next[index] = { ...next[index], title: e.target.value }; setSettings(prev => ({ ...prev, homepageRails: next })); }} className="mt-1 w-full rounded-md border border-gray-300 px-2 py-2 text-sm" /></label>
                      <label className="text-xs font-medium text-gray-600">Category<input type="text" value={rail.category || ''} onChange={(e) => { const next = normalizeHomepageRails(settings.homepageRails); next[index] = { ...next[index], category: e.target.value }; setSettings(prev => ({ ...prev, homepageRails: next })); }} className="mt-1 w-full rounded-md border border-gray-300 px-2 py-2 text-sm" placeholder="Skin Care" disabled={rail.type !== 'category'} /></label>
                      <label className="text-xs font-medium text-gray-600">Subtitle<input type="text" value={rail.subtitle || ''} onChange={(e) => { const next = normalizeHomepageRails(settings.homepageRails); next[index] = { ...next[index], subtitle: e.target.value }; setSettings(prev => ({ ...prev, homepageRails: next })); }} className="mt-1 w-full rounded-md border border-gray-300 px-2 py-2 text-sm" /></label>
                      <div className="flex items-center gap-1 md:pb-1"><button type="button" disabled={index === 0} onClick={() => setSettings(prev => ({ ...prev, homepageRails: moveListItem(normalizeHomepageRails(prev.homepageRails), index, index - 1) }))} className="rounded border border-gray-200 px-2 py-1 text-xs disabled:opacity-40">Up</button><button type="button" disabled={index === rails.length - 1} onClick={() => setSettings(prev => ({ ...prev, homepageRails: moveListItem(normalizeHomepageRails(prev.homepageRails), index, index + 1) }))} className="rounded border border-gray-200 px-2 py-1 text-xs disabled:opacity-40">Down</button><button type="button" onClick={() => setSettings(prev => ({ ...prev, homepageRails: normalizeHomepageRails(prev.homepageRails).filter((_, i) => i !== index) }))} className="p-1 text-red-600 hover:text-red-800"><Icon.Trash className="h-4 w-4" /></button></div>
                    </div>
                  ))}
                </div>
              </section>
              <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <div><h4 className="text-base font-semibold text-gray-900">Homepage Shortcuts</h4><p className="text-xs text-gray-500">Quick category and collection links shown below the hero.</p></div>
                  <div className="flex items-center gap-2">
                    <ToggleControl label="Show shortcuts" checked={settings.homepageShortcutsEnabled !== false} onChange={(checked) => setSettings((prev) => ({ ...prev, homepageShortcutsEnabled: checked }))} />
                    <button type="button" disabled={settings.homepageShortcuts.length >= 12} onClick={() => setSettings((prev) => ({ ...prev, homepageShortcuts: [...normalizeHomepageShortcuts(prev.homepageShortcuts), createHomepageShortcut()] }))} className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-40"><Icon.Plus className="h-4 w-4" />Add Shortcut</button>
                  </div>
                </div>
                {settings.homepageShortcutsEnabled !== false && (
                  <>
                    <p className="mb-3 text-xs text-gray-500">Up to 12 shortcuts. Choose a destination for each active shortcut, then save settings.</p>
                    <label className="mb-3 block max-w-[180px] text-xs font-medium text-gray-600">Layout
                      <select value={settings.homepageShortcutsLayout || 'row'} onChange={(e) => setSettings((prev) => ({ ...prev, homepageShortcutsLayout: e.target.value }))} className="mt-1 w-full rounded-md border border-gray-300 px-2 py-2 text-sm"><option value="row">Responsive rows</option><option value="grid">Compact grid</option><option value="stack">Vertical stack</option></select>
                    </label>
                    <div className="space-y-2">
                      {normalizeHomepageShortcuts(settings.homepageShortcuts).map((shortcut, index, shortcuts) => (
                        <div key={shortcut.id} className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 p-3 md:grid-cols-[auto_minmax(110px,1fr)_110px_minmax(150px,1fr)_90px_minmax(150px,0.8fr)_78px_auto] md:items-end">
                          <label className="inline-flex items-center gap-2 text-sm text-gray-700 md:pb-2"><input type="checkbox" checked={shortcut.active !== false} onChange={(e) => { const next = normalizeHomepageShortcuts(settings.homepageShortcuts); next[index] = { ...next[index], active: e.target.checked }; setSettings((prev) => ({ ...prev, homepageShortcuts: next })); }} className="rounded" />Active</label>
                          <label className="text-xs font-medium text-gray-600">Label<input type="text" value={shortcut.label || ''} onChange={(e) => { const next = normalizeHomepageShortcuts(settings.homepageShortcuts); next[index] = { ...next[index], label: e.target.value }; setSettings((prev) => ({ ...prev, homepageShortcuts: next })); }} className="mt-1 w-full rounded-md border border-gray-300 px-2 py-2 text-sm" /></label>
                          <label className="text-xs font-medium text-gray-600">Target<select value={shortcut.targetType || 'category'} onChange={(e) => { const next = normalizeHomepageShortcuts(settings.homepageShortcuts); next[index] = { ...next[index], targetType: e.target.value, targetValue: e.target.value === 'sort' ? 'newest' : e.target.value === 'stock' ? 'all' : '' }; setSettings((prev) => ({ ...prev, homepageShortcuts: next })); }} className="mt-1 w-full rounded-md border border-gray-300 px-2 py-2 text-sm"><option value="category">Category</option><option value="sort">Sort</option><option value="stock">Stock state</option></select></label>
                          <label className="text-xs font-medium text-gray-600">Destination<select value={shortcut.targetValue || ''} onChange={(e) => { const next = normalizeHomepageShortcuts(settings.homepageShortcuts); next[index] = { ...next[index], targetValue: e.target.value }; setSettings((prev) => ({ ...prev, homepageShortcuts: next })); }} className="mt-1 w-full rounded-md border border-gray-300 px-2 py-2 text-sm">{shortcut.targetType === 'category' ? <><option value="">Select category</option>{shortcut.targetValue && !shortcutCategories.includes(shortcut.targetValue) && <option value={shortcut.targetValue}>{shortcut.targetValue}</option>}{shortcutCategories.map((category) => <option key={category} value={category}>{category}</option>)}</> : shortcut.targetType === 'sort' ? <><option value="newest">Newest</option><option value="priceLow">Price: Low to High</option><option value="priceHigh">Price: High to Low</option><option value="trending">Trending</option><option value="bestseller">Best sellers</option><option value="discounted">Promo Items</option></> : <><option value="all">All products</option><option value="in">In stock</option><option value="preorder">Pre-purchase</option><option value="out">Out of stock</option><option value="upcoming">Upcoming</option></>}</select></label>
                          <label className="text-xs font-medium text-gray-600">Icon<select value={shortcut.icon || 'sparkle'} onChange={(e) => { const next = normalizeHomepageShortcuts(settings.homepageShortcuts); next[index] = { ...next[index], icon: e.target.value }; setSettings((prev) => ({ ...prev, homepageShortcuts: next })); }} className="mt-1 w-full rounded-md border border-gray-300 px-2 py-2 text-sm"><option value="sparkle">Sparkle</option><option value="drop">Drop</option><option value="heart">Heart</option><option value="star">Star</option></select></label>
                          <MediaPicker label="Custom icon" value={shortcut.iconImage || ''} onChange={(url) => { const next = normalizeHomepageShortcuts(settings.homepageShortcuts); next[index] = { ...next[index], iconImage: url }; setSettings((prev) => ({ ...prev, homepageShortcuts: next })); }} helpText="Optional: replaces the built-in icon" accept="image/*" compact />
                          <label className="text-xs font-medium text-gray-600">Color<input type="color" value={normalizeCssColorToHex(shortcut.color) || '#f9a8d4'} onChange={(e) => { const next = normalizeHomepageShortcuts(settings.homepageShortcuts); next[index] = { ...next[index], color: e.target.value }; setSettings((prev) => ({ ...prev, homepageShortcuts: next })); }} className="mt-1 h-10 w-full rounded-md border border-gray-300 p-1" /></label>
                          <div className="flex items-center gap-1 md:pb-1"><button type="button" disabled={index === 0} onClick={() => setSettings((prev) => ({ ...prev, homepageShortcuts: moveListItem(normalizeHomepageShortcuts(prev.homepageShortcuts), index, index - 1) }))} className="rounded border border-gray-200 px-2 py-1 text-xs disabled:opacity-40">Up</button><button type="button" disabled={index === shortcuts.length - 1} onClick={() => setSettings((prev) => ({ ...prev, homepageShortcuts: moveListItem(normalizeHomepageShortcuts(prev.homepageShortcuts), index, index + 1) }))} className="rounded border border-gray-200 px-2 py-1 text-xs disabled:opacity-40">Down</button><button type="button" onClick={() => setSettings((prev) => ({ ...prev, homepageShortcuts: normalizeHomepageShortcuts(prev.homepageShortcuts).filter((_, itemIndex) => itemIndex !== index) }))} className="p-1 text-red-600 hover:text-red-800" aria-label={`Remove shortcut ${index + 1}`}><Icon.Trash className="h-4 w-4" /></button></div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </section>
              <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                  <div><h4 className="text-base font-semibold text-gray-900">Closing Card</h4><p className="text-xs text-gray-500">Optional final homepage call-to-action shown after product sections.</p></div>
                  <ToggleControl label="Show closing card" checked={settings.homepageClosingCtaEnabled !== false} onChange={(checked) => setSettings(prev => ({ ...prev, homepageClosingCtaEnabled: checked }))} />
                </div>
                {settings.homepageClosingCtaEnabled !== false && (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <TextControl label="Eyebrow" value={settings.homepageClosingCtaEyebrow} onChange={(value) => setSettings(prev => ({ ...prev, homepageClosingCtaEyebrow: value }))} placeholder="Ready to order" />
                    <TextControl label="Primary Button" value={settings.homepageClosingCtaPrimaryLabel} onChange={(value) => setSettings(prev => ({ ...prev, homepageClosingCtaPrimaryLabel: value }))} placeholder="Track an order" />
                    <TextControl label="Primary Target" value={settings.homepageClosingCtaPrimaryTarget} onChange={(value) => setSettings(prev => ({ ...prev, homepageClosingCtaPrimaryTarget: value }))} placeholder="track or /catalog" />
                    <TextControl label="Secondary Button" value={settings.homepageClosingCtaSecondaryLabel} onChange={(value) => setSettings(prev => ({ ...prev, homepageClosingCtaSecondaryLabel: value }))} placeholder="Contact support" />
                    <div className="md:col-span-2"><TextControl label="Title" value={settings.homepageClosingCtaTitle} onChange={(value) => setSettings(prev => ({ ...prev, homepageClosingCtaTitle: value }))} /></div>
                    <div className="md:col-span-2"><TextControl label="Description" value={settings.homepageClosingCtaDescription} onChange={(value) => setSettings(prev => ({ ...prev, homepageClosingCtaDescription: value }))} /></div>
                    <TextControl label="Secondary Target" value={settings.homepageClosingCtaSecondaryTarget} onChange={(value) => setSettings(prev => ({ ...prev, homepageClosingCtaSecondaryTarget: value }))} placeholder="help or https://..." />
                  </div>
                )}
              </section>
              </>
              )}

              {brandingSubTab === 'banners' && (
              <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <div><h4 className="text-base font-semibold text-gray-900">Homepage Full-Width Banners</h4><p className="text-xs text-gray-500">Clickable image-only banners placed around homepage sections.</p></div>
                  <button type="button" onClick={() => setSettings(prev => ({ ...prev, homepageBanners: [...(prev.homepageBanners || []), createHomepageBanner()] }))} className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-gray-800"><Icon.Plus className="h-4 w-4" />Add Banner</button>
                </div>
                <div className="space-y-3">
                  {(settings.homepageBanners || []).length === 0 ? (
                    <p className="rounded-lg border-2 border-dashed border-gray-200 py-5 text-center text-sm text-gray-500">No homepage banners yet.</p>
                  ) : (settings.homepageBanners || []).map((banner, index) => (
                    <div key={banner.id} className="rounded-lg border border-gray-200 p-3">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-800"><input type="checkbox" checked={banner.active !== false} onChange={(e) => { const next = [...settings.homepageBanners]; next[index] = { ...next[index], active: e.target.checked }; setSettings(prev => ({ ...prev, homepageBanners: next })); }} className="rounded" />Banner {index + 1}</label>
                        <button type="button" onClick={() => setSettings(prev => ({ ...prev, homepageBanners: prev.homepageBanners.filter((_, i) => i !== index) }))} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50" aria-label={`Delete banner ${index + 1}`}><Icon.Trash className="h-4 w-4" /></button>
                      </div>
                      <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-[minmax(280px,0.9fr)_minmax(420px,1.4fr)]">
                        <div className="min-w-0">
                          <div className="mb-1 flex items-center justify-between text-xs font-medium text-gray-600"><span>Storefront Preview</span><span>16:3</span></div>
                          <div className="w-full bg-white" style={{ paddingTop: Math.max(0, Math.min(64, Number(banner.paddingTop ?? 8) || 0)), paddingBottom: Math.max(0, Math.min(64, Number(banner.paddingBottom ?? 8) || 0)) }}>
                            <div className="aspect-[16/3] w-full overflow-hidden bg-gray-100">
                              {banner.image ? <img src={resolveMediaUrl(banner.image)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-xs text-gray-400">Select a 16:3 banner</div>}
                            </div>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-500">
                            <span>Top spacing: {Math.max(0, Math.min(64, Number(banner.paddingTop ?? 8) || 0))}px</span>
                            <span className="text-right">Bottom spacing: {Math.max(0, Math.min(64, Number(banner.paddingBottom ?? 8) || 0))}px</span>
                          </div>
                        </div>
                        <div className="min-w-0 space-y-3">
                          <div className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-3">
                            <MediaPicker label="Image" value={banner.image || ''} onChange={(url) => { const next = [...settings.homepageBanners]; next[index] = { ...next[index], image: url }; setSettings(prev => ({ ...prev, homepageBanners: next })); }} helpText="Fixed storefront ratio" ratioHint="16:3" compact />
                            <label className="min-w-0 text-xs font-medium text-gray-600">Click URL<input type="text" value={banner.href || ''} onChange={(e) => { const next = [...settings.homepageBanners]; next[index] = { ...next[index], href: e.target.value }; setSettings(prev => ({ ...prev, homepageBanners: next })); }} className="mt-1 w-full min-w-0 rounded-md border border-gray-300 px-2 py-2 text-sm" placeholder="/catalog or https://..." /></label>
                            <label className="min-w-0 text-xs font-medium text-gray-600">Position<select value={banner.position || 'between_featured'} onChange={(e) => { const next = [...settings.homepageBanners]; next[index] = { ...next[index], position: e.target.value }; setSettings(prev => ({ ...prev, homepageBanners: next })); }} className="mt-1 w-full min-w-0 rounded-md border border-gray-300 px-2 py-2 text-sm"><option value="top_of_home">Top of homepage</option><option value="below_hero">Below hero</option><option value="above_featured">Above products</option><option value="between_featured">Between products</option><option value="below_featured">Below products</option><option value="above_closing">Above CTA</option><option value="below_closing">Below CTA</option></select></label>
                          </div>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <RangeControl label="Space Above" value={banner.paddingTop ?? 8} min={0} max={64} step={1} unit="px" onChange={(value) => { const next = [...settings.homepageBanners]; next[index] = { ...next[index], paddingTop: value }; setSettings(prev => ({ ...prev, homepageBanners: next })); }} />
                            <RangeControl label="Space Below" value={banner.paddingBottom ?? 8} min={0} max={64} step={1} unit="px" onChange={(value) => { const next = [...settings.homepageBanners]; next[index] = { ...next[index], paddingBottom: value }; setSettings(prev => ({ ...prev, homepageBanners: next })); }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
              )}
            </div>
          )}

          {/* Payment & Shipping Tab */}
          {activeTab === 'payment' && (
            <div className="space-y-3">
              <DesignSection
                title="Shipping Rates"
                description="Flat delivery charges used by checkout. Keep these simple and predictable for customers."
                preview={<span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600">Dhaka {settings.currency || '৳'}{settings.shippingRates.inside || 0} / Outside {settings.currency || '৳'}{settings.shippingRates.outside || 0}</span>}
              >
                <DesignGrid className="xl:grid-cols-4">
                  <DesignField label="Inside Dhaka" valueLabel={`${settings.currency || '৳'}${settings.shippingRates.inside || 0}`} help="Flat rate for deliveries inside Dhaka city.">
                    <input type="number" min="0" value={settings.shippingRates.inside} onChange={(event) => setSettings(prev => ({ ...prev, shippingRates: { ...prev.shippingRates, inside: parseInt(event.target.value) || 0 } }))} className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm" />
                  </DesignField>
                  <DesignField label="Outside Dhaka" valueLabel={`${settings.currency || '৳'}${settings.shippingRates.outside || 0}`} help="Flat rate for deliveries outside Dhaka.">
                    <input type="number" min="0" value={settings.shippingRates.outside} onChange={(event) => setSettings(prev => ({ ...prev, shippingRates: { ...prev.shippingRates, outside: parseInt(event.target.value) || 0 } }))} className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm" />
                  </DesignField>
                </DesignGrid>
              </DesignSection>

              <DesignSection
                title="bKash"
                description="Customer-facing mobile payment details shown during checkout."
                preview={<span className="rounded-full bg-pink-50 px-2 py-1 text-xs font-semibold text-pink-700">{settings.bkashNumber || 'No number set'}</span>}
              >
                <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(220px,1fr)_minmax(280px,1fr)_minmax(260px,0.9fr)]">
                  <TextControl label="bKash Number" value={settings.bkashNumber} placeholder="01XXXXXXXXX" onChange={(value) => setSettings(prev => ({ ...prev, bkashNumber: value }))} help="Customers send payments to this number." />
                  <MediaPicker label="bKash QR Code" value={settings.bkashQrUrl} onChange={(url) => setSettings(prev => ({ ...prev, bkashQrUrl: url }))} helpText="Optional QR image customers can scan at checkout" ratioHint="1:1" />
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <ToggleControl label="Cashout Charge" checked={settings.bkashCashoutEnabled} onChange={(checked) => setSettings(prev => ({ ...prev, bkashCashoutEnabled: checked }))} help="Add a fixed charge whenever customers choose bKash." />
                    {settings.bkashCashoutEnabled && (
                      <DesignField label="Charge Amount" valueLabel={`${settings.currency || '৳'}${settings.bkashCashoutAmount || 0}`}>
                        <input type="number" min="0" step="1" value={settings.bkashCashoutAmount} onChange={(event) => setSettings(prev => ({ ...prev, bkashCashoutAmount: Math.max(0, Number(event.target.value) || 0) }))} className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm" />
                      </DesignField>
                    )}
                  </div>
                </div>
                {settings.bkashQrUrl && (
                  <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-2 text-xs text-gray-600">
                    <img src={settings.bkashQrUrl} alt="bKash QR preview" className="h-16 w-16 rounded-md object-contain" />
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900">Checkout QR preview</div>
                      <div className="max-w-xl truncate text-gray-400">{settings.bkashQrUrl}</div>
                    </div>
                  </div>
                )}
              </DesignSection>

              <DesignSection title="Accepted Methods" description="Currently supported checkout payment paths.">
                <div className="flex flex-wrap gap-2 text-sm">
                  {['bKash Personal', 'Cash on Delivery', 'Bank Transfer on request'].map((method) => (
                    <span key={method} className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 font-medium text-blue-800">{method}</span>
                  ))}
                </div>
              </DesignSection>

              <DesignSection
                title="Product Detail Messaging"
                description="Customer-facing delivery estimates and price explanation text used on product pages."
              >
                <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                  <TextControl label="In-stock Delivery" value={settings.deliveryEstimateInStock || ''} placeholder="2-3 days" onChange={(value) => setSettings(prev => ({ ...prev, deliveryEstimateInStock: value }))} />
                  <TextControl label="Pre-order Delivery" value={settings.deliveryEstimatePreorder || ''} placeholder="3-4 weeks" onChange={(value) => setSettings(prev => ({ ...prev, deliveryEstimatePreorder: value }))} />
                  <DesignField label="In-stock Price Breakdown" help="Shown beside the price when the product is available for direct checkout.">
                    <textarea value={settings.priceBreakdownInStock || ''} onChange={(event) => setSettings(prev => ({ ...prev, priceBreakdownInStock: event.target.value }))} rows={3} className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm" />
                  </DesignField>
                  <DesignField label="Pre-order Price Breakdown" help="Shown beside the price when stock is zero and pre-order is active.">
                    <textarea value={settings.priceBreakdownPreorder || ''} onChange={(event) => setSettings(prev => ({ ...prev, priceBreakdownPreorder: event.target.value }))} rows={3} className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm" />
                  </DesignField>
                </div>
              </DesignSection>
            </div>
          )}
          {/* Features Tab */}
          {activeTab === 'features' && (
            <div className="space-y-3">
              <DesignSection title="Storefront State" description="Operational switches for customer-facing storefront behavior.">
                <DesignGrid className="xl:grid-cols-3">
                  <ToggleControl label="Demo Mode" checked={settings.demoMode} onChange={(checked) => setSettings(prev => ({ ...prev, demoMode: checked }))} help="Show demo data and test features." />
                  <ToggleControl label="Maintenance Mode" checked={settings.maintenanceMode || false} onChange={(checked) => setSettings(prev => ({ ...prev, maintenanceMode: checked }))} help="Hide the public site with a maintenance message. Admins remain unaffected." />
                  {settings.demoMode && <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs font-medium text-yellow-800">Demo mode may expose sample data to customers.</div>}
                </DesignGrid>
              </DesignSection>

              <ControlGroup
                title="Discount Popup"
                description="Optional promotional popup timing for homepage visitors."
                enabled={settings.enableCampaignPopup}
                onEnabledChange={(checked) => setSettings(prev => ({ ...prev, enableCampaignPopup: checked }))}
              >
                <DesignGrid className="xl:grid-cols-4">
                  <RangeControl label="Delay" value={settings.campaignPopupDelaySeconds} min={0} max={60} step={1} unit="s" onChange={(value) => setSettings(prev => ({ ...prev, campaignPopupDelaySeconds: Math.max(0, Math.min(60, Number(value))) }))} />
                </DesignGrid>
              </ControlGroup>

              <DesignSection title="Admin Notifications" description="Realtime alert sound settings for this browser and admin account.">
                <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(220px,280px)_auto_minmax(280px,1fr)] xl:items-end">
                  <SelectControl
                    label="Sound"
                    value={notificationSoundPreference}
                    onChange={(value) => {
                      if (typeof onNotificationSoundPreferenceChange === 'function') onNotificationSoundPreferenceChange(value);
                    }}
                    options={[
                      { value: 'classic', label: 'Classic' },
                      { value: 'custom', label: 'Custom' },
                      { value: 'beep', label: 'Beep' },
                      { value: 'off', label: 'Off' }
                    ]}
                  />
                  <button type="button" onClick={() => { if (typeof onPreviewNotificationSound === 'function') onPreviewNotificationSound(); }} className="inline-flex h-10 items-center justify-center rounded-md border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50">Preview</button>
                  <MediaPicker
                    label="Custom Sound"
                    value={settings.notificationSoundUrl || ''}
                    onChange={(url) => {
                      const next = String(url || '').trim();
                      setSettings(prev => ({ ...prev, notificationSoundUrl: next }));
                      if (typeof onNotificationSoundUrlChange === 'function') onNotificationSoundUrlChange(next);
                    }}
                    helpText="Upload/select MP3 or WAV from media library."
                    accept="*/*"
                    ratioHint="Audio"
                  />
                </div>
              </DesignSection>

              {canRestartSystem && (
                <DesignSection title="Container Restart" description="Permission-protected backend container restart. Use only when deployment/runtime changes require it.">
                  <div className="grid grid-cols-1 gap-3 rounded-lg border border-red-200 bg-red-50 p-3 md:grid-cols-[minmax(220px,280px)_auto_1fr] md:items-end">
                    <DesignField label="Safety Confirmation" help="Type RESTART to enable the restart action.">
                      <input type="text" value={restartConfirmText} onChange={(event) => setRestartConfirmText(event.target.value)} placeholder="RESTART" className="w-full rounded-md border border-red-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" autoComplete="off" />
                    </DesignField>
                    <button type="button" onClick={handleRestartServices} disabled={saving || restartingServices || !isRestartArmed} className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50">
                      {restartingServices && <Icon.Loading className="h-4 w-4" />}
                      {restartingServices ? 'Restarting...' : 'Restart Backend'}
                    </button>
                    <div className="text-xs text-red-700">This action is audited and restarts only the backend container, not the VPS.</div>
                  </div>
                </DesignSection>
              )}
            </div>
          )}
        </form>
        )}
      </div>
    </div>
  );
}

// OrderDetailsModal component for viewing and editing order details, including tracking URL update

function OrderDetailsModal({ order, onClose, onUpdate }) {
  const [trackingUrl, setTrackingUrl] = useState(order.tracking_url || '');
  const [savingTracking, setSavingTracking] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({
    bkashCashoutEnabled: false,
    bkashCashoutAmount: 20
  });
  const { request } = useApi();
  const { addToast } = useToasts();

  useEffect(() => {
    request('/settings')
      .then((data) => {
        setSettings({
          bkashCashoutEnabled: data?.bkashCashoutEnabled === true,
          bkashCashoutAmount: Math.max(0, Number(data?.bkashCashoutAmount ?? 20) || 20)
        });
      })
      .catch(() => {});
    request('/products')
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [request]);

  const buildDraft = useCallback((o) => {
    const cust = o.customer || {};
    const name = cust.name || `${cust.firstName || ''} ${cust.lastName || ''}`.trim();
    const firstName = cust.firstName || (name ? name.split(' ')[0] : '');
    const lastName = cust.lastName || (name ? name.split(' ').slice(1).join(' ') : '');
    const incomingAdjustments = Array.isArray(o.manualAdjustments) ? o.manualAdjustments : [];
    const hasRequiredPaymentAmount =
      o.requiredPaymentAmount !== '' &&
      o.requiredPaymentAmount !== null &&
      o.requiredPaymentAmount !== undefined &&
      Number.isFinite(Number(o.requiredPaymentAmount));
    const resolvedRequiredPaymentAmount = hasRequiredPaymentAmount
      ? Math.max(0, Number(o.requiredPaymentAmount))
      : (incomingAdjustments.some((adj) => String(adj?.label || '') === REQUIRED_PAYMENT_OVERRIDE_LABEL)
        ? Math.max(0, Number(o.total) || 0)
        : '');
    return {
      status: o.status || 'pending',
      paymentStatus: o.payment_status || (o.payment?.method === 'bkash' ? 'pending' : 'unpaid'),
      fulfillmentStatus: o.fulfillment_status || 'unfulfilled',
      customer: {
        firstName,
        lastName,
        name: name || `${firstName} ${lastName}`.trim(),
        email: cust.email || '',
        phone: cust.phone || ''
      },
      shipping: { ...(o.shipping || {}) },
      payment: { ...(o.payment || {}) },
      items: (o.items || []).map(it => ({
        id: it.id,
        productId: it.productId || it.id,
        variantId: it.variantId || it.variant_id || null,
        variantLabel: it.variantLabel || null,
        variantSku: it.variantSku || null,
        variantOptions: it.variantOptions || null,
        title: it.title,
        price: Number(it.price) || 0,
        originalPrice: Number(it.originalPrice) > Number(it.price) ? Number(it.originalPrice) : null,
        qty: Math.max(1, parseInt(it.qty) || 1),
        img: it.img || null,
        category: it.category || null,
        color: it.color || null,
        stock: it.stock || null,
        customization: it.customization || null
      })),
      manualDiscountAmount: Math.max(0, Number(o.manual_discount_amount || 0)),
      requiredPaymentAmount: resolvedRequiredPaymentAmount,
      adminNotes: o.admin_notes || '',
      refundAmount: Number(o.refund_amount || 0),
      cancelReason: o.cancel_reason || ''
    };
  }, []);

  const [draft, setDraft] = useState(() => buildDraft(order));

  useEffect(() => {
    setDraft(buildDraft(order));
    setTrackingUrl(order.tracking_url || '');
    setEditMode(false);
  }, [order, buildDraft]);

  // Extract details
  const customer = order.customer || {};
  const shipping = order.shipping || {};
  const payment = order.payment || {};
  const items = useMemo(() => (Array.isArray(order.items) ? order.items : []), [order.items]);
  const productLookup = useMemo(
    () => new Map((Array.isArray(products) ? products : []).map((product) => [String(product.id || '').trim(), product])),
    [products]
  );
  const statusHistory = Array.isArray(order.statusHistory) ? order.statusHistory : [];
  const orderAdjustments = useMemo(() => (
    Array.isArray(order.manualAdjustments)
      ? order.manualAdjustments.filter((adj) => String(adj?.label || '') !== REQUIRED_PAYMENT_OVERRIDE_LABEL)
      : []
  ), [order.manualAdjustments]);
  const computedSubtotal = items.reduce((sum, it) => sum + (Number(it.price) || 0) * (Number(it.qty) || 0), 0);
  const computedProductDiscount = items.reduce((sum, it) => {
    const qty = Math.max(1, Number(it?.qty) || 1);
    const price = Math.max(0, Number(it?.price) || 0);
    const originalPrice = Math.max(0, Number(it?.originalPrice) || 0);
    return sum + (originalPrice > price ? (originalPrice - price) * qty : 0);
  }, 0);
  const computedOriginalSubtotal = computedSubtotal + computedProductDiscount;
  const computedAdjustments = orderAdjustments.reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0);
  const computedGrandTotal = Number.isFinite(Number(order.total))
    ? Math.max(0, Number(order.total))
    : Math.max(0, computedSubtotal + (Number(shipping.cost) || 0) + computedAdjustments - (Number(order.discount_amount) || 0) - (Number(order.manual_discount_amount) || 0));
  const discountedSummaryItems = useMemo(() => {
    const safeItems = Array.isArray(items) ? items : [];
    const safeDiscount = Math.max(0, Number(order.discount_amount || 0));
    const pricedItems = safeItems.map((item, index) => {
      const qty = Math.max(1, Number(item?.qty) || 1);
      const unitPrice = Math.max(0, Number(item?.price) || 0);
      const lineTotal = unitPrice * qty;
      return { item, index, qty, unitPrice, lineTotal };
    });
    const subtotal = pricedItems.reduce((sum, entry) => sum + entry.lineTotal, 0);
    if (!safeDiscount || subtotal <= 0) {
      return pricedItems.map(({ item, qty, unitPrice, lineTotal }) => ({
        ...item,
        qty,
        originalUnitPrice: null,
        effectiveUnitPrice: unitPrice,
        originalLineTotal: null,
        effectiveLineTotal: lineTotal
      }));
    }

    const cappedDiscount = Math.min(safeDiscount, subtotal);
    const allocations = pricedItems.map(({ lineTotal, index }) => {
      const exact = lineTotal > 0 ? (lineTotal / subtotal) * cappedDiscount : 0;
      const base = Math.floor(exact);
      return { index, base, remainder: exact - base };
    });
    let remaining = Math.round(cappedDiscount - allocations.reduce((sum, entry) => sum + entry.base, 0));
    allocations
      .slice()
      .sort((a, b) => b.remainder - a.remainder || a.index - b.index)
      .forEach((entry) => {
        if (remaining <= 0) return;
        entry.base += 1;
        remaining -= 1;
      });
    const discountByIndex = new Map(allocations.map((entry) => [entry.index, entry.base]));

    return pricedItems.map(({ item, index, qty, unitPrice, lineTotal }) => {
      const lineDiscount = Math.max(0, Number(discountByIndex.get(index) || 0));
      const effectiveLineTotal = Math.max(0, lineTotal - lineDiscount);
      return {
        ...item,
        qty,
        originalUnitPrice: lineDiscount > 0 ? unitPrice : null,
        effectiveUnitPrice: effectiveLineTotal / qty,
        originalLineTotal: lineDiscount > 0 ? lineTotal : null,
        effectiveLineTotal
      };
    });
  }, [items, order.discount_amount]);
  const normalizeTrackingLink = useCallback((value) => {
    const raw = String(value || '').trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;
    if (/^[a-z]+:\/\//i.test(raw)) return raw;
    if (raw.includes('.') && !/\s/.test(raw)) return `https://${raw}`;
    return null;
  }, []);

  const handleSaveTrackingUrl = async () => {
    setSavingTracking(true);
    try {
      await request(`/orders/${order.id}/tracking`, {
        method: 'PUT',
        body: JSON.stringify({ trackingUrl: trackingUrl.trim() })
      });
      addToast('Tracking URL updated successfully', 'success');
      const updatedOrder = { ...order, tracking_url: trackingUrl.trim() };
      onUpdate(updatedOrder);
    } catch (err) {
      addToast('Failed to update tracking URL', 'error');
    } finally {
      setSavingTracking(false);
    }
  };

  const draftTotals = useMemo(() => {
    const itemsSubtotal = (draft.items || []).reduce((sum, it) => sum + (Number(it.price) || 0) * (Number(it.qty) || 0), 0);
    const productDiscount = (draft.items || []).reduce((sum, it) => {
      const qty = Math.max(1, Number(it?.qty) || 1);
      const price = Math.max(0, Number(it?.price) || 0);
      const originalPrice = Math.max(0, Number(it?.originalPrice) || 0);
      return sum + (originalPrice > price ? (originalPrice - price) * qty : 0);
    }, 0);
    const shippingCost = Number(draft.shipping?.cost || 0);
    const adjustments = orderAdjustments.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
    const discount = Math.max(0, Number(order.discount_amount || 0));
    const manualDiscount = Math.max(0, Number(draft.manualDiscountAmount || 0));
    const paymentCashoutCharge = draft.payment?.method === 'bkash' && settings.bkashCashoutEnabled
      ? Math.max(0, Number(draft.payment?.cashoutCharge ?? settings.bkashCashoutAmount) || 0)
      : 0;
    const hasRequiredPaymentAmount = draft.requiredPaymentAmount !== '' && draft.requiredPaymentAmount !== null && Number.isFinite(Number(draft.requiredPaymentAmount));
    const total = hasRequiredPaymentAmount
      ? Math.max(0, Math.round(Number(draft.requiredPaymentAmount)))
      : Math.max(0, Math.round(itemsSubtotal + shippingCost + adjustments + paymentCashoutCharge - discount - manualDiscount));
    return { itemsSubtotal, productDiscount, shippingCost, adjustments, discount, manualDiscount, paymentCashoutCharge, hasRequiredPaymentAmount, total };
  }, [draft, order.discount_amount, orderAdjustments, settings]);

  const saveOrderChanges = async () => {
    setSavingOrder(true);
    try {
      const now = new Date().toISOString();
      const resolvedPaymentStatus = draft.status === 'delivered' ? 'paid' : draft.paymentStatus;
      const payload = {
        status: draft.status,
        paymentStatus: resolvedPaymentStatus,
        fulfillmentStatus: draft.fulfillmentStatus,
        customer: {
          firstName: draft.customer.firstName,
          lastName: draft.customer.lastName,
          name: `${draft.customer.firstName || ''} ${draft.customer.lastName || ''}`.trim() || draft.customer.name,
          email: draft.customer.email,
          phone: draft.customer.phone
        },
        shipping: { ...draft.shipping },
        payment: { ...draft.payment },
        items: draft.items,
        manualDiscountAmount: Math.max(0, Number(draft.manualDiscountAmount) || 0),
        requiredPaymentAmount: draftTotals.hasRequiredPaymentAmount ? Number(draft.requiredPaymentAmount) : null,
        adminNotes: draft.adminNotes,
        refundAmount: draft.refundAmount,
        cancelReason: draft.cancelReason,
        shippingOverride: Number(draft.shipping?.cost || 0)
      };
      await request(`/orders/${order.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      const nextHistory = Array.isArray(statusHistory) ? [...statusHistory] : [];
      if (draft.status !== order.status) {
        nextHistory.push({ at: now, action: 'status_update', from: order.status, to: draft.status, by: 'admin' });
      }
      if (resolvedPaymentStatus !== order.payment_status) {
        nextHistory.push({ at: now, action: 'payment_status', from: order.payment_status, to: resolvedPaymentStatus, by: 'admin' });
      }
      if (draft.fulfillmentStatus !== order.fulfillment_status) {
        nextHistory.push({ at: now, action: 'fulfillment_status', from: order.fulfillment_status, to: draft.fulfillmentStatus, by: 'admin' });
      }
      nextHistory.push({ at: now, action: 'order_edit', by: 'admin' });
      const updatedOrder = {
        ...order,
        status: draft.status,
        payment_status: resolvedPaymentStatus,
        fulfillment_status: draft.fulfillmentStatus,
        customer: payload.customer,
        shipping: payload.shipping,
        payment: payload.payment,
        items: payload.items,
        manual_discount_amount: payload.manualDiscountAmount,
        manualAdjustments: order.manualAdjustments,
        requiredPaymentAmount: payload.requiredPaymentAmount,
        admin_notes: payload.adminNotes,
        refund_amount: payload.refundAmount,
        cancel_reason: payload.cancelReason,
        statusHistory: nextHistory,
        total: draftTotals.total
      };
      onUpdate(updatedOrder);
      addToast('Order updated', 'success');
      setEditMode(false);
    } catch (err) {
      addToast('Failed to update order', 'error');
    } finally {
      setSavingOrder(false);
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[220] bg-black/60 backdrop-blur-[1px] flex items-start sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-7xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Order Details</h2>
            <p className="text-sm text-gray-500 mt-1">Order ID: {order.display_order_id || order.id}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setEditMode(true)}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50"
            >
              Edit order
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <Icon.X className="w-6 h-6" />
            </button>
          </div>
        </div>
        <div className="p-6 overflow-y-auto min-h-0">
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-6">
            <div className="space-y-6">
          {/* Customer Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Customer</h3>
              <div className="text-gray-800 font-semibold">{customer.name || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || '-'}</div>
              <div className="text-gray-600 text-sm">Email: {customer.email}</div>
              <div className="text-gray-600 text-sm">Phone: {customer.phone}</div>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Shipping</h3>
              <div className="text-gray-800">{shipping.address || shipping.house}, {shipping.city}</div>
              <div className="text-gray-600 text-sm">Option: {shipping.option || shipping.location || '-'}</div>
              <div className="text-gray-600 text-sm">Cost: {shipping.cost ? `${shipping.cost}৳` : '-'}</div>
            </div>
          </div>
          {/* Order Status + Payment */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Order Status</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-gray-500">Status</div>
                <div className="text-gray-900 font-medium">{order.status}</div>
              </div>
              <div>
                <div className="text-gray-500">Payment status</div>
                <div className="text-gray-900 font-medium">{order.payment_status || (payment.method === 'bkash' ? 'pending' : 'unpaid')}</div>
              </div>
              <div>
                <div className="text-gray-500">Fulfillment status</div>
                <div className="text-gray-900 font-medium">{order.fulfillment_status || 'unfulfilled'}</div>
              </div>
              <div>
                <div className="text-gray-500">Payment method</div>
                <div className="text-gray-900 font-medium">{payment.method || '-'}</div>
              </div>
              <div>
                <div className="text-gray-500">Created</div>
                <div className="text-gray-900 font-medium">{order.created_at ? new Date(order.created_at).toLocaleString() : '-'}</div>
              </div>
              {payment.bkashFrom && (
                <div>
                  <div className="text-gray-500">bKash from</div>
                  <div className="text-gray-900 font-medium">{payment.bkashFrom}</div>
                </div>
              )}
              {payment.bkashTxn && (
                <div>
                  <div className="text-gray-500">bKash transaction</div>
                  <div className="text-gray-900 font-medium">{payment.bkashTxn}</div>
                </div>
              )}
            </div>
            {payment.bkashScreenshotUrl && (
              <div className="mt-4">
                <div className="text-sm text-gray-700 mb-1">Payment screenshot</div>
                <a href={(String(payment.bkashScreenshotUrl).startsWith('/uploads') ? `/shopapi${payment.bkashScreenshotUrl}` : payment.bkashScreenshotUrl)} target="_blank" rel="noopener noreferrer">
                  <img src={(String(payment.bkashScreenshotUrl).startsWith('/uploads') ? `/shopapi${payment.bkashScreenshotUrl}` : payment.bkashScreenshotUrl)} alt="bKash receipt" className="h-24 w-24 object-cover rounded border" />
                </a>
              </div>
            )}
          </div>
          {editMode && (
            <div
              className="fixed inset-0 z-[230] bg-black/60 flex items-center justify-center p-4"
              onClick={() => {
                setDraft(buildDraft(order));
                setEditMode(false);
              }}
            >
              <div
                className="bg-white rounded-lg shadow-lg w-full max-w-6xl max-h-[92vh] overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">Edit Order</h3>
                    <p className="text-sm text-gray-500 mt-1">Order ID: {order.display_order_id || order.id}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={saveOrderChanges} disabled={savingOrder} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">{savingOrder && <Icon.Loading className="h-4 w-4" />}{savingOrder ? 'Saving...' : 'Save changes'}</button>
                    <button type="button" onClick={() => { setDraft(buildDraft(order)); setEditMode(false); }} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100" aria-label="Close order editor"><Icon.X className="h-5 w-5" /></button>
                  </div>
                </div>
                <div className="p-6 overflow-y-auto max-h-[calc(92vh-96px)]">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Order status</label>
                    <select
                      value={draft.status}
                      onChange={(e) => setDraft(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment status</label>
                    <select
                      value={draft.paymentStatus}
                      onChange={(e) => setDraft(prev => ({ ...prev, paymentStatus: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="unpaid">Unpaid</option>
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="refunded">Refunded</option>
                      <option value="partial_refund">Partial Refund</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fulfillment status</label>
                    <select
                      value={draft.fulfillmentStatus}
                      onChange={(e) => setDraft(prev => ({ ...prev, fulfillmentStatus: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="unfulfilled">Unfulfilled</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="returned">Returned</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-900">Customer</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={draft.customer.firstName}
                        onChange={(e) => setDraft(prev => ({ ...prev, customer: { ...prev.customer, firstName: e.target.value } }))}
                        className="px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="First name"
                      />
                      <input
                        type="text"
                        value={draft.customer.lastName}
                        onChange={(e) => setDraft(prev => ({ ...prev, customer: { ...prev.customer, lastName: e.target.value } }))}
                        className="px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="Last name"
                      />
                    </div>
                    <input
                      type="email"
                      value={draft.customer.email}
                      onChange={(e) => setDraft(prev => ({ ...prev, customer: { ...prev.customer, email: e.target.value } }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Email"
                    />
                    <input
                      type="text"
                      value={draft.customer.phone}
                      onChange={(e) => setDraft(prev => ({ ...prev, customer: { ...prev.customer, phone: e.target.value } }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-900">Shipping</h4>
                    <input
                      type="text"
                      value={draft.shipping.house || draft.shipping.address || ''}
                      onChange={(e) => setDraft(prev => ({ ...prev, shipping: { ...prev.shipping, house: e.target.value, address: e.target.value } }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Address"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={draft.shipping.city || ''}
                        onChange={(e) => setDraft(prev => ({ ...prev, shipping: { ...prev.shipping, city: e.target.value } }))}
                        className="px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="City"
                      />
                      <input
                        type="number"
                        min="0"
                        value={Number(draft.shipping.cost || 0)}
                        onChange={(e) => setDraft(prev => ({ ...prev, shipping: { ...prev.shipping, cost: parseInt(e.target.value) || 0 } }))}
                        className="px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="Shipping cost"
                      />
                    </div>
                    <input
                      type="text"
                      value={draft.shipping.option || ''}
                      onChange={(e) => setDraft(prev => ({ ...prev, shipping: { ...prev.shipping, option: e.target.value } }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Shipping option"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-900">Payment</h4>
                    <select
                      value={draft.payment.method || 'cod'}
                      onChange={(e) => setDraft(prev => ({ ...prev, payment: { ...prev.payment, method: e.target.value } }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="cod">Cash on Delivery</option>
                      <option value="bkash">bKash</option>
                      <option value="bank">Bank transfer</option>
                    </select>
                    {draft.payment.method === 'bkash' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={draft.payment.bkashFrom || ''}
                          onChange={(e) => setDraft(prev => ({ ...prev, payment: { ...prev.payment, bkashFrom: e.target.value } }))}
                          className="px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="bKash from"
                        />
                        <input
                          type="text"
                          value={draft.payment.bkashTxn || ''}
                          onChange={(e) => setDraft(prev => ({ ...prev, payment: { ...prev.payment, bkashTxn: e.target.value } }))}
                          className="px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="bKash txn"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Special Discount (৳)</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={draft.manualDiscountAmount}
                        onChange={(e) => setDraft(prev => ({ ...prev, manualDiscountAmount: Math.max(0, Number(e.target.value) || 0) }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="0"
                      />
                      <div className="mt-1 text-xs text-gray-500">Applies only to this order and appears separately on its invoice.</div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Required payment amount</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={draft.requiredPaymentAmount}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setDraft(prev => ({
                            ...prev,
                            requiredPaymentAmount: raw === '' ? '' : Math.max(0, Number(raw) || 0)
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="Leave empty to auto-calculate"
                      />
                      <div className="mt-1 text-xs text-gray-500">Set an exact amount to collect for this order.</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-900">Admin notes</h4>
                    <textarea
                      rows={3}
                      value={draft.adminNotes}
                      onChange={(e) => setDraft(prev => ({ ...prev, adminNotes: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Internal notes visible to admins only"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900">Line items</h4>
                  <div className="overflow-x-auto border border-gray-200 rounded-md bg-white">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Item</th>
                          <th className="px-3 py-2 text-left">Qty</th>
                          <th className="px-3 py-2 text-left">Price</th>
                          <th className="px-3 py-2 text-left">Total</th>
                          <th className="px-3 py-2"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {draft.items.map((it, idx) => (
                          <tr key={it.id || idx}>
                            <td className="px-3 py-2">
                              <div className="space-y-1">
                                <input
                                  type="text"
                                  value={it.title}
                                  onChange={(e) => {
                                    const next = [...draft.items];
                                    next[idx] = { ...next[idx], title: e.target.value };
                                    setDraft(prev => ({ ...prev, items: next }));
                                  }}
                                  className="w-full px-2 py-1 border border-gray-300 rounded-md"
                                />
                                {getAdminOrderOptionLabel(it, productLookup) && (
                                  <div className="text-xs text-gray-500">
                                    Option: {getAdminOrderOptionLabel(it, productLookup)}
                                  </div>
                                )}
                                {it.variantSku && (
                                  <div className="text-xs text-gray-500">SKU: {it.variantSku}</div>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2 w-24">
                              <input
                                type="number"
                                min="1"
                                value={it.qty}
                                onChange={(e) => {
                                  const next = [...draft.items];
                                  next[idx] = { ...next[idx], qty: Math.max(1, parseInt(e.target.value) || 1) };
                                  setDraft(prev => ({ ...prev, items: next }));
                                }}
                                className="w-full px-2 py-1 border border-gray-300 rounded-md"
                              />
                            </td>
                            <td className="px-3 py-2 w-28">
                              <input
                                type="number"
                                min="0"
                                value={it.price}
                                onChange={(e) => {
                                  const next = [...draft.items];
                                  next[idx] = { ...next[idx], price: Number(e.target.value) || 0 };
                                  setDraft(prev => ({ ...prev, items: next }));
                                }}
                                className="w-full px-2 py-1 border border-gray-300 rounded-md"
                              />
                            </td>
                            <td className="px-3 py-2">{currency((Number(it.price) || 0) * (Number(it.qty) || 0))}</td>
                            <td className="px-3 py-2 text-right">
                              <button
                                type="button"
                                onClick={() => setDraft(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }))}
                                className="text-red-600 hover:text-red-800 text-xs"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDraft(prev => ({ ...prev, items: [...prev.items, { id: `manual_${Date.now()}`, title: 'New item', price: 0, qty: 1 }] }))}
                    className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50"
                  >
                    Add item
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cancel reason</label>
                    <input
                      type="text"
                      value={draft.cancelReason}
                      onChange={(e) => setDraft(prev => ({ ...prev, cancelReason: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Reason for cancellation"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Refund amount</label>
                    <input
                      type="number"
                      min="0"
                      value={draft.refundAmount}
                      onChange={(e) => setDraft(prev => ({ ...prev, refundAmount: Number(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="rounded-md border border-gray-200 bg-white p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span>Items subtotal</span>
                    <span className="font-medium">{currency(draftTotals.itemsSubtotal + draftTotals.productDiscount)}</span>
                  </div>
                  {draftTotals.productDiscount > 0 && (
                    <div className="flex items-center justify-between text-green-700">
                      <span>Product discount</span>
                      <span className="font-medium">-{currency(draftTotals.productDiscount)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span>Shipping</span>
                    <span className="font-medium">{currency(draftTotals.shippingCost)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Manual adjustments</span>
                    <span className="font-medium">{currency(draftTotals.adjustments)}</span>
                  </div>
                  {draftTotals.paymentCashoutCharge > 0 && (
                    <div className="flex items-center justify-between">
                      <span>bKash cashout charge</span>
                      <span className="font-medium">{currency(draftTotals.paymentCashoutCharge)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span>Sale / promotion discount{order.promo_code ? ` (${order.promo_code})` : ''}</span>
                    <span className="font-medium text-green-700">-{currency(draftTotals.discount)}</span>
                  </div>
                  {draftTotals.manualDiscount > 0 && (
                    <div className="flex items-center justify-between text-green-700">
                      <span>Special discount</span>
                      <span className="font-medium">-{currency(draftTotals.manualDiscount)}</span>
                    </div>
                  )}
                  {draftTotals.hasRequiredPaymentAmount && (
                    <div className="flex items-center justify-between">
                      <span>Amount to collect</span>
                      <span className="font-medium text-blue-700">{currency(draftTotals.total)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-base font-semibold mt-1">
                    <span>Total</span>
                    <span>{currency(draftTotals.total)}</span>
                  </div>
                </div>

              </div>
                </div>
              </div>
            </div>
          )}
          {statusHistory.length > 0 && (
            <div className="rounded-lg border border-gray-200 p-4 bg-white">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Activity</h3>
              <div className="space-y-2 text-sm text-gray-600 max-h-40 overflow-auto">
                {statusHistory.slice().reverse().map((entry, idx) => (
                  <div key={`${entry.at}-${idx}`} className="flex items-start gap-2">
                    <div className="text-xs text-gray-400 min-w-[120px]">{entry.at ? new Date(entry.at).toLocaleString() : '-'}</div>
                    <div>
                      <div className="text-gray-800">
                        {entry.action || 'update'} {entry.from ? `: ${entry.from}` : ''}{entry.to ? ` → ${entry.to}` : ''}
                      </div>
                      {entry.by && <div className="text-xs text-gray-500">by {entry.by}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
          <aside className="space-y-4 xl:sticky xl:top-0 self-start">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Summary</h3>
              <div className="mb-3 pb-3 border-b border-gray-200">
                <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Items</h4>
                {items.length === 0 ? (
                  <div className="text-sm text-gray-500">No items</div>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-auto pr-1">
                    {discountedSummaryItems.map((item, idx) => (
                      <div key={`${item.id || item.title || 'item'}-${idx}`} className="flex items-start justify-between gap-3 text-sm">
                        <div className="min-w-0">
                          <div className="text-gray-900 font-medium truncate">{item.title || 'Untitled item'}</div>
                          {getAdminOrderOptionLabel(item, productLookup) && (
                            <div className="text-xs text-gray-500">Option: {getAdminOrderOptionLabel(item, productLookup)}</div>
                          )}
                          {item.variantSku && <div className="text-xs text-gray-500">SKU: {item.variantSku}</div>}
                          <div className="text-xs text-gray-500">{item.qty}x</div>
                        </div>
                        <AdminPriceDisplay
                          price={item.effectiveLineTotal}
                          originalPrice={item.originalLineTotal}
                          compact
                          align="right"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex items-center justify-between text-gray-700">
                  <span>Subtotal</span>
                  <span className="font-medium">{currency(computedOriginalSubtotal)}</span>
                </div>
                {computedProductDiscount > 0 && (
                  <div className="flex items-center justify-between text-green-700">
                    <span>Product discount</span>
                    <span className="font-medium">-{currency(computedProductDiscount)}</span>
                  </div>
                )}
                {order.discount_amount > 0 && (
                  <div className="flex items-center justify-between text-green-700">
                    <span>Sale / promotion discount{order.promo_code ? ` (${order.promo_code})` : ''}</span>
                    <span className="font-medium">-{currency(order.discount_amount)}</span>
                  </div>
                )}
                {Number(order.manual_discount_amount || 0) > 0 && (
                  <div className="flex items-center justify-between text-green-700">
                    <span>Special discount</span>
                    <span className="font-medium">-{currency(order.manual_discount_amount)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-gray-700">
                  <span>Shipping</span>
                  <span className="font-medium">{currency(shipping.cost || 0)}</span>
                </div>
                <div className="flex items-center justify-between text-base font-semibold pt-2 mt-2 border-t border-gray-200">
                  <span>Grand Total</span>
                  <span>{currency(computedGrandTotal)}</span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-2">Tracking</h3>
              <input
                type="text"
                value={trackingUrl}
                onChange={(e) => setTrackingUrl(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                placeholder="Enter tracking URL or tracking number"
              />
              <button
                onClick={handleSaveTrackingUrl}
                disabled={savingTracking}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {savingTracking && <Icon.Loading className="w-4 h-4" />}
                {savingTracking ? 'Saving...' : 'Save Tracking'}
              </button>
              {(order.tracking_url || trackingUrl) && (
                <div className="mt-2">
                  {normalizeTrackingLink(order.tracking_url || trackingUrl) ? (
                    <a href={normalizeTrackingLink(order.tracking_url || trackingUrl)} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-sm">
                      Open Tracking
                    </a>
                  ) : (
                    <div className="text-sm text-gray-600 break-all">{order.tracking_url || trackingUrl}</div>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-2">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-1">Quick Actions</h3>
              <button
                onClick={() => openShippingPaper(order.id)}
                className="w-full px-3 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
              >
                Print shipping slip
              </button>
              <button
                onClick={() => openInvoice(order.id)}
                className="w-full px-3 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
              >
                See invoice
              </button>
            </div>
          </aside>
        </div>
      </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modalContent;
  return createPortal(modalContent, document.body);
}



















